import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePullToRefresh } from '../../utils/pullToRefresh/usePullToRefresh';
import { PullToRefreshIndicator } from '../../utils/pullToRefresh/PullToRefreshIndicator';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useSharedReportDateFilter } from '../../redux/reportFilters/reportFiltersHooks';
import { fetchLiveByChannelRequest, fetchLiveBySourceRequest, fetchLiveItemSalesV2Request, fetchSnapshotDetailsRequest, fetchSnapshotComparisonRequest, fetchLiveRefundDetailsRequest, fetchLiveRefundSummaryRequest, fetchLiveVoidSummaryRequest, fetchVoidByStaffRequest, fetchVoidKpisRequest, fetchLiveVoidDetailsRequest, fetchHourlySalesRequest, fetchLivePaymentsRequest, fetchLiveStaffPerformanceRequest, fetchLiveStaffActiveClockingRequest, fetchLiveStaffActiveClockingV2Request, fetchLiveUnpaidOrdersRequest, fetchLiveUnpaidLogRequest, fetchLiveCompOrderLogRequest, fetchLiveCompReasonsRequest, fetchLiveCompByStaffRequest, fetchOrderLogRequest, exportOrderLogRequest, fetchOrderDetailRequest, clearOrderDetail, fetchChannelNamesRequest, fetchLiveStaffRefundsRequest, fetchCancelledOrderLogRequest } from '../../redux/reports/clickHouse/clickHouseActions';
import { getLiveUnpaidOrdersApi, getOrderLogApi, getOrderLogExportApi, getLiveItemSalesV2Api, getLiveRefundDetailsApi, getLiveVoidDetailsApi, getLiveCompOrderLogApi, getLiveStaffActiveClockingV2Api, getSnapshotDetails, getLiveRefundSummaryApi, getLiveStaffActiveClockingApi, getLiveVoidSummaryApi, getLiveByChannelApi, getLivePaymentsApi } from '../../redux/reports/clickHouse/clickHouseApi';
import { formatMonetaryExport } from '../../utils/exportUtils';
import { escapeCsvCell, CSV_BOM, type ExportCell, type ExportFormat } from '../../utils/reportExport';
import { isNativePlatform, exportTableXlsxNative } from '../../utils/capacitorExport';
import { ReportSectionExportButton } from './components/ReportExportButton';
import { DataTableCard, SectionCard, type Col } from './components/DataTableCard';
import { OrderDetailSlider } from './components/OrderDetailSlider';
import {
    OrderLogTable, computeOrderLogTotals, OrderLogColumnsMenu, OrderLogStatusSegments,
    OrderLogCard, OrderLogDensityButton, useOrderLogCardDensity,
    buildOrderLogColumns,
    visibleColumnDefs, toOrderLogRows, toOrderLogRow,
    sortRowsClientSide, orderLogRowsToExportTable, orderLogExportTitle, cardViewExportColumns, SERVER_SORT_KEYS,
    useOrderLogColumns,
    type OrderLogColumnDef, type OrderLogStatus,
} from './components/OrderLog';
import { DiscountBreakdownAndLog } from './components/DiscountsDrilldown';
import { TipsGratuityDrill, buildTipsGratuityData, combineTgDeltaPct, TG_CARD_LABEL, TG_DRILL_TITLE } from './components/TipsGratuity';
// import DiscountsDrilldown from './components/DiscountsDrilldown';
import type { CompByStaffItem, CompOrderLogItem, CompReasonItem, HourlySalesResponse, LiveByChannelItem, LiveItemSalesV2Item, LivePaymentsItem, LiveRefundDetailsItem, LiveRefundSummaryItem, LiveStaffActiveClockingItem, LiveStaffActiveClockingV2Item, LiveStaffPerformanceItem, LiveStaffRefundsItem, LiveVoidByStaffItem, LiveVoidDetailsItem, LiveVoidKpisResponse, LiveVoidSummaryItem, OrderLogItem, OrderDetailResponse, SnapshotComparisonResponse, SnapshotDetailsResponse, UnpaidOrderItem } from '../../interface/clickHouseInterface';
import HourlyBarChart from './components/charts/reusable/HourlyBarChart';
import DoughnutChart, { computeDisplayPcts } from './components/charts/reusable/DoughnutChart';
import type { DoughnutSlice } from './components/charts/reusable/DoughnutChart';
import { SalesByChannelSourceCard } from './components/SalesByChannelSource';
import SalesWaterfall, { type WaterfallSource, type SalesWaterfallData, buildWaterfallData, getTotalPaidValue, getTotalPaidLabel, getTotalPaidSub } from './components/SalesWaterfall';
import { useRefundLogPeriod } from './refunds/useRefundLogPeriod';
import { waterfallRefundContextFromSnapshot, refundRatePct, REFUND_KPI_TOOLTIP, tipRefundsByCheckOwner, itemRefundsByCheckOwner } from './refunds/refundModel';
import { refundsPath } from './refunds/refundsPath';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import DrillNetSales from '../DrillNetSales';
import DrillOrders from '../DrillOrders';
import DrillCancelled from '../DrillCancelled';
import DrillStaff from '../DrillStaff';
import DrillVoids from '../DrillVoids';
import CancelledOrdersDrill from './drills/CancelledOrdersDrill';
import DrillTips from '../DrillTips';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import { getOwnerLocationCount } from '../../context/OwnerViewContext';
import { getCurrencySymbol, moneyHeader, formatGroupedMonetary, formatGroupedNumber, formatMoneyForExport } from '../../utils/currency';
import { useDesktopTopBar } from '../../context/DesktopLayoutContext';
import DesktopLayout from '../layout/DesktopLayout';
import TopBar from '../layout/TopBar';
import Phone from '../Phone';
import BottomNav from '../BottomNav';
import KPI from '../KPI';
import KpiTooltip from '../KpiTooltip';
import { useServiceChargeEnabled } from '../../hooks/useServiceChargeEnabled';
import { readServiceCharge, serviceChargeWaterfallInputs, serviceChargeTooltip, fmtServiceChargeMobile, SERVICE_CHARGE_LABEL } from './serviceCharge';
import StatKPI from '../StatKPI';
import Arrow from '../Arrow';
import Icon from '../Icon';
import { C } from '../theme';
import { useStoreTimeZone, getStoreTimeZone } from '../../utils/dateTimeUtils';
import { resolveTokenRange, normalizeToken, comparisonTooltip, hasComparison, exportPeriodFor } from '../../utils/reportPeriod';
import { isHistoricalReportDateFilter } from '../../utils/reportDateFilter';
import './reports.css';
import './SnapshotScreen.scss';
import { T, useIsDesktop, Bone, REPORT_BASIS_NOTE } from "./reportTokens";
import SectionError from "./SectionError";
import SectionEmpty from "./SectionEmpty";
import TruncatedCell from "./TruncatedCell";
import { makeReportTracker, type ReportTracker } from '../../utils/reportsAnalytics';
import { PIN_API_ENABLED, readLocalPins, writeLocalPins, fetchServerPins, saveServerPin, clearServerPins, type PinAction } from '../../services/snapshotPinsApi';
import { useSwipeBack } from '../../hooks/useSwipeBack';
import { useDrillHistoryBack } from '../../hooks/useDrillHistoryBack';
import {
    getSalesSummary,
    getSalesByChannel,
    getVoidedOrderSummary,
    getVoidedDetailsSummary,
    getDailyCheckInStatus,
    getCheckInOverviewTotals,
    getDailyGuest,
    getPeakSummary,
    getProductCategoryItemSales,
    getLiveCategoriesV2,
    getLiveItemSalesV2,
    getDiscountSummary,
    getEmployeeSalesOverview,
    getLiveBilledOrders,
    getLiveOrders,
    getLiveOrderNonDineIn,
    getPaidVoidedOrders,
    getPaidComplementaryOrders,
    getLiveVoidedByCategory,
    getPaymentDetails,
    getTenderTypes,
    getUnpaidOrdersSummary,
    getUnpaidOrdersLog,
    todayStr,
    yesterdayStr,
    isTodayRange,
} from '../../services/reportsApi';
import DiscountsDrilldown from './components/DiscountsDrilldown';

// Parse the staff-active-clocking `totalHours` field into decimal hours.
// The API returns it as an "H:MM" string (e.g. "0:12", "0:00"), so a plain
// Number() coercion yields NaN — sum it via H + M/60 instead. Also tolerates
// plain decimal strings/numbers as a fallback.
const parseClockHours = (v: unknown): number => {
    if (v == null) return 0;
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    const str = String(v).trim();
    if (!str) return 0;
    const colon = str.match(/^(\d+):(\d{1,2})$/);
    if (colon) return Number(colon[1]) + Number(colon[2]) / 60;
    const n = Number(str);
    return Number.isFinite(n) ? n : 0;
};

// =========================
// Desktop implementation
// =========================

// Desktop shimmer — real labels visible, only values shimmer
const DesktopShimmer = ({ isToday = false }: { isToday?: boolean }) => {
    // USSE-1: Check-Ins/Guests hidden in historical — keep shimmer labels in sync with the live KPI rows
    const kpiRow1 = isToday
        ? ['Paid orders', 'Unpaid orders', 'Avg Order', 'Service Charge', 'Refund', 'Tips & Grat']
        : ['Paid orders', 'Avg Order', 'Service Charge', 'Refund', 'Tips & Grat' /* , 'Check-Ins' */];
    const kpiRow2 = isToday
        ? ['Voids', 'Taxes', 'Discounts']
        : ['Voids', 'Complimentary', 'Discounts' /* , 'Guests' */];
    const bottomTitles = isToday ? ['Items Overview'] : ['Items Overview', 'Staff Leaderboard'];
    return (
        <div className="rpt-page">
            {/* Hero card */}
            <div className="snapshot-hero-card">
                <div className="snapshot-hero-label" style={{ marginBottom: 8 }}>Net Sales</div>
                <Bone w={200} h={36} mb={8} />
                <Bone w={120} h={11} />
            </div>
            {/* KPI row 1 */}
            <div className="snapshot-stat-row">
                {kpiRow1.map(label => (
                    <div key={label} className="snapshot-tile snapshot-tile--small" style={{ flex: 1, minWidth: 110 }}>
                        <div className="snapshot-tile-label">{label}</div>
                        <Bone w={90} h={28} mb={6} />
                        <Bone w={50} h={10} />
                    </div>
                ))}
            </div>
            {/* KPI row 2 */}
            <div className="snapshot-stat-row">
                {kpiRow2.map(label => (
                    <div key={label} className="snapshot-tile snapshot-tile--small" style={{ flex: 1, minWidth: 110 }}>
                        <div className="snapshot-tile-label">{label}</div>
                        <Bone w={65} h={22} />
                    </div>
                ))}
            </div>
            {/* Charts row — hidden in Today mode */}
            {!isToday && (
                <div className="snapshot-chart-row">
                    <div className="rpt-card rpt-card--r16" style={{ flex: 2 }}>
                        <div className="rpt-section-title">Hourly Revenue</div>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120 }}>
                            {Array.from({ length: 12 }).map((_, i) => (
                                <Bone key={i} w={null} h={20 + ((i * 37 + 17) % 90)} r={4} style={{ flex: 1 }} />
                            ))}
                        </div>
                    </div>
                    <div className="rpt-card rpt-card--r16" style={{ flex: 1 }}>
                        <div className="rpt-section-title">Sales by Channel</div>
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                                <Bone w={80} h={12} />
                                <Bone w={55} h={12} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {/* Bottom row */}
            <div className="rpt-flex-row rpt-gap-16">
                {bottomTitles.map(title => (
                    <div key={title} className="rpt-card" style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.t1, marginBottom: 14 }}>{title}</div>
                        {[1, 2, 3, 4, 5].map(j => (
                            <div key={j} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <Bone w={90} h={11} />
                                <Bone w={55} h={11} />
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

// Mobile shimmer — real labels visible, only values shimmer
const MobileShimmer = ({ isToday = false }: { isToday?: boolean }) => {
    const kpiRows = isToday
        ? [
            ['Paid orders', 'Avg Order', 'Service Charge'],
            ['Refund', 'Voids', 'Tips & Grat'],
            ['Taxes', 'Discounts'],
        ]
        : [
            // USSE-1: Check-Ins/Guests hidden in historical — keep shimmer rows in sync with the live KPI grid
            ['Paid orders', 'Avg Order', 'Service Charge'],
            ['Refund', 'Voids', 'Tips & Grat'],
            ['Compl', 'Discounts'],
            // ['Discounts', 'Check-Ins'],
            // ['Guests'],
        ];
    return (
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 16px' }}>
            {/* Hero card */}
            <div style={{
                background: `linear-gradient(135deg, ${C.accent}0D, ${C.purple ?? '#7c3aed'}0A)`,
                border: `1px solid ${C.accent}22`, borderRadius: 16, padding: '16px 18px', marginBottom: 10,
                position: 'relative', overflow: 'hidden', boxShadow: C.shadow,
            }}>
                <div style={{ fontSize: 10, color: C.t2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Net Sales</div>
                <Bone w={160} h={30} mb={6} />
                <Bone w={100} h={9} />
            </div>
            {/* KPI grid rows */}
            {kpiRows.map((row, ri) => (
                <div key={ri} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                    {row.map(label => (
                        <div key={label} style={{ background: C.card, borderRadius: 12, padding: '12px 10px', border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow }}>
                            <div style={{ fontSize: 8, fontWeight: 600, color: C.t3, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
                            <Bone w={55} h={20} />
                        </div>
                    ))}
                </div>
            ))}
            {/* Top items */}
            <div style={{ background: C.card, borderRadius: 12, padding: '12px 14px', border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Top 3 Items</div>
                {[1, 2, 3].map(i => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < 3 ? `1px solid ${C.divider}` : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 18, height: 18, borderRadius: 5, background: i === 1 ? C.accentSoft : C.hover, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: i === 1 ? C.accent : C.t3 }}>{i}</span>
                            <Bone w={80} h={11} />
                        </div>
                        <Bone w={55} h={11} />
                    </div>
                ))}
            </div>
        </div>
    );
};

const findToken = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    const tokenKeys = ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'bearerToken', 'authToken', 'userToken', 'sessionToken', 'Authorization', 'authorization', 'idToken', 'id_token'];
    for (const key of tokenKeys) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const findLocationId = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['locationId', 'location_id', 'locationID', 'locId', 'outletId', 'outlet_id']) {
        if (obj[key] && typeof obj[key] === 'string') return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findLocationId(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const unwrapRes = (res: any) => {
    if (res == null) return res;
    const d = res.metaDataInfo?.data ?? res.responseBody ?? res.content ?? res.data ?? res.result ?? res.response;
    return d != null && d !== res ? d : res;
};

const pick = (res: any, ...keys: any[]) => {
    if (res == null) return null;
    for (const key of keys) {
        const v = res?.[key];
        if (v !== undefined && v !== null) return v;
    }
    const r = unwrapRes(res);
    for (const key of keys) {
        const v = r?.[key];
        if (v !== undefined && v !== null) return v;
    }
    const raw = Array.isArray(r) ? r : (r?.content ?? r?.data ?? r);
    const obj = Array.isArray(raw) ? raw[0] : raw;
    if (!obj || typeof obj !== 'object') return null;
    for (const key of keys) {
        const v = obj[key];
        if (v !== undefined && v !== null) return v;
    }
    // Fuzzy key matching removed — it caused false matches (e.g. 'revenue' matching 'avgRevenuePerHour',
    // 'netSales' matching 'netSalesYesterday' or 'netSalesChangePercent'). All snapshot-summary fields
    // are now matched by exact key name only (via the loops above).
    return null;
};

const pickFromList = (res: any, key: any) => {
    const list = listFrom(res);
    if (!list.length) return null;
    return list[0]?.[key] ?? list[0]?.value ?? list[0]?.amount ?? list[0]?.total ?? null;
};

const listFrom = (res: any) => {
    const r = unwrapRes(res);
    let raw = r?.content ?? r?.data ?? r?.items ?? r?.records ?? r?.categories ?? r?.channelList ?? r?.employees ?? r?.staff ?? r?.staffList ?? r?.staffTips ?? r?.tipDetails ?? r?.voids ?? r?.voidList ?? r?.voidDetails ?? r?.voidItems ?? r?.orders ?? r?.orderList ?? r?.canceledOrders ?? (Array.isArray(r) ? r : null);
    if (!Array.isArray(raw) && r && typeof r === 'object') {
        for (const val of Object.values(r)) {
            if (Array.isArray(val)) {
                raw = val;
                break;
            }
        }
    }
    return Array.isArray(raw) ? raw : [];
};

const sumFromList = (res: any, key: any) => listFrom(res).reduce((sum: any, item: any) => sum + (Number(item?.[key] ?? item?.amount ?? item?.value ?? item?.total ?? 0) || 0), 0);

// Map getCategorySales response to top-items shape (matches maghil-portal Items Sales: categoryName, totalQuantity, totalPrice)
const mapCategorySalesToTopItems = (res: any) => {
    const list = listFrom(res);
    if (!list.length) return [];
    const parseNum = (v: any) => {
        if (v == null) return 0;
        if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
        if (typeof v === 'string') return Number(String(v).replace(/,/g, '')) || 0;
        return 0;
    };
    return list.map((row) => {
        const name = String(row.categoryName ?? row.itemName ?? row.name ?? row.menuItemName ?? row.productName ?? row.label ?? '').trim() || '—';
        const revenue = parseNum(row.totalPrice ?? row.totalSales ?? row.totalAmount ?? row.revenue ?? row.grossSales ?? row.amount);
        const qty = parseNum(row.totalQuantity ?? row.salesQty ?? row.sales_qty ?? row.itemCount ?? row.quantitySold ?? row.qty ?? row.quantity ?? row.count ?? 0);
        return {
            itemName: name,
            name,
            totalItemPrice: revenue,
            grossSales: revenue,
            totalSales: revenue,
            revenue,
            amount: revenue,
            salesQty: qty,
            itemCount: qty,
            qty,
            quantity: qty,
            count: qty,
        };
    }).filter((item) => item.name && item.name !== '—');
};

const num = (v: any) => Number(v ?? 0) || 0;
const fmtCurrency = (v: any) => `${getCurrencySymbol()}${num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Table-cell amount: whole numbers drop decimals ($27), fractional keep 2 ($27.50).
// Matches the export formatting rule for monetary table columns.
const fmtCurrencyCell = (v: any) => {
    const n = num(v);
    const sym = getCurrencySymbol();
    return Number.isInteger(n)
        ? `${sym}${n.toLocaleString('en-US')}`
        : `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtCurrencyShort = (v: any) => {
    const n = num(v);
    const sym = getCurrencySymbol();
    if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${sym}${(n / 1_000).toFixed(1)}K`;
    return `${sym}${n.toFixed(2)}`;
};
// KPI tile amount: abbreviates at ≥100K (same thresholds as fmtCurrencyMobile) so long
// values can't overflow the fixed-width stat tiles (SSE-11). Exact amounts remain
// available in the drill-downs.
const fmtCurrencyKpi = (v: any) => {
    const n = num(v);
    const abs = Math.abs(n);
    const sym = getCurrencySymbol();
    if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 100_000) return `${sym}${(n / 1_000).toFixed(1)}K`;
    return fmtCurrency(n);
};
const fmtCurrencyChannel = (v: any) => {
    const n = num(v);
    const sym = getCurrencySymbol();
    if (n >= 1_000_000) {
        const m = n / 1_000_000;
        return `${sym}${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
    }
    return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtNum = (v: any) => num(v).toLocaleString('en-US');
// CANC-1 — cancelled orders are low-volume, so one un-paginated fetch feeds the KPI count and
// the drill's FE-paginated log. Raise this only if a location can exceed it in one period.
export const CANCELLED_LOG_FETCH_SIZE = 1000;
// Distinct voided orders for a /void-summary response: the backend's period-level totalOrders,
// falling back to summing per-reason orderCount (over-counts orders voided under 2+ reasons)
// for a backend that doesn't send it yet.
const voidOrdersFrom = (rows: Array<{ orderCount?: number; totalOrders?: number }>): number =>
    rows.length > 0 && rows[0].totalOrders != null
        ? Number(rows[0].totalOrders)
        : rows.reduce((s, item) => s + (Number(item.orderCount) || 0), 0);
const VOIDED_ITEMS_KPI_TOOLTIP = 'Items that were sent to the kitchen but removed before the guest was served and before any payment was taken.';
const fmtQty = (v: any, isWeight: any) =>
    Number(isWeight) === 1
        ? num(v).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
        : fmtNum(v);

const getChannelColor = (channel: string): string => {
    const key = channel.toLowerCase().replace(/[\s-]+/g, '');
    if (/instore|inhouse/.test(key)) return T.accent;
    if (/dinein/.test(key)) return '#E86B6B';
    if (/pickup/.test(key)) return T.orange;
    if (/delivery/.test(key)) return T.green;
    return T.t3;
};

const getCanonicalChannel = (channel: string | null): string => {
    const key = (channel ?? '').toLowerCase().replace(/[\s\-_]+/g, '');
    if (/dinein/.test(key)) return 'Dine-In';
    if (/pickup/.test(key)) return 'Pickup';
    if (/delivery/.test(key)) return 'Delivery';
    if (/instore|inhouse/.test(key)) return 'Instore';
    return '3rd Party';
};

const distribute100 = (values: number[]): number[] => {
    const total = values.reduce((s, v) => s + v, 0);
    if (total === 0) return values.map(() => 0);
    const rawPcts = values.map((v) => (v / total) * 100);
    const floored = rawPcts.map((p) => Math.floor(p));
    let remainder = 100 - floored.reduce((s, v) => s + v, 0);
    // Largest Remainder Method: hand the leftover points to the biggest fractional parts
    const indices = rawPcts
        .map((p, i) => ({ i, frac: p - Math.floor(p) }))
        .sort((a, b) => b.frac - a.frac);
    for (const { i } of indices) {
        if (remainder <= 0) break;
        floored[i] += 1;
        remainder -= 1;
    }
    return floored;
};

/** Distribute decimal percentages so they always sum to exactly 100.00 (2dp).
 *  Uses the Largest Remainder Method scaled to hundredths of a percent. */
const distribute100At2dp = (values: number[]): number[] => {
    const total = values.reduce((s, v) => s + v, 0);
    if (total === 0) return values.map(() => 0);
    // Work in units of 0.01% (scale raw % by 100 → integer cents)
    const rawCents = values.map((v) => (v / total) * 10000);
    const flooredCents = rawCents.map((p) => Math.floor(p));
    const remainderCents = 10000 - flooredCents.reduce((s, v) => s + v, 0);
    const fracs = rawCents
        .map((p, i) => ({ i, frac: p - Math.floor(p) }))
        .sort((a, b) => b.frac - a.frac);
    for (let j = 0; j < remainderCents; j++) {
        flooredCents[fracs[j % fracs.length].i] += 1;
    }
    return flooredCents.map((c) => c / 100);
};

// Stamp-KPI export filename: encodes the applied date range (or single selected
// date, DD-MM-YYYY) and channel filter so downloaded files are distinguishable, e.g.
// "snapshot-kpis-08-07-2026-overall" or "snapshot-kpis-01-07-2026_to_08-07-2026-direct".
const fmtFilenameDate = (isoDate: string) => {
    const [y, m, d] = isoDate.split('-');
    return `${d}-${m}-${y}`;
};
// YYYYMMDD-HHmmss — appended to the Stamp-KPI export filename so repeat exports for the
// same date range + channel never collide with a previously-saved file. On Android 10+
// scoped storage, overwriting a filename MediaProvider already indexed is denied and
// surfaces as a generic "Could not save file. Please allow storage access and try again."
// toast even though permission was actually granted — this is the fix, not a permission bug.
const reportExportTimestamp = (): string => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
};
const kpiExportFilename = (base: string, startDate: string, endDate: string, orderSource?: string) => {
    const dateSeg = startDate === endDate ? fmtFilenameDate(startDate) : `${fmtFilenameDate(startDate)}_to_${fmtFilenameDate(endDate)}`;
    return `${base}-${dateSeg}-${orderSource ?? 'overall'}-${reportExportTimestamp()}`;
};

// Stamp-KPI export content: From/To/Channel Filter shown as the first rows of the
// exported table so the applied date range and channel are visible inside the
// document itself, e.g. "8/7/2026".
const fmtExportDate = (iso?: string) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return `${d}/${m}/${y}`;
};

// Refund / void ClickHouse rows come back with `orderDate: null` — the actual date sits
// in `voidedTime` ("2026-07-01 10:15:54"). Fall back to its date portion so the Order Log
// DATE column doesn't render "—" for every row.
const refundRowDate = (o: any): string => {
    const raw = o?.orderDate ?? (typeof o?.voidedTime === 'string' ? o.voidedTime.split(/[ T]/)[0] : '');
    return raw || '';
};

// Void-log TIME cell: `voidedTime` already arrives formatted in the store's own
// 12-hour clock ("02:39 PM"), so it is rendered verbatim. Do NOT run it through
// `new Date()` + `toLocaleTimeString()`: a time-only string yields Invalid Date,
// and a full timestamp would silently be re-rendered in the *device's* timezone
// instead of the store's.
const voidRowTime = (o: any): string => {
    const raw = o?.voidedTime ?? o?.ordertime ?? o?.orderTime ?? o?.voidTime ?? o?.time ?? o?.createdAt ?? '';
    const s = String(raw).trim();
    return s && s !== '—' ? s : '—';
};

// The Stamp-KPI export header's date range comes from `exportPeriodFor` (DPFIX-05).
//
// The local `getFilterLabel` it replaces ended in `if (filter === 'Custom') {…} return filter`
// — `'Custom'` being the token ZAR-1639 replaced with `custom:<from>:<to>`, so a hand-picked
// range matched no branch and the RAW TOKEN went into the file: the "All outlets" header read
// "A2b • 9 outlets • custom:2013-08-01:2026-08-13". Taking the label from the resolver's
// `selectionLabel` means the file and the date control that produced it always word the
// period the same way, and no screen-local copy is left to fall out of step with the token
// vocabulary again.
// Mirrors Sales' getFilterLabel — see the note on that twin: this one previously lacked
// the `custom:` token branch and leaked the raw "custom:2026-08-19:2026-08-27" token into
// on-screen subtitles instead of a human-readable range.
const getFilterLabel = (filter: any, startDate: any, endDate: any) => {
    if (!filter) return 'Today';
    if (filter === 'Today') return 'Today';
    if (filter === 'Yesterday') return 'Yesterday';
    if (filter === '7D') return 'Last 7 Days';
    if (filter === '30D') return 'Last 30 Days';
    if (filter === 'This Month') return 'This Month';
    const fmt = (d: string) => {
        const dt = new Date(`${d}T12:00:00`);
        return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    };
    if (filter.startsWith('custom:')) {
        const p = filter.split(':');
        if (p.length === 3) return p[1] === p[2] ? fmt(p[1]) : `${fmt(p[1])} to ${fmt(p[2])}`;
    }
    if (filter === 'Custom' && startDate && endDate) {
        return startDate === endDate ? fmt(startDate) : `${fmt(startDate)} to ${fmt(endDate)}`;
    }
    return filter;
};

const getStaffTipAmt = (s: any) => {
    if (!s || typeof s !== 'object') return 0;
    // StaffTipSummaryDTO: { tip, fullName, total, orders }
    const direct = s.tip ?? s.tips ?? s.amount ?? s.totalTips ?? s.tipAmount ?? s.gratuity ?? s.tipTotal;
    if (direct != null && direct !== '' && !Number.isNaN(Number(direct))) return Number(direct);
    const nested = s.tipAndGratuity ?? s.tipSummary ?? s.tipDetails ?? s.gratuityDetails;
    if (nested && typeof nested === 'object') {
        const n = nested.amount ?? nested.total ?? nested.totalTips ?? nested.tipAmount ?? nested.gratuity ?? nested.value;
        if (n != null && n !== '' && !Number.isNaN(Number(n))) return Number(n);
    }
    return 0;
};

const Chev = ({ s = 10, c = T.t3, r = 0 }: any) => (
    <svg width={s} height={s} viewBox="0 0 10 10" style={{ transform: `rotate(${r}deg)`, transition: 'transform 0.2s', flexShrink: 0 }}>
        <path d="M2.5 4L5 6.5L7.5 4" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" />
    </svg>
);

const DrillArrow = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: 'absolute', top: 6, right: 6, opacity: 0.45 }}>
        <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Every stat-tile value renders at the same size (`.snapshot-tile-value`, 28px) — no
// per-length shrinking. Shrinking individual tiles reads as inconsistent across a row
// (CANC-1 hit this: a 7-tile row narrowed each tile enough that one value shrank while
// its neighbors didn't). Instead `.snapshot-tile-value` is `white-space: nowrap` and
// `.snapshot-tile--small` no longer has a fixed min-width lower than its content, so a
// tile that can't fit at full size flows to the next line as a whole unit via the
// container's `flex-wrap: wrap` — never a font-size mismatch, never a mid-number wrap.
const TileValue = ({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) => (
    <div className={className ?? 'snapshot-tile-value'} style={style}>
        {children}
    </div>
);

// Historical presets are anchored on the store-timezone anchor date (5 AM cutoff),
// not the raw `yesterday` arg — see getAnchorDate / DateProdIssue.md.
const getStartEndFromFilter = (filter: any, today: any, yesterday: any, timeZoneCd: string = getStoreTimeZone()) => {
    if (normalizeToken(filter) === 'today') return { startDate: today, endDate: today };
    return resolveTokenRange(filter, timeZoneCd);
};

const getInitials = (name: any) => {
    if (!name || typeof name !== 'string') return '—';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
    return name.slice(0, 2).toUpperCase();
};

// ── Items Overview export utilities ───────────────────────────────────────
const pickNumDeepExport = (obj: any, keys: string[]): number => {
    if (!obj || typeof obj !== 'object') return 0;
    for (const key of keys) {
        const value = obj[key];
        if (value != null && value !== '' && !Number.isNaN(Number(value))) return Number(value);
    }
    for (const value of Object.values(obj)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            const nested = pickNumDeepExport(value, keys);
            if (nested > 0) return nested;
        }
    }
    return 0;
};

async function exportItemsCSV(items: { rank: number; name: string; qty: number; rev: number }[], currencySymbol: string) {
    const header = ['#', 'Item', 'Qty', 'Revenue'];
    const rows = items.map(item => [String(item.rank), item.name, String(item.qty), formatMoneyForExport(item.rev)]);
    // Native saves .xlsx so the currency symbol renders in Google Sheets Android too.
    if (isNativePlatform()) {
        await exportTableXlsxNative(header, rows, 'items_overview.xlsx');
        return;
    }
    const csv = `${CSV_BOM}${[header, ...rows].map(r => r.map((c) => escapeCsvCell(c)).join(',')).join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'items_overview.csv';
    a.click();
    URL.revokeObjectURL(url);
}

function mapApiResponseToExportItems(res: any, isToday: boolean): { rank: number; name: string; qty: number; rev: number }[] {
    let list: any[] = [];
    if (isToday) {
        const raw = res?.content ?? res?.data ?? (Array.isArray(res) ? res : []);
        list = Array.isArray(raw) ? raw : [];
    } else {
        list = mapCategorySalesToTopItems(res);
    }
    return list
        .map((item, i) => ({
            rank: i + 1,
            name: String(item.itemName ?? item.name ?? item.categoryName ?? item.menuItemName ?? '—').trim(),
            qty: pickNumDeepExport(item, ['salesQty', 'sales_qty', 'itemCount', 'quantitySold', 'qty', 'quantity', 'count']),
            rev: pickNumDeepExport(item, ['price', 'totalItemPrice', 'grossSales', 'gross_sales', 'itemTotal', 'totalSales', 'amount', 'revenue']),
        }))
        .filter(item => item.name && item.name !== '—');
}

const SNAPSHOT_REFRESH_TS_KEY = 'reportsSnapshotScreenRefreshedTime';
const formatRefreshedTime = (iso: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};
// ─────────────────────────────────────────────────────────────────────────────
// Items Overview — Search / Page-size / Pin (SNAP-IO-1..4)
//
// Shared between the desktop and mobile Snapshot bodies so the four stories are
// implemented ONCE. The host passes its server-paginated state plus a fetch-all
// loader; the component keeps the default (no search / no pins / base page size)
// on the server path and only loads the full dataset for client-side
// search · pin · custom page size.
// ─────────────────────────────────────────────────────────────────────────────

const MAX_PINNED = 5;
const PAGE_SIZE_CHOICES = [10, 25, 50];
const SEARCH_MIN_CHARS = 1;
const SEARCH_DEBOUNCE_MS = 300;

interface IOItemRow { itemId: string; name: string; qty: number; rev: number; isWeight: number; category?: string; }
interface IORankedRow extends IOItemRow { rank: number; } // rank 0 ⇒ no sales ⇒ render "—"

// Flatten a fetch-all response into item rows. Handles a flat list (Today live
// item-sales) AND the category-grouped shape returned by
// /product/insights/category-item-sales ({ categories: [{ categoryName, items }] }),
// attaching each parent category onto its items. Mirrors getCategoryItemSalesRows
// from the full Item Performance table so the category dimension is reused intact.
function ioExtractRows(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (!res || typeof res !== 'object') return [];
    const direct = res.content ?? res.data ?? null;
    if (Array.isArray(direct) && direct.length) return direct;
    if (Array.isArray(res.items) && res.items.length) return res.items;
    if (Array.isArray(res.categories)) {
        return res.categories.flatMap((cat: any) => {
            const categoryName = cat?.categoryName ?? cat?.name ?? '';
            const items = Array.isArray(cat?.items) ? cat.items : [];
            return items.map((it: any) => ({ ...it, categoryName: it?.categoryName ?? categoryName }));
        });
    }
    return Array.isArray(direct) ? direct : [];
}

// Map a fetch-all API response into ranked rows (rank = position in the full
// rev-desc sorted list). Both the Today source (live item-sales) and the
// historical source (/product/insights/category-item-sales, reused from the full
// Item Performance table) yield rows with a real itemName + categoryName, so a
// single uniform mapping resolves name and category for every date range.
function mapAllItemsForView(res: any, _isToday: boolean): IORankedRow[] {
    const raw: any[] = ioExtractRows(res);
    return (raw as any[])
        .map((item) => ({
            itemId: String(item.itemId ?? '').trim(),
            name: String(item.itemName ?? item.name ?? item.menuItemName ?? item.productName ?? item.label ?? '—').trim(),
            qty: pickNumDeepExport(item, ['quantity', 'totalQuantity', 'salesQty', 'sales_qty', 'itemCount', 'quantitySold', 'qty', 'count']),
            rev: pickNumDeepExport(item, ['price', 'totalPrice', 'totalItemPrice', 'grossSales', 'gross_sales', 'itemTotal', 'totalSales', 'totalAmount', 'amount', 'revenue']),
            isWeight: Number(item.isWeight ?? 0),
            category: String(item.categoryName ?? item.category ?? item.categoryTitle ?? item.menuCategory ?? '').trim(),
        }))
        .filter((i) => i.name !== '—' || i.rev > 0 || i.qty > 0)
        .sort((a, b) => b.rev - a.rev)
        .map((i, idx) => ({ ...i, rank: idx + 1 }));
}

function useDebouncedValue<T>(value: T, delay: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debounced;
}

// Pins are keyed by itemId (a UUID) — never by item name. Older sessions may still
// have name-based values sitting in localStorage from before pins switched to ids;
// this filters those out so a stale name never gets sent as `pinnedItemNames`.
const isValidItemId = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const sanitizePinIds = (ids: string[]) => ids.filter(isValidItemId);

// Pin state: localStorage is the active store (per user + per outlet); the
// server API mirrors it when PIN_API_ENABLED (SNAP-IO-4). Pins are keyed by itemId.
function useSnapshotPins({ userId, outletId, token }: { userId: string; outletId: string; token: string | null }) {
    const [pinnedNames, setPinnedNames] = useState<string[]>(() => sanitizePinIds(readLocalPins(userId, outletId)));
    const [error, setError] = useState<{ name: string; action: PinAction } | null>(null);

    useEffect(() => {
        const local = readLocalPins(userId, outletId);
        const cleaned = sanitizePinIds(local);
        setPinnedNames(cleaned);
        // Self-heal: drop any legacy name-based pins from storage so they never resurface.
        if (cleaned.length !== local.length) writeLocalPins(userId, outletId, cleaned);
        if (PIN_API_ENABLED) {
            fetchServerPins(outletId, token)
                .then((server) => {
                    if (server && server.length) {
                        setPinnedNames(sanitizePinIds(server));
                    }
                })
                .catch(() => { /* keep local */ });
        }
    }, [userId, outletId, token]);

    const persist = useCallback((next: string[]) => writeLocalPins(userId, outletId, next), [userId, outletId]);

    const commitServer = useCallback((name: string, action: PinAction) => {
        if (!PIN_API_ENABLED) return;
        saveServerPin({ userId, outletId, itemId: name, action }, token).catch(() => setError({ name, action }));
    }, [userId, outletId, token]);

    const togglePin = useCallback((name: string): 'pinned' | 'unpinned' | 'max' => {
        if (pinnedNames.includes(name)) {
            const next = pinnedNames.filter((n) => n !== name);
            setPinnedNames(next); persist(next); commitServer(name, 'unpin');
            return 'unpinned';
        }
        if (pinnedNames.length >= MAX_PINNED) return 'max';
        const next = [...pinnedNames, name];
        setPinnedNames(next); persist(next); commitServer(name, 'pin');
        return 'pinned';
    }, [pinnedNames, persist, commitServer]);

    const clearAll = useCallback((): number => {
        const count = pinnedNames.length;
        setPinnedNames([]); persist([]);
        if (PIN_API_ENABLED) clearServerPins(userId, outletId, token).catch(() => { /* best effort */ });
        return count;
    }, [pinnedNames, persist, userId, outletId, token]);

    const reconcile = useCallback((validNames: string[]) => {
        const validSet = new Set(validNames);
        const next = pinnedNames.filter((n) => validSet.has(n));
        if (next.length !== pinnedNames.length) {
            setPinnedNames(next);
        }
    }, [pinnedNames]);

    const retry = useCallback(() => {
        if (!error) return;
        const { name, action } = error;
        saveServerPin({ userId, outletId, itemId: name, action }, token)
            .then(() => setError(null))
            .catch(() => {
                // Second failure → revert optimistic change.
                setPinnedNames((prev) => action === 'pin' ? prev.filter((n) => n !== name) : Array.from(new Set([...prev, name])));
                setError(null);
            });
    }, [error, userId, outletId, token]);

    return { pinnedNames, isPinned: (n: string) => pinnedNames.includes(n), togglePin, clearAll, reconcile, count: pinnedNames.length, error, retry, dismissError: () => setError(null) };
}

const PinGlyph = ({ filled, size, color }: { filled: boolean; size: number; color: string }) => (
    <span
        className="material-symbols-rounded"
        style={{ fontSize: size, color, lineHeight: 1, fontVariationSettings: filled ? "'FILL' 1" : "'FILL' 0", display: 'inline-block' }}
    >push_pin</span>
);

interface SnapshotItemsOverviewProps {
    variant: 'desktop' | 'mobile';
    tk: any;
    title: string;
    /** Optional note rendered behind an info icon beside the card title. */
    titleInfo?: string;
    subtitle?: string;
    exportSlot: (disabled: boolean, fetchAll: (overrideLocationId?: string) => Promise<any>) => React.ReactNode;
    startDate?: string;
    endDate?: string;
    datasetKey: string;
    userId: string;
    outletId: string;
    token: string | null;
    orderSource?: 'direct' | 'thirdparty';
    fmtQty: (v: any, isWeight: any) => string;
    fmtCurrencyCell: (v: any) => string;
    tracker: ReportTracker;
}

type IOSortCol = 'rank' | 'qty' | 'rev';
type IOSortDir = 'asc' | 'desc';

// Compare two rows by the active column, with a STABLE secondary sort by item
// name (A→Z) so equal values never "jump" between renders (SNAP-IO-5 edge case).
function compareRows(a: IORankedRow, b: IORankedRow, col: IOSortCol, dir: IOSortDir): number {
    let d: number;
    if (col === 'qty') d = a.qty - b.qty;
    else if (col === 'rev') d = a.rev - b.rev;
    else d = (a.rank || Number.MAX_SAFE_INTEGER) - (b.rank || Number.MAX_SAFE_INTEGER);
    if (dir === 'desc') d = -d;
    if (d !== 0) return d;
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
}

function SnapshotItemsOverview(props: SnapshotItemsOverviewProps) {
    const {
        variant, tk, title, titleInfo, subtitle, exportSlot,
        startDate, endDate, datasetKey,
        userId, outletId, token, orderSource, fmtQty, fmtCurrencyCell, tracker,
    } = props;
    const isMobile = variant === 'mobile';

    const [query, setQuery] = useState('');
    const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);
    const [pageSizeMenuOpen, setPagSizeMenuOpen] = useState(false);
    const [hoverKey, setHoverKey] = useState<string | null>(null);
    const [confirmClear, setConfirmClear] = useState(false);
    const [toast, setToast] = useState<string | null>(null);
    const toastTimer = useRef<any>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    // Cache of item data keyed by itemId. Populated from every API response so
    // pinned items remain visible even when they don't match the current search term.
    const pinnedDataRef = useRef<Map<string, IORankedRow>>(new Map());

    // Sort state — applied client-side to the current page rows.
    const [sortCol, setSortCol] = useState<IOSortCol>('rank');
    const [sortDir, setSortDir] = useState<IOSortDir>('asc');
    const sortActive = !(sortCol === 'rank' && sortDir === 'asc');
    // Category filter.
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [catMenuOpen, setCatMenuOpen] = useState(false);
    const [catNoneSelected, setCatNoneSelected] = useState(true);
    const catMenuRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!catMenuOpen) return;
        const handler = (e: MouseEvent) => {
            if (catMenuRef.current && !catMenuRef.current.contains(e.target as Node)) {
                setCatMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [catMenuOpen]);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    const [sheetDraft, setSheetDraft] = useState<string[]>([]);
    const categoryActive = selectedCategories.length > 0;

    // API-driven item state.
    const [items, setItems] = useState<IORankedRow[]>([]);
    const [pinnedItems, setPinnedItems] = useState<IORankedRow[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [apiLoading, setApiLoading] = useState(false);
    const [apiError, setApiError] = useState(false);

    // Category list from dedicated API.
    const [categoryList, setCategoryList] = useState<{ categoryId: string; categoryName: string }[]>([]);
    const categoryListRef = useRef(categoryList);
    useEffect(() => { categoryListRef.current = categoryList; }, [categoryList]);
    const [catListLoading, setCatListLoading] = useState(false);
    useEffect(() => {
        if (!outletId || !token) return;
        setCatListLoading(true);
        getLiveCategoriesV2({ locationId: outletId, token })
            .then((res: any) => { setCategoryList(Array.isArray(res) ? res : []); })
            .finally(() => setCatListLoading(false));
    }, [outletId, token]);

    const pins = useSnapshotPins({ userId, outletId, token });

    const showToast = useCallback((msg: string) => {
        setToast(msg);
        if (toastTimer.current) clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 4000);
    }, []);
    useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

    const searchActive = debouncedQuery.trim().length >= SEARCH_MIN_CHARS;

    // Reset page and filters when the dataset (date range) changes.
    useEffect(() => {
        setPage(0); setSortCol('rank'); setSortDir('asc'); setSelectedCategories([]); setCatNoneSelected(true);
        setCatMenuOpen(false); setFilterSheetOpen(false); setQuery('');
    }, [datasetKey]);
    // Reset to page 0 on filter/search/size/sort changes.
    useEffect(() => { setPage(0); }, [debouncedQuery, pageSize, selectedCategories, sortCol, sortDir]);

    // Fetch items from the server whenever any filter/page param changes.
    useEffect(() => {
        // Wait for auth to resolve so pins are read from the correct localStorage key before the first fetch.
        if (!outletId || !token || !userId) return;
        // Guard against the render-cycle race: userId just became truthy but useSnapshotPins hasn't
        // synced localStorage into state yet. If localStorage has pins but state still shows none, wait.
        const localPins = readLocalPins(userId, outletId);
        if (localPins.length > 0 && pins.pinnedNames.length === 0) return;
        setApiLoading(true);
        setApiError(false);
        const catIds = selectedCategories
            .map((n) => categoryListRef.current.find((c) => c.categoryName === n)?.categoryId)
            .filter(Boolean) as string[];
        const q = debouncedQuery.trim();
        const apiSortBy = sortCol === 'rev' ? 'revenue' : sortCol === 'qty' ? 'quantity' : 'item_name';
        const apiSortAsc = sortDir === 'asc';
        getLiveItemSalesV2({
            locationId: outletId,
            page,
            size: pageSize,
            startDate,
            endDate,
            categoryIds: catIds.length ? catIds : undefined,
            searchItem: q.length >= SEARCH_MIN_CHARS ? q : undefined,
            pinnedItemNames: pins.pinnedNames.length ? pins.pinnedNames : undefined,
            sortBy: apiSortBy,
            sortAsc: apiSortAsc,
            orderSource,
            token,
        })
            .then((res: any) => {
                const mapRow = (item: any, idx: number, offset: number): IORankedRow => ({
                    itemId: String(item.itemId ?? '').trim(),
                    name: String(item.itemName ?? item.name ?? '').trim() || '—',
                    qty: Number(item.quantity ?? item.qty ?? 0),
                    rev: Number(item.price ?? item.totalPrice ?? 0),
                    isWeight: Number(item.isWeight ?? 0),
                    category: String(item.categoryName ?? '').trim(),
                    rank: offset + idx + 1,
                });
                const contentRows: IORankedRow[] = (res.content ?? []).map((item: any, i: number) => mapRow(item, i, page * pageSize));
                setItems(contentRows);

                const returnedPinned: IORankedRow[] = (res.pinnedItems ?? []).map((item: any, i: number) => mapRow(item, i, 0));
                // Merge API-returned pin data into the cache keyed by item id.
                returnedPinned.forEach((row) => { if (row.itemId) pinnedDataRef.current.set(row.itemId, row); });
                // Also seed the cache from the current page's content — the backend's
                // dedicated pinnedItems lookup can miss items (id-match gaps, or it may
                // omit items already present in content) even though they're pinned and
                // visibly on this page, so don't rely on pinnedItems alone.
                pins.pinnedNames.forEach((id) => {
                    if (pinnedDataRef.current.has(id)) return;
                    const row = contentRows.find((r) => r.itemId === id);
                    if (row) pinnedDataRef.current.set(id, row);
                });
                // Build a set of item ids the API confirmed exist in this response.
                // A pinned item is only displayed if it appears in content or pinnedItems —
                // this prevents stale cache entries from showing items that no longer match
                // the current date range, filters, or no longer exist in the data.
                // Exception: when search is active, show all cached pins so the user can
                // see their pinned items even if they don't match the current search term.
                const apiConfirmedIds = new Set<string>([
                    ...contentRows.map((r) => r.itemId),
                    ...returnedPinned.map((r) => r.itemId),
                ]);
                const isSearchActive = q.length >= SEARCH_MIN_CHARS;
                const displayed = pins.pinnedNames
                    .map((id) => pinnedDataRef.current.get(id))
                    .filter((r): r is IORankedRow => r !== undefined && (isSearchActive || apiConfirmedIds.has(r.itemId)));
                setPinnedItems(displayed.map((row, i) => ({ ...row, rank: i + 1 })));

                setTotalElements(res.totalElements ?? 0);
                setTotalPages(res.totalPages ?? 0);
            })
            .catch(() => setApiError(true))
            .finally(() => setApiLoading(false));
    }, [outletId, token, startDate, endDate, page, pageSize, debouncedQuery, selectedCategories, pins.pinnedNames, datasetKey, sortCol, sortDir, orderSource]); // eslint-disable-line react-hooks/exhaustive-deps

    // Lazily load the full dataset the first time it is needed for this datasetKey.
    // Keyed off datasetKey (stable) — not fetchAllItems (volatile) — and uses async
    // Fire GA4 search filter event on debounced query (≥ min chars).
    useEffect(() => {
        const q = debouncedQuery.trim();
        if (q.length >= SEARCH_MIN_CHARS) {
            tracker.filter({ report_name: 'Snapshot', filter_type: 'search', filter_value: q, table_name: 'items_overview' });
        }
    }, [debouncedQuery]); // eslint-disable-line react-hooks/exhaustive-deps

    const clearSearch = useCallback(() => { setQuery(''); }, []);

    const handlePageSize = useCallback((size: number) => {
        setPageSize(size);
        tracker.tableConfig({ report_name: 'Snapshot', table_name: 'items_overview', config_type: 'page_size', config_value: String(size) });
    }, [tracker]);

    const handleTogglePin = useCallback((row: IORankedRow | IOItemRow & { rank?: number }) => {
        const result = pins.togglePin(row.itemId);
        if (result === 'max') {
            showToast('Maximum 5 items can be pinned. Unpin an item to add another.');
            return;
        }
        tracker.pin({ report_name: 'Snapshot', table_name: 'items_overview', action: result === 'pinned' ? 'pin' : 'unpin', item_name: row.name, item_rank: (row as any).rank ?? '—' });
    }, [pins, showToast, tracker]);

    const handleClearAll = useCallback(() => {
        const n = pins.clearAll();
        setConfirmClear(false);
        tracker.pinClear({ report_name: 'Snapshot', table_name: 'items_overview', cleared_count: n });
    }, [pins, tracker]);

    const emitSort = useCallback((col: IOSortCol, dir: IOSortDir) => {
        tracker.sort({ report_name: 'Snapshot', table_name: 'items_overview', sort_column: col, sort_direction: dir });
    }, [tracker]);

    // Desktop header click: 3-state cycle per column (QTY/REV: desc → asc → default).
    // The # header toggles rank asc/desc; default resting state is rank asc.
    const cycleSort = useCallback((col: IOSortCol) => {
        let nextCol: IOSortCol = col, nextDir: IOSortDir = 'desc';
        if (col === 'rank') {
            nextCol = 'rank';
            nextDir = (sortCol === 'rank' && sortDir === 'asc') ? 'desc' : 'asc';
        } else if (sortCol !== col) {
            nextDir = 'desc';                                   // first click → desc
        } else if (sortDir === 'desc') {
            nextDir = 'asc';                                    // second click → asc
        } else {
            nextCol = 'rank'; nextDir = 'asc';                  // third click → default
        }
        setSortCol(nextCol); setSortDir(nextDir); emitSort(nextCol, nextDir);
    }, [sortCol, sortDir, emitSort]);

    // Category dropdown from dedicated API — deduplicated, alphabetically sorted.
    const categories = useMemo(() => {
        const seen = new Set<string>();
        return categoryList
            .filter((c) => { if (seen.has(c.categoryName)) return false; seen.add(c.categoryName); return true; })
            .map((c) => ({ name: c.categoryName }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [categoryList]);

    const toggleCategory = useCallback((name: string) => {
        tracker.filter({ report_name: 'Snapshot', filter_type: 'category', filter_value: name, table_name: 'items_overview' });
        if (catNoneSelected) {
            // Initial state → select just this one
            setCatNoneSelected(false);
            setSelectedCategories([name]);
            return;
        }
        let next: string[];
        if (selectedCategories.length === 0) {
            // All Categories state → deselect one → all except this one
            next = categories.map((c) => c.name).filter((n) => n !== name);
        } else {
            next = selectedCategories.includes(name)
                ? selectedCategories.filter((c) => c !== name)
                : [...selectedCategories, name];
            // All individually checked → collapse to All Categories state
            if (next.length === categories.length) next = [];
        }
        // Removing the last explicit selection → back to initial
        if (next.length === 0 && selectedCategories.length > 0) {
            setCatNoneSelected(true);
        }
        setSelectedCategories(next);
    }, [categories, catNoneSelected, selectedCategories, tracker]);
    const clearCategories = useCallback(() => { setCatNoneSelected(true); setSelectedCategories([]); }, []);

    // Sorting is server-side; items from the API are already in the correct order.
    const sortedItems = items;

    const isExportDisabled = !apiLoading && !apiError && sortedItems.length === 0 && pinnedItems.length === 0;

    // Fetches all rows matching the current search / category filter for export.
    // `overrideLocationId` (outlet-scoped export, an OTHER outlet than the one this
    // component is bound to) can't reuse this component's own `totalElements` —
    // that count only applies to `outletId`. Probe with size=1 first to learn the
    // other outlet's real total, then re-fetch with that.
    const fetchAllForExport = useCallback(async (overrideLocationId?: string): Promise<any> => {
        const catIds = selectedCategories
            .map((n) => categoryList.find((c) => c.categoryName === n)?.categoryId)
            .filter(Boolean) as string[];
        const q = debouncedQuery.trim();
        const exportSortBy = sortCol === 'rev' ? 'revenue' : sortCol === 'qty' ? 'quantity' : 'item_name';
        const exportSortAsc = sortDir === 'asc';
        const loc = overrideLocationId ?? outletId;
        let size = totalElements || 1000;
        if (overrideLocationId) {
            const probe: any = await getLiveItemSalesV2({
                locationId: loc,
                page: 0,
                size: 1,
                startDate,
                endDate,
                categoryIds: catIds.length ? catIds : undefined,
                searchItem: q.length >= SEARCH_MIN_CHARS ? q : undefined,
                sortBy: exportSortBy,
                sortAsc: exportSortAsc,
                orderSource,
                token,
            });
            size = probe?.totalElements ?? probe?.total ?? 1000;
        }
        return getLiveItemSalesV2({
            locationId: loc,
            page: 0,
            size: size || 1000,
            startDate,
            endDate,
            categoryIds: catIds.length ? catIds : undefined,
            searchItem: q.length >= SEARCH_MIN_CHARS ? q : undefined,
            sortBy: exportSortBy,
            sortAsc: exportSortAsc,
            orderSource,
            token,
        });
    }, [outletId, token, startDate, endDate, totalElements, debouncedQuery, selectedCategories, categoryList, sortCol, sortDir, orderSource]);

    // ── Small UI atoms ──────────────────────────────────────────────────────
    const colDims = isMobile
        ? { pin: 40, rank: 28, qty: 48, rev: 68, fsHead: 9, fsCell: 11, rowPad: '7px 0' }
        : { pin: 26, rank: 28, qty: 70, rev: 90, fsHead: 10, fsCell: 12, rowPad: '7px 0' };

    // Sortable column header — identical 3-state tap/click cycle on desktop AND
    // mobile (SNAP-IO-5 / SNAP-IO-5.1). Mobile adds haptic + a pressed-flash.
    const SortHead = ({ col, label, width, flex, align, fitContent }: { col: IOSortCol; label: string; width?: number; flex?: number; align: 'left' | 'right'; fitContent?: boolean }) => {
        const showActive = sortCol === col && (col !== 'rank' || sortActive);
        const icon = showActive ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more';
        const activeColor = isMobile ? tk.t1 : tk.accent; // mobile=primary, desktop=accent
        const [hov, setHov] = useState(false);
        const [pressed, setPressed] = useState(false);
        const onActivate = () => {
            if (isMobile) {
                setPressed(true);
                if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
                    try { navigator.vibrate(10); } catch { /* unsupported */ }
                }
                setTimeout(() => setPressed(false), 150);
            }
            cycleSort(col);
        };
        // Mobile always shows the icon (persistent affordance); desktop reveals on
        // hover/active. The slot is always 14px wide so swapping icons never shifts text.
        const iconVisible = isMobile || showActive || hov;
        return (
            <span
                onClick={onActivate}
                onMouseEnter={() => setHov(true)}
                onMouseLeave={() => setHov(false)}
                style={{
                    width, flex, flexShrink: flex ? undefined : 0, minWidth: 0,
                    fontSize: colDims.fsHead, fontWeight: 600, color: showActive ? tk.t2 : tk.t3,
                    textTransform: 'uppercase', textAlign: align, cursor: 'pointer', touchAction: 'manipulation',
                    display: fitContent ? 'inline-flex' : 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                    gap: 4, userSelect: 'none', minHeight: isMobile ? 44 : undefined, boxSizing: 'border-box',
                    background: 'transparent', borderRadius: 4, padding: '2px 2px',
                }}
            >
                {align === 'right' && (
                    <span className="material-symbols-rounded" style={{ fontSize: 14, width: 14, color: showActive ? activeColor : tk.t3, visibility: iconVisible ? 'visible' : 'hidden' }}>{icon}</span>
                )}
                {label}
                {align !== 'right' && (
                    <span className="material-symbols-rounded" style={{ fontSize: 14, width: 14, color: showActive ? activeColor : tk.t3, visibility: iconVisible ? 'visible' : 'hidden' }}>{icon}</span>
                )}
            </span>
        );
    };

    const TableHeader = (
        <div style={{ display: 'flex', alignItems: 'center', padding: isMobile ? '5px 0' : '6px 0', borderBottom: `1px solid ${tk.divider}`, marginBottom: 2 }}>
            <span style={{ width: colDims.pin, flexShrink: 0 }} />
            <span style={{ width: colDims.rank, flexShrink: 0, fontSize: colDims.fsHead, fontWeight: 600, color: tk.t3, textTransform: 'uppercase' }}>#</span>
            <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                <SortHead col="rank" label="Item" align="left" fitContent />
            </span>
            <SortHead col="qty" label="Qty" width={colDims.qty} align="right" />
            <SortHead col="rev" label="Rev" width={colDims.rev} align="right" />
        </div>
    );

    const renderRow = (row: IORankedRow, opts: { pinned: boolean; lastInGroup: boolean }) => {
        const key = row.itemId || row.name;
        const hovered = hoverKey === key;
        const showPin = opts.pinned || hovered || isMobile;
        const rankLabel = row.rank > 0 ? row.rank : '—';
        // Pin lives in the leftmost column on BOTH desktop and mobile (SNAP-IO-3.1).
        // Mobile: always visible, 16px icon inside a 40px tap zone. Desktop: revealed
        // on row hover (or always when pinned).
        const pinCell = (
            <span style={{ width: colDims.pin, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <button
                    type="button"
                    aria-label={opts.pinned ? `Unpin ${row.name}` : `Pin ${row.name}`}
                    onClick={() => handleTogglePin(row)}
                    style={{ background: 'transparent', border: 'none', padding: isMobile ? 12 : 0, margin: isMobile ? '-12px 0' : 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: showPin ? 1 : 0, transition: 'opacity .12s', touchAction: 'manipulation' }}
                >
                    <PinGlyph filled={opts.pinned} size={16} color={opts.pinned ? tk.accent : tk.t3} />
                </button>
            </span>
        );
        return (
            <div
                key={`${opts.pinned ? 'pin' : 'row'}-${key}`}
                onMouseEnter={() => !isMobile && setHoverKey(key)}
                onMouseLeave={() => !isMobile && setHoverKey((h) => (h === key ? null : h))}
                style={{ display: 'flex', alignItems: 'center', padding: colDims.rowPad, borderBottom: opts.lastInGroup ? 'none' : `1px solid ${tk.divider}`, minWidth: 0, background: opts.pinned ? `${tk.accent}08` : 'transparent' }}
            >
                {pinCell}
                <span style={{ width: colDims.rank, flexShrink: 0, fontSize: colDims.fsCell, fontWeight: 600, color: row.rank === 1 ? tk.accent : tk.t3 }}>{rankLabel}</span>
                <span title={row.name} style={{ flex: 1, minWidth: 0, fontSize: colDims.fsCell, fontWeight: 600, color: tk.t1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 8 }}>{row.name}</span>
                <span style={{ width: colDims.qty, flexShrink: 0, fontSize: colDims.fsCell, color: tk.t2, textAlign: 'right' }}>{row.qty ? fmtQty(row.qty, row.isWeight) : '—'}</span>
                <span style={{ width: colDims.rev, flexShrink: 0, fontSize: colDims.fsCell, fontWeight: 600, color: tk.t1, textAlign: 'right' }}>{fmtCurrencyCell(row.rev)}</span>
            </div>
        );
    };

    const skeletonRows = (n: number) => Array.from({ length: n }).map((_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', padding: colDims.rowPad, borderBottom: i < n - 1 ? `1px solid ${tk.divider}` : 'none', gap: 8 }}>
            <Bone w={18} h={18} r={5} />
            <Bone h={13} r={4} style={{ flex: 1 }} />
            <Bone w={36} h={13} r={4} />
            <Bone w={60} h={13} r={4} />
        </div>
    ));

    const sizeChoices = PAGE_SIZE_CHOICES;
    const showSizeSelector = totalElements > 10;

    const pageSizeFontSize = isMobile ? 10 : 11;
    const PageSizeSelector = showSizeSelector ? (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: pageSizeFontSize, color: tk.t3, fontFamily: tk.font }}>
            Rows per page:
            <div style={{ position: 'relative' }}>
                <button
                    type="button"
                    onClick={() => setPagSizeMenuOpen((o) => !o)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: pageSizeFontSize, fontWeight: 600, color: tk.t1, fontFamily: tk.font, padding: isMobile ? '2px 4px' : '3px 6px', borderRadius: 6, border: `1px solid ${tk.cardBorder}`, background: tk.card, cursor: 'pointer', lineHeight: 1 }}
                >
                    {pageSize}
                    <span className="material-symbols-rounded" style={{ fontSize: pageSizeFontSize + 2, lineHeight: 1, color: tk.t3 }}>expand_more</span>
                </button>
                {pageSizeMenuOpen && (
                    <>
                        <div onClick={() => setPagSizeMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 500 }} />
                        <div style={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, background: tk.card, border: `1px solid ${tk.cardBorder}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 501, overflow: 'hidden', minWidth: '100%' }}>
                            {sizeChoices.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => { handlePageSize(s); setPagSizeMenuOpen(false); }}
                                    style={{ display: 'block', width: '100%', textAlign: 'center', fontSize: pageSizeFontSize, fontWeight: s === pageSize ? 700 : 500, color: s === pageSize ? tk.accent : tk.t1, fontFamily: tk.font, padding: '7px 14px', border: 'none', background: s === pageSize ? `${tk.accent}12` : 'transparent', cursor: 'pointer' }}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    ) : null;

    // ── Header (title + search + export) ────────────────────────────────────
    const SearchFieldDesktop = (
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <span className="material-symbols-rounded" style={{ position: 'absolute', left: 8, fontSize: 16, color: tk.t3, pointerEvents: 'none' }}>search</span>
            <input
                ref={searchInputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search items…"
                aria-label="Search items"
                style={{ width: 200, padding: '7px 28px 7px 30px', borderRadius: 8, border: `1px solid ${searchFocused ? tk.accent : tk.cardBorder}`, fontSize: 12, color: tk.t1, fontFamily: tk.font, outline: 'none', background: tk.card }}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => { setSearchFocused(false); }}
            />
            {(query || searchFocused) && (
                <button
                    type="button"
                    aria-label="Clear search"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { clearSearch(); searchInputRef.current?.blur(); setSearchFocused(false); }}
                    style={{ position: 'absolute', right: 6, background: 'transparent', border: 'none', padding: 2, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                    <span className="material-symbols-rounded" style={{ fontSize: 15, color: tk.t3 }}>close</span>
                </button>
            )}
        </div>
    );

    // ── Category filter helpers (SNAP-IO-6) ─────────────────────────────────
    // allCatsSelected: only true when explicitly in the "All Categories" state (not initial)
    const allCatsSelected = !catNoneSelected && selectedCategories.length === 0;
    // Both initial and All Categories show "All Categories" label since both send no filter
    const catLabel = allCatsSelected ? 'All Categories'
        : catNoneSelected ? 'All Categories'
        : selectedCategories.length === 1 ? selectedCategories[0]
            : `${selectedCategories.length} Categories`;
    const handleAllCategoriesClick = () => {
        if (allCatsSelected) {
            // All Categories → back to initial (all unchecked)
            setCatNoneSelected(true);
            setSelectedCategories([]);
        } else {
            // Initial or partial → All Categories (all checked)
            setCatNoneSelected(false);
            setSelectedCategories([]);
        }
    };
    const trunc = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1) + '…' : s);
    const CheckBox = ({ on }: { on: boolean }) => (
        <span className="material-symbols-rounded" style={{ fontSize: 18, color: on ? tk.accent : tk.t3 }}>{on ? 'check_box' : 'check_box_outline_blank'}</span>
    );
    const catOptStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', fontSize: 12, color: tk.t1, cursor: 'pointer', borderRadius: 6, fontFamily: tk.font };
    const renderCatOptions = (selected: string[], onToggle: (n: string) => void, forMobile = false) => (
        catListLoading ? <div style={{ padding: 8, fontSize: 12, color: tk.t3 }}>Loading…</div>
            : categories.length === 0 ? <div style={{ padding: 8, fontSize: 12, color: tk.t3 }}>No categories</div>
                : categories.map((c) => (
                    <div key={c.name} title={c.name} onClick={() => onToggle(c.name)} style={catOptStyle}
                        onMouseEnter={(e) => (e.currentTarget.style.background = tk.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                        <CheckBox on={forMobile ? selected.includes(c.name) : !catNoneSelected && (selected.length === 0 || selected.includes(c.name))} />
                        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{trunc(c.name, 25)}</span>
                    </div>
                ))
    );

    const CategoryDropdownDesktop = (
        <div ref={catMenuRef} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setCatMenuOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 8, border: `1px solid ${catMenuOpen ? tk.accent : tk.cardBorder}`, background: tk.card, fontSize: 12, color: tk.t1, fontFamily: tk.font, fontWeight: 600, cursor: 'pointer', maxWidth: 190 }}>
                <span className="material-symbols-rounded" style={{ fontSize: 16, color: tk.t3 }}>category</span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{catLabel}</span>
                <span className="material-symbols-rounded" style={{ fontSize: 16, color: tk.t3 }}>{catMenuOpen ? 'expand_less' : 'expand_more'}</span>
            </button>
            {catMenuOpen && (
                <>
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 61, minWidth: 230, maxHeight: 280, overflowY: 'auto', background: tk.card, border: `1px solid ${tk.cardBorder}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 4 }}>
                        <div onClick={handleAllCategoriesClick} style={catOptStyle} onMouseEnter={(e) => (e.currentTarget.style.background = tk.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                            <CheckBox on={allCatsSelected} />
                            <span style={{ flex: 1 }}>All Categories</span>
                        </div>
                        {renderCatOptions(selectedCategories, toggleCategory)}
                    </div>
                </>
            )}
        </div>
    );

    const categoryChips = categoryActive ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, alignItems: 'center' }}>
            {selectedCategories.map((c) => (
                <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#F5F6FA', color: '#1A1D2E', borderRadius: 6, padding: '4px 8px', fontSize: 11, fontWeight: 600 }}>
                    {c}
                    <span className="material-symbols-rounded" onClick={() => toggleCategory(c)} style={{ fontSize: 14, color: '#6B7280', cursor: 'pointer' }}>close</span>
                </span>
            ))}
            <button type="button" onClick={clearCategories} style={{ background: 'transparent', border: 'none', color: tk.accent, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Clear filters</button>
        </div>
    ) : null;

    const header = isMobile ? (
        <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: tk.t1, display: 'flex', alignItems: 'center', gap: 4 }}>{title}{titleInfo && <KpiTooltip text={titleInfo} isMobile placement="right" />}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button type="button" aria-label="Filters" onClick={() => { setSheetDraft(selectedCategories); setFilterSheetOpen(true); }} style={{ display: 'flex', alignItems: 'center', gap: 3, background: categoryActive ? `${tk.accent}14` : 'transparent', border: 'none', borderRadius: 6, padding: '4px 6px', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: categoryActive ? tk.accent : tk.t2, fontFamily: tk.font }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 18 }}>tune</span>
                        {categoryActive ? `(${selectedCategories.length})` : ''}
                    </button>
                    <button type="button" aria-label="Search items" onClick={() => setMobileSearchOpen((o) => !o)} style={{ background: mobileSearchOpen ? `${tk.accent}14` : 'transparent', border: 'none', borderRadius: 6, padding: 4, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 18, color: mobileSearchOpen ? tk.accent : tk.t2 }}>search</span>
                    </button>
                    {!mobileSearchOpen && exportSlot(isExportDisabled, fetchAllForExport)}
                </div>
            </div>
            {mobileSearchOpen && (
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                    <span className="material-symbols-rounded" style={{ position: 'absolute', left: 8, fontSize: 16, color: tk.t3, pointerEvents: 'none' }}>search</span>
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search items…"
                        aria-label="Search items"
                        autoFocus
                        style={{ width: '100%', padding: '8px 30px 8px 30px', borderRadius: 8, border: `1px solid ${tk.cardBorder}`, fontSize: 10, color: tk.t1, fontFamily: tk.font, outline: 'none', background: tk.card }}
                    />
                    <button
                        type="button"
                        aria-label="Clear search"
                        onClick={() => { clearSearch(); if (!query) setMobileSearchOpen(false); }}
                        style={{ position: 'absolute', right: 6, background: 'transparent', border: 'none', padding: 2, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                        <span className="material-symbols-rounded" style={{ fontSize: 16, color: tk.t3 }}>close</span>
                    </button>
                </div>
            )}
            {categoryChips}
        </>
    ) : (
        <>
            <div className="snapshot-card-title-wrap snapshot-items-title-wrap">
                <div style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                    <div className="snapshot-card-title" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{title}{titleInfo && <KpiTooltip text={titleInfo} placement="right" />}</div>
                    {subtitle && <div className="snapshot-card-subtitle">{subtitle}</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {CategoryDropdownDesktop}
                    {SearchFieldDesktop}
                    {exportSlot(isExportDisabled, fetchAllForExport)}
                </div>
            </div>
            {categoryChips}
        </>
    );

    // ── Mobile Filters bottom sheet (SNAP-IO-6) ─────────────────────────────
    const filterSheet = filterSheetOpen ? (
        <div onClick={() => setFilterSheetOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 4200, display: 'flex', alignItems: 'flex-end' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: tk.card, width: '100%', borderTopLeftRadius: 16, borderTopRightRadius: 16, padding: 16, maxHeight: '70vh', display: 'flex', flexDirection: 'column', fontFamily: tk.font }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: tk.cardBorder, margin: '0 auto 12px' }} />
                <div style={{ fontSize: 14, fontWeight: 700, color: tk.t1, marginBottom: 10 }}>Category</div>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                    {renderCatOptions(sheetDraft, (n) => setSheetDraft((prev) => prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]), true)}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    <button type="button" onClick={() => { setSheetDraft([]); setSelectedCategories([]); setCatNoneSelected(true); setFilterSheetOpen(false); }} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: `1px solid ${tk.cardBorder}`, background: tk.card, color: tk.t1, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Reset</button>
                    <button type="button" onClick={() => { setSelectedCategories(sheetDraft); setCatNoneSelected(sheetDraft.length === 0); setFilterSheetOpen(false); }} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: tk.accent, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Apply</button>
                </div>
            </div>
        </div>
    ) : null;

    // ── Pinned section (from API response.pinnedItems) ──────────────────────
    const pinnedScroll = isMobile && pinnedItems.length > 3;
    const pinnedSection = pinnedItems.length > 0 ? (
        <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'space-between' : 'flex-start', gap: 6, padding: isMobile ? '4px 0' : '6px 0', fontSize: isMobile ? 11 : 12, color: '#6B7280', fontWeight: 600 }}>
                <span>Pinned ({pinnedItems.length})</span>
                {!isMobile && <span style={{ color: tk.t3 }}>·</span>}
                <button type="button" onClick={() => setConfirmClear(true)} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: isMobile ? '#6B7280' : tk.accent, fontSize: isMobile ? 11 : 12, fontWeight: 600, textDecoration: isMobile ? 'underline' : 'none' }}>Clear all</button>
            </div>
            <div style={pinnedScroll ? { maxHeight: 132, overflowY: 'auto' } : undefined}>
                {pinnedItems.map((row, i) => renderRow(row, { pinned: true, lastInGroup: i === pinnedItems.length - 1 }))}
            </div>
            <div style={{ borderBottom: `1px solid #E2E5EC`, margin: '6px 0' }} />
        </div>
    ) : null;

    // ── Body ─────────────────────────────────────────────────────────────────
    let body: React.ReactNode;
    if (apiLoading) {
        body = <>{TableHeader}{skeletonRows(pageSize)}</>;
    } else if (apiError) {
        body = <SectionError kpiName="items overview" onRetry={() => setApiError(false)} />;
    } else if (!sortedItems.length && !pinnedItems.length) {
        if (searchActive || categoryActive) {
            const emptyMsg = searchActive ? 'No items match your search.' : 'No items found for the selected category.';
            body = <div style={{ textAlign: 'center', padding: '28px 12px', fontSize: isMobile ? 12 : 13, color: tk.t3 }}>{emptyMsg}</div>;
        } else {
            body = <SectionEmpty kpiName="items overview" />;
        }
    } else {
        const showFrom = totalElements === 0 ? 0 : page * pageSize + 1;
        const showTo = Math.min((page + 1) * pageSize, totalElements);
        const countText = `Showing ${showFrom}–${showTo} of ${totalElements} items${pinnedItems.length ? ` · ${pinnedItems.length} pinned` : ''}`;
        const emptyMsg = searchActive
            ? 'No items match your search.'
            : categoryActive
                ? 'No items found for the selected category.'
                : null;
        const bodyRows = sortedItems.map((row, i) => ({ ...row, rank: page * pageSize + i + 1 }));
        body = (
            <>
                {pinnedSection}
                {TableHeader}
                {bodyRows.length === 0
                    ? <div style={{ textAlign: 'center', padding: '28px 12px', fontSize: isMobile ? 12 : 13, color: tk.t3 }}>{emptyMsg}</div>
                    : bodyRows.map((row, i) => renderRow(row, { pinned: pins.isPinned(row.itemId), lastInGroup: i === bodyRows.length - 1 }))
                }
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${tk.divider}` }}>
                    <div style={{ fontSize: isMobile ? 10 : 11, color: tk.t3 }}>{countText}</div>
                    {(showSizeSelector || totalPages > 1) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexWrap: 'wrap' }}>
                            {PageSizeSelector}
                            {totalPages > 1 && (
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} style={pagBtn(tk, page === 0, isMobile)}>← Prev</button>
                                    <span style={{ fontSize: isMobile ? 10 : 11, color: tk.t2, padding: '4px 6px' }}>{page + 1} / {totalPages}</span>
                                    <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={pagBtn(tk, page >= totalPages - 1, isMobile)}>Next →</button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </>
        );
    }

    return (
        <>
            {header}
            {body}
            {filterSheet}

            {toast && (
                <div style={{ position: 'fixed', left: '50%', bottom: 28, transform: 'translateX(-50%)', zIndex: 4000, background: tk.t1, color: '#fff', padding: '10px 16px', borderRadius: 10, fontSize: 12, fontWeight: 600, fontFamily: tk.font, boxShadow: '0 6px 20px rgba(0,0,0,0.2)', maxWidth: 340, textAlign: 'center' }}>
                    {toast}
                </div>
            )}

            {pins.error && (
                <div style={{ position: 'fixed', left: '50%', bottom: 28, transform: 'translateX(-50%)', zIndex: 4000, background: tk.t1, color: '#fff', padding: '10px 16px', borderRadius: 10, fontSize: 12, fontWeight: 600, fontFamily: tk.font, boxShadow: '0 6px 20px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span>Couldn't save pin.</span>
                    <button type="button" onClick={pins.retry} style={{ background: 'transparent', border: 'none', color: tk.accent, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Tap to retry</button>
                </div>
            )}

            {confirmClear && (
                <div onClick={() => setConfirmClear(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 4100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: tk.card, borderRadius: 14, padding: 22, maxWidth: 320, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', fontFamily: tk.font }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: tk.t1, marginBottom: 8 }}>Unpin all {pins.count} items?</div>
                        <div style={{ fontSize: 12, color: tk.t2, marginBottom: 18 }}>This removes every pinned item from the top of the table.</div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <button type="button" onClick={() => setConfirmClear(false)} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${tk.cardBorder}`, background: tk.card, color: tk.t1, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                            <button type="button" onClick={handleClearAll} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: tk.accent, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Clear</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function pagBtn(tk: any, disabled: boolean, isMobile: boolean): React.CSSProperties {
    return {
        padding: isMobile ? '3px 8px' : '4px 10px',
        borderRadius: isMobile ? 5 : 6,
        border: `1px solid ${tk.cardBorder}`,
        background: disabled ? tk.hover : tk.card,
        color: disabled ? tk.t3 : tk.t1,
        fontSize: isMobile ? 10 : 11,
        fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
    };
}

// Global sales-channel filter — single control governing the whole report.
// Maps the UI labels to the `orderSource` query param; "Overall sales" sends nothing.
const CHANNEL_OPTIONS = ['Overall sales', 'Direct sales', '3rd party sales'];
const channelToOrderSource = (c: string): 'direct' | 'thirdparty' | undefined =>
    c === 'Direct sales' ? 'direct' : c === '3rd party sales' ? 'thirdparty' : undefined;

const DesktopSnapshotScreen = () => {
    // Outlet captures a service charge? (location-details uiFeatureFlags) — gates the KPI and waterfall line.
    const scEnabled = useServiceChargeEnabled();
    const location = useLocation();
    const navigate = useNavigate();
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const setTopBarProps = useDesktopTopBar();
    const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? findLocationId(auth) ?? '', [auth, branch]);
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);
    const itemsTracker = useMemo(() => makeReportTracker({ location_id: locationId, outlet_id: locationId, user_id: auth?.userId ?? '', user_role: auth?.role ?? '' }), [locationId, auth]);
    const dispatch = useAppDispatch();
    const snapshotDetails: SnapshotDetailsResponse | null = useAppSelector((s) => s.clickHouse.snapshotDetailsSuccess);
    const snapshotDetailsLoading: boolean = useAppSelector((s) => s.clickHouse.snapshotDetailsLoading);
    const snapshotComparison: SnapshotComparisonResponse | null = useAppSelector((s) => s.clickHouse.snapshotComparisonSuccess);
    const snapshotComparisonLoading: boolean = useAppSelector((s) => s.clickHouse.snapshotComparisonLoading);
    const liveItemSalesV2: LiveItemSalesV2Item[] = useAppSelector((s) => s.clickHouse.liveItemSalesV2Success);
    const liveItemSalesV2Loading: boolean = useAppSelector((s) => s.clickHouse.liveItemSalesV2Loading);
    const liveItemSalesV2Failure: boolean = useAppSelector((s) => s.clickHouse.liveItemSalesV2Failure);
    const liveItemSalesV2TotalElements: number = useAppSelector((s) => s.clickHouse.liveItemSalesV2TotalElements);
    const liveItemSalesV2TotalPages: number = useAppSelector((s) => s.clickHouse.liveItemSalesV2TotalPages);
    const liveByChannel: LiveByChannelItem[] = useAppSelector((s) => s.clickHouse.liveByChannelSuccess);
    const liveByChannelLoading: boolean = useAppSelector((s) => s.clickHouse.liveByChannelLoading);
    const liveByChannelFailure: boolean = useAppSelector((s) => s.clickHouse.liveByChannelFailure);
    const liveRefundSummary: LiveRefundSummaryItem[] = useAppSelector((s) => s.clickHouse.liveRefundSummarySuccess);
    const liveRefundSummaryLoading: boolean = useAppSelector((s) => s.clickHouse.liveRefundSummaryLoading);
    const liveRefundSummaryFailure: boolean = useAppSelector((s) => s.clickHouse.liveRefundSummaryFailure);
    const liveRefundDetails: LiveRefundDetailsItem[] = useAppSelector((s) => s.clickHouse.liveRefundDetailsSuccess);
    const liveRefundDetailsLoading: boolean = useAppSelector((s) => s.clickHouse.liveRefundDetailsLoading);
    const liveRefundDetailsFailure: boolean = useAppSelector((s) => s.clickHouse.liveRefundDetailsFailure);
    const liveRefundDetailsTotalPages: number = useAppSelector((s) => s.clickHouse.liveRefundDetailsTotalPages);
    const liveRefundDetailsTotalElements: number = useAppSelector((s) => s.clickHouse.liveRefundDetailsTotalElements);
    const liveStaffRefunds = useAppSelector((s) => s.clickHouse.liveStaffRefundsSuccess) as LiveStaffRefundsItem[];
    const liveStaffRefundsLoading = useAppSelector((s) => s.clickHouse.liveStaffRefundsLoading);
    const liveStaffRefundsFailure = useAppSelector((s) => s.clickHouse.liveStaffRefundsFailure);
    const cancelledOrders: OrderLogItem[] = useAppSelector((s) => s.clickHouse.cancelledOrderLogOrders);
    const cancelledOrdersLoading: boolean = useAppSelector((s) => s.clickHouse.cancelledOrderLogLoading);
    const cancelledOrdersFailure: boolean = useAppSelector((s) => s.clickHouse.cancelledOrderLogFailure);
    const liveVoidSummary: LiveVoidSummaryItem[] = useAppSelector((s) => s.clickHouse.liveVoidSummarySuccess);
    const liveVoidSummaryLoading: boolean = useAppSelector((s) => s.clickHouse.liveVoidSummaryLoading);
    const liveVoidSummaryFailure: boolean = useAppSelector((s) => s.clickHouse.liveVoidSummaryFailure);
    // Voids by Staff ranked bars — NOT YET DEPLOYED backend endpoint (see getVoidByStaffApi).
    const voidByStaff: LiveVoidByStaffItem[] = useAppSelector((s) => s.clickHouse.voidByStaffSuccess);
    const voidByStaffLoading: boolean = useAppSelector((s) => s.clickHouse.voidByStaffLoading);
    const voidByStaffFailure: boolean = useAppSelector((s) => s.clickHouse.voidByStaffFailure);
    // Void drill-in Mini-KPIs row (Voided Items · Void Value · Void Rate · Avg Void Value).
    const voidKpis: LiveVoidKpisResponse | null = useAppSelector((s) => s.clickHouse.voidKpisSuccess);
    const voidKpisLoading: boolean = useAppSelector((s) => s.clickHouse.voidKpisLoading);
    const voidKpisFailure: boolean = useAppSelector((s) => s.clickHouse.voidKpisFailure);
    const liveVoidDetails: LiveVoidDetailsItem[] = useAppSelector((s) => s.clickHouse.liveVoidDetailsSuccess);
    const liveVoidDetailsLoading: boolean = useAppSelector((s) => s.clickHouse.liveVoidDetailsLoading);
    const liveVoidDetailsFailure: boolean = useAppSelector((s) => s.clickHouse.liveVoidDetailsFailure);
    const liveVoidDetailsTotalPages: number = useAppSelector((s) => s.clickHouse.liveVoidDetailsTotalPages);
    const liveVoidDetailsTotalElements: number = useAppSelector((s) => s.clickHouse.liveVoidDetailsTotalElements);
    const hourlySalesData: HourlySalesResponse | null = useAppSelector((s) => s.clickHouse.hourlySalesData);
    const hourlySalesLoading: boolean = useAppSelector((s) => s.clickHouse.hourlySalesLoading);
    const hourlySalesFailure: boolean = useAppSelector((s) => s.clickHouse.hourlySalesFailure);
    const livePayments: LivePaymentsItem[] = useAppSelector((s) => s.clickHouse.livePaymentsSuccess);
    const livePaymentsLoading: boolean = useAppSelector((s) => s.clickHouse.livePaymentsLoading);
    const livePaymentsFailure: boolean = useAppSelector((s) => s.clickHouse.livePaymentsFailure);
    const liveStaffPerformance: LiveStaffPerformanceItem[] = useAppSelector((s) => s.clickHouse.liveStaffPerformanceSuccess);
    const liveStaffPerformanceLoading: boolean = useAppSelector((s) => s.clickHouse.liveStaffPerformanceLoading);
    const liveStaffPerformanceFailure: boolean = useAppSelector((s) => s.clickHouse.liveStaffPerformanceFailure);
    const liveStaffActiveClocking: LiveStaffActiveClockingItem[] = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingSuccess);
    const liveStaffActiveClockingLoading: boolean = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingLoading);
    const liveStaffActiveClockingFailure: boolean = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingFailure);
    // Staff Roster (Active Staff KPI drilldown) — /sales/live/v2/staff-active-clocking-v2
    const staffRosterV2Items: LiveStaffActiveClockingV2Item[] = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2Items);
    const staffRosterV2Loading: boolean = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2Loading);
    const staffRosterV2Failure: boolean = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2Failure);
    const staffRosterV2TotalElements: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2TotalElements);
    const staffRosterV2TotalPages: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2TotalPages);
    const staffRosterV2StaffWorked: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2StaffWorked);
    const staffRosterV2ActiveCount: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2ActiveCount);
    const staffRosterV2BreakCount: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2BreakCount);
    const staffRosterV2TotalWorkedHours: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2TotalWorkedHours);
    const staffRosterV2AvgSalesPerStaff: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2AvgSalesPerStaff);
    // Unpaid Orders (Today only)
    const liveUnpaidOrders: UnpaidOrderItem[] = useAppSelector((s) => s.clickHouse.liveUnpaidOrders);
    const liveUnpaidOrdersLoading: boolean = useAppSelector((s) => s.clickHouse.liveUnpaidOrdersLoading);
    const liveUnpaidOrdersFailure: boolean = useAppSelector((s) => s.clickHouse.liveUnpaidOrdersFailure);
    const liveUnpaidOrdersTotalElements: number = useAppSelector((s) => s.clickHouse.liveUnpaidOrdersTotalElements);
    const liveUnpaidLog: UnpaidOrderItem[] = useAppSelector((s) => s.clickHouse.liveUnpaidLog);
    const liveUnpaidLogLoading: boolean = useAppSelector((s) => s.clickHouse.liveUnpaidLogLoading);
    const liveUnpaidLogFailure: boolean = useAppSelector((s) => s.clickHouse.liveUnpaidLogFailure);
    // Comp Orders (Today only)
    const liveCompOrderLog: CompOrderLogItem[] = useAppSelector((s) => s.clickHouse.liveCompOrderLog);
    const liveCompOrderLogLoading: boolean = useAppSelector((s) => s.clickHouse.liveCompOrderLogLoading);
    const liveCompOrderLogFailure: boolean = useAppSelector((s) => s.clickHouse.liveCompOrderLogFailure);
    const liveCompOrderLogTotalElements: number = useAppSelector((s) => s.clickHouse.liveCompOrderLogTotalElements);
    const liveCompOrderLogTotalPages: number = useAppSelector((s) => s.clickHouse.liveCompOrderLogTotalPages);
    const liveCompReasons: CompReasonItem[] = useAppSelector((s) => s.clickHouse.liveCompReasons);
    const liveCompReasonsLoading: boolean = useAppSelector((s) => s.clickHouse.liveCompReasonsLoading);
    const liveCompReasonsFailure: boolean = useAppSelector((s) => s.clickHouse.liveCompReasonsFailure);
    const liveCompByStaff: CompByStaffItem[] = useAppSelector((s) => s.clickHouse.liveCompByStaff);
    const liveCompByStaffLoading: boolean = useAppSelector((s) => s.clickHouse.liveCompByStaffLoading);
    const liveCompByStaffFailure: boolean = useAppSelector((s) => s.clickHouse.liveCompByStaffFailure);
    const liveUnpaidLogTotalElements: number = useAppSelector((s) => s.clickHouse.liveUnpaidLogTotalElements);
    const liveUnpaidLogTotalPages: number = useAppSelector((s) => s.clickHouse.liveUnpaidLogTotalPages);
    const liveUnpaidLogDineInAvailable: boolean = useAppSelector((s) => s.clickHouse.liveUnpaidLogDineInAvailable);
    // Order Log (Paid Orders — Today only, used in Orders drill)
    const orderLogOrders = useAppSelector((s) => s.clickHouse.orderLogOrders) as OrderLogItem[];
    const orderLogLoading = useAppSelector((s) => s.clickHouse.orderLogLoading);
    const orderLogFailure = useAppSelector((s) => s.clickHouse.orderLogFailure);
    const orderLogTotalElements = useAppSelector((s) => s.clickHouse.orderLogTotalElements);
    const orderLogTotalPages = useAppSelector((s) => s.clickHouse.orderLogTotalPages);
    const orderLogPaidCount = useAppSelector((s) => s.clickHouse.orderLogPaidCount);
    const orderLogCancelledCount = useAppSelector((s) => s.clickHouse.orderLogCancelledCount);
    const orderLogDineInAvailable = useAppSelector((s) => s.clickHouse.orderLogDineInAvailable);
    const exportOrderLogLoading = useAppSelector((s) => s.clickHouse.exportOrderLogLoading);
    const channelNames = useAppSelector((s) => s.clickHouse.channelNames);
    const orderDetailData = useAppSelector((s) => s.clickHouse.orderDetailData) as OrderDetailResponse | null;
    const orderDetailLoading = useAppSelector((s) => s.clickHouse.orderDetailLoading);
    const orderDetailError = useAppSelector((s) => s.clickHouse.orderDetailError);

    // Memoize today/yesterday, anchored on the store's timezone. Recompute when the
    // outlet's timezone resolves (it can arrive after first paint on a cold load).
    const storeTz = useStoreTimeZone();
    const today = useMemo(() => todayStr(storeTz), [storeTz]);
    const yesterday = useMemo(() => yesterdayStr(storeTz), [storeTz]);
    const [selectedDate, setSelectedDate] = useState(today);
    const [activeDate, setActiveDate] = useSharedReportDateFilter('Today');
    // Global sales-channel filter — per-report, defaults to Overall.
    const [channel, setChannel] = useState('Overall sales');
    const orderSourceVal = channelToOrderSource(channel);
    const orderSourceParam: { orderSource?: 'direct' | 'thirdparty' } = orderSourceVal ? { orderSource: orderSourceVal } : {};
    const isThirdParty = orderSourceVal === 'thirdparty';
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // SW3 — deep-link into a specific waterfall deduction drill via ?drill=<key>, e.g. from
    // Sales/EOD's waterfall (which don't have their own Voids/Complimentary drill content).
    // 'refunds' (the waterfall row's own key) maps to this screen's local 'cancelled' drill.
    const [searchParams] = useSearchParams();
    // Several other effects unconditionally clear drillView on mount too (the [location.key]
    // reset below, and the [activeDate] one further down — the latter fires a SECOND time
    // once the "always open on Today" effect forces activeDate back to 'Today', if the shared
    // date filter arrived non-Today from Sales/EOD). A single self-consuming flag can't survive
    // that multi-render mount cascade, so this clears itself on the next tick instead — after
    // React has settled every mount-time render, but still well before any real user click.
    const drillFromUrlRef = useRef(false);
    // Where to send the user when they back out of a deep-linked drill — the page (and its own
    // drilldown, if it has one) they were on when they clicked the deduction link. Set from
    // location.state.returnTo, which Sales/EOD attach when they navigate here; cleared once
    // goBack actually uses it, so a later LOCAL drill-close (e.g. after backing out and opening
    // a different Snapshot KPI tile by clicking it) doesn't also redirect away.
    const deepLinkReturnToRef = useRef<string | null>(null);
    const [drillView, setDrillView] = useState<string | null>(() => {
        const raw = searchParams.get('drill');
        if (!raw) return null;
        drillFromUrlRef.current = true;
        deepLinkReturnToRef.current = (location.state as { returnTo?: string } | null)?.returnTo ?? null;
        return raw === 'refunds' ? 'cancelled' : raw;
    });
    // Where a deduction click was opened FROM, when it was a LOCAL jump (net-sales's own Voids/
    // Discounts/Complimentary/Refunds links) rather than a fresh entry from a main-page KPI tile.
    // Auto-resets whenever drillView closes through ANY path (date-filter change, 3rd-party
    // hide, this same goBack) — so it can never leak into an unrelated later drill.
    const [drillOrigin, setDrillOrigin] = useState<'net-sales' | null>(null);
    useEffect(() => { if (!drillView) setDrillOrigin(null); }, [drillView]);
    const netSalesKpiRowRef = useRef<HTMLDivElement>(null);
    const scrollToNetSalesKpiRef = useRef(false);
    useEffect(() => {
        if (drillView !== 'net-sales' || !scrollToNetSalesKpiRef.current) return;
        scrollToNetSalesKpiRef.current = false;
        requestAnimationFrame(() => netSalesKpiRowRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' }));
    }, [drillView]);
    const goBack = useCallback(() => {
        if (deepLinkReturnToRef.current) {
            const returnTo = deepLinkReturnToRef.current;
            deepLinkReturnToRef.current = null;
            navigate(returnTo, { replace: true });
            return;
        }
        if (drillOrigin === 'net-sales') {
            setDrillOrigin(null);
            scrollToNetSalesKpiRef.current = true;
            setDrillView('net-sales');
            return;
        }
        setDrillView(null);
    }, [navigate, drillOrigin]);
    useEffect(() => {
        if (!drillFromUrlRef.current) return;
        const t = setTimeout(() => { drillFromUrlRef.current = false; }, 0);
        return () => clearTimeout(t);
    }, []);
    useEffect(() => {
        // Skip while the deep-link flag is still active — see drillFromUrlRef above.
        if (drillFromUrlRef.current) return;
        setDrillView(null);
    }, [location.key]);
    const [cancelLogPage, setCancelLogPage] = useState(1);
    const CANCEL_PAGE_SIZE = 10;
    // Void details pagination — Redux, page 1-based, size 10 (Today + historical)
    const [voidDetailsPage, setVoidDetailsPage] = useState(1);
    const VOID_DETAILS_PAGE_SIZE = 10;
    // Whole-check void groups (same order number) expanded in the Void Log table.
    const [expandedVoidGroups, setExpandedVoidGroups] = useState<Set<string>>(new Set());
    const toggleVoidGroup = (key: string) => setExpandedVoidGroups((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const [voidReasonsPage, setVoidReasonsPage] = useState(1);
    const VOID_REASONS_PAGE_SIZE = 10;
    // Comp log pagination — Historical (legacy API) and Today (ClickHouse)
    const [compLogPage, setCompLogPage] = useState(0);
    const COMP_PAGE_SIZE = 10;
    const [compLogPageToday, setCompLogPageToday] = useState(0);
    const [compLogItems, setCompLogItems] = useState<any[]>([]);
    const [compLogTotalPages, setCompLogTotalPages] = useState(1);
    const [compLogTotalElements, setCompLogTotalElements] = useState(0);
    const [compLogPageLoading, setCompLogPageLoading] = useState(false);
    const [staffRosterPage, setStaffRosterPage] = useState(0);
    const STAFF_ROSTER_PAGE_SIZE = 10;
    const [staffClockingItems, setStaffClockingItems] = useState<any[]>([]);
    const [staffClockingTotalPages, setStaffClockingTotalPages] = useState(1);
    const [staffClockingTotalElements, setStaffClockingTotalElements] = useState(0);
    const [staffClockingPageLoading, setStaffClockingPageLoading] = useState(false);
    // Historical Active Staff = count of UNIQUE Employee Names across the FULL Staff Roster
    // (de-duplicated, not just the visible page). Computed by a dedicated fetch-all effect.
    const [rosterUniqueStaffCount, setRosterUniqueStaffCount] = useState(0);
    // Staff Roster drilldown (Active Staff KPI) — /sales/live/v2/staff-active-clocking-v2,
    // server-paginated/searchable/sortable, used for BOTH Today and historical date ranges.
    const [staffRosterV2Page, setStaffRosterV2Page] = useState(1);
    const [staffRosterV2PageSize, setStaffRosterV2PageSize] = useState(10);
    const [staffRosterV2Search, setStaffRosterV2Search] = useState('');
    const [staffRosterV2DebouncedSearch, setStaffRosterV2DebouncedSearch] = useState('');
    const [staffRosterV2SortBy, setStaffRosterV2SortBy] = useState('date');
    const [staffRosterV2SortAsc, setStaffRosterV2SortAsc] = useState(false);
    const [staffLeaderPage, setStaffLeaderPage] = useState(0);
    const STAFF_LEADER_PAGE_SIZE = 10;
    const [topItemsPage, setTopItemsPage] = useState(0);
    const TOP_ITEMS_PAGE_SIZE = 10;
    const [donutTooltip, setDonutTooltip] = useState<{ item: any; x: number; y: number } | null>(null);
    // Unpaid Orders (SNAP-14/15) — historical only; Today uses Redux
    const [unpaidCount, setUnpaidCount] = useState<number | null>(null);
    const [unpaidValue, setUnpaidValue] = useState<number | null>(null);
    const [unpaidAvg, setUnpaidAvg] = useState<number | null>(null);
    const [unpaidPriorCount, setUnpaidPriorCount] = useState<number | null>(null);
    const [unpaidServers, setUnpaidServers] = useState<any[]>([]);
    const [unpaidLogItems, setUnpaidLogItems] = useState<any[]>([]);
    const [unpaidLogTotal, setUnpaidLogTotal] = useState(0);
    const [unpaidLogPage, setUnpaidLogPage] = useState(0);
    const UNPAID_PAGE_SIZE = 25;
    // Today unpaid log pagination (Redux)
    const [unpaidLogPageToday, setUnpaidLogPageToday] = useState(1);
    const UNPAID_LOG_PAGE_SIZE_TODAY = 10;
    // Rows per page — one control for the whole Unpaid log, so both its segments
    // page at the same size (the Paid log has the same control).
    const [unpaidPageSize, setUnpaidPageSize] = useState(UNPAID_LOG_PAGE_SIZE_TODAY);
    const [unpaidSortBy, setUnpaidSortBy] = useState<string>('amount');
    const [unpaidSortDir, setUnpaidSortDir] = useState<'desc' | 'asc'>('desc');
    const [unpaidSearch, setUnpaidSearch] = useState('');
    const [unpaidDebouncedSearch, setUnpaidDebouncedSearch] = useState('');
    const [unpaidOrderTypes, setUnpaidOrderTypes] = useState<string[]>([]);
    const [unpaidTypeMenuOpen, setUnpaidTypeMenuOpen] = useState(false);
    const [unpaidLogLoading, setUnpaidLogLoading] = useState(false);
    const [unpaidExportLoading, setUnpaidExportLoading] = useState(false);
    // Today unpaid — full fetch for By Server KPI (decoupled from Order Log pagination)
    const [allUnpaidForByServer, setAllUnpaidForByServer] = useState<UnpaidOrderItem[]>([]);
    const [allUnpaidForByServerLoading, setAllUnpaidForByServerLoading] = useState(false);
    // ── Order Log state (Orders drill — Today only) ──
    const [olPage, setOlPage] = useState(0);
    const [olSort, setOlSort] = useState('orderTime');
    const [olSortDir, setOlSortDir] = useState<'desc' | 'asc'>('desc');
    const [olSearch, setOlSearch] = useState('');
    const [olDebouncedSearch, setOlDebouncedSearch] = useState('');
    const [olOrderTypes, setOlOrderTypes] = useState<string[]>([]);
    const [olMinAmt, setOlMinAmt] = useState('');
    const [olMaxAmt, setOlMaxAmt] = useState('');
    const [olMinGuests, setOlMinGuests] = useState('');
    const [olPageSize, setOlPageSize] = useState(10);
    const [olTypeMenuOpen, setOlTypeMenuOpen] = useState(false);
    const [olPhoneVisible, setOlPhoneVisible] = useState(false);
    const [olSelectedOrder, setOlSelectedOrder] = useState<any>(null);
    // ORD-1.3 — Paid / Cancelled segments. Resets to 'paid' on navigation away.
    const [olStatus, setOlStatus] = useState<OrderLogStatus>('paid');
    // ORD-1.4 — null means "the Operations preset", so a new column added to the
    // registry shows up without every user having to re-pick their columns.
    // ORD-1.3 — remembered per user, per log type; see useOrderLogColumns.
    const { setStored: setOlVisibleCols, resolve: resolveOlColumns } = useOrderLogColumns('snapshot-paid', auth?.userId ?? '');
    // ORD-1.4 — the Unpaid log keeps its own column selection.
    const { setStored: setUnpaidVisibleCols, resolve: resolveUnpaidColumns } = useOrderLogColumns('snapshot-unpaid', auth?.userId ?? '');
    const [unpaidSelectedOrder, setUnpaidSelectedOrder] = useState<UnpaidOrderItem | OrderLogItem | null>(null);
    // ── Unpaid log — Unpaid / Cancelled segments ──
    // The Unpaid side reads /unpaid-orders; the Cancelled side reads the same
    // /order-log?type=cancel endpoint the Paid log's own Cancelled segment uses,
    // so the two logs show one identical set of cancelled orders. Each segment
    // keeps its own page, sort and column selection — the two datasets share
    // neither sort ids nor columns.
    const [unpaidStatus, setUnpaidStatus] = useState<'unpaid' | 'cancelled'>('unpaid');
    const [unpaidCancelPage, setUnpaidCancelPage] = useState(1);
    const [unpaidCancelSort, setUnpaidCancelSort] = useState('orderTime');
    const [unpaidCancelSortDir, setUnpaidCancelSortDir] = useState<'desc' | 'asc'>('desc');
    const { setStored: setUnpaidCancelVisibleCols, resolve: resolveUnpaidCancelColumns } = useOrderLogColumns('snapshot-unpaid-cancelled', auth?.userId ?? '');
    const fetchIdRef = useRef(0);
    const snapshotFetchKeyRef = useRef<string | null>(null);
    const liveItemSalesFetchKeyRef = useRef<string | null>(null);
    const allTopItemsRef = useRef<any[]>([]);
    const kpiExportRef = useRef<Record<string, any>>({});
    const retryFunctionsRef = useRef<Record<string, () => void>>({});
    const [sectionErrors, setSectionErrors] = useState<Record<string, boolean>>({});
    const markSectionError = useCallback((key: string, hasError: boolean) => {
        setSectionErrors((prev) => (prev[key] === hasError ? prev : { ...prev, [key]: hasError }));
    }, []);
    const retrySection = useCallback((key: string) => {
        retryFunctionsRef.current[key]?.();
    }, []);
    const [refreshedTime, setRefreshedTime] = useState<string | null>(() => localStorage.getItem(SNAPSHOT_REFRESH_TS_KEY));
    const [refreshNonce, setRefreshNonce] = useState(0);
    const recordRefreshedTime = useCallback(() => {
        const ts = new Date().toISOString();
        localStorage.setItem(SNAPSHOT_REFRESH_TS_KEY, ts);
        setRefreshedTime(ts);
    }, []);
    const [isSpinningD, setIsSpinningD] = useState(false);
    const handleManualRefresh = useCallback(() => {
        setRefreshNonce(n => n + 1);
        recordRefreshedTime();
        setIsSpinningD(true);
    }, [recordRefreshedTime]);
    // Historical discount summary (offer + staff breakdown) loading flag — error is
    // tracked via sectionErrors.discounts / retrySection('discounts').
    const [discountsLoading, setDiscountsLoading] = useState(false);
    const [discountsStaffPage, setDiscountsStaffPage] = useState(0);
    const [data, setData] = useState<any>({
        channel: null,
        voidSummary: null,
        voidDetails: null,
        voidPieChart: null,
        checkIn: null,
        guests: null,
        peak: null,
        hourly: null,
        categoryItemSales: null,
        categoryItemSalesTotalElements: null,
        tenderPayment: null,
        // Live today-only fields
        liveBilledOrders: null,
        liveBilledOrdersCount: null,
        liveCancelledOrders: null,
        liveVoidOrders: null,
        liveCompOrders: null,
        liveItemSales: null,
        liveTracking: null,
        liveTrackingPaid: null,
        liveTables: null,
        isToday: false,
    });

    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activeDate, today, yesterday, storeTz);
        if (range) return range;
        return { startDate: selectedDate, endDate: selectedDate };
    }, [activeDate, selectedDate, today, yesterday, storeTz]);

    /*
     * REF-1 — the period's refund component vector, from the one shared accessor. Real
     * data off GET /sales/live/v2/refund-log — the same hook the REF-2 Refunds drill-in
     * uses, so this screen's netting and that page's own figures can never drift.
     * Declared here (rather than beside the waterfall) so the Tips & Gratuity drill data,
     * which computes earlier in the file, can read it too.
     */
    const { summary: refundSummary } = useRefundLogPeriod({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        source: orderSourceVal === 'direct' ? 'direct' : orderSourceVal === 'thirdparty' ? 'thirdparty' : 'overall',
    });

    // Human-readable active date range for the "All outlets" export file header.
    const exportDateRangeLabel = exportPeriodFor(activeDate, dateRange)?.label ?? activeDate;

    // ClickHouse date params for drill effects / retries — omitted for Today (live path), passed for historical.
    // Resolved from the range itself, not `data.isToday` — that flag only flips inside the date-change
    // effect, so on the first render after a date change it still describes the previous range.
    const chDatesView: { startDate?: string; endDate?: string } = isTodayRange(dateRange.startDate, dateRange.endDate, today) ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };

    // CANC-1: the `cancelled` key is the (pre-existing) Refunds drill; cancelled ORDERS is
    // its own drill under `cancelled-orders`.
    const DRILL_TITLES: Record<string, string> = { 'net-sales': 'Net Sales', orders: 'Paid Orders', cancelled: 'Refunds', 'cancelled-orders': 'Cancelled Orders', staff: 'Staff Worked', voids: 'Voids', tips: TG_DRILL_TITLE, unpaid: 'Unpaid Orders', complimentary: 'Complimentary Orders', discounts: 'Discounts' };

    // CANC-1 — Cancelled Orders KPI + drill, from /sales/live/v2/order-log with type=cancel.
    // One fetch feeds both: the KPI shows the row count, the drill FE-paginates the same rows,
    // so the card and the log can never disagree. Cancelled value is loss-tracking only and is
    // never added into Net Sales, Paid Orders or any other revenue KPI.
    const fetchCancelledOrders = useCallback(() => {
        if (!locationId) return;
        dispatch(fetchCancelledOrderLogRequest({
            locationId,
            page: 1,
            size: CANCELLED_LOG_FETCH_SIZE,
            sortBy: 'orderTime',
            sortDir: 'desc',
            ...chDatesView,
            ...orderSourceParam,
            type: 'cancel',
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal]);
    useEffect(() => { fetchCancelledOrders(); }, [fetchCancelledOrders, refreshNonce]);

    // CANC-1 — one outlet's cancelled orders, for the "All outlets" export scope. The export
    // button calls this per accessible outlet, so it must NOT reuse the on-screen redux rows.
    const fetchCancelledOrdersForExport = useCallback(async (loc: string): Promise<OrderLogItem[]> => {
        const res: any = await getOrderLogApi({
            locationId: loc,
            page: 1,
            size: CANCELLED_LOG_FETCH_SIZE,
            sortBy: 'orderTime',
            sortDir: 'desc',
            ...chDatesView,
            ...orderSourceParam,
            type: 'cancel',
        });
        return Array.isArray(res?.orders) ? res.orders : (Array.isArray(res) ? res : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chDatesView.startDate, chDatesView.endDate, orderSourceVal]);


    // Close drill when date filter changes; also clear Order Log (Paid Orders) search + filters so they don't leak into the new range
    // SW3 — this fires on mount too (like every effect, and can fire a second time on mount if
    // the "always open on Today" effect below forces activeDate to change), which would
    // otherwise stomp a ?drill=<key> deep link before the user ever sees it open. See
    // drillFromUrlRef above.
    useEffect(() => { if (!drillFromUrlRef.current) setDrillView(null); setCancelLogPage(1); setCompLogPage(0); setStaffLeaderPage(0); setTopItemsPage(0); setUnpaidLogPage(0); setUnpaidLogPageToday(1); setUnpaidStatus('unpaid'); setUnpaidCancelPage(1); setUnpaidSortBy('amount'); setUnpaidSortDir('desc'); setUnpaidExportLoading(false); setAllUnpaidForByServer([]); setAllUnpaidForByServerLoading(false); allTopItemsRef.current = []; setOlSearch(''); setOlDebouncedSearch(''); setOlOrderTypes([]); setOlMinAmt(''); setOlMaxAmt(''); setOlMinGuests(''); setOlPage(0); setOlTypeMenuOpen(false); setOlStatus('paid'); }, [activeDate]);

    // Under 3rd party, the Active Staff / Void / Complimentary KPIs are hidden — close their drills if open.
    useEffect(() => { if (isThirdParty && (drillView === 'staff' || drillView === 'voids' || drillView === 'complimentary')) setDrillView(null); }, [isThirdParty, drillView]);

    // Global sales-channel change also clears the Paid + Unpaid Orders drill search + filters so they
    // don't leak across channels. Done in the change handler (not an effect) so the fetch effects see
    // the new channel and the cleared filters in one pass — no intermediate stale-filter request.
    const handleChannelChange = useCallback((c: string) => {
        setChannel(c);
        setOlSearch(''); setOlDebouncedSearch(''); setOlOrderTypes([]); setOlMinAmt(''); setOlMaxAmt(''); setOlMinGuests(''); setOlPage(0); setOlTypeMenuOpen(false); setOlSelectedOrder(null);
        setUnpaidSearch(''); setUnpaidDebouncedSearch(''); setUnpaidOrderTypes([]); setUnpaidTypeMenuOpen(false); setUnpaidSortBy('amount'); setUnpaidSortDir('desc'); setUnpaidLogPageToday(1); setUnpaidLogPage(0); setUnpaidStatus('unpaid'); setUnpaidCancelPage(1); setUnpaidSelectedOrder(null);
    }, []);

    // Push title + date filters to the shared TopBar via context (check-in analytics pattern)
    const buildSnapshotKpiExport = useCallback(async (_format?: ExportFormat, opts?: { locationId?: string }) => {
        let k: Record<string, any> = kpiExportRef.current;
        if (opts?.locationId) {
            const loc = opts.locationId;
            const { chDates, orderSourceVal: os, token: tok } = kpiExportRef.current;
            // ZAR-1762 — Tips & Grat now comes straight off snapshot-details' tipAndGratuity,
            // so this refetch no longer needs the staff-performance call it only summed tips from.
            const [snap, refundRes, staffV2Res, voidRes, compRes, cancelledRes]: any[] = await Promise.all([
                getSnapshotDetails({ locationId: loc, ...chDates, orderSource: os, token: tok }),
                getLiveRefundSummaryApi({ locationId: loc, ...chDates, orderSource: os, token: tok }),
                getLiveStaffActiveClockingV2Api({ locationId: loc, ...chDates, page: 1, size: 1, sortBy: 'date', sortAsc: false, orderSource: os }),
                getLiveVoidSummaryApi(loc, tok, chDates?.startDate, chDates?.endDate, os),
                getLiveCompOrderLogApi({ locationId: loc, page: 1, size: COMP_PAGE_SIZE, ...chDates, orderSource: os }),
                // CANC-1 — Cancelled orders for the override outlet, same call as fetchCancelledOrdersForExport.
                getOrderLogApi({ locationId: loc, page: 1, size: CANCELLED_LOG_FETCH_SIZE, sortBy: 'orderTime', sortDir: 'desc', ...chDates, orderSource: os, type: 'cancel' }),
            ]);
            const refundItems: any[] = Array.isArray(refundRes) ? refundRes : (refundRes?.data ?? []);
            const voidItems: any[] = Array.isArray(voidRes) ? voidRes : (voidRes?.data ?? []);
            const compItems: any[] = Array.isArray(compRes?.orders) ? compRes.orders : (Array.isArray(compRes?.content) ? compRes.content : (Array.isArray(compRes) ? compRes : []));
            const cancelledCountOther = Array.isArray(cancelledRes?.orders) ? cancelledRes.orders.length : (Array.isArray(cancelledRes) ? cancelledRes.length : 0);
            const unpaidProbe: any = await getLiveUnpaidOrdersApi({ locationId: loc, page: 1, size: 1, ...chDates, orderSource: os });
            const unpaidTotal = num(unpaidProbe?.totalElements ?? unpaidProbe?.total ?? 0);
            let unpaidAmountOther = 0;
            if (unpaidTotal > 0) {
                const unpaidAll: any = await getLiveUnpaidOrdersApi({ locationId: loc, page: 1, size: unpaidTotal, ...chDates, orderSource: os });
                const unpaidList: any[] = Array.isArray(unpaidAll?.orders) ? unpaidAll.orders : [];
                unpaidAmountOther = unpaidList.reduce((s: number, o: any) => s + (Number(o.amount) || 0), 0);
            }
            const netSalesOther = num(snap?.netSales);
            const totalOrdersOther = num(snap?.orderCount);
            k = {
                ...kpiExportRef.current,
                netSales: netSalesOther,
                spendPerGuest: num(snap?.guestCount) > 0 ? num(snap?.spendPerGuest) : null,
                totalOrders: totalOrdersOther,
                unpaidCount: unpaidTotal,
                unpaidAmount: unpaidAmountOther,
                avgOrder: totalOrdersOther > 0 ? netSalesOther / totalOrdersOther : 0,
                serviceCharge: readServiceCharge(snap, scEnabled)?.amount ?? null,
                refund: parseFloat(refundItems.reduce((s: number, item: any) => s + (item.cancelledAmt || 0), 0).toFixed(2)),
                // ZAR-1762 / TG-1 — export mirrors the on-screen card: the backend's own combined figure.
                tipsAndGratuity: num(snap?.tipAndGratuity),
                staffWorked: num(staffV2Res?.staffWorked),
                voidCount: voidOrdersFrom(voidItems),
                compCount: num(compRes?.totalElements ?? compRes?.total ?? compItems.length),
                compAmount: compItems.reduce((s: number, o: any) => s + (Number(o.amount) || 0), 0),
                cancelledCount: cancelledCountOther,
                discounts: num(snap?.discount),
                guestCount: num(snap?.guestCount),
            };
        }
        const sym = getCurrencySymbol();
        const fmtMoney = (v: number) => `${sym}${formatMonetaryExport(v)}`;
        const rows: ExportCell[][] = [
            ['From', fmtExportDate(k.dateRange?.startDate)],
            ['To', fmtExportDate(k.dateRange?.endDate)],
            ['Channel Filter', k.channel],
            ['Net Sales', fmtMoney(num(k.netSales))],
            ['Paid orders', fmtNum(k.totalOrders)],
            ['Unpaid orders', k.unpaidCount ? `${fmtNum(k.unpaidCount)} (${fmtMoney(num(k.unpaidAmount))})` : fmtNum(k.unpaidCount)],
            ['Avg Order', fmtMoney(num(k.avgOrder))],
            ...(k.serviceCharge != null ? [['Service Charge', fmtMoney(num(k.serviceCharge))] as ExportCell[]] : []),
            ['Refund', fmtMoney(num(k.refund))],
        ];
        // Staff Worked / Void / Complimentary tiles are hidden on screen under the
        // 3rd-party channel filter — match that on export.
        if (!k.isThirdParty) {
            rows.push(['Staff Worked', fmtNum(k.staffWorked)]);
            rows.push(['Voided Items', fmtNum(k.voidCount)]);
        }
        rows.push(['Tips & Grat', fmtMoney(num(k.tipsAndGratuity))]);
        if (!k.isThirdParty) {
            rows.push(['Complimentary', k.compAmount > 0 ? `${fmtNum(k.compCount)} (${fmtMoney(num(k.compAmount))})` : fmtNum(k.compCount)]);
        }
        // CANC-1 — Cancelled orders. Shown under every source (unlike Staff Worked / Void /
        // Complimentary), matching the on-screen "Cancelled" tile which is never hidden.
        rows.push(['Cancelled', fmtNum(k.cancelledCount)]);
        rows.push(['Discounts', fmtMoney(num(k.discounts))]);
        if (!k.isThirdParty) {
            rows.push(['Guests', fmtNum(k.guestCount)]);
        }
        rows.push(['Spend / Guest', k.spendPerGuest == null ? '—' : fmtMoney(num(k.spendPerGuest))]);
        return { headers: ['KPI', 'Value'], rows };
    }, []);

    useEffect(() => {
        setTopBarProps({
            breadcrumbs: drillView ? 'Reports › Snapshot' : 'Reports',
            title: drillView ? DRILL_TITLES[drillView] ?? 'Snapshot' : 'Snapshot',
            dateFilters: drillView ? undefined : {
                value: activeDate,
                onChange: setActiveDate,
            },
            // Active Staff / Voids / Complimentary drills: hide the global sales-channel filter —
            // these KPIs are hidden under 3rd party and their drills shouldn't offer channel switching.
            sourceFilters: (drillView === 'staff' || drillView === 'voids' || drillView === 'complimentary') ? undefined : {
                options: CHANNEL_OPTIONS,
                value: channel,
                onChange: handleChannelChange,
            },
            leftAddon: drillView ? (
                <div onClick={goBack} style={{ width: 28, height: 28, borderRadius: 6, background: T.hover, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginRight: 4 }}>
                    <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t2 }}>arrow_back</span>
                </div>
            ) : null,
            onExport: drillView ? undefined : () => {
                const items = allTopItemsRef.current;
                if (!items.length) return;
                // Routed through exportItemsCSV (rep-stg-1.8 Stamp-KPIs refactor): it already
                // saves .xlsx on native and CSV_BOM + escapeCsvCell on web (SSE-23).
                exportItemsCSV(items.map((item, i) => ({ rank: i + 1, name: item.name, qty: item.qty, rev: item.rev })), getCurrencySymbol());
            },
            extraContent: (!drillView) ? (
                <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                    title="Snapshot KPIs"
                    filename={kpiExportFilename('snapshot-kpis', dateRange.startDate, dateRange.endDate, orderSourceVal)}
                    buildExport={buildSnapshotKpiExport}
                    variant="desktop"
                    iconOnly
                />
            ) : undefined,
        });
    }, [setTopBarProps, activeDate, channel, handleChannelChange, drillView, goBack, refreshedTime, handleManualRefresh, isSpinningD, buildSnapshotKpiExport, dateRange.startDate, dateRange.endDate, orderSourceVal]);

    useEffect(() => {
        if (!locationId || !token) {
            setLoading(false);
            return;
        }

        // Guard against duplicate/stale fetches (StrictMode double-mount, rapid date changes)
        const id = ++fetchIdRef.current;
        // Resolved-range check, not the filter label — the date picker's today
        // (`custom:<today>:<today>`) must take the same live path as the Today pill,
        // the way Sales already does. See isTodayRange (ZAR-1762).
        const isToday = isTodayRange(dateRange.startDate, dateRange.endDate, today);

        const opts = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
        // ClickHouse date params — omitted for Today (live path), passed only for historical ranges
        const chDates: { startDate?: string; endDate?: string } = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        setLoading(true);
        setError('');

        // Helper: update a single data field progressively (skip if stale fetch)
        const patch = (fields: Record<string, any>) => {
            if (id !== fetchIdRef.current) return;
            setData((prev: any) => ({ ...prev, ...fields }));
        };

        // Helper: run a section-scoped fetch, track its error state, and stash a retry
        // closure so the section's Retry button can re-run this exact call.
        const runSection = (key: string, promiseFactory: () => Promise<any>, field: string, defaultValue: any = null) => {
            const run = () => {
                markSectionError(key, false);
                promiseFactory()
                    .then((r) => {
                        patch({ [field]: r });
                        markSectionError(key, false);
                    })
                    .catch(() => {
                        patch({ [field]: defaultValue });
                        markSectionError(key, true);
                    });
            };
            retryFunctionsRef.current[key] = run;
            run();
        };

        if (isToday) {
            const fetchKey = `${locationId}-${dateRange.startDate}-${refreshNonce}-${channel}`;
            if (snapshotFetchKeyRef.current !== fetchKey) {
                snapshotFetchKeyRef.current = fetchKey;
                recordRefreshedTime();
                dispatch(fetchSnapshotDetailsRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchSnapshotComparisonRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchLiveByChannelRequest({ locationId, ...chDates, ...orderSourceParam }));
                // ZAR-2371 / SBS-03 — Source rows fetched alongside Channel, so the card's toggle only swaps data.
                dispatch(fetchLiveBySourceRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveRefundSummaryRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchLiveStaffRefundsRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveVoidSummaryRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }));
                dispatch(fetchLivePaymentsRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveStaffActiveClockingRequest(locationId, orderSourceVal));
                dispatch(fetchLiveStaffActiveClockingV2Request({ locationId, ...chDates, search: '', page: 1, size: 10, sortBy: 'date', sortAsc: false, ...orderSourceParam }));
                dispatch(fetchLiveUnpaidOrdersRequest({ locationId, size: 10, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveCompOrderLogRequest({ locationId, page: 1, size: COMP_PAGE_SIZE, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveCompReasonsRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveCompByStaffRequest({ locationId, ...chDates, ...orderSourceParam }));
            }
            // Reset to today shape immediately
            setTopItemsPage(0);
            setData((prev: any) => ({ ...prev, isToday: true, channel: null, voidSummary: null, voidDetails: null, categoryItemSales: null, categoryItemSalesTotalElements: null, voidPieChart: null, liveTrackingPaid: null, checkIn: null, guests: null, peak: null, hourly: null, tenderPayment: null }));

            setLoading(false);
        } else {
            // Reset so switching back to Today re-triggers snapshot details fetch
            snapshotFetchKeyRef.current = null;
            // Reset to historical shape immediately
            setData((prev: any) => ({ ...prev, isToday: false, liveBilledOrders: null, liveCancelledOrders: null, liveVoidOrders: null, liveItemSales: null, liveTracking: null, liveTrackingPaid: null, liveVoidedByCategory: null, tenderPayment: null }));

            // ClickHouse historical dispatches — same endpoints as Today, with startDate/endDate
            dispatch(fetchSnapshotDetailsRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchSnapshotComparisonRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchLiveByChannelRequest({ locationId, ...chDates, ...orderSourceParam }));
            // ZAR-2371 / SBS-03 — Source rows fetched alongside Channel, so the card's toggle only swaps data.
            dispatch(fetchLiveBySourceRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveRefundSummaryRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchLiveStaffRefundsRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveStaffActiveClockingRequest(locationId, orderSourceVal, chDates.startDate, chDates.endDate));
            dispatch(fetchLiveStaffActiveClockingV2Request({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, search: '', page: 1, size: 10, sortBy: 'date', sortAsc: false, ...orderSourceParam }));
            dispatch(fetchLivePaymentsRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveUnpaidOrdersRequest({ locationId, size: 10, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveCompOrderLogRequest({ locationId, page: 1, size: COMP_PAGE_SIZE, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveCompReasonsRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveCompByStaffRequest({ locationId, ...chDates, ...orderSourceParam }));

            // USSE-3: legacy /sales/snapshot-summary calls removed — Today never calls it;
            // historical runs on the same ClickHouse endpoints as Today (values from
            // snapshot-details, deltas from snapshot-details/comparison, leaderboard from
            // staff-performance). Redux loading flags drive the shimmers, same as Today.
            setLoading(false);

            dispatch(fetchLiveVoidSummaryRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }));
            // Discounts drill (offer & staff breakdown) is fetched in a dedicated effect that
            // runs for BOTH Today and historical (see the discounts useEffect below).
            // Refunds (historical): same ClickHouse endpoints as Today (refund-summary +
            // staff-refunds above, refund-details lazily when the drill opens), with
            // startDate/endDate. Legacy /sales/cancelSummary is no longer used here.
            // Void log (/sales/live/v2/paid-void-details) is fetched lazily when the user opens the voids drill.
        }
    }, [locationId, token, dateRange.startDate, dateRange.endDate, today, activeDate, orderSourceVal, refreshNonce, recordRefreshedTime]);

    useEffect(() => { setDiscountsStaffPage(0); }, [liveStaffPerformance]); // eslint-disable-line react-hooks/exhaustive-deps


    // Today item-sales fetch (Desktop) — ref guard prevents React StrictMode double-fire.
    // No dateRange dep so a dateRange recalculation on the same day never fires a second request.
    useEffect(() => {
        if (!locationId || !token || activeDate !== 'Today') return;
        const fetchKey = `${locationId}-${topItemsPage}-${refreshNonce}-${channel}`;
        if (liveItemSalesFetchKeyRef.current === fetchKey) return;
        liveItemSalesFetchKeyRef.current = fetchKey;
        const pins = readLocalPins(auth?.userId ?? '', locationId);
        dispatch(fetchLiveItemSalesV2Request(locationId, topItemsPage, TOP_ITEMS_PAGE_SIZE, pins.length ? pins : undefined, undefined, undefined, orderSourceVal));
    }, [locationId, token, activeDate, topItemsPage, refreshNonce, orderSourceVal]);

    // Historical (Desktop) — reset the Today ref so switching back to Today re-fetches.
    // USSE-2: the legacy /sales/category/getCategorySales fetch that lived here was removed —
    // data.categoryItemSales no longer backs any visible UI (the Items Overview card fetches
    // /sales/live/v2/item-sales itself for both Today and historical).
    useEffect(() => {
        if (!locationId || !token || activeDate === 'Today') return;
        liveItemSalesFetchKeyRef.current = null; // allow fresh fetch on return to Today
    }, [locationId, token, activeDate]);

    // Reset the cancelled drill when the user leaves it: page back to 1 so re-entry starts fresh.
    useEffect(() => {
        if (drillView !== 'cancelled') setCancelLogPage(1);
    }, [drillView]);

    // Reset void details page when leaving the voids drill or when date changes
    useEffect(() => {
        if (drillView !== 'voids') { setVoidDetailsPage(1); setVoidReasonsPage(1); setExpandedVoidGroups(new Set()); }
    }, [drillView]);
    useEffect(() => { setVoidDetailsPage(1); setVoidReasonsPage(1); setExpandedVoidGroups(new Set()); }, [dateRange.startDate, dateRange.endDate]);

    useEffect(() => {
        if (drillView !== 'complimentary') { setCompLogPage(0); setCompLogPageToday(0); }
    }, [drillView]);

    // Comp log — lazy fetch triggered when complimentary drill is opened (always ClickHouse)
    useEffect(() => {
        if (drillView !== 'complimentary' || !locationId || !token) return;
        dispatch(fetchLiveCompOrderLogRequest({ locationId, page: compLogPageToday + 1, size: COMP_PAGE_SIZE, ...chDatesView, ...orderSourceParam }));
        if (compLogPageToday === 0) {
            dispatch(fetchLiveCompReasonsRequest({ locationId, ...chDatesView, ...orderSourceParam }));
            dispatch(fetchLiveCompByStaffRequest({ locationId, ...chDatesView, ...orderSourceParam }));
        }
    }, [drillView, compLogPageToday, locationId, token, dateRange.startDate, dateRange.endDate, orderSourceVal]);

    // Void details — Redux, page 1-based. Fires lazily when drill opens (Today + historical).
    useEffect(() => {
        if (drillView !== 'voids' || !locationId) return;
        dispatch(fetchLiveVoidDetailsRequest(locationId, 1, 1000, chDatesView.startDate, chDatesView.endDate, orderSourceVal));
    }, [drillView, locationId, dateRange.startDate, dateRange.endDate, dispatch, orderSourceVal]);

    // Voids by Staff — lazily fetched when the voids drill opens (Today + historical). The
    // backend endpoint is not deployed yet, so this will fail until it ships; the section
    // shows its own failure/retry state (see SectionError below) rather than blocking the drill.
    useEffect(() => {
        if (drillView !== 'voids' || !locationId) return;
        dispatch(fetchVoidByStaffRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal));
    }, [drillView, locationId, dateRange.startDate, dateRange.endDate, dispatch, orderSourceVal]);

    // Void drill-in Mini-KPIs — lazily fetched when the voids drill opens (Today + historical).
    useEffect(() => {
        if (drillView !== 'voids' || !locationId) return;
        dispatch(fetchVoidKpisRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal));
    }, [drillView, locationId, dateRange.startDate, dateRange.endDate, dispatch, orderSourceVal]);

    // Staff Roster (v2) — reset paging/search/sort when leaving the staff drill or when the date changes.
    useEffect(() => {
        if (drillView !== 'staff') {
            setStaffRosterV2Page(1);
            setStaffRosterV2Search('');
            setStaffRosterV2DebouncedSearch('');
            setStaffRosterV2SortBy('date');
            setStaffRosterV2SortAsc(false);
        }
    }, [drillView]);
    useEffect(() => { setStaffRosterV2Page(1); }, [dateRange.startDate, dateRange.endDate]);

    // Debounce Staff Roster search — 300ms after last keystroke
    useEffect(() => {
        const t = setTimeout(() => setStaffRosterV2DebouncedSearch(staffRosterV2Search), 300);
        return () => clearTimeout(t);
    }, [staffRosterV2Search]);
    useEffect(() => { setStaffRosterV2Page(1); }, [staffRosterV2DebouncedSearch, staffRosterV2SortBy, staffRosterV2SortAsc]);

    // Staff Roster (v2) — /sales/live/v2/staff-active-clocking-v2, lazy fetch when the Active Staff
    // drill is opened; re-fetches on page/search/sort/date change. Backs BOTH the mini KPI row
    // (Active/On Break/Total Hours/Avg Sales per Staff) and the roster table for Today + historical.
    useEffect(() => {
        if (drillView !== 'staff' || !locationId) return;
        const isTodayRoster = activeDate === 'Today';
        dispatch(fetchLiveStaffActiveClockingV2Request({
            locationId,
            ...(isTodayRoster ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate }),
            search: staffRosterV2DebouncedSearch,
            page: staffRosterV2Page,
            size: staffRosterV2PageSize,
            sortBy: staffRosterV2SortBy,
            sortAsc: staffRosterV2SortAsc,
            ...orderSourceParam,
        }));
    }, [drillView, locationId, activeDate, dateRange.startDate, dateRange.endDate, staffRosterV2Page, staffRosterV2PageSize, staffRosterV2DebouncedSearch, staffRosterV2SortBy, staffRosterV2SortAsc, orderSourceVal, dispatch]);

    // Refund Order Log — same ClickHouse endpoint for Today and historical; historical just
    // adds startDate/endDate. Backend pagination via page/size.
    useEffect(() => {
        if (drillView !== 'cancelled' || !locationId) return;
        dispatch(fetchLiveRefundDetailsRequest(locationId, cancelLogPage, CANCEL_PAGE_SIZE, orderSourceVal, chDatesView.startDate, chDatesView.endDate));
    }, [drillView, cancelLogPage, locationId, dateRange.startDate, dateRange.endDate, data.isToday, orderSourceVal, refreshNonce, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounce unpaid search — 3 s after last keystroke
    useEffect(() => {
        const t = setTimeout(() => setUnpaidDebouncedSearch(unpaidSearch), 500);
        return () => clearTimeout(t);
    }, [unpaidSearch]);

    // Reset to page 1 when debounced search or order type filter changes — both segments
    // share the search box and the Order Type filter, so both go back to their first page.
    useEffect(() => { setUnpaidLogPageToday(1); setUnpaidCancelPage(1); }, [unpaidDebouncedSearch, unpaidOrderTypes, unpaidPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    // Unpaid Order Log — always uses Redux ClickHouse (supports both Today and historical)
    useEffect(() => {
        // Only the Unpaid segment reads this endpoint; the Cancelled one has its own fetch below.
        if (drillView !== 'unpaid' || unpaidStatus !== 'unpaid' || !locationId) return;
        dispatch(fetchLiveUnpaidLogRequest({ locationId, page: unpaidLogPageToday, size: unpaidPageSize, sortBy: unpaidSortBy, sortDir: unpaidSortDir, search: unpaidDebouncedSearch || undefined, orderType: unpaidOrderTypes.length ? unpaidOrderTypes.join(',') : undefined, ...chDatesView, ...orderSourceParam }));
    }, [drillView, unpaidStatus, unpaidLogPageToday, unpaidPageSize, unpaidSortBy, unpaidSortDir, unpaidDebouncedSearch, unpaidOrderTypes, locationId, dateRange.startDate, dateRange.endDate, dispatch, orderSourceVal]);

    // Unpaid drill → Cancelled segment — the same /order-log?type=cancel call the Paid log's
    // Cancelled segment makes, so both logs show one identical set of cancelled orders. It
    // writes the shared orderLog* slice, which is safe because only one drill is open at a
    // time and the Paid drill refetches on its own when it reopens.
    useEffect(() => {
        if (drillView !== 'unpaid' || unpaidStatus !== 'cancelled' || !locationId) return;
        dispatch(fetchOrderLogRequest({
            locationId,
            page: unpaidCancelPage,
            size: unpaidPageSize,
            // Columns the API has no sort id for are ordered client-side — the request
            // keeps the default Time ordering for those.
            sortBy: SERVER_SORT_KEYS.indexOf(unpaidCancelSort) >= 0 ? unpaidCancelSort : 'orderTime',
            sortDir: unpaidCancelSortDir,
            search: unpaidDebouncedSearch || undefined,
            channelName: unpaidOrderTypes.length ? unpaidOrderTypes.join(',') : undefined,
            ...chDatesView,
            ...orderSourceParam,
            type: 'cancel' as const,
        }));
    }, [drillView, unpaidStatus, unpaidCancelPage, unpaidPageSize, unpaidCancelSort, unpaidCancelSortDir, unpaidDebouncedSearch, unpaidOrderTypes, locationId, dateRange.startDate, dateRange.endDate, dispatch, orderSourceVal]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch ALL unpaid orders for By Server KPI + Total Value/Avg — always ClickHouse (Today + historical).
    // Runs once the size-10 stats fetch returns totalElements; decouples By Server from Order Log pagination.
    useEffect(() => {
        // Wait for the count of the CURRENT scope — on a date/outlet/channel change this effect first runs
        // with the previous scope's count. Clear the list so a 0 count never sums a previous scope's orders.
        if (!locationId || liveUnpaidOrdersLoading || liveUnpaidOrdersTotalElements <= 0) {
            setAllUnpaidForByServer([]);
            setAllUnpaidForByServerLoading(false);
            return;
        }
        let stale = false;
        setAllUnpaidForByServerLoading(true);
        getLiveUnpaidOrdersApi({ locationId, page: 1, size: liveUnpaidOrdersTotalElements, ...chDatesView, ...orderSourceParam })
            .then((res: any) => {
                if (!stale) setAllUnpaidForByServer(Array.isArray(res?.orders) ? res.orders : []);
            })
            .catch(() => { if (!stale) setAllUnpaidForByServer([]); })
            .finally(() => { if (!stale) setAllUnpaidForByServerLoading(false); });
        // Drop the response if the scope changed while it was in flight.
        return () => { stale = true; };
    }, [locationId, liveUnpaidOrdersLoading, liveUnpaidOrdersTotalElements, dateRange.startDate, dateRange.endDate, orderSourceVal]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Order Log (Orders drill, Today only) ──
    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => setOlDebouncedSearch(olSearch), 300);
        return () => clearTimeout(t);
    }, [olSearch]);

    // Reset page when filters or date change
    useEffect(() => { setOlPage(0); }, [olDebouncedSearch, olOrderTypes, olMinAmt, olMaxAmt, olMinGuests, olPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset ol state when leaving orders drill — search + filters cleared too so a reopened
    // drill starts fresh (mirrors the unpaid reset below)
    useEffect(() => {
        if (drillView !== 'orders') {
            setOlPage(0);
            setOlSelectedOrder(null);
            setOlSearch('');
            setOlDebouncedSearch('');
            setOlOrderTypes([]);
            setOlMinAmt('');
            setOlMaxAmt('');
            setOlMinGuests('');
            setOlTypeMenuOpen(false);
            setOlStatus('paid');
        }
        if (drillView !== 'unpaid') {
            setUnpaidSelectedOrder(null);
            setUnpaidSearch('');
            setUnpaidDebouncedSearch('');
            setUnpaidOrderTypes([]);
            setUnpaidTypeMenuOpen(false);
            setUnpaidStatus('unpaid');
            setUnpaidCancelPage(1);
            setUnpaidCancelSort('orderTime');
            setUnpaidCancelSortDir('desc');
        }
        if (drillView !== 'orders' && drillView !== 'unpaid') {
            dispatch(clearOrderDetail());
        }
    }, [drillView]); // eslint-disable-line react-hooks/exhaustive-deps

    // Channel Names — fetch once per location (for Order Type dropdown)
    useEffect(() => {
        if (!locationId) return;
        dispatch(fetchChannelNamesRequest(locationId));
    }, [locationId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch Order Log when Orders drill is open
    useEffect(() => {
        if (drillView !== 'orders' || !locationId) return;
        const amtErr = olMinAmt && olMaxAmt && Number(olMinAmt) >= Number(olMaxAmt);
        if (amtErr) return;
        dispatch(fetchOrderLogRequest({
            locationId,
            page: olPage + 1,
            size: olPageSize,
            // Table / staff / financial columns are sorted client-side (the API has
            // no sort id for them) — the request keeps the default Time ordering.
            sortBy: SERVER_SORT_KEYS.indexOf(olSort) >= 0 ? olSort : 'orderTime',
            sortDir: olSortDir,
            search: olDebouncedSearch || undefined,
            channelName: olOrderTypes.length ? olOrderTypes.join(',') : undefined,
            guestCount: olMinGuests ? Number(olMinGuests) : undefined,
            minAmount: olMinAmt ? Number(olMinAmt) : undefined,
            maxAmount: olMaxAmt ? Number(olMaxAmt) : undefined,
            ...chDatesView,
            ...orderSourceParam,
            // Cancelled is the same endpoint with type=cancel (ORD-1.3).
            ...(olStatus === 'cancelled' ? { type: 'cancel' as const } : {}),
        }));
    }, [drillView, locationId, olPage, olPageSize, olSort, olSortDir, olDebouncedSearch, olOrderTypes, olMinAmt, olMaxAmt, olMinGuests, olStatus, dateRange.startDate, dateRange.endDate, data.isToday, orderSourceVal]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch Order Detail when an order row is clicked
    useEffect(() => {
        if (!olSelectedOrder || !locationId) { dispatch(clearOrderDetail()); return; }
        const orderId = (olSelectedOrder as OrderLogItem).orderId ?? '';
        if (orderId) dispatch(fetchOrderDetailRequest({ locationId, orderId, ...(isHistoricalReportDateFilter(activeDate) ? { type: 'historical' } : {}) }));
    }, [olSelectedOrder, locationId, activeDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch Order Detail when an unpaid order row is clicked
    useEffect(() => {
        if (!unpaidSelectedOrder || !locationId) return;
        dispatch(fetchOrderDetailRequest({ locationId, orderId: unpaidSelectedOrder.orderId ?? '', ...(isHistoricalReportDateFilter(activeDate) ? { type: 'historical' } : {}) }));
    }, [unpaidSelectedOrder, locationId, activeDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Stamp KPIs — always from snapshotDetails (ClickHouse, dispatched with startDate/endDate for historical)
    const billedSrc = unwrapRes(data.liveBilledOrders);
    const netSales = snapshotDetails?.netSales ?? pick(billedSrc, 'totalNetSales', 'netSales', 'totalSales', 'net_sales', 'total_sales', 'revenue');
    const netSalesYesterday = pick(billedSrc, 'netSalesYesterday');

    const totalOrders = snapshotDetails?.orderCount
        ?? pick(billedSrc, 'totalTransactions', 'totalOrders', 'orders', 'total_orders', 'orderCount', 'totalOrderCount', 'number_of_orders');

    const avgOrder = snapshotDetails?.orderCount
        ? num(snapshotDetails.netSales) / num(snapshotDetails.orderCount)
        : num(pick(billedSrc, 'avgOrderValue'));
    const tips = num(snapshotDetails?.tip);
    // ZAR-1762 / TG-1 — the card face reads the backend's own combined total and combined
    // delta (`tipAndGratuity` on snapshot-details and on its /comparison twin), so the FE
    // never re-derives either figure.
    const tipsAndGratuity = num(snapshotDetails?.tipAndGratuity);
    const tipsGratuityPct = snapshotComparison?.tipAndGratuity != null && Number.isFinite(Number(snapshotComparison.tipAndGratuity))
        ? Number(snapshotComparison.tipAndGratuity)
        : null;
    // Service Charge stamp KPI — only for outlets that capture one (0 included). Not drillable. "—" when
    // the prior period had none (backend sends a null %); a $0.00 period still shows its delta (0.00%) like every other tile.
    const serviceCharge = readServiceCharge(snapshotDetails, scEnabled);
    const serviceChargePct = snapshotComparison?.serviceFee != null && Number.isFinite(Number(snapshotComparison.serviceFee))
        ? Number(snapshotComparison.serviceFee)
        : null;
    const tax = snapshotDetails?.tax ?? pick(billedSrc, 'tax', 'totalTax', 'taxAmount');
    // ZAR-1762 / TG-2 — both drill-in sections, rebuilt whenever the source toggle or date
    // range changes so no value from a previous period/source survives. Totals, order counts
    // and Net Sales come from snapshot-details; staff-performance and by-channel supply only
    // the two rankings and the two channel panels, which snapshot-details cannot provide.
    const tipsGratuityDrillData = useMemo(() => buildTipsGratuityData({
        staff: liveStaffPerformance.map((s) => ({
            name: s.staff_name,
            tip: s.tip ?? 0,
            gratuity: s.gratuity ?? 0,
            orderCount: s.orderCount ?? 0,
            netSales: s.netSales ?? 0,
        })),
        channels: liveByChannel.map((c) => ({ channel: c.channel, tip: c.tip ?? 0, gratuity: c.gratuity ?? 0 })),
        // Tip Rate / Gratuity Rate share the screen's Net Sales denominator.
        netSales: num(snapshotDetails?.netSales),
        totalOrders: num(snapshotDetails?.orderCount),
        tipsTotal: num(snapshotDetails?.tip),
        gratuityTotal: num(snapshotDetails?.gratuity),
        ordersWithTips: num(snapshotDetails?.ordersWithTips),
        ordersWithGratuity: num(snapshotDetails?.ordersWithGratuity),
        // REF-6 — nets the Staff Tips ranking and carries the tip-out exposure icon.
        tipRefundsByCheckOwner: tipRefundsByCheckOwner(refundSummary.records),
    }), [liveStaffPerformance, liveByChannel, snapshotDetails, refundSummary]);
    const discounts = snapshotDetails?.discount ?? pick(billedSrc, 'discounts', 'totalDiscount', 'totalDiscounts');
    // Guests stamp KPI — value from snapshot-details guestCount (Today + historical)
    const guests = snapshotDetails?.guestCount ?? 0;
    // SPG-1: Spend per Guest stamp KPI — value from snapshot-details spendPerGuest, scoped to
    // guest-bearing orders. "—" when there are no guests in the active period/source (incalculable),
    // never $0.00.
    const spendPerGuest = guests > 0 ? num(snapshotDetails?.spendPerGuest) : null;

    // ── Historical discount breakdown (offers + staff) — sourced from getDiscountSummary ──
    const discountList = listFrom(data.discounts);
    const discountOffers = discountList
        .map((d: any) => ({
            name: d.offerName ?? d.discountName ?? d.name ?? d.offer ?? '—',
            type: d.discountType ?? d.type ?? '—',
            rate: Number(d.discountRate ?? d.rate ?? d.percentage ?? 0) || 0,
            sales: Number(d.salesAmount ?? d.grossSales ?? d.sales ?? 0) || 0,
            amount: Number(d.discountAmount ?? d.amount ?? d.totalDiscount ?? d.value ?? 0) || 0,
            count: Number(d.orderCount ?? d.count ?? d.orders ?? 0) || 0,
        }))
        .filter((d: any) => d.amount > 0)
        .sort((a: any, b: any) => b.amount - a.amount)
        .slice(0, 10);
    const staffDiscountMap: Record<string, { name: string; role: string; total: number; count: number }> = {};
    discountList.forEach((d: any) => {
        const staffName = d.stew ?? d.staffName ?? d.employeeName ?? d.staff ?? '';
        if (staffName) {
            if (!staffDiscountMap[staffName]) staffDiscountMap[staffName] = { name: staffName, role: d.role ?? d.staffRole ?? '', total: 0, count: 0 };
            staffDiscountMap[staffName].total += Number(d.discountAmount ?? d.amount ?? 0) || 0;
            staffDiscountMap[staffName].count += Number(d.orderCount ?? d.count ?? 1) || 0;
        }
    });
    const staffDiscounts = Object.values(staffDiscountMap).sort((a, b) => b.total - a.total).slice(0, 6);
    // Discounted order count — sum of per-offer order counts from getDiscountSummary
    const discOrders = discountOffers.reduce((s: number, d: any) => s + d.count, 0);

    // Today unpaid derived values — sum the FULL all-orders fetch (same dataset as By Server)
    // so Total Value matches the By Server total. Fall back to the size-10 stats fetch only
    // until the all-orders fetch resolves.
    const todayUnpaidValue = useMemo(() => {
        const source = allUnpaidForByServer.length > 0 ? allUnpaidForByServer : liveUnpaidOrders;
        return source.reduce((s, o) => s + (Number(o.amount) || 0), 0);
    }, [allUnpaidForByServer, liveUnpaidOrders]);
    const todayUnpaidAvg = liveUnpaidOrdersTotalElements > 0
        ? todayUnpaidValue / liveUnpaidOrdersTotalElements
        : 0;
    const todayUnpaidServers = useMemo(() => {
        const map = new Map<string, { name: string; count: number; amount: number }>();
        liveUnpaidOrders.forEach((o) => {
            const key = o.serverName?.trim() || 'Unknown';
            const existing = map.get(key) ?? { name: key, count: 0, amount: 0 };
            map.set(key, { name: existing.name, count: existing.count + 1, amount: existing.amount + (Number(o.amount) || 0) });
        });
        return [...map.values()].sort((a, b) => b.amount - a.amount);
    }, [liveUnpaidOrders]);

    // Full By Server aggregation from the all-orders fetch (Today only)
    const byServerFromAll = useMemo(() => {
        const map = new Map<string, { name: string; count: number; amount: number }>();
        allUnpaidForByServer.forEach((o) => {
            const key = (o?.serverName ?? '').trim() || 'Unknown';
            const existing = map.get(key) ?? { name: key, count: 0, amount: 0 };
            map.set(key, { name: existing.name, count: existing.count + 1, amount: existing.amount + (Number(o.amount) || 0) });
        });
        return [...map.values()].sort((a, b) => b.amount - a.amount);
    }, [allUnpaidForByServer]);

    // Unified unpaid values — always from Redux ClickHouse
    const displayUnpaidCount = liveUnpaidOrdersTotalElements;
    const displayUnpaidValue = todayUnpaidValue;
    const displayUnpaidAvg = todayUnpaidAvg;
    const displayUnpaidServers = todayUnpaidServers;

    // Unpaid delta: percentage change vs prior day count (inverted — increase is bad)
    const unpaidPct = (displayUnpaidCount > 0 && unpaidPriorCount != null && unpaidPriorCount > 0)
        ? ((displayUnpaidCount - unpaidPriorCount) / unpaidPriorCount) * 100
        : null;
    const unpaidLogTotalPages = Math.max(1, Math.ceil(unpaidLogTotal / UNPAID_PAGE_SIZE));
   
    // Complimentary orders
    const apiCompCount = num(pick(billedSrc, 'compCount', 'complementaryCount', 'complimentaryCount', 'compOrderCount', 'totalComps', 'compsCount'));
    const apiCompAmount = num(pick(billedSrc, 'compAmount', 'complementaryAmount', 'complimentaryAmount', 'compOrderAmount', 'totalCompAmount', 'compsAmount'));
    // Always use Redux (ClickHouse) comp data
    const compCount = liveCompOrderLogTotalElements;
    const compAmount = liveCompOrderLog.reduce((s, o) => s + (Number(o.amount) || 0), 0);

    // Channel data: Today → liveByChannel Redux (from /sales/live/v2/by-channel), Historical → getSalesByChannel
    const liveTablesOrders = listFrom(data.liveTables);
    const dineInFromTables = data.isToday && liveTablesOrders.length > 0
        ? Math.round(liveTablesOrders.reduce((sum: number, o: any) => sum + (Number(o.orderAmount) || 0), 0) * 100) / 100
        : null;
    const trackingList = listFrom(data.liveTracking);
    const channelArr = listFrom(data.channel);

    // Primary channels displayed individually; everything else collapses into "Others"
    const PRIMARY_CHANNELS = /^(dinein|dine-in|dine in|instore|in-store|in store|pickup|pick-up|pick up|delivery)$/i;
    const normalizedChannelArr = (() => {
        let othersValue = 0;
        let othersCount = 0;
        const primary: any[] = [];
        channelArr.forEach((item: any) => {
            const name = item.channelName ?? item.channel ?? item.name ?? '';
            const value = Math.round((Number(item.totalSales ?? item.sales ?? item.amount ?? item.value ?? item.netSales ?? 0) || 0) * 100) / 100;
            const count = Number(item.orderCount ?? item.count ?? item.totalOrders ?? item.orders ?? item.noOfOrders ?? item.numOrders ?? 0) || 0;
            if (PRIMARY_CHANNELS.test(name.trim())) {
                primary.push(item);
            } else if (value > 0) {
                othersValue += value;
                othersCount += count;
            }
        });
        if (othersValue > 0) {
            primary.push({ channelName: '3rd Party', totalSales: Math.round(othersValue * 100) / 100, orderCount: othersCount });
        }
        return primary;
    })();

    // Net Sales channel mini-points for hero card — Today only, uses netSales from liveByChannel Redux
    const netSalesByChannel = useMemo((): { label: string; netSales: number; color: string }[] => {
        const PRIMARIES = ['Dine-In', 'Delivery', 'Pickup', 'Instore'] as const;
        const colorOf = (ch: string) => ch === 'Dine-In' ? '#E86B6B' : ch === 'Delivery' ? T.green : ch === 'Pickup' ? T.orange : T.accent;
        const map = new Map<string, number>();
        let thirdParty = 0;
        liveByChannel.forEach((item: LiveByChannelItem) => {
            const canonical = getCanonicalChannel(item.channel);
            if ((PRIMARIES as readonly string[]).includes(canonical)) {
                map.set(canonical, (map.get(canonical) ?? 0) + item.netSales);
            } else {
                thirdParty += item.netSales;
            }
        });
        const result: { label: string; netSales: number; color: string }[] = [];
        PRIMARIES.forEach(ch => {
            const val = map.get(ch) ?? 0;
            if (val > 0) result.push({ label: ch, netSales: val, color: colorOf(ch) });
        });
        if (thirdParty > 0) result.push({ label: '3rd Party', netSales: thirdParty, color: T.t3 });
        return result.sort((a, b) => b.netSales - a.netSales);
    }, [liveByChannel]);

    const channelColors = [T.accent, '#E86B6B', T.orange, T.green, T.t3];
    const channelData = normalizedChannelArr.map((item, index) => ({
        name: item.channelName ?? item.channel ?? item.name ?? `Channel ${index + 1}`,
        value: Number(item.totalSales ?? item.sales ?? item.amount ?? item.value ?? item.netSales ?? 0) || 0,
        count: Number(item.orderCount ?? item.count ?? item.totalOrders ?? item.orders ?? item.noOfOrders ?? item.numOrders ?? 0) || 0,
        color: channelColors[index % channelColors.length],
    })).filter((item) => item.value > 0).sort((a, b) => b.value - a.value);
    const channelTotal = channelData.reduce((sum, item) => sum + item.value, 0);
    const channelPcts = distribute100(channelData.map((d) => d.value));
    const channelPctsExact = distribute100At2dp(channelData.map((d) => d.value));
    const channelBreakdown = channelData.map((item, i) => ({ ...item, pct: channelPcts[i], pctExact: channelPctsExact[i] }));
    const dineIn = dineInFromTables ?? (num(pick(billedSrc, 'maghilSales', 'dineInSales')) || (channelBreakdown.find((item) => /dine|in.?house|in-house/i.test(item.name))?.value ?? channelBreakdown[0]?.value ?? 0));
    const offPremise = num(pick(billedSrc, 'thirdPartySales', 'offPremiseSales')) || channelBreakdown.filter((item) => item.value !== dineIn).reduce((sum, item) => sum + item.value, 0);

    const tipsTotal = num(tips);
    const tipsByChannelRaw = channelTotal > 0 && channelBreakdown.length
        ? channelBreakdown.map((item) => ({ ...item, tipValue: (item.value / channelTotal) * tipsTotal }))
        : [];
    const tipPcts = distribute100(tipsByChannelRaw.map((d) => d.tipValue));
    const tipsByChannelData = tipsByChannelRaw.map((item, i) => ({ ...item, tipPct: tipPcts[i] }));
    const tipsByChannelTotal = tipsByChannelData.reduce((s, i) => s + i.tipValue, 0);
    const avgPerOrderTip = num(pick(billedSrc, 'avgTipPerOrder'));
    const tipRatePct = num(pick(billedSrc, 'tipRatePercent'));

    // Cancel data — ClickHouse refund-summary for BOTH Today and historical (historical is
    // dispatched with startDate/endDate). No data.isToday branching, no legacy cancelSummary.
    const liveRefundOrderCount = liveRefundSummary.reduce((sum, item) => sum + item.orderCount, 0);
    const liveRefundTotalAmt = liveRefundSummary.reduce((sum, item) => sum + (item.cancelledAmt ?? 0), 0);
    const cancelledAmount = liveRefundTotalAmt;
    const cancelledOrderCount = liveRefundOrderCount;
    // REF-3 — the Refund stamp KPI reads item + fee refund straight off snapshot-details now
    // (real data), rather than the refund-summary sum above. Everything else on this screen
    // that reads liveRefundTotalAmt (drills, export) is unaffected — this only replaces the
    // stamp tile.
    const refundStampItem = num(snapshotDetails?.refund);
    const refundStampTotal = refundStampItem + num(snapshotDetails?.feeRefund);
    // GLOBAL-DP-4 (ZAR-1663): the per-card "vs Yesterday" text is gone — the comparison basis
    // is stated once in the unified date control. Each delta keeps the resolved comparison
    // period as a hover tooltip so the basis stays recoverable per card.
    const comparisonHint = comparisonTooltip(activeDate, storeTz);
    // ZAR-1661: delta visibility follows the SHAPE of the selected period, not a literal
    // token list. "This month", "This year", "Last 30 days" and every stepped/hand-picked
    // range that matches a registered shape now carry a comparison — and a stepper emits
    // `custom:from:to`, which no hardcoded list could have covered.
    const showDelta = hasComparison(activeDate, storeTz);
    // invert: for "lower is better" metrics (e.g. Refund) — colors/arrow flip (increase = red ↓),
    // but the displayed sign always matches the actual value.
    // cancelledOrdersArrow: for "lower is better" metrics (Cancelled, Refund). The arrow
    // reflects the actual direction of the value (↑ for an increase, ↓ for a decrease)
    // instead of flipping with `invert`; the color still flips green on a decrease.
    const getDeltaNode = (pct: any, invert: boolean = false, cancelledOrdersArrow: boolean = false) => {
        if (pct == null || Number.isNaN(Number(pct))) return null;
        const num = Number(pct);
        const isZero = num === 0;
        const isUp = num >= 0;
        const isGood = invert ? !isUp : isUp;
        const showUpArrow = cancelledOrdersArrow ? isUp : isGood;
        return (
            <div className="snapshot-tile-delta" title={comparisonHint} style={{ color: isZero ? T.t2 : isGood ? T.green : T.red }}>
                {!isZero && <>{showUpArrow ? '↑' : '↓'} {num >= 0 ? '+' : '-'}</>}{Math.abs(num).toFixed(2)}%
            </div>
        );
    };

    // Active Staff KPI: /sales/live/v2/staff-active-clocking redux for BOTH Today and historical
    // (historical dispatches with startDate/endDate). Count of items where currentStatus === 'active'.
    const isStatusActive = (s: string | null | undefined) => String(s ?? '').trim().toLowerCase() === 'active';
    const isStatusBreak = (s: string | null | undefined) => String(s ?? '').trim().toLowerCase() === 'break';
    const activeStaffActiveCount = liveStaffActiveClocking.reduce((n, it) => n + (isStatusActive(it.currentStatus) ? 1 : 0), 0);
    const activeStaffOnBreakCount = liveStaffActiveClocking.reduce((n, it) => n + (isStatusBreak(it.currentStatus) ? 1 : 0), 0);
    // LBR-11: the Active Staff stamp KPI shows the total headcount in the response
    // (every clocking row), not just the 'active' subset. The Active/On Break split
    // remains available in the drilldown below.
    const activeStaffTotalCount = liveStaffActiveClocking.length;
    const activeStaffTotalHoursDecimal = liveStaffActiveClocking.reduce((s, it) => s + parseClockHours(it.totalHours), 0);
    const activeStaffNetSalesSum = liveStaffActiveClocking.reduce((s, it) => s + (Number(it.netSales) || 0), 0);
    const activeStaffAvgSales = liveStaffActiveClocking.length > 0
        ? activeStaffNetSalesSum / liveStaffActiveClocking.length
        : 0;

    // Void KPI: /sales/live/v2/void-summary for both Today and historical (with startDate/endDate for historical).
    const liveVoidTotalCount = voidOrdersFrom(liveVoidSummary);
    const liveVoidTotalAmt = liveVoidSummary.reduce((s, item) => s + item.cancelledAmt, 0);
    const voidCount = liveVoidTotalCount;
    const voidAmount = liveVoidTotalAmt;
    // Voided Items (stamp KPI value) — removed-item quantity, distinct from `voidCount`
    // (orders affected), which now only surfaces as the tile's "N orders" subline. The
    // drill-in page keeps reading voidCount/voidAmount for its own header/mini-KPIs.
    const voidedItemsCount = liveVoidSummary.reduce((s, item) => s + (item.removedQty ?? 0), 0);
    // Voided Items delta — `delta` is a period-level figure repeated on every reason row, so
    // the first row's value is the tile's percent change vs. the comparison period.
    const voidDeltaPct = liveVoidSummary.length > 0 && liveVoidSummary[0].delta != null ? Number(liveVoidSummary[0].delta) : null;
    // USSE-1: Check-Ins KPI hidden in historical view
    // const checkInCount = pick(data.checkIn, 'totalCheckin', 'totalCheckIns', 'checkInCount', 'count', 'totalCheckIn', 'checkIns') ?? pickFromList(data.checkIn, 'checkInCount') ?? listFrom(data.checkIn).length;
    const guestCount = num(pick(billedSrc, 'guestsCount') ?? pick(data.guests, 'totalGuests', 'guestCount', 'count', 'totalGuestCount', 'total') ?? 0);
    const avgWait = num(pick(billedSrc, 'avgWaitTime') ?? pick(data.peak, 'avgWaitTime', 'averageWait', 'avgWait', 'avgWaitTimeMinutes', 'averageWaitTime') ?? 0);

    // Snapshot report-level "Export KPIs" — mirrors exactly what's on screen, including the
    // Guests tile that only renders for historical dates (hidden for Today).
    kpiExportRef.current = {
        activeDate,
        dateRange,
        chDates: chDatesView,
        orderSourceVal,
        token,
        channel,
        isToday: data.isToday,
        isThirdParty,
        netSales, totalOrders,
        unpaidCount: liveUnpaidOrdersFailure ? 0 : displayUnpaidCount,
        unpaidAmount: displayUnpaidValue,
        avgOrder,
        serviceCharge: serviceCharge?.amount ?? null,
        refund: data.isToday ? (liveRefundSummaryFailure ? 0 : liveRefundTotalAmt) : cancelledAmount,
        tipsAndGratuity: liveStaffPerformanceFailure ? 0 : tipsAndGratuity,
        // Staff Worked — same source as the on-screen "Staff Worked" tile
        // (/sales/live/v2/staff-active-clocking-v2's staffWorked field), not the
        // legacy staff-active-clocking roster length.
        staffWorked: staffRosterV2Failure ? 0 : staffRosterV2StaffWorked,
        voidCount: liveVoidSummaryFailure ? 0 : voidCount,
        compCount: liveCompOrderLogFailure ? 0 : compCount,
        compAmount,
        // CANC-1 — Cancelled orders, same source as the on-screen "Cancelled" tile.
        cancelledCount: cancelledOrdersFailure ? 0 : cancelledOrders.length,
        discounts,
        guestCount: guests,
        spendPerGuest,
    };

    // Hourly chart data — unified source: /sales/live/v2/hourly-sales?startDate=&endDate=
    // Used for BOTH Today and Historical; both Hourly Revenue and Hourly Orders share the
    // same time axis derived from operatingStartHour / operatingEndHour.
    const hourlyHours = hourlySalesData?.hours ?? [];
    const operatingStartHour = hourlySalesData?.operatingStartHour ?? 6;
    const operatingEndHour = hourlySalesData?.operatingEndHour ?? 23;
    const operatingHourCount = hourlySalesData?.operatingHourCount ?? 0;
    const buildHourlyChartData = (valueKey: 'itemTotal' | 'orderCount'): { label: string; value: number }[] =>
        hourlyHours.map(h => {
            const hv = h.hourValue % 24;
            const suffix = hv >= 12 ? ' PM' : ' AM';
            const disp = hv % 12 || 12;
            return { label: `${disp}${suffix}`, value: h[valueKey] };
        });

    // Revenue series (itemTotal) — shared between Hourly Revenue chart and its drill
    const hourlyRevenueChartData = buildHourlyChartData('itemTotal');
    // Orders series (orderCount) — shared between Hourly Orders chart and its drill
    const hourlyOrdersChartData = buildHourlyChartData('orderCount');

    // Peak indices — each series uses its own peak, NOT the other series' peak
    const revenuePeakValue = hourlyRevenueChartData.reduce((m, d) => Math.max(m, d.value), 0);
    const revenuePeakIndex = revenuePeakValue > 0 ? hourlyRevenueChartData.findIndex(d => d.value === revenuePeakValue) : -1;
    const ordersPeakValue = hourlyOrdersChartData.reduce((m, d) => Math.max(m, d.value), 0);
    const ordersPeakIndex = ordersPeakValue > 0 ? hourlyOrdersChartData.findIndex(d => d.value === ordersPeakValue) : -1;

    // Avg/Hour — sum of operating-window hours divided by operatingHourCount
    const windowHours = hourlyHours.filter(h => h.hourValue >= operatingStartHour && h.hourValue <= operatingEndHour);
    const avgRevenuePerHour = operatingHourCount > 0
        ? windowHours.reduce((s, h) => s + h.itemTotal, 0) / operatingHourCount
        : 0;
    const avgOrdersPerHour = operatingHourCount > 0
        ? windowHours.reduce((s, h) => s + h.orderCount, 0) / operatingHourCount
        : 0;
    const salesPerHourDesktop = avgRevenuePerHour > 0 ? avgRevenuePerHour : null;

    // ── Sales-breakdown waterfall (ZAR-1462) ── real per-source KPI values.
    // Each field comes from the same source that backs its standalone stamp KPI
    // (voids ← void-summary, discounts ← snapshot-details, comp ← comp-order-log,
    // refunds ← refund-summary, tax/tips/gratuity/otherCharges ← snapshot-details/staff-perf,
    // totalCollected ← grandTotal, i.e. the Sales screen's "Total Collected" stamp KPI).
    // Gross Sales itself is summed from the ladder rows inside buildWaterfallData.
    const waterfallSource: WaterfallSource = orderSourceVal === 'direct' ? 'direct' : orderSourceVal === 'thirdparty' ? 'thirdparty' : 'overall';
    const waterfallData: SalesWaterfallData = buildWaterfallData({
        voids: num(voidAmount),
        discounts: num(discounts),
        complimentary: num(compAmount),
        refunds: num(cancelledAmount),
        netSales: num(netSales),
        tax: num(tax),
        tips: num(tips),
        gratuity: num(snapshotDetails?.gratuity),
        otherCharges: num(snapshotDetails?.otherCharges),
        totalCollected: num(snapshotDetails?.grandTotal),
        ...serviceChargeWaterfallInputs(snapshotDetails, scEnabled),
        // REF-1 — real item/fee refund split off snapshot-details, replacing the
        // fixture-backed refundSummary for the waterfall's Refunds/Others rows.
        refundContext: waterfallRefundContextFromSnapshot(snapshotDetails?.refund, snapshotDetails?.feeRefund),
    });
    const thirdPartyItemTotal = waterfallData.itemTotal;
    const totalPaidKpiValue = getTotalPaidValue(waterfallSource, waterfallData);

    // Top items: Today → v2 Redux only, Historical → categoryItemSales
    const itemSalesSource = data.isToday && liveItemSalesV2.length > 0 ? liveItemSalesV2 : (() => {
        const raw = unwrapRes(data.categoryItemSales);
        return Array.isArray(raw) ? raw : listFrom(data.categoryItemSales);
    })();
    const pickNumDeep = (obj: any, keys: any) => {
        if (!obj || typeof obj !== 'object') return 0;
        for (const key of keys) {
            const value = obj[key];
            if (value != null && value !== '' && !Number.isNaN(Number(value))) return Number(value);
        }
        for (const value of Object.values(obj)) {
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                const nested = pickNumDeep(value, keys);
                if (nested > 0) return nested;
            }
        }
        return 0;
    };
    const itemsTotalElements = data.isToday
        ? liveItemSalesV2TotalElements
        : (Number(data.categoryItemSalesTotalElements) || 0);


    const topItems = itemSalesSource
        .map((item) => ({
            name: item.itemName ?? item.name ?? item.menuItemName ?? item.label ?? '—',
            qty: pickNumDeep(item, ['quantity', 'salesQty', 'sales_qty', 'itemCount', 'quantitySold', 'qty', 'count']),
            rev: pickNumDeep(item, ['price', 'totalItemPrice', 'grossSales', 'gross_sales', 'itemTotal', 'totalSales', 'amount', 'revenue']),
            isWeight: Number(item.isWeight ?? 0),
        }))
        .filter((item) => item.name !== '—' || item.rev > 0 || item.qty > 0)
        .sort((a, b) => b.rev - a.rev);
    // Accumulate across pages so export can use already-loaded data without extra API calls
    if (topItems.length > 0) {
        const existingNames = new Set(allTopItemsRef.current.map((i) => i.name));
        const newItems = topItems.filter((i) => !existingNames.has(i.name));
        if (newItems.length > 0) {
            allTopItemsRef.current = [...allTopItemsRef.current, ...newItems];
        }
    }

    // Staff Sales Leaderboard (historical-only card) — USSE-3: ClickHouse staff-performance
    // (same redux the Tips KPI and discount-by-staff read), aggregated per staff since the
    // endpoint may return one row per staff × channel; hours enriched from the
    // staff-active-clocking roster. Replaces the legacy /sales/snapshot-summary leaderboard.
    const clockingHoursByName: Record<string, number> = {};
    for (const c of liveStaffActiveClocking) {
        const key = String(c.staffName ?? '').trim().toLowerCase();
        if (key) clockingHoursByName[key] = (clockingHoursByName[key] || 0) + parseClockHours(c.totalHours);
    }
    const staffPerfAgg: Record<string, { name: string; sales: number; qty: number }> = {};
    for (const item of liveStaffPerformance) {
        const name = String(item.staff_name ?? '').trim();
        if (!name) continue;
        const key = name.toLowerCase();
        if (!staffPerfAgg[key]) staffPerfAgg[key] = { name, sales: 0, qty: 0 };
        staffPerfAgg[key].sales += Number(item.netSales) || 0;
        staffPerfAgg[key].qty += Number(item.orderCount) || 0;
    }
    // REF-6 — "Item refunds reduce Staff Sales Leaderboard revenue. Fee refunds do not."
    // Attributed to the CHECK OWNER, same rule as the Staff Tips ranking.
    const itemRefundsByStaffLeaderboard = itemRefundsByCheckOwner(refundSummary.records);
    const staffLeaderboard = Object.values(staffPerfAgg)
        .map(({ name, sales, qty }) => {
            const totalHrs = clockingHoursByName[name.toLowerCase()] ?? 0;
            return {
                name,
                role: '',
                totalHrs,
                hoursLabel: totalHrs > 0 ? `${totalHrs.toFixed(1)}h` : '',
                sales: Number((sales - (itemRefundsByStaffLeaderboard[name] ?? 0)).toFixed(2)),
                qty,
            };
        })
        .filter((item) => item.sales > 0 || item.qty > 0)
        .sort((a, b) => b.sales - a.sales);

    /* ── Snapshot Drill Panel Renderers ── */

    // Mini KPI card used inside drill panels
    const MiniK = ({ label, value, color, sub, loading: isLoading }: any) => (
        <div className="rpt-card rpt-card--r12 snapshot-mini-k">
            <div className="snapshot-mini-k-label">{label}</div>
            <div className="snapshot-mini-k-value" style={{ color: color || T.t1 }}>
                {isLoading ? <Bone w={80} h={22} r={4} /> : value}
            </div>
            {!isLoading && sub && <div className="snapshot-mini-k-sub">{sub}</div>}
        </div>
    );

    // Progress bar row used in reasons breakdowns
    const PBarRow = ({ label, val, pct, max = 100, color }: any) => (
        <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 11, fontWeight: 500, color: T.t1 }}>{label}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.t1 }}>{val}</span>
            </div>
            <div style={{ height: 5, background: T.hover, borderRadius: 3 }}>
                <div style={{ height: 5, borderRadius: 3, width: `${Math.min((pct / max) * 100, 100)}%`, background: color, opacity: 0.7 }} />
            </div>
        </div>
    );

    const renderNetSalesDrill = () => {
        const peakLbl = revenuePeakIndex >= 0 ? (hourlyRevenueChartData[revenuePeakIndex]?.label ?? '--') : '--';
        // Revenue booked in the peak hour — the hour alone doesn't say how big it was.
        const peakSub = revenuePeakIndex >= 0 && !hourlySalesFailure ? fmtCurrency(revenuePeakValue) : undefined;
        const primaryLabel = isThirdParty ? 'Item Total' : 'Net Sales';
        // Keep the KPI row on the same (mock) source scope as the waterfall so they reconcile.
        const primaryValue = isThirdParty ? thirdPartyItemTotal : waterfallData.netSales;
        const subLabel = isThirdParty
            ? `${fmtCurrency(thirdPartyItemTotal)} item total · ${exportDateRangeLabel}`
            : `${fmtCurrency(waterfallData.netSales)} total revenue · ${exportDateRangeLabel}`;
        // SW 10: the waterfall's Voids/Complimentary/Refunds rows come from three separate
        // async fetches on top of snapshot-details — kpiLoading alone drops before they land,
        // so the bars would render stale/zero values then jump once each one resolves.
        const waterfallLoading = kpiLoading || liveVoidSummaryLoading || liveCompOrderLogLoading || liveRefundSummaryLoading;
        return (
            <div className="snapshot-drill-wrap">
                <div className="snapshot-drill-subtext">{kpiLoading ? <Bone w={200} h={12} r={4} /> : subLabel}</div>
                <div ref={netSalesKpiRowRef} className="snapshot-drill-kpi-row">
                    <MiniK label={primaryLabel} value={fmtCurrency(primaryValue)} color={T.green} loading={kpiLoading} />
                    <MiniK label={getTotalPaidLabel(waterfallSource)} value={fmtCurrency(totalPaidKpiValue)} sub={getTotalPaidSub(waterfallSource)} loading={kpiLoading} />
                    <MiniK label="Peak Hour" value={hourlySalesFailure ? '--' : peakLbl} sub={peakSub} color={T.accent} loading={hourlySalesLoading} />
                    <MiniK label="Avg/Hour" value={fmtCurrency(avgRevenuePerHour)} loading={hourlySalesLoading} />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <SalesWaterfall
                        source={waterfallSource}
                        data={waterfallData}
                        fmt={fmtCurrency}
                        loading={waterfallLoading}
                        // SW3 — remember this sub-drill was opened FROM net-sales, so goBack
                        // returns here (scrolled to the KPI row) instead of the plain top page.
                        onDeductionClick={(key) => {
                            // REF-2 — the waterfall's own "Refunds ›" row opens the ONE shared
                            // Refunds drill-in too, same destination as the Refund KPI tile.
                            if (key === 'refunds') {
                                navigate(refundsPath({ origin: 'snapshot', dateFilter: activeDate, startDate: dateRange.startDate, endDate: dateRange.endDate, source: waterfallSource }));
                                return;
                            }
                            setDrillOrigin('net-sales');
                            setDrillView(key);
                        }}
                    />
                </div>
                <div className="rpt-flex-row rpt-gap-16">
                    <div className="rpt-card" style={{ flex: 2 }}>
                        <div className="snapshot-drill-title">Hourly Revenue</div>
                        <div className="snapshot-drill-subtitle">Revenue distribution across service hours</div>
                        <HourlyBarChart
                            data={hourlyRevenueChartData}
                            loading={hourlySalesLoading}
                            error={hourlySalesFailure}
                            onRetry={() => dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }))}
                            kpiName="hourly revenue"
                            barColor={T.accent}
                            chartHeight={120}
                            showCurrency={true}
                            avgOverride={avgRevenuePerHour}
                        />
                    </div>
                </div>
            </div>
        );
    };

    // ── Order Log derived values & helpers (Orders drill, Today only) ──
    const activeOlOrders = orderLogOrders;
    const activeOlLoading = orderLogLoading;
    const activeOlTotal = orderLogTotalElements;
    const activeOlTotalPages = orderLogTotalPages;
    const activeOlError = orderLogFailure;
    const olHasFilters = !!(olDebouncedSearch || olOrderTypes.length || olMinAmt || olMaxAmt || olMinGuests);
    const olTotalPages = activeOlTotalPages;
    const olAmtError = olMinAmt && olMaxAmt && Number(olMinAmt) >= Number(olMaxAmt) ? 'Min must be less than Max' : null;

    // ── Order Log rows + columns (ORD-1.2 / ORD-1.3 / ORD-1.4) ──
    // Paid and Cancelled are the same endpoint: the status adds `type=cancel`,
    // so both are server-paged, server-filtered and server-searched. Switching
    // status refetches; the counts come back on BOTH responses so the segments
    // stay populated either way.
    const olIsCancelled = olStatus === 'cancelled';
    const olRowsAll = useMemo(() => toOrderLogRows(activeOlOrders), [activeOlOrders]);

    // The outlet's own answer on table service — no table column at all when
    // it is off, rather than a column of em dashes (ORD-1.2).
    // Order ID / Date sort client-side on the loaded page, same as the Unpaid log.
    const olAllColumns = useMemo(
        () => buildOrderLogColumns({ hasTableService: orderLogDineInAvailable, status: olStatus, hasServiceCharge: scEnabled, sortKeys: { orderNo: 'orderNo', orderDate: 'orderDate' } }),
        [orderLogDineInAvailable, olStatus, scEnabled],
    );
    const olVisibleKeys = resolveOlColumns(olAllColumns);
    const olColumns = visibleColumnDefs(olAllColumns, olVisibleKeys);
    // The totals footer belongs to the financial waterfall — Grand Total alone
    // (always on) does not summon it.
    const olShowTotals = olColumns.some(c => c.group === 'financial' && !c.pinned);

    // Table, Staff and the financial columns have no server sort id, so the
    // loaded page is ordered here; everything else arrives sorted.
    const olRows = useMemo(
        () => (SERVER_SORT_KEYS.indexOf(olSort) >= 0 ? olRowsAll : sortRowsClientSide(olRowsAll, olSort, olSortDir)),
        [olRowsAll, olSort, olSortDir],
    );

    const olDisplayTotal = activeOlTotal;
    const olDisplayTotalPages = olTotalPages;
    const olShowStart = olRows.length > 0 ? olPage * olPageSize + 1 : 0;
    const olShowEnd = olPage * olPageSize + olRows.length;
    // Covers the rows currently loaded — the log has no server-side totals row.
    const olTotalsFooter = olShowTotals ? computeOrderLogTotals(olRows, olColumns) : null;
    const olCancelledCount = orderLogCancelledCount;
    const olPaidCount = orderLogPaidCount;

    // Three-state header sort: default direction → reverse → back to Time desc.
    const handleOlColumnSort = (col: OrderLogColumnDef) => {
        const id = col.sortKey;
        if (!id) return;
        const first = col.defaultDir ?? 'desc';
        if (olSort !== id) { setOlSort(id); setOlSortDir(first); }
        else if (olSortDir === first) { setOlSortDir(first === 'asc' ? 'desc' : 'asc'); }
        else { setOlSort('orderTime'); setOlSortDir('desc'); }
        setOlPage(0);
    };

    // Switching status keeps the user's column choice, swapping only the
    // Items ⇄ Reason slot that the two datasets disagree on.
    const handleOlStatusChange = (next: OrderLogStatus) => {
        setOlStatus(next);
        setOlPage(0);
        setOlSelectedOrder(null);
        setOlVisibleCols(prev => {
            if (!prev) return prev;
            const from = next === 'cancelled' ? 'itemPreview' : 'cancelReason';
            const to = next === 'cancelled' ? 'cancelReason' : 'itemPreview';
            return prev.indexOf(from) >= 0 ? prev.map(k => (k === from ? to : k)) : prev;
        });
    };

    const olClearFilters = () => { setOlSearch(''); setOlOrderTypes([]); setOlMinAmt(''); setOlMaxAmt(''); setOlMinGuests(''); };

    const renderOrdersDrill = () => {
        const ordersPeakLbl = ordersPeakIndex >= 0 ? (hourlyOrdersChartData[ordersPeakIndex]?.label ?? '--') : '--';
        return (
        <div className="snapshot-drill-wrap">
            <div className="snapshot-drill-subtext">{kpiLoading ? <Bone w={200} h={12} r={4} /> : `${fmtNum(totalOrders)} total orders — hourly distribution`}</div>
            <div className="snapshot-drill-kpi-row">
                <MiniK label="Total Orders" value={fmtNum(totalOrders)} loading={kpiLoading} />
                <MiniK label="Peak Hour" value={hourlySalesFailure ? '--' : ordersPeakLbl} color={T.accent} loading={hourlySalesLoading} />
                <MiniK label="Avg/Hour" value={hourlySalesFailure ? '--' : String(Math.round(avgOrdersPerHour))} loading={hourlySalesLoading} />
                <MiniK label="Avg Order" value={fmtCurrency(avgOrder)} loading={kpiLoading} />
            </div>
            <div className="rpt-flex-row rpt-gap-16">
                {/* Hourly Orders — same time axis as Hourly Revenue (shared HOUR_START/HOUR_END) */}
                <div className="rpt-card" style={{ flex: 2 }}>
                    <div className="snapshot-drill-title">Hourly Orders</div>
                    <div className="snapshot-drill-subtitle">Order volume by hour</div>
                    <HourlyBarChart
                        data={hourlyOrdersChartData}
                        loading={hourlySalesLoading}
                        error={hourlySalesFailure}
                        onRetry={() => dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }))}
                        kpiName="hourly orders"
                        barColor={T.green}
                        chartHeight={120}
                        formatValue={(v) => String(Math.round(Number(v)))}
                        showCurrency={false}
                        avgOverride={avgOrdersPerHour}
                    />
                </div>
                <div className="rpt-card" style={{ flex: 1 }}>
                    <div className="snapshot-drill-title snapshot-drill-title--spaced">By Channel</div>
                    {liveByChannelFailure ? (
                        <SectionError kpiName="by channel" onRetry={() => dispatch(fetchLiveByChannelRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={150} />
                    ) : liveByChannelLoading ? (
                        <div>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < 4 ? `1px solid ${T.divider}` : 'none' }}>
                                    <Bone h={12} r={3} style={{ width: `${45 + i * 10}%` }} />
                                    <Bone w={50} h={12} r={3} />
                                </div>
                            ))}
                        </div>
                    ) : liveByChannel.length ? (() => {
                        const BY_CH_PRIMARIES = [
                            { pattern: /^in.?store$/i, color: T.accent },
                            { pattern: /^dine.?in$/i, color: '#E86B6B' },
                            { pattern: /^pick.?up$/i, color: T.orange },
                            { pattern: /^delivery$/i, color: T.green },
                        ];
                        const grouped: { channel: string; orderCount: number; color: string }[] = [];
                        let thirdPartyCount = 0;
                        BY_CH_PRIMARIES.forEach(({ pattern, color }) => {
                            const match = liveByChannel.find(item => pattern.test(item.channel));
                            if (match && match.orderCount > 0) grouped.push({ channel: match.channel, orderCount: match.orderCount, color });
                        });
                        liveByChannel.forEach(item => {
                            if (!BY_CH_PRIMARIES.some(({ pattern }) => pattern.test(item.channel))) thirdPartyCount += item.orderCount;
                        });
                        if (thirdPartyCount > 0) grouped.push({ channel: '3rd Party', orderCount: thirdPartyCount, color: T.t3 });
                        if (!grouped.length) return <SectionEmpty kpiName="by channel" minHeight={150} />;
                        const chTotal = grouped.reduce((s, d) => s + d.orderCount, 0);
                        const pcts = (() => {
                            const raw = grouped.map(d => chTotal > 0 ? parseFloat(((d.orderCount / chTotal) * 100).toFixed(2)) : 0);
                            const diff = parseFloat((100 - raw.reduce((s, p) => s + p, 0)).toFixed(2));
                            if (diff !== 0) { const mi = raw.reduce((mi, p, i, a) => p > a[mi] ? i : mi, 0); raw[mi] = parseFloat((raw[mi] + diff).toFixed(2)); }
                            return raw;
                        })();
                        return grouped.map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < grouped.length - 1 ? `1px solid ${T.divider}` : 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <div style={{ width: 8, height: 8, borderRadius: 2, background: item.color }} />
                                    <span style={{ fontSize: 12, color: T.t1 }}>{item.channel}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <span style={{ fontSize: 11, color: T.t3 }}>{pcts[i].toFixed(2)}%</span>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, width: 36, textAlign: 'right' }}>{fmtNum(item.orderCount)}</span>
                                </div>
                            </div>
                        ));
                    })() : (
                        <SectionEmpty kpiName="by channel" minHeight={150} />
                    )}
                </div>
            </div>

            {/* ── Order Log (Paid / Cancelled) — ORD-1.2 / ORD-1.3 / ORD-1.4 ── */}
            <div className="rpt-card" style={{ marginTop: 16 }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
                    <div>
                        <div className="snapshot-drill-title" style={{ marginBottom: 2 }}>{olIsCancelled ? 'Order Log (Cancelled Orders)' : 'Order Log (Paid Orders)'}</div>
                        <div className="snapshot-drill-subtitle">{olIsCancelled ? (data.isToday ? 'All cancelled orders for today' : 'All cancelled orders for the selected period') : (data.isToday ? 'All paid orders for today' : 'All paid orders for the selected period')}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        {/* ORD-1.3 deep-link to the Cancelled Orders drill-in — hidden for now.
                            Restore by uncommenting; nothing else depends on it.
                        {olIsCancelled && (
                            <button
                                onClick={() => setDrillView('cancelled')}
                                style={{ background: 'none', border: 'none', padding: 0, marginRight: 2, color: T.accent, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: T.font, whiteSpace: 'nowrap' }}
                            >
                                View cancellation reasons →
                            </button>
                        )}
                        */}
                        <OrderLogColumnsMenu columns={olAllColumns} visible={olVisibleKeys} onChange={setOlVisibleCols} />
                        {olRows.length > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                title={orderLogExportTitle(olStatus)}
                                filename={`order-log-${olStatus}-orders-${dateRange.startDate}`}
                                buildExport={async (_format, opts) => {
                                    // The file mirrors the columns on screen, in the same
                                    // order, under the active status, sort and filters.
                                    const loc = opts?.locationId ?? locationId;
                                    const params = { locationId: loc, sortBy: SERVER_SORT_KEYS.indexOf(olSort) >= 0 ? olSort : 'orderTime', sortDir: olSortDir, search: olDebouncedSearch || undefined, channelName: olOrderTypes.length ? olOrderTypes.join(',') : undefined, guestCount: olMinGuests ? Number(olMinGuests) : undefined, minAmount: olMinAmt ? Number(olMinAmt) : undefined, maxAmount: olMaxAmt ? Number(olMaxAmt) : undefined, ...chDatesView, ...orderSourceParam, ...(olIsCancelled ? { type: 'cancel' as const } : {}) };
                                    let size = orderLogTotalElements || 1;
                                    if (opts?.locationId) {
                                        const probe: any = await getOrderLogExportApi({ ...params, page: 1, size: 1 });
                                        size = Number(probe?.totalElements) || 1;
                                    }
                                    const res: any = await getOrderLogExportApi({ ...params, page: 1, size });
                                    const orders = res?.orders ?? (Array.isArray(res) ? res : []);
                                    const rows = sortRowsClientSide(toOrderLogRows(orders), olSort, olSortDir);
                                    return orderLogRowsToExportTable(rows, olColumns);
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Status segments — above the refine-filter row, visually distinct */}
                <OrderLogStatusSegments
                    value={olStatus}
                    onChange={handleOlStatusChange}
                    paidCount={olPaidCount}
                    cancelledCount={olCancelledCount}
                />

                {/* Filter row */}
                <div style={{ display: 'flex', gap: 8, marginBottom: olAmtError ? 4 : 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ position: 'relative', flex: '1 1 240px' }}>
                        <input
                            value={olSearch}
                            onChange={e => setOlSearch(e.target.value)}
                            placeholder="Search by order ID, customer, phone, or table"
                            style={{ width: '100%', height: 32, padding: olSearch ? '0 28px 0 32px' : '0 10px 0 32px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, outline: 'none', fontFamily: T.font, boxSizing: 'border-box' }}
                        />
                        <span className="material-symbols-rounded" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: T.t3, pointerEvents: 'none' }}>search</span>
                        {olSearch && (
                            <button onClick={() => setOlSearch('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: T.t3 }} aria-label="Clear search">
                                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>close</span>
                            </button>
                        )}
                    </div>
                    {/* Order Type multi-select */}
                    {olTypeMenuOpen && <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setOlTypeMenuOpen(false)} />}
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => setOlTypeMenuOpen(v => !v)}
                            style={{ height: 32, padding: '0 10px', border: `1px solid ${olOrderTypes.length ? T.accent : T.cardBorder}`, borderRadius: 6, fontSize: 12, color: olOrderTypes.length ? T.accent : T.t2, background: olOrderTypes.length ? `${T.accent}10` : T.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', fontFamily: T.font }}
                        >
                            Order Type {olOrderTypes.length > 0 ? `(${olOrderTypes.length})` : ''}<span style={{ fontSize: 10 }}>▾</span>
                        </button>
                        {olTypeMenuOpen && (
                            <div style={{ position: 'absolute', top: 36, left: 0, zIndex: 200, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: T.shadow, padding: '6px 0', minWidth: 180 }}>
                                {(channelNames.length > 0 ? channelNames : []).map(ot => (
                                    <div key={ot} onClick={() => setOlOrderTypes(prev => prev.includes(ot) ? prev.filter(x => x !== ot) : [...prev, ot])} style={{ padding: '7px 14px', fontSize: 12, color: olOrderTypes.includes(ot) ? T.accent : T.t1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: olOrderTypes.includes(ot) ? `${T.accent}08` : 'transparent' }}>
                                        <span style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${olOrderTypes.includes(ot) ? T.accent : T.t3}`, background: olOrderTypes.includes(ot) ? T.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            {olOrderTypes.includes(ot) && <span style={{ color: '#fff', fontSize: 9, lineHeight: 1 }}>✓</span>}
                                        </span>
                                        {ot}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <input value={olMinAmt} onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 0) setOlMinAmt(v); }} placeholder="Min $" type="number" min="0" style={{ width: 72, height: 32, padding: '0 8px', border: `1px solid ${olAmtError ? T.red : T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, outline: 'none', fontFamily: T.font }} />
                    <input value={olMaxAmt} onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 0) setOlMaxAmt(v); }} placeholder="Max $" type="number" min="0" style={{ width: 72, height: 32, padding: '0 8px', border: `1px solid ${olAmtError ? T.red : T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, outline: 'none', fontFamily: T.font }} />
                    <input value={olMinGuests} onChange={e => { const v = e.target.value; if (v === '' || Number(v) >= 1) setOlMinGuests(v); }} placeholder="Guests ≥" type="number" min="1" style={{ width: 76, height: 32, padding: '0 8px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, outline: 'none', fontFamily: T.font }} />
                    {olHasFilters && <button onClick={olClearFilters} style={{ height: 32, padding: '0 10px', border: 'none', borderRadius: 6, fontSize: 12, color: T.accent, background: 'transparent', cursor: 'pointer', fontFamily: T.font, textDecoration: 'underline' }}>Clear filters</button>}
                </div>
                {olAmtError && <div style={{ fontSize: 11, color: T.red, marginBottom: 6 }}>{olAmtError}</div>}

                {/* Selected type chips */}
                {olOrderTypes.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                        {olOrderTypes.map(ot => (
                            <span key={ot} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px 2px 10px', background: `${T.accent}12`, borderRadius: 12, fontSize: 11, color: T.accent, fontWeight: 500 }}>
                                {ot}<span onClick={() => setOlOrderTypes(prev => prev.filter(x => x !== ot))} style={{ cursor: 'pointer', fontSize: 12, lineHeight: 1, opacity: 0.7 }}>×</span>
                            </span>
                        ))}
                    </div>
                )}

                {/* Showing + Rows per page */}
                {olDisplayTotal > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <span style={{ fontSize: 11, color: T.t3 }}>{activeOlLoading ? <Bone w={140} h={11} r={3} style={{ display: 'inline-block' }} /> : `Showing ${olShowStart}–${olShowEnd} of ${olDisplayTotal} orders`}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 11, color: T.t3 }}>Rows per page:</span>
                            <select value={olPageSize} onChange={e => setOlPageSize(Number(e.target.value))} style={{ height: 26, padding: '0 6px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, cursor: 'pointer', fontFamily: T.font }}>
                                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                    </div>
                )}

                {/* Table */}
                {activeOlLoading ? (
                    <OrderLogTable
                        columns={olColumns}
                        rows={[]}
                        sortKey={olSort}
                        sortDir={olSortDir}
                        onSort={handleOlColumnSort}
                        fmtMoney={fmtCurrencyCell}
                        loading
                    />
                ) : activeOlError ? (
                    <SectionError kpiName="Order Log" onRetry={() => dispatch(fetchOrderLogRequest({ locationId, page: olPage + 1, size: olPageSize, sortBy: SERVER_SORT_KEYS.indexOf(olSort) >= 0 ? olSort : 'orderTime', sortDir: olSortDir, search: olDebouncedSearch || undefined, ...chDatesView, ...orderSourceParam, ...(olIsCancelled ? { type: 'cancel' as const } : {}) }))} />
                ) : olRows.length === 0 ? (
                    <div style={{ padding: '40px 0', textAlign: 'center' }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 32, color: T.t3, display: 'block', marginBottom: 8 }}>receipt_long</span>
                        <div style={{ fontSize: 13, color: T.t3 }}>
                            {olIsCancelled
                                ? (olHasFilters ? 'No cancelled orders found for the selected filters.' : 'No cancelled orders for the selected period.')
                                : (olHasFilters ? 'No orders found for the selected filters.' : (data.isToday ? 'No paid orders found for today.' : 'No paid orders found for the selected period.'))}
                        </div>
                    </div>
                ) : (
                    <>
                        <OrderLogTable
                            columns={olColumns}
                            rows={olRows}
                            sortKey={olSort}
                            sortDir={olSortDir}
                            onSort={handleOlColumnSort}
                            onRowClick={row => setOlSelectedOrder(row.raw)}
                            phoneVisible={olPhoneVisible}
                            onTogglePhone={() => setOlPhoneVisible(v => !v)}
                            status={olStatus}
                            totals={olTotalsFooter}
                            fmtMoney={fmtCurrencyCell}
                        />
                        {/* Pagination */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.divider}` }}>
                            <span style={{ fontSize: 11, color: T.t3 }}>Page {olPage + 1} of {olDisplayTotalPages || 1}</span>
                            <div style={{ display: 'flex', gap: 4 }}>
                                <button onClick={() => setOlPage(p => Math.max(0, p - 1))} disabled={olPage === 0} style={{ height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: olPage === 0 ? T.hover : T.surface, color: olPage === 0 ? T.t3 : T.t1, cursor: olPage === 0 ? 'default' : 'pointer', fontSize: 12, fontFamily: T.font }}>‹ Prev</button>
                                {Array.from({ length: Math.min(olDisplayTotalPages || 1, 7) }, (_, idx) => {
                                    const total = olDisplayTotalPages || 1;
                                    const pg = total <= 7 ? idx : (olPage < 4 ? idx : (olPage > total - 4 ? total - 7 + idx : olPage - 3 + idx));
                                    return <button key={pg} onClick={() => setOlPage(pg)} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${pg === olPage ? T.accent : T.cardBorder}`, background: pg === olPage ? T.accent : T.surface, color: pg === olPage ? '#fff' : T.t2, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: T.font }}>{pg + 1}</button>;
                                })}
                                <button onClick={() => setOlPage(p => Math.min((olDisplayTotalPages || 1) - 1, p + 1))} disabled={olPage >= (olDisplayTotalPages || 1) - 1} style={{ height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: olPage >= (olDisplayTotalPages || 1) - 1 ? T.hover : T.surface, color: olPage >= (olDisplayTotalPages || 1) - 1 ? T.t3 : T.t1, cursor: olPage >= (olDisplayTotalPages || 1) - 1 ? 'default' : 'pointer', fontSize: 12, fontFamily: T.font }}>Next ›</button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
        );
    };

    const renderCancelledDrill = () => {
        // Same ClickHouse data sources for Today AND historical (historical dispatches carry
        // startDate/endDate): refund-summary → reasons, refund-details → order log,
        // staff-refunds → refunds by staff.
        const sortedStaffRefunds = [...liveStaffRefunds].sort((a, b) => b.refund - a.refund);
        const totalStaffRefundAmt = liveStaffRefunds.reduce((s, item) => s + (item.refund || 0), 0);
        const maxRefundCount = liveRefundSummary.reduce((m, item) => Math.max(m, item.orderCount), 1);
        const refundLogItems = liveRefundDetails;
        const refundLogLoading = liveRefundDetailsLoading;
        const refundLogFailure = liveRefundDetailsFailure;
        const refundLogTotalPages = liveRefundDetailsTotalPages;
        return (
            <div className="snapshot-drill-wrap">
                <div className="snapshot-drill-subtext">
                    {liveRefundSummaryLoading
                        ? <Bone w={200} h={12} r={4} />
                        : `${fmtNum(liveRefundOrderCount)} orders · ${fmtCurrency(liveRefundTotalAmt)} total refund value`}
                </div>
                <div className="rpt-flex-row rpt-gap-16">
                    <div className="rpt-card" style={{ flex: 1 }}>
                        <div className="snapshot-drill-title">Cancel Reasons</div>
                        <div className="snapshot-drill-subtitle">Distribution by reason</div>
                        {liveRefundSummaryLoading ? (
                            <div>
                                {[1, 2, 3, 4].map(i => (
                                    <div key={i} style={{ marginBottom: 10 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <Bone h={12} r={3} style={{ width: `${45 + i * 10}%` }} />
                                            <Bone w={28} h={12} r={3} />
                                        </div>
                                        <Bone h={5} r={2} />
                                    </div>
                                ))}
                            </div>
                        ) : liveRefundSummaryFailure ? (
                            <SectionError kpiName="cancel reasons" onRetry={() => dispatch(fetchLiveRefundSummaryRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal))} minHeight={160} />
                        ) : liveRefundSummary.length ? (
                            liveRefundSummary.map((item, i) => (
                                <PBarRow key={i} label={item.cancelReason} val={`${item.orderCount}x`} pct={item.orderCount} max={maxRefundCount} color={T.red} />
                            ))
                        ) : (
                            <SectionEmpty kpiName="cancel reasons" minHeight={160} />
                        )}
                    </div>
                    <div className="rpt-card" style={{ flex: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                            <div>
                                <div className="snapshot-drill-title">Order Log</div>
                                <div className="snapshot-drill-subtitle">Recent refund orders</div>
                            </div>
                            {refundLogItems.length > 0 && (
                                <ReportSectionExportButton
                                    dateRangeLabel={exportDateRangeLabel}
                                    variant="desktop"
                                    title="Refund Order Log"
                                    filename="snapshot-refund-order-log"
                                    buildExport={async (format, opts) => {
                                        const loc = opts?.locationId ?? locationId;
                                        let total = liveRefundDetailsTotalElements || 1;
                                        if (opts?.locationId) {
                                            const probe: any = await getLiveRefundDetailsApi({ locationId: loc, page: 1, size: 1, ...chDatesView, ...orderSourceParam, token });
                                            total = probe?.totalElements ?? probe?.total ?? 1;
                                        }
                                        const res: any = await getLiveRefundDetailsApi({ locationId: loc, page: 1, size: total || 1, ...chDatesView, ...orderSourceParam, token });
                                        const list: any[] = Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : []);
                                        return {
                                            headers: data.isToday
                                                ? ['Order', 'Staff', 'Reason', 'Channel', 'Amount']
                                                : ['Order', 'Date', 'Staff', 'Reason', 'Channel', 'Amount'],
                                            rows: list.map((o: any) => {
                                                const row: any[] = [o.orderNo ?? o.orderNumber ?? '—'];
                                                if (!data.isToday) row.push(refundRowDate(o) || '—');
                                                row.push(
                                                    o.staffName ?? o.steward ?? o.staff ?? o.employeeName ?? o.cancelledBy ?? '-',
                                                    o.cancelReason ?? o.reason ?? o.cancelledReason ?? o.refundReason ?? '-',
                                                    o.channel ?? o.channelName ?? o.orderChannel ?? o.orderSource ?? o.orderType ?? '-',
                                                    formatMoneyForExport(o.cancelledAmt ?? o.refundAmount ?? o.canceledAmount ?? o.cancelAmount ?? o.amount ?? 0),
                                                );
                                                return row;
                                            }),
                                        };
                                    }}
                                />
                            )}
                        </div>
                        {refundLogLoading ? (
                            <div style={{ padding: '4px 0' }}>
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', borderBottom: i < 5 ? `1px solid ${T.divider}` : 'none' }}>
                                        <Bone w={60} h={11} r={3} />
                                        <Bone h={11} r={3} style={{ flex: 1 }} />
                                        <Bone w={50} h={11} r={3} />
                                        <Bone w={55} h={11} r={3} />
                                    </div>
                                ))}
                            </div>
                        ) : refundLogFailure ? (
                            <SectionError kpiName="refund order log" onRetry={() => dispatch(fetchLiveRefundDetailsRequest(locationId, cancelLogPage, CANCEL_PAGE_SIZE, orderSourceVal, chDatesView.startDate, chDatesView.endDate))} minHeight={160} />
                        ) : refundLogItems.length === 0 ? (
                            <SectionEmpty kpiName="refund order log" minHeight={160} />
                        ) : (() => {
                            const pageItems: any[] = refundLogItems;
                            const cancelCols = data.isToday
                                ? { tpl: '0.75fr 1fr 1.5fr 0.85fr 0.9fr', heads: ['Order', 'Staff', 'Reason', 'Channel', 'Amount'], amtIdx: 4 }
                                : { tpl: '0.8fr 0.9fr 1.2fr 2fr 0.8fr 0.9fr', heads: ['Order', 'Date', 'Staff', 'Reason', 'Channel', 'Amount'], amtIdx: 5 };
                            return (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: cancelCols.tpl, padding: '6px 0', borderBottom: `1px solid ${T.divider}`, marginBottom: 4 }}>
                                        {cancelCols.heads.map((h, i) => (
                                            <div key={i} style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: i === cancelCols.amtIdx ? 'right' : 'left' }}>{h}</div>
                                        ))}
                                    </div>
                                    {pageItems.map((o, i) => {
                                        return (
                                            <div key={i} style={{ display: 'grid', gridTemplateColumns: cancelCols.tpl, padding: '8px 0', borderBottom: i < pageItems.length - 1 ? `1px solid ${T.divider}` : 'none' }}>
                                                <div style={{ fontSize: 11.5, fontWeight: 600, color: T.t1 }}>#{o.orderNo ?? o.orderNumber ?? ((cancelLogPage - 1) * CANCEL_PAGE_SIZE + i + 1)}</div>
                                                {!data.isToday && <div style={{ fontSize: 11, color: T.t3, whiteSpace: 'nowrap' }}>{refundRowDate(o) || '—'}</div>}
                                                <div style={{ fontSize: 11.5, fontWeight: 600, color: T.accent }}>{o.staffName ?? o.steward ?? o.staff ?? o.employeeName ?? o.cancelledBy ?? '-'}</div>
                                                <div style={{ fontSize: 11.5, color: T.t2 }}>{o.cancelReason ?? o.reason ?? o.cancelledReason ?? o.refundReason ?? '-'}</div>
                                                <div style={{ fontSize: 11.5, color: T.t3 }}>{o.channel ?? o.channelName ?? o.orderChannel ?? o.orderSource ?? o.orderType ?? '-'}</div>
                                                <div style={{ fontSize: 11.5, fontWeight: 600, color: T.red, textAlign: 'right' }}>{fmtCurrency(o.cancelledAmt ?? o.refundAmount ?? o.canceledAmount ?? o.cancelAmount ?? o.amount ?? 0)}</div>
                                            </div>
                                        );
                                    })}
                                    {refundLogTotalPages > 1 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
                                            <div style={{ fontSize: 11, color: T.t3 }}>
                                                Page {cancelLogPage} of {refundLogTotalPages}
                                            </div>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <button onClick={() => setCancelLogPage((p) => Math.max(1, p - 1))} disabled={cancelLogPage === 1} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: cancelLogPage === 1 ? T.hover : T.card, color: cancelLogPage === 1 ? T.t3 : T.t1, fontSize: 11, fontWeight: 600, cursor: cancelLogPage === 1 ? 'default' : 'pointer' }}>Prev</button>
                                                <span style={{ fontSize: 11, color: T.t2, padding: '4px 6px' }}>{cancelLogPage} / {refundLogTotalPages}</span>
                                                <button onClick={() => setCancelLogPage((p) => Math.min(refundLogTotalPages, p + 1))} disabled={cancelLogPage >= refundLogTotalPages} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: cancelLogPage >= refundLogTotalPages ? T.hover : T.card, color: cancelLogPage >= refundLogTotalPages ? T.t3 : T.t1, fontSize: 11, fontWeight: 600, cursor: cancelLogPage >= refundLogTotalPages ? 'default' : 'pointer' }}>Next</button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </div>
                <div className="rpt-card" style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Refunds by Staff</div>
                    <div style={{ fontSize: 11, color: T.t3, marginBottom: 12 }}>Staff members who processed refunds</div>
                    {liveStaffRefundsLoading ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: i < 5 ? `1px solid ${T.divider}` : 'none' }}>
                                        <Bone w={20} h={20} r={6} />
                                        <div style={{ flex: 1 }}><Bone w={100} h={12} mb={4} /><Bone w={70} h={10} /></div>
                                        <Bone w={40} h={16} />
                                    </div>
                                ))}
                            </div>
                        ) : liveStaffRefundsFailure ? (
                            <SectionError kpiName="refunds by staff" onRetry={() => dispatch(fetchLiveStaffRefundsRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={160} />
                        ) : sortedStaffRefunds.length ? (
                            <>
                                {sortedStaffRefunds.slice(0, 5).map((s: LiveStaffRefundsItem, i: number) => {
                                    const refundRate = totalStaffRefundAmt > 0 ? (s.refund / totalStaffRefundAmt) * 100 : 0;
                                    return (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: i < Math.min(sortedStaffRefunds.length, 5) - 1 ? `1px solid ${T.divider}` : 'none' }}>
                                            <div style={{ width: 22, height: 22, borderRadius: 6, background: i === 0 ? `${T.red}20` : T.hover, color: i === 0 ? T.red : T.t3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: T.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.staff_name}</div>
                                                <div style={{ fontSize: 11, color: T.t3, marginTop: 1 }}>{s.orderCount} refunds · {refundRate.toFixed(1)}% rate</div>
                                            </div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: T.red, flexShrink: 0 }}>{fmtCurrency(s.refund)}</div>
                                        </div>
                                    );
                                })}
                                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.t3 }}>
                                    <span>{liveStaffRefunds.length} staff</span>
                                    <span>Total: <span style={{ color: T.red, fontWeight: 600 }}>{fmtCurrency(totalStaffRefundAmt)}</span></span>
                                </div>
                            </>
                        ) : (
                            <SectionEmpty kpiName="refunds by staff" minHeight={160} />
                    )}
                </div>
            </div>
        );
    };

    const renderStaffDrill = () => {
        // Staff Roster (Active Staff KPI drilldown) — unified across Today + historical via
        // /sales/live/v2/staff-active-clocking-v2 (paginated/searchable/sortable, any date range).
        // Table UI reuses the shared DataTableCard (same component as LaborReport's Working Hours Log).
        const formatStatus = (s: string | null | undefined) => {
            const v = String(s ?? '').trim();
            if (!v) return '—';
            return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
        };
        const statusColor = (s: string | null | undefined) => {
            if (isStatusActive(s)) return T.green;
            if (isStatusBreak(s)) return T.orange;
            return T.t3;
        };
        // clockOut arrives as "InProgress" for open shifts — style like LaborReport's Clock-Out
        const isClockOutInProgress = (v: string | null | undefined) => String(v ?? '').replace(/\s+/g, '').toLowerCase() === 'inprogress';
        const renderClockOut = (v: string | null | undefined) => isClockOutInProgress(v)
            ? <span style={{ color: T.green, fontStyle: 'italic' }}>In Progress</span>
            : (v ?? '—');
        // breakHours "0h 0m" → dash, same as LaborReport's Working Hours Log
        const isZeroDuration = (v: string | null | undefined) => {
            const nums = String(v ?? '').match(/\d+/g);
            return !nums || nums.every((n) => Number(n) === 0);
        };
        const renderBreakHours = (v: string | null | undefined) => isZeroDuration(v)
            ? <span style={{ color: T.t2 }}>—</span>
            : v;
        // Zero worked hours on a completed shift (clock-in/out within a minute) → ⚠ -, same as LaborReport
        const isZeroWorkedAnomaly = (r: LiveStaffActiveClockingV2Item) => isZeroDuration(r.workedHours) && r.clockOut != null && !isClockOutInProgress(r.clockOut);
        const renderWorkedHours = (r: LiveStaffActiveClockingV2Item) => isZeroWorkedAnomaly(r)
            ? <span style={{ color: T.orange, fontWeight: 600 }}>⚠ -</span>
            : (r.workedHours ?? '—');
        const rosterLoadingInitial = staffRosterV2Loading && staffRosterV2Items.length === 0;
        const totalHoursLabel = `${staffRosterV2TotalWorkedHours.toFixed(2)}h`;

        const columns: Col[] = [
            { key: 'date', label: 'Date', width: '12%', sortKey: 'date', value: (r: LiveStaffActiveClockingV2Item) => r.date ?? '', render: (r: LiveStaffActiveClockingV2Item) => r.date ?? '—' },
            { key: 'name', label: 'Employee Name', width: '19%', sortKey: 'employeeName', value: (r: LiveStaffActiveClockingV2Item) => r.employeeName ?? '', render: (r: LiveStaffActiveClockingV2Item) => { const name = r.employeeName?.trim() || '—'; return <span title={name} style={{ color: T.accent, fontWeight: 600, display: 'inline-block', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>{name}</span>; } },
            { key: 'in', label: 'Clock-In', width: '12%', sortKey: 'clockIn', value: (r: LiveStaffActiveClockingV2Item) => r.clockIn ?? '', render: (r: LiveStaffActiveClockingV2Item) => r.clockIn ?? '—' },
            { key: 'out', label: 'Clock-Out', width: '12%', sortKey: 'clockOut', value: (r: LiveStaffActiveClockingV2Item) => r.clockOut ?? '', render: (r: LiveStaffActiveClockingV2Item) => renderClockOut(r.clockOut) },
            { key: 'worked', label: 'Worked Hours', align: 'right', width: '12%', sortKey: 'workedHours', value: (r: LiveStaffActiveClockingV2Item) => r.workedHours ?? '', render: (r: LiveStaffActiveClockingV2Item) => renderWorkedHours(r) },
            { key: 'break', label: 'Break Hours', align: 'right', width: '12%', sortKey: 'breakHours', value: (r: LiveStaffActiveClockingV2Item) => r.breakHours ?? '', render: (r: LiveStaffActiveClockingV2Item) => renderBreakHours(r.breakHours) },
            { key: 'status', label: 'Status', align: 'right', width: '10%', sortKey: 'status', value: (r: LiveStaffActiveClockingV2Item) => r.status ?? '', render: (r: LiveStaffActiveClockingV2Item) => <span style={{ color: statusColor(r.status), fontWeight: 600 }}>{formatStatus(r.status)}</span> },
            { key: 'sales', label: 'Sales', align: 'right', width: '11%', sortKey: 'netSales', value: (r: LiveStaffActiveClockingV2Item) => Number(r.netSales) || 0, render: (r: LiveStaffActiveClockingV2Item) => fmtCurrency(Number(r.netSales) || 0) },
        ];

        const mobileCard = (r: LiveStaffActiveClockingV2Item) => (
            <div style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 12, background: T.surface }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.t1, flexShrink: 0 }}>{r.date ?? '—'}</span>
                    <span title={r.employeeName?.trim() || '—'} style={{ color: T.accent, fontWeight: 600, fontSize: 12, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.employeeName?.trim() || '—'}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 11 }}>
                    <div><span style={{ color: T.t3 }}>Clock-In: </span>{r.clockIn ?? '—'}</div>
                    <div><span style={{ color: T.t3 }}>Clock-Out: </span>{renderClockOut(r.clockOut)}</div>
                    <div><span style={{ color: T.t3 }}>Worked: </span>{renderWorkedHours(r)}</div>
                    <div><span style={{ color: T.t3 }}>Break: </span>{renderBreakHours(r.breakHours)}</div>
                    <div><span style={{ color: T.t3 }}>Status: </span><span style={{ color: statusColor(r.status), fontWeight: 600 }}>{formatStatus(r.status)}</span></div>
                    <div><span style={{ color: T.t3 }}>Sales: </span>{fmtCurrency(Number(r.netSales) || 0)}</div>
                </div>
            </div>
        );

        return (
            <div className="snapshot-drill-wrap">
                <div className="snapshot-drill-subtext">
                    {rosterLoadingInitial
                        ? <Bone w={220} h={12} r={4} />
                        : `${fmtNum(staffRosterV2StaffWorked)} staff worked · ${totalHoursLabel} total`}
                </div>
                <div className="snapshot-drill-kpi-row">
                    <MiniK label="Active" value={staffRosterV2Failure ? '0' : fmtNum(staffRosterV2ActiveCount)} color={T.green} loading={rosterLoadingInitial} />
                    <MiniK label="On Break" value={staffRosterV2Failure ? '0' : fmtNum(staffRosterV2BreakCount)} color={T.orange} loading={rosterLoadingInitial} />
                    <MiniK label="Total Hours" value={staffRosterV2Failure ? '--' : totalHoursLabel} loading={rosterLoadingInitial} />
                    <MiniK label="Avg Sales/Staff" value={staffRosterV2Failure ? fmtCurrency(0) : fmtCurrency(staffRosterV2AvgSalesPerStaff)} loading={rosterLoadingInitial} />
                </div>
                {staffRosterV2Failure ? (
                    <SectionCard title="Staff Roster" subtitle={data.isToday ? "Today's shift details" : 'Shift details'}>
                        <SectionError
                            kpiName="staff roster"
                            onRetry={() => dispatch(fetchLiveStaffActiveClockingV2Request({ locationId, ...(data.isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate }), search: staffRosterV2DebouncedSearch, page: staffRosterV2Page, size: staffRosterV2PageSize, sortBy: staffRosterV2SortBy, sortAsc: staffRosterV2SortAsc, ...orderSourceParam }))}
                            minHeight={160}
                        />
                    </SectionCard>
                ) : (
                    <DataTableCard
                        id="snapshot-staff-roster"
                        title="Staff Roster"
                        subtitle={data.isToday ? "Today's shift details" : 'Shift details'}
                        columns={columns}
                        rows={staffRosterV2Items}
                        mobileCard={mobileCard}
                        searchValue={(r: LiveStaffActiveClockingV2Item) => r.employeeName}
                        loading={staffRosterV2Loading}
                        serverPagination={{
                            totalElements: staffRosterV2TotalElements,
                            page: staffRosterV2Page,
                            pageSize: staffRosterV2PageSize,
                            onPageChange: (page: number, pageSize: number) => { setStaffRosterV2Page(page); setStaffRosterV2PageSize(pageSize); },
                        }}
                        onSearch={(v: string) => setStaffRosterV2Search(v)}
                        searchTerm={staffRosterV2Search}
                        serverSort={{
                            sortBy: staffRosterV2SortBy,
                            sortDir: staffRosterV2SortAsc ? 'asc' : 'desc',
                            onSortChange: (sortBy: string, sortDir: 'asc' | 'desc') => { setStaffRosterV2SortBy(sortBy); setStaffRosterV2SortAsc(sortDir === 'asc'); },
                        }}
                        exportConfig={{
                            title: 'Staff Roster',
                            filename: 'snapshot-staff-roster',
                            dateRangeLabel: exportDateRangeLabel,
                            buildExport: async (_format, opts) => {
                                const loc = opts?.locationId ?? locationId;
                                // staffRosterV2TotalElements is THIS outlet's count — invalid for an
                                // override outlet during "All outlets" export. Probe with size:1 first
                                // to learn the override outlet's real total, same pattern as every other
                                // multi-outlet-aware export in this file (e.g. Void Log above).
                                let total = staffRosterV2TotalElements || 1;
                                if (opts?.locationId) {
                                    const probe: any = await getLiveStaffActiveClockingV2Api({ locationId: loc, ...(data.isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate }), search: staffRosterV2DebouncedSearch, page: 1, size: 1, sortBy: staffRosterV2SortBy, sortAsc: staffRosterV2SortAsc, ...orderSourceParam });
                                    total = probe?.totalElements ?? probe?.total ?? 1;
                                }
                                const res: any = await getLiveStaffActiveClockingV2Api({ locationId: loc, ...(data.isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate }), search: staffRosterV2DebouncedSearch, page: 1, size: total, sortBy: staffRosterV2SortBy, sortAsc: staffRosterV2SortAsc, ...orderSourceParam });
                                const list: any[] = Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : []);
                                return {
                                    headers: ['Date', 'Employee Name', 'Clock-In', 'Clock-Out', 'Worked Hours', 'Break Hours', 'Status', 'Sales'],
                                    rows: list.map((s: any) => [
                                        s.date ?? '—',
                                        s.employeeName ?? '—',
                                        s.clockIn ?? '—',
                                        isClockOutInProgress(s.clockOut) ? 'In Progress' : (s.clockOut ?? '—'),
                                        s.workedHours ?? '—',
                                        s.breakHours ?? '—',
                                        formatStatus(s.status),
                                        formatMoneyForExport(Number(s.netSales) || 0),
                                    ]),
                                };
                            },
                        }}
                    />
                )}
            </div>
        );
    };

    const renderCompDrill = () => {
        // ── All data from Redux ClickHouse endpoints ──────────────────
        if (liveCompOrderLogLoading) {
                return (
                    <div className="snapshot-drill-wrap">
                        <div className="snapshot-drill-kpi-row">
                            {['Total Comps', 'Total Value', 'Avg Comp Value', 'Top Reason'].map(label => (
                                <div key={label} className="snapshot-minik-tile">
                                    <div className="snapshot-minik-label">{label}</div>
                                    <Bone w={80} h={24} style={{ marginTop: 4 }} />
                                </div>
                            ))}
                        </div>
                        <div className="rpt-flex-row rpt-gap-16" style={{ alignItems: 'flex-start' }}>
                            <div className="rpt-card" style={{ flex: 1 }}>
                                <div className="snapshot-drill-title snapshot-drill-title--spaced">Comp Reasons</div>
                                {[1, 2, 3, 4].map(i => <Bone key={i} w={'100%' as any} h={12} mb={10} />)}
                                <div className="snapshot-drill-title snapshot-drill-title--spaced" style={{ marginTop: 16 }}>Comp by Staff</div>
                                {[1, 2, 3].map(i => <Bone key={i} w={'100%' as any} h={12} mb={10} />)}
                            </div>
                            <div className="rpt-card" style={{ flex: 2 }}>
                                <div className="snapshot-drill-title">Comp Order Log</div>
                                <div className="snapshot-drill-subtitle">Today's complimentary orders</div>
                                {[1, 2, 3, 4, 5].map(i => <Bone key={i} w={'100%' as any} h={14} mb={10} />)}
                            </div>
                        </div>
                    </div>
                );
            }
            if (liveCompOrderLogFailure) {
                return (
                    <div className="snapshot-drill-wrap">
                        <SectionError
                            kpiName="complimentary orders"
                            size="medium"
                            onRetry={() => {
                                dispatch(fetchLiveCompOrderLogRequest({ locationId, page: compLogPageToday + 1, size: COMP_PAGE_SIZE, ...chDatesView, ...orderSourceParam }));
                                dispatch(fetchLiveCompReasonsRequest({ locationId, ...chDatesView, ...orderSourceParam }));
                                dispatch(fetchLiveCompByStaffRequest({ locationId, ...chDatesView, ...orderSourceParam }));
                            }}
                        />
                    </div>
                );
            }
            const todayTotal = liveCompOrderLog.reduce((s, o) => s + (Number(o.amount) || 0), 0);
            const todayCount = liveCompOrderLogTotalElements;
            const avgComp = todayCount > 0 ? todayTotal / todayCount : 0;
            const topReasonToday = [...liveCompReasons].sort((a, b) => b.occurrenceCount - a.occurrenceCount)[0]?.cancelReason ?? '—';
            const maxOccurrence = liveCompReasons.reduce((m, r) => Math.max(m, r.occurrenceCount), 1);
            const alertStaffToday = [...liveCompByStaff].sort((a, b) => b.compCount - a.compCount).slice(0, 1).filter(s => s.compCount >= 5);
            const maxStaffValue = liveCompByStaff.reduce((m, s) => Math.max(m, s.totalCompValue), 1);
            const colTpl = data.isToday ? '1.2fr 1.2fr 1.5fr 1.5fr 1.5fr 1fr' : '1.2fr 1fr 1.2fr 1.5fr 1.5fr 1.5fr 1fr';
            return (
                <div className="snapshot-drill-wrap">
                    <div className="snapshot-drill-subtext">
                        {fmtNum(todayCount)} complimentary orders · {fmtCurrency(todayTotal)} total value
                    </div>
                    {alertStaffToday.map(s => (
                        <div key={s.staffName} style={{ background: `${T.orange}18`, border: `1px solid ${T.orange}44`, borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: T.t1 }}>
                            <span style={{ fontWeight: 600, color: T.orange }}>⚠ {s.staffName}</span> has applied {s.compCount} of {todayCount} comps today ({fmtCurrency(s.totalCompValue)} total).
                        </div>
                    ))}
                    <div className="snapshot-drill-kpi-row">
                        <MiniK label="Total Comps" value={fmtNum(todayCount)} color={T.accent} />
                        <MiniK label="Total Value" value={fmtCurrency(todayTotal)} />
                        <MiniK label="Avg Comp Value" value={fmtCurrency(avgComp)} />
                        <MiniK label="Top Reason" value={topReasonToday} loading={liveCompReasonsLoading} />
                    </div>
                    {!liveCompOrderLogLoading && todayCount === 0 ? (
                        <div className="rpt-card"><SectionEmpty kpiName="complimentary orders" /></div>
                    ) : (
                        <div className="rpt-flex-row rpt-gap-16" style={{ alignItems: 'flex-start' }}>
                            {/* Comp Reasons */}
                            <div className="rpt-card" style={{ flex: 1 }}>
                                <div className="snapshot-drill-title snapshot-drill-title--spaced">Comp Reasons</div>
                                {liveCompReasonsLoading ? (
                                    <>{[1,2,3,4].map(i => <Bone key={i} w={'100%' as any} h={12} mb={10} />)}</>
                                ) : liveCompReasonsFailure ? (
                                    <SectionError kpiName="comp reasons" onRetry={() => dispatch(fetchLiveCompReasonsRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={60} />
                                ) : liveCompReasons.length === 0 ? (
                                    <SectionEmpty kpiName="comp reasons" size="small" minHeight={60} />
                                ) : liveCompReasons.slice(0, 8).map((r, i) => (
                                    <div key={i} style={{ marginBottom: 10 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                            <span style={{ fontSize: 12, color: T.t2 }}>{r.cancelReason}</span>
                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                <span style={{ fontSize: 11, color: T.t3 }}>{r.occurrenceCount}x</span>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, width: 60, textAlign: 'right' }}>{fmtCurrency(r.totalAmount)}</span>
                                            </div>
                                        </div>
                                        <div style={{ height: 5, background: T.hover ?? '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${Math.round((r.occurrenceCount / maxOccurrence) * 100)}%`, background: T.green, borderRadius: 3, opacity: 0.65 }} />
                                        </div>
                                    </div>
                                ))}
                                {/* Comp by Staff */}
                                <div className="snapshot-drill-title snapshot-drill-title--spaced" style={{ marginTop: 16 }}>Comp by Staff</div>
                                {liveCompByStaffLoading ? (
                                    <>{[1,2,3].map(i => <Bone key={i} w={'100%' as any} h={12} mb={10} />)}</>
                                ) : liveCompByStaffFailure ? (
                                    <SectionError kpiName="comp by staff" onRetry={() => dispatch(fetchLiveCompByStaffRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={60} />
                                ) : liveCompByStaff.length === 0 ? (
                                    <SectionEmpty kpiName="comp by staff" size="small" minHeight={60} />
                                ) : liveCompByStaff.slice(0, 6).map((s, i) => (
                                    <div key={i} style={{ marginBottom: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                            <span style={{ fontSize: 12, color: T.accent, fontWeight: 500 }}>{s.staffName}</span>
                                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                                <span style={{ fontSize: 11, color: T.t3 }}>{s.compCount}x</span>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, width: 60, textAlign: 'right' }}>{fmtCurrency(s.totalCompValue)}</span>
                                            </div>
                                        </div>
                                        <div style={{ height: 4, background: T.hover ?? '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${Math.round((s.totalCompValue / maxStaffValue) * 100)}%`, background: T.accent, borderRadius: 3, opacity: 0.55 }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Comp Order Log */}
                            <div className="rpt-card" style={{ flex: 2, overflow: 'hidden' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                                    <div>
                                        <div className="snapshot-drill-title">Comp Order Log</div>
                                        <div className="snapshot-drill-subtitle">Today's complimentary orders</div>
                                    </div>
                                    {liveCompOrderLog.length > 0 && (
                                        <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                            variant="desktop"
                                            title="Comp Order Log"
                                            filename="snapshot-comp-order-log-today"
                                            buildExport={async (format, opts) => {
                                                const loc = opts?.locationId ?? locationId;
                                                let total = liveCompOrderLogTotalElements || 1;
                                                if (opts?.locationId) {
                                                    const probe: any = await getLiveCompOrderLogApi({ locationId: loc, page: 1, size: 1, ...chDatesView, ...orderSourceParam });
                                                    total = probe?.totalElements ?? probe?.total ?? 1;
                                                }
                                                const res: any = await getLiveCompOrderLogApi({ locationId: loc, page: 1, size: total || 1, ...chDatesView, ...orderSourceParam });
                                                const list: any[] = Array.isArray(res?.orders) ? res.orders : (Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : liveCompOrderLog));
                                                return {
                                                    headers: data.isToday ? ['Order ID', 'Time', 'Staff', 'Reason', 'Items', 'Amount'] : ['Order ID', 'Date', 'Time', 'Staff', 'Reason', 'Items', 'Amount'],
                                                    rows: list.map((row: any) => {
                                                        const displayTime = (() => { const m = String(row.orderTime).match(/(\d{1,2}:\d{2}\s*(?:[AaPp][Mm])?)/); return m ? m[1].trim() : row.orderTime; })();
                                                        const itemDisplay = Array.isArray(row.items) ? row.items[0] ?? '—' : String(row.items ?? '—');
                                                        return data.isToday
                                                            ? [row.orderNo, displayTime, row.staffName, row.cancelReason, itemDisplay, formatMoneyForExport(row.amount)]
                                                            : [row.orderNo, row.orderDate ?? '—', displayTime, row.staffName, row.cancelReason, itemDisplay, formatMoneyForExport(row.amount)];
                                                    }),
                                                };
                                            }}
                                        />
                                    )}
                                </div>
                                {liveCompOrderLogLoading ? (
                                    <>{[1,2,3,4,5].map(i => <Bone key={i} w={'100%' as any} h={14} mb={10} />)}</>
                                ) : liveCompOrderLogFailure ? (
                                    <SectionError kpiName="comp order log" onRetry={() => dispatch(fetchLiveCompOrderLogRequest({ locationId, page: compLogPageToday + 1, size: COMP_PAGE_SIZE, ...chDatesView, ...orderSourceParam }))} minHeight={80} />
                                ) : liveCompOrderLog.length === 0 ? (
                                    <SectionEmpty kpiName="comp order log" minHeight={80} />
                                ) : (
                                    <>
                                        <div style={{ display: 'grid', gridTemplateColumns: colTpl, gap: '4px 8px', borderBottom: `1px solid ${T.divider}`, paddingBottom: 6, marginBottom: 4 }}>
                                            {(data.isToday ? ['Order ID', 'Time', 'Staff', 'Reason', 'Items', 'Amount'] : ['Order ID', 'Date', 'Time', 'Staff', 'Reason', 'Items', 'Amount']).map(h => (
                                                <div key={h} style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.3 }}>{h}</div>
                                            ))}
                                        </div>
                                        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                                            {liveCompOrderLog.map((row, i) => {
                                                const displayTime = (() => { const m = String(row.orderTime).match(/(\d{1,2}:\d{2}\s*(?:[AaPp][Mm])?)/); return m ? m[1].trim() : row.orderTime; })();
                                                const itemDisplay = Array.isArray(row.items) ? row.items[0] ?? '—' : String(row.items ?? '—');
                                                return (
                                                    <div key={i} style={{ display: 'grid', gridTemplateColumns: colTpl, gap: '4px 8px', padding: '7px 0', borderBottom: i < liveCompOrderLog.length - 1 ? `1px solid ${T.divider}` : 'none', alignItems: 'center' }}>
                                                        <span style={{ fontSize: 11, color: T.accent, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.orderNo}</span>
                                                        {!data.isToday && <span style={{ fontSize: 11, color: T.t3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(row as any).orderDate ?? '—'}</span>}
                                                        <span style={{ fontSize: 11, color: T.t2 }}>{displayTime}</span>
                                                        <span style={{ fontSize: 11, color: T.accent, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.staffName}</span>
                                                        <span style={{ fontSize: 11, color: T.t2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.cancelReason}</span>
                                                        <span style={{ fontSize: 11, color: T.t2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{itemDisplay}</span>
                                                        <span style={{ fontSize: 11, fontWeight: 600, color: T.t1, textAlign: 'right' }}>{fmtCurrency(row.amount)}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {liveCompOrderLogTotalPages > 1 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${T.divider}` }}>
                                                <span style={{ fontSize: 11, color: T.t3 }}>
                                                    {compLogPageToday * COMP_PAGE_SIZE + 1}–{Math.min((compLogPageToday + 1) * COMP_PAGE_SIZE, liveCompOrderLogTotalElements)} of {liveCompOrderLogTotalElements}
                                                </span>
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <button onClick={() => setCompLogPageToday(p => Math.max(0, p - 1))} disabled={compLogPageToday === 0} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: compLogPageToday === 0 ? T.hover : T.card, color: compLogPageToday === 0 ? T.t3 : T.t1, fontSize: 11, fontWeight: 600, cursor: compLogPageToday === 0 ? 'default' : 'pointer' }}>← Prev</button>
                                                    <span style={{ fontSize: 11, color: T.t2, padding: '4px 6px' }}>{compLogPageToday + 1} / {liveCompOrderLogTotalPages}</span>
                                                    <button onClick={() => setCompLogPageToday(p => Math.min(liveCompOrderLogTotalPages - 1, p + 1))} disabled={compLogPageToday >= liveCompOrderLogTotalPages - 1} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: compLogPageToday >= liveCompOrderLogTotalPages - 1 ? T.hover : T.card, color: compLogPageToday >= liveCompOrderLogTotalPages - 1 ? T.t3 : T.t1, fontSize: 11, fontWeight: 600, cursor: compLogPageToday >= liveCompOrderLogTotalPages - 1 ? 'default' : 'pointer' }}>Next →</button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            );
    };

    const renderVoidsDrill = () => {
        // Sorted desc by count so ordering matches mobile; bar widths stay relative to the
        // overall max (not the page max) so bars are comparable across pages.
        // Void Reasons count = voided item quantity (removedQty), not distinct orders.
        const sortedVoidSummary = [...liveVoidSummary].sort((a, b) => (Number(b.removedQty) || 0) - (Number(a.removedQty) || 0));
        const maxVoidSummaryCount = sortedVoidSummary.length > 0
            ? sortedVoidSummary.reduce((m, item) => Math.max(m, Number(item.removedQty) || 0), 1)
            : 1;
        const voidReasonsTotalPages = Math.max(1, Math.ceil(sortedVoidSummary.length / VOID_REASONS_PAGE_SIZE));
        const voidReasonsSafePage = Math.min(voidReasonsPage, voidReasonsTotalPages);
        const voidReasonsPageRows = sortedVoidSummary.slice((voidReasonsSafePage - 1) * VOID_REASONS_PAGE_SIZE, voidReasonsSafePage * VOID_REASONS_PAGE_SIZE);
        // Voids by Staff — aggregated from the full void log (liveVoidDetails, fetched at
        // size:1000 for the whole period) so a staff member who voided ≥2 items anywhere in
        // the period is flagged, not just within the current log page.
        const staffVoidMap: Record<string, { name: string; count: number; amount: number }> = {};
        liveVoidDetails.forEach((v) => {
            const name = (v.staffName ?? '').trim();
            if (!name) return;
            if (!staffVoidMap[name]) staffVoidMap[name] = { name, count: 0, amount: 0 };
            staffVoidMap[name].count += Number(v.voidedQty) || 1; // voided item quantity, not log rows
            staffVoidMap[name].amount += Number(v.cancelledAmt) || 0;
        });
        const alertStaffVoids = Object.values(staffVoidMap).filter((s) => s.count >= 2).sort((a, b) => b.count - a.count);
        // Unified empty state — gated on /void-kpis having actually resolved to zero (not
        // merely loading or failed, where section-level shimmers/errors below still apply).
        const voidsEmpty = !voidKpisLoading && !voidKpisFailure && Number(voidKpis?.voidedItems ?? 0) === 0;
        return (
            <div className="snapshot-drill-wrap">
                <div className="snapshot-drill-subtext">
                    {voidKpisLoading ? <Bone w={240} h={12} r={4} /> : voidKpisFailure
                        ? `${fmtNum(voidCount)} voids · ${fmtCurrency(voidAmount)} total voided value`
                        : `${fmtNum(voidKpis?.voidedItems ?? 0)} voided items · ${fmtCurrency(voidKpis?.voidValue ?? 0)} total voided value · ${fmtNum(voidCount)} orders`}
                </div>
                <div className="snapshot-drill-kpi-row">
                    <MiniK label="Voided Items" value={voidKpisFailure ? '--' : fmtNum(voidKpis?.voidedItems ?? 0)} color={T.orange} loading={voidKpisLoading} />
                    <MiniK label="Void Value" value={voidKpisFailure ? '--' : fmtCurrency(voidKpis?.voidValue ?? 0)} loading={voidKpisLoading} />
                    <MiniK label="Void Rate" value={voidKpisFailure ? '--' : `${Number(voidKpis?.voidRate ?? 0).toFixed(1)}%`} loading={voidKpisLoading} />
                    <MiniK label="Avg Void Value" value={voidKpisFailure ? '--' : fmtCurrency(voidKpis?.avgVoidValue ?? 0)} loading={voidKpisLoading} />
                </div>
                {voidsEmpty ? (
                    <SectionEmpty kpiName="voids" size="medium" minHeight={220} message="No voids — nothing was removed from a check after firing." />
                ) : (
                <>
                {!liveVoidDetailsLoading && !liveVoidDetailsFailure && alertStaffVoids.map((s) => (
                    <div key={s.name} style={{ background: `${T.orange}18`, border: `1px solid ${T.orange}44`, borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: T.t1 }}>
                        <span style={{ fontWeight: 600, color: T.orange }}>⚠ {s.name}</span> voided {s.count} items ({fmtCurrency(s.amount)} total). Consider reviewing with manager.
                    </div>
                ))}
                    {/* Void Log — /sales/live/v2/paid-void-details (Today + historical) — solo row, full width */}
                    <div className="rpt-card" style={{ overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                            <div>
                                <div className="snapshot-drill-title">Void Log</div>
                                <div className="snapshot-drill-subtitle">Voided order details</div>
                            </div>
                            {liveVoidDetails.length > 0 && (
                                <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                    variant="desktop"
                                    title="Void Log"
                                    filename="snapshot-void-log"
                                    buildExport={async (format, opts) => {
                                        const loc = opts?.locationId ?? locationId;
                                        let total = liveVoidDetailsTotalElements || 1;
                                        if (opts?.locationId) {
                                            const probe: any = await getLiveVoidDetailsApi({ locationId: loc, page: 1, size: 1, ...chDatesView, ...orderSourceParam, token });
                                            total = probe?.totalElements ?? probe?.total ?? 1;
                                        }
                                        const res: any = await getLiveVoidDetailsApi({ locationId: loc, page: 1, size: total || 1, ...chDatesView, ...orderSourceParam, token });
                                        const list: any[] = Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : []);
                                        return {
                                            headers: ['Order #', 'Date', 'Time', 'Item', 'Staff', 'Reason', 'Qty', 'Amount'],
                                            rows: list.map((v: any) => [`#${v.orderNo}`, v.orderDate ?? '—', voidRowTime(v), v.itemName || '—', v.staffName || '—', v.cancelReason || '—', v.voidedQty ?? '—', formatMoneyForExport(v.cancelledAmt)]),
                                        };
                                    }}
                                />
                            )}
                        </div>
                        {(() => {
                            const cellClip = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
                            if (liveVoidDetailsLoading) return <div style={{ padding: '4px 0' }}>{[1, 2, 3, 4, 5].map(i => (<div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', borderBottom: i < 5 ? `1px solid ${T.divider}` : 'none' }}><Bone w={60} h={11} r={3} /><Bone h={11} r={3} style={{ flex: 1 }} /><Bone w={50} h={11} r={3} /><Bone w={55} h={11} r={3} /></div>))}</div>;
                            if (liveVoidDetailsFailure) return <SectionError kpiName="void log" onRetry={() => dispatch(fetchLiveVoidDetailsRequest(locationId, 1, 1000, chDatesView.startDate, chDatesView.endDate, orderSourceVal))} minHeight={160} />;
                            if (!liveVoidDetails.length) return <SectionEmpty kpiName="void log" minHeight={160} />;
                            // Whole-check voids: every voided item sharing an order number collapses into
                            // one row (order #, staff, reason, item count, combined amount), expandable to
                            // each item's own price. Single-item orders render exactly as before. Each item
                            // still counts individually toward Voided Items and the Voids by Staff ranking —
                            // this groups only how the log TABLE presents them.
                            type VoidLogGroup = { key: string; orderNo: string; staffName: string; amount: number; qty: number; items: typeof liveVoidDetails };
                            const voidLogGroups: VoidLogGroup[] = (() => {
                                const map = new Map<string, VoidLogGroup>();
                                const order: string[] = [];
                                liveVoidDetails.forEach((v) => {
                                    const key = v.orderNo || `_${order.length}`;
                                    if (!map.has(key)) {
                                        map.set(key, { key, orderNo: v.orderNo, staffName: v.staffName || '—', amount: 0, qty: 0, items: [] });
                                        order.push(key);
                                    }
                                    const g = map.get(key)!;
                                    g.amount += Number(v.cancelledAmt) || 0;
                                    g.qty += Number(v.voidedQty) || 0;
                                    g.items.push(v);
                                });
                                return order.map((k) => map.get(k)!);
                            })();
                            const feVoidTotalPages = Math.max(1, Math.ceil(voidLogGroups.length / VOID_DETAILS_PAGE_SIZE));
                            const feVoidPageGroups = voidLogGroups.slice((voidDetailsPage - 1) * VOID_DETAILS_PAGE_SIZE, voidDetailsPage * VOID_DETAILS_PAGE_SIZE);
                            // Rows carry BOTH orderDate and voidedTime, so the log shows a DATE column
                            // beside TIME for Today and historical ranges alike (matching the mobile void card).
                            const voidTpl = '1.2fr 1.2fr 0.9fr 2fr 1.2fr 2fr 0.6fr 0.9fr';
                            const voidHeads = ['Order #', 'Date', 'Time', 'Item', 'Staff', 'Reason', 'Qty', 'Amount'];
                            return (
                                <div>
                                    <div style={{ overflowX: 'auto' }}>
                                        <div style={{ minWidth: 680 }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: voidTpl, gap: 8, padding: '6px 0', borderBottom: `1px solid ${T.divider}`, marginBottom: 4 }}>
                                                {voidHeads.map((h, i) => (
                                                    <div key={i} style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: (i === voidHeads.length - 1 || h === 'Qty') ? 'right' : 'left' }}>{h}</div>
                                                ))}
                                            </div>
                                            {feVoidPageGroups.map((g, i) => {
                                                const isGroup = g.items.length > 1;
                                                const isExpanded = expandedVoidGroups.has(g.key);
                                                const first = g.items[0];
                                                const dispTime = voidRowTime(first);
                                                const itemLabel = isGroup ? `${g.items.length} items` : (first.itemName || '—');
                                                const groupReasons = Array.from(new Set(g.items.map((it) => it.cancelReason || '—')));
                                                const reasonLabel = groupReasons.length > 1 ? `${groupReasons.length} reasons` : groupReasons[0];
                                                const isLast = i === feVoidPageGroups.length - 1;
                                                return (
                                                <div key={g.key}>
                                                    <div
                                                        onClick={isGroup ? () => toggleVoidGroup(g.key) : undefined}
                                                        style={{ display: 'grid', gridTemplateColumns: voidTpl, gap: 8, padding: '8px 0', alignItems: 'center', borderBottom: (isLast && !(isGroup && isExpanded)) ? 'none' : `1px solid ${T.divider}`, cursor: isGroup ? 'pointer' : 'default' }}
                                                    >
                                                        <div title={g.orderNo} style={{ fontSize: 11.5, fontWeight: 600, color: T.t2, display: 'flex', alignItems: 'center', gap: 4, ...cellClip }}>
                                                            {isGroup && <Chev s={9} c={T.t3} r={isExpanded ? 180 : 0} />}
                                                            #{g.orderNo}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: T.t3, whiteSpace: 'nowrap' }}>{first.orderDate ?? '—'}</div>
                                                        <div style={{ fontSize: 11, color: T.t3, whiteSpace: 'nowrap' }}>{dispTime}</div>
                                                        <TruncatedCell text={itemLabel} style={{ fontSize: 11.5, fontWeight: 600, color: T.t1 }} />
                                                        <div title={g.staffName} style={{ fontSize: 11.5, fontWeight: 600, color: T.accent, ...cellClip }}>{g.staffName}</div>
                                                        <div title={groupReasons.join(', ')} style={{ fontSize: 11.5, color: T.t2, ...cellClip }}>{reasonLabel}</div>
                                                        <div style={{ fontSize: 11.5, fontWeight: 600, color: T.t2, textAlign: 'right', whiteSpace: 'nowrap' }}>{g.qty}</div>
                                                        <div style={{ fontSize: 11.5, fontWeight: 600, color: T.orange, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtCurrency(g.amount)}</div>
                                                    </div>
                                                    {isGroup && isExpanded && g.items.map((v, vi) => {
                                                        const vDispTime = voidRowTime(v);
                                                        return (
                                                        <div key={vi} style={{ display: 'grid', gridTemplateColumns: voidTpl, gap: 8, padding: '6px 0', alignItems: 'center', background: T.hover, borderBottom: (isLast && vi === g.items.length - 1) ? 'none' : `1px solid ${T.divider}` }}>
                                                            <div />
                                                            <div style={{ fontSize: 11, color: T.t3, whiteSpace: 'nowrap' }}>{v.orderDate ?? '—'}</div>
                                                            <div style={{ fontSize: 11, color: T.t3, whiteSpace: 'nowrap' }}>{vDispTime}</div>
                                                            <TruncatedCell text={v.itemName || '—'} style={{ fontSize: 11, color: T.t2 }} />
                                                            <div title={v.staffName} style={{ fontSize: 11, color: T.t2, ...cellClip }}>{v.staffName || '—'}</div>
                                                            <div title={v.cancelReason} style={{ fontSize: 11, color: T.t2, ...cellClip }}>{v.cancelReason || '—'}</div>
                                                            <div style={{ fontSize: 11, fontWeight: 500, color: T.t2, textAlign: 'right', whiteSpace: 'nowrap' }}>{v.voidedQty ?? '—'}</div>
                                                            <div style={{ fontSize: 11, fontWeight: 500, color: T.t2, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtCurrency(v.cancelledAmt)}</div>
                                                        </div>
                                                        );
                                                    })}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {feVoidTotalPages > 1 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
                                            <span style={{ fontSize: 11, color: T.t3 }}>
                                                {(voidDetailsPage - 1) * VOID_DETAILS_PAGE_SIZE + 1}–{Math.min(voidDetailsPage * VOID_DETAILS_PAGE_SIZE, voidLogGroups.length)} of {voidLogGroups.length}
                                            </span>
                                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                <button onClick={() => setVoidDetailsPage((p) => Math.max(1, p - 1))} disabled={voidDetailsPage === 1} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: voidDetailsPage === 1 ? T.hover : T.card, color: voidDetailsPage === 1 ? T.t3 : T.t1, fontSize: 11, fontWeight: 600, cursor: voidDetailsPage === 1 ? 'default' : 'pointer' }}>← Prev</button>
                                                <span style={{ fontSize: 11, color: T.t2, padding: '4px 6px' }}>{voidDetailsPage} / {feVoidTotalPages}</span>
                                                <button onClick={() => setVoidDetailsPage((p) => Math.min(feVoidTotalPages, p + 1))} disabled={voidDetailsPage >= feVoidTotalPages} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: voidDetailsPage >= feVoidTotalPages ? T.hover : T.card, color: voidDetailsPage >= feVoidTotalPages ? T.t3 : T.t1, fontSize: 11, fontWeight: 600, cursor: voidDetailsPage >= feVoidTotalPages ? 'default' : 'pointer' }}>Next →</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                <div className="rpt-flex-row rpt-gap-16" style={{ alignItems: 'flex-start', marginTop: 16 }}>
                    {/* Void Reasons */}
                    <div className="rpt-card" style={{ flex: 1 }}>
                        <div className="snapshot-drill-title snapshot-drill-title--spaced">Void Reasons</div>
                        {liveVoidSummaryLoading ? (
                            <div>
                                {[1, 2, 3, 4].map(i => (
                                    <div key={i} style={{ marginBottom: 10 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <Bone h={12} r={3} style={{ width: `${45 + i * 10}%` }} />
                                            <Bone w={28} h={12} r={3} />
                                        </div>
                                        <Bone h={5} r={2} />
                                    </div>
                                ))}
                            </div>
                        ) : liveVoidSummaryFailure ? (
                            <SectionError kpiName="void reasons" onRetry={() => dispatch(fetchLiveVoidSummaryRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal))} minHeight={160} />
                        ) : liveVoidSummary.length ? (
                            <>
                                {voidReasonsPageRows.map((item, i) => (
                                    <PBarRow key={i} label={item.cancelReason} val={`${Number(item.removedQty) || 0}x`} pct={Number(item.removedQty) || 0} max={maxVoidSummaryCount} color={T.orange} />
                                ))}
                                {voidReasonsTotalPages > 1 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
                                        <span style={{ fontSize: 11, color: T.t3 }}>
                                            {(voidReasonsSafePage - 1) * VOID_REASONS_PAGE_SIZE + 1}–{Math.min(voidReasonsSafePage * VOID_REASONS_PAGE_SIZE, sortedVoidSummary.length)} of {sortedVoidSummary.length}
                                        </span>
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                            <button onClick={() => setVoidReasonsPage(Math.max(1, voidReasonsSafePage - 1))} disabled={voidReasonsSafePage === 1} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: voidReasonsSafePage === 1 ? T.hover : T.card, color: voidReasonsSafePage === 1 ? T.t3 : T.t1, fontSize: 11, fontWeight: 600, cursor: voidReasonsSafePage === 1 ? 'default' : 'pointer' }}>← Prev</button>
                                            <span style={{ fontSize: 11, color: T.t2, padding: '4px 6px' }}>{voidReasonsSafePage} / {voidReasonsTotalPages}</span>
                                            <button onClick={() => setVoidReasonsPage(Math.min(voidReasonsTotalPages, voidReasonsSafePage + 1))} disabled={voidReasonsSafePage >= voidReasonsTotalPages} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: voidReasonsSafePage >= voidReasonsTotalPages ? T.hover : T.card, color: voidReasonsSafePage >= voidReasonsTotalPages ? T.t3 : T.t1, fontSize: 11, fontWeight: 600, cursor: voidReasonsSafePage >= voidReasonsTotalPages ? 'default' : 'pointer' }}>Next →</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <SectionEmpty kpiName="void reasons" minHeight={160} />
                        )}
                    </div>
                    {/* Voids by Staff — /void-staff (NOT YET DEPLOYED, see getVoidByStaffApi) */}
                    <div className="rpt-card" style={{ flex: 1 }}>
                        <div className="snapshot-drill-title">Voids by Staff</div>
                    <div className="snapshot-drill-subtitle">Staff members with the most voided items</div>
                    {voidByStaffLoading ? (
                        <div style={{ marginTop: 8 }}>
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <Bone h={12} r={3} style={{ width: `${45 + i * 8}%` }} />
                                        <Bone w={60} h={12} r={3} />
                                    </div>
                                    <Bone h={6} r={3} />
                                </div>
                            ))}
                        </div>
                    ) : voidByStaffFailure ? (
                        <SectionError kpiName="voids by staff" onRetry={() => dispatch(fetchVoidByStaffRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal))} minHeight={120} />
                    ) : voidByStaff.length ? (() => {
                        const sortedStaffVoids = [...voidByStaff].sort((a, b) => b.cancelledAmt - a.cancelledAmt);
                        const maxStaffVoidAmt = sortedStaffVoids.reduce((m, s) => Math.max(m, s.cancelledAmt), 1);
                        return (
                            <div style={{ marginTop: 8 }}>
                                {sortedStaffVoids.map((s, i) => (
                                    <div key={i} style={{ marginBottom: i < sortedStaffVoids.length - 1 ? 10 : 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: T.accent }}>{s.staffName}</span>
                                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                <span style={{ fontSize: 11, color: T.t3 }}>{fmtNum(s.removedQty)} items</span>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, width: 70, textAlign: 'right' }}>{fmtCurrency(s.cancelledAmt)}</span>
                                            </div>
                                        </div>
                                        <div style={{ height: 6, background: T.hover, borderRadius: 3, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${Math.min((s.cancelledAmt / maxStaffVoidAmt) * 100, 100)}%`, background: T.orange, borderRadius: 3, opacity: 0.7 }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })() : (
                        <SectionEmpty kpiName="voids by staff" minHeight={120} />
                    )}
                </div>
                </div>
                </>
                )}
            </div>
        );
    };

    // ZAR-1762 / TG-2 — "Tips & Gratuity" drill-in. Both sections come from the same two
    // ClickHouse feeds the card reads (staff-performance for totals/staff, by-channel for
    // the channel split), so the drill can never disagree with the card that opened it.
    const renderTipsDrill = () => (
        <div className="snapshot-drill-wrap">
            <TipsGratuityDrill
                data={tipsGratuityDrillData}
                fmt={fmtCurrency}
                loading={snapshotDetailsLoading || liveStaffPerformanceLoading}
                staffFailure={liveStaffPerformanceFailure}
                onRetryStaff={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDatesView, ...orderSourceParam }))}
                channelLoading={liveByChannelLoading}
                channelFailure={liveByChannelFailure}
                onRetryChannel={() => dispatch(fetchLiveByChannelRequest({ locationId, ...chDatesView, ...orderSourceParam }))}
            />
        </div>
    );

    const renderUnpaidDrill = () => {
        // Always use Redux ClickHouse
        const logLoading = liveUnpaidLogLoading;
        const logItems = liveUnpaidLog;
        const logTotalElements = liveUnpaidLogTotalElements;
        const logTotalPages = liveUnpaidLogTotalPages;
        const logPage = unpaidLogPageToday - 1; // 0-based display
        const setLogPage = (p: number) => setUnpaidLogPageToday(p + 1);

        // By Server: always from Redux ClickHouse
        const pageUnpaidServers = byServerFromAll;
        const maxUnpaidVal = pageUnpaidServers.reduce((m, s) => Math.max(m, s.amount), 0) || 1;

        // ── Unpaid Order Log rows + columns (ORD-1.2 / ORD-1.4) ──
        // Same renderer as the Paid log. This endpoint returns no financial
        // components, so the waterfall group is omitted rather than shown as a
        // row of em dashes; Grand Total stays pinned.
        const unpaidRows = toOrderLogRows(logItems);
        const unpaidAllColumns = buildOrderLogColumns({
            // The outlet's own answer on table service, from THIS endpoint's
            // `isDineInAvailable` — the Paid log's flag belongs to /order-log,
            // which the Unpaid segment never calls (ORD-1.2).
            hasTableService: liveUnpaidLogDineInAvailable,
            // Service Charge column only for outlets that capture one.
            hasServiceCharge: scEnabled,
            // This log has its own sort ids and cannot sort on customer,
            // guests or the item preview.
            sortKeys: { orderNo: 'orderNo', orderDate: 'orderDate', customerName: null, guests: null, staffName: 'serverName' },
            // Unpaid orders haven't been refunded — Refund Type and Refunded are
            // never meaningful on this log.
        }).filter(c => c.key !== 'refundType' && c.key !== 'refunded');
        const unpaidVisibleKeys = resolveUnpaidColumns(unpaidAllColumns);
        const unpaidColumns = visibleColumnDefs(unpaidAllColumns, unpaidVisibleKeys);
        // The totals footer belongs to the financial waterfall — Grand Total
        // alone (always on) does not summon it.
        const unpaidTotals = unpaidColumns.some(c => c.group === 'financial' && !c.pinned)
            ? computeOrderLogTotals(unpaidRows, unpaidColumns)
            : null;

        // Three-state header sort: default direction → reverse → back to Amount desc.
        const handleUnpaidSort = (col: OrderLogColumnDef) => {
            const key = col.sortKey;
            if (!key) return;
            const first = col.defaultDir ?? 'desc';
            if (unpaidSortBy !== key) { setUnpaidSortBy(key); setUnpaidSortDir(first); }
            else if (unpaidSortDir === first) { setUnpaidSortDir(first === 'asc' ? 'desc' : 'asc'); }
            else { setUnpaidSortBy('amount'); setUnpaidSortDir('desc'); }
            setUnpaidLogPageToday(1);
        };

        // ── Cancelled segment — /order-log?type=cancel, the Paid log's own cancelled
        // dataset, so the two logs can never show different cancelled orders. It brings
        // the full financial waterfall and the Reason column the unpaid endpoint lacks.
        const unpaidIsCancelled = unpaidStatus === 'cancelled';
        const cancelRowsAll = toOrderLogRows(orderLogOrders);
        const cancelAllColumns = buildOrderLogColumns({ hasTableService: orderLogDineInAvailable, status: 'cancelled', hasServiceCharge: scEnabled, sortKeys: { orderNo: 'orderNo', orderDate: 'orderDate' } });
        const cancelVisibleKeys = resolveUnpaidCancelColumns(cancelAllColumns);
        const cancelColumns = visibleColumnDefs(cancelAllColumns, cancelVisibleKeys);
        // Columns with no server sort id are ordered on the loaded page.
        const cancelRows = SERVER_SORT_KEYS.indexOf(unpaidCancelSort) >= 0
            ? cancelRowsAll
            : sortRowsClientSide(cancelRowsAll, unpaidCancelSort, unpaidCancelSortDir);
        const cancelTotals = cancelColumns.some(c => c.group === 'financial' && !c.pinned)
            ? computeOrderLogTotals(cancelRows, cancelColumns)
            : null;

        // Three-state header sort: default direction → reverse → back to Time desc.
        const handleUnpaidCancelSort = (col: OrderLogColumnDef) => {
            const key = col.sortKey;
            if (!key) return;
            const first = col.defaultDir ?? 'desc';
            if (unpaidCancelSort !== key) { setUnpaidCancelSort(key); setUnpaidCancelSortDir(first); }
            else if (unpaidCancelSortDir === first) { setUnpaidCancelSortDir(first === 'asc' ? 'desc' : 'asc'); }
            else { setUnpaidCancelSort('orderTime'); setUnpaidCancelSortDir('desc'); }
            setUnpaidCancelPage(1);
        };

        const fetchUnpaidCancelled = () => dispatch(fetchOrderLogRequest({
            locationId,
            page: unpaidCancelPage,
            size: unpaidPageSize,
            sortBy: SERVER_SORT_KEYS.indexOf(unpaidCancelSort) >= 0 ? unpaidCancelSort : 'orderTime',
            sortDir: unpaidCancelSortDir,
            search: unpaidDebouncedSearch || undefined,
            channelName: unpaidOrderTypes.length ? unpaidOrderTypes.join(',') : undefined,
            ...chDatesView,
            ...orderSourceParam,
            type: 'cancel' as const,
        }));

        // One view model for whichever segment is on screen — everything below renders
        // through it, so the two segments share the header, filters and pagination chrome.
        const segAllColumns = unpaidIsCancelled ? cancelAllColumns : unpaidAllColumns;
        const segVisibleKeys = unpaidIsCancelled ? cancelVisibleKeys : unpaidVisibleKeys;
        const segSetVisibleCols = unpaidIsCancelled ? setUnpaidCancelVisibleCols : setUnpaidVisibleCols;
        const segColumns = unpaidIsCancelled ? cancelColumns : unpaidColumns;
        const segRows = unpaidIsCancelled ? cancelRows : unpaidRows;
        const segTotals = unpaidIsCancelled ? cancelTotals : unpaidTotals;
        const segLoading = unpaidIsCancelled ? orderLogLoading : logLoading;
        const segFailure = unpaidIsCancelled ? orderLogFailure : (data.isToday && liveUnpaidLogFailure);
        const segTotalElements = unpaidIsCancelled ? orderLogTotalElements : logTotalElements;
        const segTotalPages = unpaidIsCancelled ? orderLogTotalPages : logTotalPages;
        const segPage = unpaidIsCancelled ? unpaidCancelPage - 1 : logPage; // 0-based display
        const segSetPage = (p: number) => (unpaidIsCancelled ? setUnpaidCancelPage(p + 1) : setLogPage(p));
        const segSortKey = unpaidIsCancelled ? unpaidCancelSort : unpaidSortBy;
        const segSortDir = unpaidIsCancelled ? unpaidCancelSortDir : unpaidSortDir;
        const segOnSort = unpaidIsCancelled ? handleUnpaidCancelSort : handleUnpaidSort;

        // Loading/error state for the top-level drill (stats section)
        if (liveUnpaidOrdersLoading) {
            return (
                <div className="snapshot-drill-wrap">
                    <div className="snapshot-drill-kpi-row">
                        {['Total Unpaid', 'Total Value', 'Avg Unpaid'].map(label => (
                            <div key={label} className="snapshot-minik-tile">
                                <div className="snapshot-minik-label">{label}</div>
                                <Bone w={80} h={24} style={{ marginTop: 4 }} />
                            </div>
                        ))}
                    </div>
                    <div className="rpt-card" style={{ marginBottom: 16, minHeight: 100 }}>
                        <div className="snapshot-drill-title">By Server</div>
                        {[1, 2, 3].map(i => <Bone key={i} w={'100%' as any} h={14} mb={10} />)}
                    </div>
                    <div className="rpt-card">
                        <Bone w={160} h={16} mb={14} />
                        {Array.from({ length: 5 }).map((_, i) => <Bone key={i} w={'100%' as any} h={14} mb={10} />)}
                    </div>
                </div>
            );
        }

        // SNAP-DRILL-ERR-1 — a failed top-level fetch used to blank the entire drill (KPI row,
        // By Server, Order Log all wiped for one Retry button). The Order Log below already has
        // its own scoped SectionError; By Server now gets one too, so a failure here only takes
        // out the section that actually depends on it, mirroring the Cancelled Orders drill shell.
        // displayUnpaidCount/Value/Avg naturally read 0 on failure (the reducer resets
        // liveUnpaidOrdersTotalElements to 0), so the subtext + KPI row need no extra branching.
        return (
            <div className="snapshot-drill-wrap">
                <div className="snapshot-drill-subtext">
                    {fmtNum(displayUnpaidCount)} unpaid orders — {fmtCurrency(displayUnpaidValue)} total outstanding
                </div>
                <div className="snapshot-drill-kpi-row">
                    <MiniK label="Total Unpaid" value={fmtNum(displayUnpaidCount)} color={T.red} />
                    <MiniK label="Total Value" value={fmtCurrency(displayUnpaidValue)} color={T.orange} />
                    <MiniK label="Avg Unpaid" value={fmtCurrency(displayUnpaidAvg)} />
                </div>
                {/* By Server — progress bars */}
                <div className="rpt-card" style={{ marginBottom: 16 }}>
                    <div className="snapshot-drill-title">By Server</div>
                    {liveUnpaidOrdersFailure ? (
                        <SectionError
                            kpiName="unpaid by server"
                            size="small"
                            onRetry={() => dispatch(fetchLiveUnpaidOrdersRequest({ locationId, size: 10, ...chDatesView, ...orderSourceParam }))}
                        />
                    ) : allUnpaidForByServerLoading ? (
                        <div style={{ marginTop: 12 }}>
                            {[1, 2, 3].map(i => <Bone key={i} w={'100%' as any} h={14} mb={10} />)}
                        </div>
                    ) : pageUnpaidServers.length === 0 ? (
                        <SectionEmpty kpiName="unpaid by server" size="small" />
                    ) : (
                        <div style={{ marginTop: 12 }}>
                            {pageUnpaidServers.map((s: any, i: number) => {
                                const val = Number(s?.amount ?? s?.totalAmount ?? 0);
                                const count = Number(s?.count ?? s?.orderCount ?? 0);
                                const name = s?.name ?? s?.serverName ?? s?.staffName ?? `Server ${i + 1}`;
                                return (
                                    <div key={i} style={{ marginBottom: 12 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                            <span style={{ fontSize: 12, color: T.t1, fontWeight: 500 }}>{name}</span>
                                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                {count > 0 && <span style={{ fontSize: 11, color: T.t3 }}>{count} orders</span>}
                                                <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{fmtCurrency(val)}</span>
                                            </div>
                                        </div>
                                        <div style={{ height: 6, background: T.hover, borderRadius: 3, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${Math.min((val / maxUnpaidVal) * 100, 100)}%`, background: T.orange, borderRadius: 3, opacity: 0.8 }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                {/* Order Log — server-side paginated table */}
                <div className="rpt-card">
                    {/* Title + Columns + Export */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                        <div>
                            <div className="snapshot-drill-title" style={{ marginBottom: 0 }}>{unpaidIsCancelled ? 'Order Log (Cancelled Orders)' : 'Order Log (Unpaid Orders)'}</div>
                            {segTotalElements > 0 && <div className="snapshot-drill-subtitle" style={{ marginTop: 2 }}>{segTotalElements} orders</div>}
                        </div>
                        {/* Columns + Export, same header slot and order as the Paid log. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            <OrderLogColumnsMenu columns={segAllColumns} visible={segVisibleKeys} onChange={segSetVisibleCols} />
                            {/* Export — always shown (matches paid orders pattern) */}
                            <ReportSectionExportButton
                                    dateRangeLabel={exportDateRangeLabel}
                                title={unpaidIsCancelled ? orderLogExportTitle('cancelled') : 'Order Log (Unpaid Orders)'}
                                filename={unpaidIsCancelled ? 'order-log-cancelled-orders' : 'order-log-unpaid-orders'}
                                buildExport={async (format, opts) => {
                                    const loc = opts?.locationId ?? locationId;
                                    // Each segment exports its own endpoint, under the active
                                    // search / Order Type / sort, with the columns on screen.
                                    if (unpaidIsCancelled) {
                                        const params = { locationId: loc, sortBy: SERVER_SORT_KEYS.indexOf(unpaidCancelSort) >= 0 ? unpaidCancelSort : 'orderTime', sortDir: unpaidCancelSortDir, search: unpaidDebouncedSearch || undefined, channelName: unpaidOrderTypes.length ? unpaidOrderTypes.join(',') : undefined, ...chDatesView, ...orderSourceParam, type: 'cancel' as const };
                                        let cancelSize = orderLogTotalElements || 1;
                                        if (opts?.locationId) {
                                            const probe: any = await getOrderLogExportApi({ ...params, page: 1, size: 1 });
                                            cancelSize = Number(probe?.totalElements) || 1;
                                        }
                                        const res: any = await getOrderLogExportApi({ ...params, page: 1, size: cancelSize });
                                        const rows = sortRowsClientSide(toOrderLogRows(res?.orders ?? (Array.isArray(res) ? res : [])), unpaidCancelSort, unpaidCancelSortDir);
                                        return orderLogRowsToExportTable(rows, cancelColumns);
                                    }
                                    let size = logTotalElements || 1;
                                    if (opts?.locationId) {
                                        const probe: any = await getLiveUnpaidOrdersApi({ locationId: loc, page: 1, size: 1, sortBy: unpaidSortBy, sortDir: unpaidSortDir, search: unpaidDebouncedSearch || undefined, orderType: unpaidOrderTypes.length ? unpaidOrderTypes.join(',') : undefined, ...chDatesView, ...orderSourceParam });
                                        size = Number(probe?.totalElements) || 1;
                                    }
                                    const response: any = await getLiveUnpaidOrdersApi({ locationId: loc, page: 1, size, sortBy: unpaidSortBy, sortDir: unpaidSortDir, search: unpaidDebouncedSearch || undefined, orderType: unpaidOrderTypes.length ? unpaidOrderTypes.join(',') : undefined, ...chDatesView, ...orderSourceParam });
                                    // Mirrors the columns on screen, in the same order.
                                    return orderLogRowsToExportTable(toOrderLogRows(response?.orders ?? []), unpaidColumns);
                                }}
                            />
                        </div>
                    </div>

                    {/* Unpaid / Cancelled segments — above the refine-filter row, same
                        control and placement as the Paid log's own status segments.
                        Both counts are the period totals behind the Unpaid and Cancelled
                        KPI tiles, so the pills can't disagree with the cards. */}
                    <OrderLogStatusSegments
                        value={unpaidStatus}
                        onChange={(next: 'unpaid' | 'cancelled') => { setUnpaidStatus(next); setUnpaidSelectedOrder(null); setUnpaidLogPageToday(1); setUnpaidCancelPage(1); }}
                        primaryKey="unpaid"
                        primaryLabel="Unpaid"
                        paidCount={liveUnpaidOrdersFailure ? 0 : displayUnpaidCount}
                        cancelledCount={cancelledOrdersFailure ? 0 : cancelledOrders.length}
                    />

                    {/* Filter row: search | Order Type */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: '1 1 240px' }}>
                            <input
                                value={unpaidSearch}
                                onChange={e => setUnpaidSearch(e.target.value)}
                                placeholder="Search by order ID, customer, phone, or table"
                                style={{ width: '100%', height: 32, padding: unpaidSearch ? '0 28px 0 32px' : '0 10px 0 32px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, outline: 'none', fontFamily: T.font, boxSizing: 'border-box' }}
                            />
                            <span className="material-symbols-rounded" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: T.t3, pointerEvents: 'none' }}>search</span>
                            {unpaidSearch && (
                                <button onClick={() => setUnpaidSearch('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: T.t3 }} aria-label="Clear search">
                                    <span className="material-symbols-rounded" style={{ fontSize: 16 }}>close</span>
                                </button>
                            )}
                        </div>
                        {/* Order Type multi-select */}
                        {unpaidTypeMenuOpen && <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setUnpaidTypeMenuOpen(false)} />}
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => setUnpaidTypeMenuOpen(v => !v)}
                                style={{ height: 32, padding: '0 10px', border: `1px solid ${unpaidOrderTypes.length ? T.accent : T.cardBorder}`, borderRadius: 6, fontSize: 12, color: unpaidOrderTypes.length ? T.accent : T.t2, background: unpaidOrderTypes.length ? `${T.accent}10` : T.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', fontFamily: T.font }}
                            >
                                Order Type {unpaidOrderTypes.length > 0 ? `(${unpaidOrderTypes.length})` : ''}<span style={{ fontSize: 10 }}>▾</span>
                            </button>
                            {unpaidTypeMenuOpen && (
                                <div style={{ position: 'absolute', top: 36, left: 0, zIndex: 200, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: T.shadow, padding: '6px 0', minWidth: 180 }}>
                                    {(channelNames.length > 0 ? channelNames : []).map(ot => (
                                        <div key={ot} onClick={() => setUnpaidOrderTypes(prev => prev.includes(ot) ? prev.filter(x => x !== ot) : [...prev, ot])} style={{ padding: '7px 14px', fontSize: 12, color: unpaidOrderTypes.includes(ot) ? T.accent : T.t1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: unpaidOrderTypes.includes(ot) ? `${T.accent}08` : 'transparent' }}>
                                            <span style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${unpaidOrderTypes.includes(ot) ? T.accent : T.t3}`, background: unpaidOrderTypes.includes(ot) ? T.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                {unpaidOrderTypes.includes(ot) && <span style={{ color: '#fff', fontSize: 9, lineHeight: 1 }}>✓</span>}
                                            </span>
                                            {ot}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Selected type chips */}
                    {unpaidOrderTypes.length > 0 && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                            {unpaidOrderTypes.map(ot => (
                                <span key={ot} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px 2px 10px', background: `${T.accent}12`, borderRadius: 12, fontSize: 11, color: T.accent, fontWeight: 500 }}>
                                    {ot}<span onClick={() => setUnpaidOrderTypes(prev => prev.filter(x => x !== ot))} style={{ cursor: 'pointer', fontSize: 12, lineHeight: 1, opacity: 0.7 }}>×</span>
                                </span>
                            ))}
                        </div>
                    )}
                    {/* Showing + Rows per page */}
                    {segTotalElements > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                            <span style={{ fontSize: 11, color: T.t3 }}>{segLoading ? <Bone w={140} h={11} r={3} style={{ display: 'inline-block' }} /> : `Showing ${segPage * unpaidPageSize + 1}–${Math.min(segPage * unpaidPageSize + segRows.length, segTotalElements)} of ${segTotalElements} orders`}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 11, color: T.t3 }}>Rows per page:</span>
                                <select value={unpaidPageSize} onChange={e => setUnpaidPageSize(Number(e.target.value))} style={{ height: 26, padding: '0 6px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.surface, cursor: 'pointer', fontFamily: T.font }}>
                                    {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                        </div>
                    )}

                    {segLoading ? (
                        <OrderLogTable
                            columns={segColumns}
                            rows={[]}
                            sortKey={segSortKey}
                            sortDir={segSortDir}
                            onSort={segOnSort}
                            fmtMoney={fmtCurrencyCell}
                            loading
                        />
                    ) : segFailure ? (
                        <SectionError
                            kpiName="order log"
                            size="small"
                            onRetry={() => (unpaidIsCancelled
                                ? fetchUnpaidCancelled()
                                : dispatch(fetchLiveUnpaidLogRequest({ locationId, page: unpaidLogPageToday, size: unpaidPageSize, sortBy: unpaidSortBy, sortDir: unpaidSortDir, search: unpaidDebouncedSearch || undefined, orderType: unpaidOrderTypes.length ? unpaidOrderTypes.join(',') : undefined, ...chDatesView, ...orderSourceParam })))}
                        />
                    ) : segRows.length === 0 ? (
                        <SectionEmpty kpiName={unpaidIsCancelled ? 'cancelled orders' : 'unpaid orders'} size="small" />
                    ) : (
                        <OrderLogTable
                            columns={segColumns}
                            rows={segRows}
                            sortKey={segSortKey}
                            sortDir={segSortDir}
                            onSort={segOnSort}
                            onRowClick={row => setUnpaidSelectedOrder(row.raw)}
                            status={unpaidIsCancelled ? 'cancelled' : undefined}
                            totals={segTotals}
                            fmtMoney={fmtCurrencyCell}
                        />
                    )}
                    {segRows.length > 0 && (() => {
                        const total = segTotalPages || 1;
                        return (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.divider}` }}>
                                <span style={{ fontSize: 11, color: T.t3 }}>Page {segPage + 1} of {total}</span>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    <button onClick={() => segSetPage(segPage - 1)} disabled={segPage === 0}
                                        style={{ height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: segPage === 0 ? T.hover : T.surface, color: segPage === 0 ? T.t3 : T.t1, cursor: segPage === 0 ? 'default' : 'pointer', fontSize: 12 }}>‹ Prev</button>
                                    {Array.from({ length: Math.min(total, 7) }, (_, idx) => {
                                        const pg = total <= 7 ? idx : (segPage < 4 ? idx : (segPage > total - 4 ? total - 7 + idx : segPage - 3 + idx));
                                        return (
                                            <button key={pg} onClick={() => segSetPage(pg)}
                                                style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${pg === segPage ? T.accent : T.cardBorder}`, background: pg === segPage ? T.accent : T.surface, color: pg === segPage ? '#fff' : T.t2, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>{pg + 1}</button>
                                        );
                                    })}
                                    <button onClick={() => segSetPage(segPage + 1)} disabled={segPage >= total - 1}
                                        style={{ height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: segPage >= total - 1 ? T.hover : T.surface, color: segPage >= total - 1 ? T.t3 : T.t1, cursor: segPage >= total - 1 ? 'default' : 'pointer', fontSize: 12 }}>Next ›</button>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>
        );
    };

    const renderDiscountsDrill = () => {
        // ClickHouse liveStaffPerformance for both Today and historical.
        const totalDiscountsPf = liveStaffPerformance.reduce((s, item) => s + (item.discount || 0), 0);
        const discOrdersPf = liveStaffPerformance.reduce((s, item) => s + (item.orderCount || 0), 0);
        const totalGrandTotalPf = liveStaffPerformance.reduce((s, item) => s + (item.grandTotal || 0), 0);
        const sortedByDiscount = [...liveStaffPerformance].filter(s => s.discount > 0).sort((a, b) => b.discount - a.discount);
        const topDiscounterPf: LiveStaffPerformanceItem | null = sortedByDiscount[0] ?? null;
        const DISC_STAFF_PAGE_SIZE = 5;
        const discTotalPages = data.isToday ? 1 : Math.ceil(sortedByDiscount.length / DISC_STAFF_PAGE_SIZE);
        const discSafePage = data.isToday ? 0 : Math.min(discountsStaffPage, Math.max(0, discTotalPages - 1));
        const discPageSlice = data.isToday
            ? sortedByDiscount.slice(0, DISC_STAFF_PAGE_SIZE)
            : sortedByDiscount.slice(discSafePage * DISC_STAFF_PAGE_SIZE, discSafePage * DISC_STAFF_PAGE_SIZE + DISC_STAFF_PAGE_SIZE);
        const abbreviateDiscName = (name: string) => {
            const parts = name.trim().split(/\s+/);
            if (parts.length === 1) return parts[0];
            return `${parts[0]} ${parts[parts.length - 1][0]}.`;
        };
        return (
            <div className="snapshot-drill-wrap">
                <div className="snapshot-drill-subtext">
                    {liveStaffPerformanceLoading ? <Bone w={200} h={11} r={3} style={{ display: 'inline-block' }} /> : `${fmtCurrency(totalDiscountsPf)} total discounts · staff breakdown`}
                </div>
                {liveStaffPerformanceLoading ? (
                    <div className="snapshot-drill-kpi-row">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="rpt-card rpt-card--r12" style={{ padding: '14px 16px', flex: 1, minWidth: 100 }}>
                                <Bone w={60} h={10} mb={8} />
                                <Bone w={90} h={22} mb={6} />
                                <Bone w={50} h={10} />
                            </div>
                        ))}
                    </div>
                ) : liveStaffPerformanceFailure ? (
                    <SectionError kpiName="discounts" onRetry={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={80} />
                ) : (
                    <div className="snapshot-drill-kpi-row">
                        <MiniK label="Total Discounts" value={fmtCurrency(totalDiscountsPf)} sub={totalGrandTotalPf > 0 ? `${((totalDiscountsPf / totalGrandTotalPf) * 100).toFixed(1)}% of Gross` : undefined} color={T.orange} />
                        <MiniK label="Disc. Orders" value={fmtNum(discOrdersPf)} sub={num(totalOrders) > 0 ? `${((discOrdersPf / num(totalOrders)) * 100).toFixed(1)}% of all` : undefined} />
                    </div>
                )}
                <DiscountBreakdownAndLog
                    sym={getCurrencySymbol()}
                    startISO={data.isToday ? today : (dateRange.startDate || today)}
                    endISO={data.isToday ? today : (dateRange.endDate || today)}
                    outletId={null}
                    locationId={locationId}
                    isToday={data.isToday}
                    orderSource={orderSourceVal || undefined}
                    token={token}
                    onOpenOrder={(r) => setOlSelectedOrder({ orderId: r.orderId, orderNo: r.orderNo })}
                >
                    <div className="rpt-card" style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Discounts by Staff/ Platform</div>
                        <div style={{ fontSize: 11, color: T.t3, marginBottom: 12 }}>{data.isToday ? 'Staff members who applied discounts today' : 'Staff discount breakdown'}</div>
                        {liveStaffPerformanceLoading ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: i < 5 ? `1px solid ${T.divider}` : 'none' }}>
                                        <Bone w={20} h={20} r={6} />
                                        <div style={{ flex: 1 }}><Bone w={100} h={12} mb={4} /><Bone w={70} h={10} /></div>
                                        <Bone w={55} h={16} />
                                    </div>
                                ))}
                            </div>
                        ) : liveStaffPerformanceFailure ? (
                            <SectionError kpiName="discounts by staff" onRetry={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={160} />
                        ) : sortedByDiscount.length ? (
                            <>
                                {discPageSlice.map((s: LiveStaffPerformanceItem, i: number) => {
                                    const globalRank = discSafePage * DISC_STAFF_PAGE_SIZE + i;
                                    const pct = totalDiscountsPf > 0 ? ((s.discount / totalDiscountsPf) * 100).toFixed(1) : '0.0';
                                    return (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: i < discPageSlice.length - 1 ? `1px solid ${T.divider}` : 'none' }}>
                                            <div style={{ width: 22, height: 22, borderRadius: 6, background: globalRank === 0 ? T.orangeSoft : T.hover, color: globalRank === 0 ? T.orange : T.t3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{globalRank + 1}</div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: T.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.staff_name}</div>
                                                <div style={{ fontSize: 11, color: T.t3, marginTop: 1 }}>{s.orderCount} orders · {pct}%</div>
                                            </div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: T.orange, flexShrink: 0 }}>{fmtCurrency(s.discount)}</div>
                                        </div>
                                    );
                                })}
                                {!data.isToday && discTotalPages > 1 && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, paddingTop: 4 }}>
                                        <button onClick={() => setDiscountsStaffPage((p) => Math.max(0, p - 1))} disabled={discSafePage === 0} style={{ fontSize: 11, color: discSafePage === 0 ? T.t3 : T.accent, background: 'none', border: `1px solid ${discSafePage === 0 ? T.divider : T.accent}`, borderRadius: 4, cursor: discSafePage === 0 ? 'default' : 'pointer', padding: '2px 8px' }}>← Prev</button>
                                        <span style={{ fontSize: 10, color: T.t3 }}>{discSafePage + 1} / {discTotalPages}</span>
                                        <button onClick={() => setDiscountsStaffPage((p) => Math.min(discTotalPages - 1, p + 1))} disabled={discSafePage === discTotalPages - 1} style={{ fontSize: 11, color: discSafePage === discTotalPages - 1 ? T.t3 : T.accent, background: 'none', border: `1px solid ${discSafePage === discTotalPages - 1 ? T.divider : T.accent}`, borderRadius: 4, cursor: discSafePage === discTotalPages - 1 ? 'default' : 'pointer', padding: '2px 8px' }}>Next →</button>
                                    </div>
                                )}
                                {sortedByDiscount.length > 0 && (
                                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.t3 }}>
                                        <span>{sortedByDiscount.length} staff</span>
                                        <span>Total: <span style={{ color: T.orange, fontWeight: 600 }}>{fmtCurrency(totalDiscountsPf)}</span></span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <SectionEmpty kpiName="staff discounts" minHeight={160} />
                        )}
                    </div>
                </DiscountBreakdownAndLog>
            </div>
        );
    };

    // Stamp-KPI loading — snapshot-details drives the stamp KPIs for BOTH Today and
    // historical (SSE-18): while it refetches (date/channel change) the `?? billedSrc`
    // fallback would briefly show the previous range's legacy values, so shimmer instead.
    // Declared before the drill early-return so drill renderers can use it too.
    const kpiLoading = loading || snapshotDetailsLoading;
    // USSE-3: comparison API is fetched for historical too (with startDate/endDate), so the
    // delta shimmer no longer gates on Today.
    const deltaLoading = kpiLoading || snapshotComparisonLoading;

    // Drill view — render full-page drill content (check-in analytics pattern)
    const drillMap: Record<string, () => React.ReactNode> = {
        'net-sales': renderNetSalesDrill,
        orders: renderOrdersDrill,
        cancelled: renderCancelledDrill,
        'cancelled-orders': () => (
            <CancelledOrdersDrill
                surface="snapshot"
                orders={cancelledOrders}
                loading={cancelledOrdersLoading}
                failure={cancelledOrdersFailure}
                onRetry={fetchCancelledOrders}
                exportDateRangeLabel={exportDateRangeLabel}
                fetchOrdersForExport={fetchCancelledOrdersForExport}
            />
        ),
        staff: renderStaffDrill,
        voids: renderVoidsDrill,
        tips: renderTipsDrill,
        complimentary: renderCompDrill,
        unpaid: renderUnpaidDrill,
        discounts: renderDiscountsDrill,
    };
    if (drillView && drillMap[drillView]) {
        return (
            <div className="rpt-page">
                {error ? (
                    <div className="snapshot-error-banner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <span>{error}</span>
                        <button
                            type="button"
                            onClick={() => setRefreshNonce(n => n + 1)}
                            style={{ background: 'none', border: `1px solid ${T.red}4D`, borderRadius: 6, padding: '4px 14px', fontSize: 12, fontWeight: 600, color: T.red, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                            Try again
                        </button>
                    </div>
                ) : null}
                {drillMap[drillView]()}
                {/* Order Detail slide-over — shown when a paid or unpaid order row is clicked */}
                <OrderDetailSlider
                    visible={((drillView === 'orders' || drillView === 'discounts') && !!olSelectedOrder) || (drillView === 'unpaid' && !!unpaidSelectedOrder)}
                    onClose={() => {
                        if (drillView === 'unpaid') { setUnpaidSelectedOrder(null); } else { setOlSelectedOrder(null); }
                        dispatch(clearOrderDetail());
                    }}
                    orderNo={drillView === 'unpaid' ? unpaidSelectedOrder?.orderNo : (olSelectedOrder as OrderLogItem)?.orderNo}
                    orderType={drillView === 'unpaid' ? unpaidSelectedOrder?.orderType : (olSelectedOrder as OrderLogItem)?.orderType}
                    loading={orderDetailLoading}
                    error={orderDetailError}
                    onRetry={() => {
                        const orderId = drillView === 'unpaid' ? (unpaidSelectedOrder?.orderId ?? '') : ((olSelectedOrder as OrderLogItem)?.orderId ?? '');
                        dispatch(fetchOrderDetailRequest({ locationId, orderId, ...(isHistoricalReportDateFilter(activeDate) ? { type: 'historical' } : {}) }));
                    }}
                    detail={orderDetailData}
                    {...(() => {
                        // The log row's own waterfall, so the slide-over and the row
                        // never disagree. In the Unpaid drill only the Cancelled segment
                        // carries one — the unpaid endpoint returns no financial components.
                        if (drillView === 'unpaid') {
                            if (unpaidStatus !== 'cancelled' || !unpaidSelectedOrder) return {};
                            const cancelRow = toOrderLogRow(unpaidSelectedOrder, 0);
                            return { financials: cancelRow.financials, tables: cancelRow.tableList };
                        }
                        if (!olSelectedOrder) return {};
                        const row = toOrderLogRow(olSelectedOrder, 0);
                        return { financials: row.financials, tables: row.tableList };
                    })()}
                />
            </div>
        );
    }

    return (
        <div className="rpt-page">
                    {error ? (
                        <div className="snapshot-error-banner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <span>{error}</span>
                            <button
                                type="button"
                                onClick={() => setRefreshNonce(n => n + 1)}
                                style={{ background: 'none', border: `1px solid ${T.red}4D`, borderRadius: 6, padding: '4px 14px', fontSize: 12, fontWeight: 600, color: T.red, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                                Try again
                            </button>
                        </div>
                    ) : null}

                    <div className="snapshot-hero-card snapshot-tile--drill" onClick={() => setDrillView('net-sales')} style={{ cursor: 'pointer', position: 'relative' }}>
                        <DrillArrow />
                        <div className="snapshot-hero-glow" style={{ background: T.accentSoft }} />
                        <div className="snapshot-hero-row">
                            <div>
                                {/* Net Sales 1 */}
                                <div className="snapshot-hero-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Net Sales<KpiTooltip text="From Total collected after deducting tax, tips & gratuity. The revenue retained by you after all deductions." /></div>
                                <div className="snapshot-hero-value-row">
                                    {kpiLoading ? <Bone w={110} h={32} mb={4} /> : <span className="snapshot-hero-value">{fmtCurrency(netSales)}</span>}
                                    {/* USSE-3: delta from the ClickHouse comparison API for Today AND historical */}
                                    {(data.isToday || showDelta) && deltaLoading ? <Bone w={80} h={14} r={4} /> : (data.isToday || showDelta) && snapshotComparison != null ? (
                                        <span style={{ fontSize: 13, fontWeight: 600, color: snapshotComparison.netSales === 0 ? T.t2 : snapshotComparison.netSales >= 0 ? T.green : T.red }}>
                                            {snapshotComparison.netSales !== 0 && <>{snapshotComparison.netSales >= 0 ? '↑' : '↓'} {snapshotComparison.netSales >= 0 ? '+' : '-'}</>}{Math.abs(snapshotComparison.netSales).toFixed(2)}%
                                        </span>
                                    ) : null}
                                </div>
                                {/* GLOBAL-DP-4: the hero's own "vs yesterday $X" line is removed —
                                    the comparison basis is stated once in the date control. */}
                            </div>
                            {data.isToday && (
                                <div className="snapshot-hero-channels">
                                    {liveByChannelLoading ? (
                                        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                                            {[1, 2, 3, 4].map(i => <Bone key={i} w={44} h={40} r={6} />)}
                                        </div>
                                    ) : liveByChannelFailure ? (
                                        <SectionError kpiName="channel breakdown" onRetry={() => dispatch(fetchLiveByChannelRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={56} />
                                    ) : netSalesByChannel.length === 0 ? (
                                        <SectionEmpty kpiName="channel breakdown" minHeight={56} />
                                    ) : (
                                        netSalesByChannel.map(ch => (
                                            <div key={ch.label} className="snapshot-hero-channel">
                                                <div className="snapshot-hero-channel-dot" style={{ background: ch.color }} />
                                                <div className="snapshot-hero-channel-value">{fmtCurrencyChannel(ch.netSales)}</div>
                                                <div className="snapshot-hero-channel-name">{ch.label}</div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ZAR-2091 — desktop stamp KPIs sorted per the Snapshot re-ordering spec:
                        Paid/Unpaid/Avg, Cancelled/Refund/Void, Tips&Grat/Discount/Comp, Staff/Guests/Spend-per-guest.
                        Desktop keeps its existing 2-row flex structure — only the tile order changed. */}
                    <div className="snapshot-stat-row">
                        <div className="snapshot-tile snapshot-tile--small snapshot-tile--drill" onClick={() => setDrillView('orders')}>
                            <DrillArrow />
                            <div className="snapshot-tile-label">Paid orders</div>
                            <TileValue>{kpiLoading ? <Bone w={65} h={22} /> : fmtNum(totalOrders)}</TileValue>
                            {(data.isToday || showDelta) && deltaLoading ? <Bone w={80} h={14} r={4} /> : (data.isToday || showDelta) && snapshotComparison != null ? getDeltaNode(snapshotComparison.orderCount) : null}
                        </div>
                        <div className="snapshot-tile snapshot-tile--small snapshot-tile--drill" onClick={() => setDrillView('unpaid')} style={{ background: '#fff' }}>
                            <DrillArrow />
                            <div className="snapshot-tile-label">Unpaid orders</div>
                            <TileValue style={{ color: T.t1 }}>
                                {liveUnpaidOrdersLoading ? <Bone w={65} h={22} /> : liveUnpaidOrdersFailure ? '0' : fmtNum(displayUnpaidCount)}
                            </TileValue>
                            {(liveUnpaidOrdersLoading || allUnpaidForByServerLoading) ? <Bone w={80} h={14} r={4} /> : <div className="snapshot-tile-delta" style={{ color: T.t3 }}>{fmtCurrencyKpi(displayUnpaidValue)}</div>}
                        </div>
                        <div className="rpt-card" style={{ padding: '20px 22px', flex: 1, minWidth: 'max-content' }}>
                            <div className="snapshot-tile-label">Avg Order</div>
                            <TileValue>{kpiLoading ? <Bone w={65} h={22} /> : fmtCurrencyKpi(avgOrder)}</TileValue>
                            {(data.isToday || showDelta) && (deltaLoading ? <Bone w={80} h={14} r={4} /> : snapshotComparison != null ? getDeltaNode(snapshotComparison.avgOrderValue) : null)}
                        </div>
                        {/* Service Charge — revenue-side, sits right after Avg Order and deliberately
                            away from Tips & Grat. Only for outlets that capture one (0 included). Not drillable. */}
                        {serviceCharge && (
                        <div className="rpt-card" style={{ padding: '20px 22px', flex: 1, minWidth: 'max-content' }}>
                            <div className="snapshot-tile-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{SERVICE_CHARGE_LABEL}<KpiTooltip text={serviceChargeTooltip(serviceCharge.retained)} /></div>
                            <TileValue>{kpiLoading ? <Bone w={65} h={22} /> : fmtCurrencyKpi(serviceCharge.amount)}</TileValue>
                            {(data.isToday || showDelta) && (deltaLoading ? <Bone w={80} h={14} r={4} /> : serviceChargePct != null ? getDeltaNode(serviceChargePct) : (
                                <div className="snapshot-tile-delta" style={{ color: T.t3 }}>—</div>
                            ))}
                        </div>
                        )}
                        {/* CANC-1 — Cancelled orders. Mirrors the Void tile's treatment; stays visible
                            under every source (the count recalculates for the active one) and the delta
                            is inverted because fewer cancellations is an improvement. */}
                        <div className="snapshot-tile snapshot-tile--small snapshot-tile--drill" onClick={() => setDrillView('cancelled-orders')}>
                            <DrillArrow />
                            <div className="snapshot-tile-label">Cancelled</div>
                            <TileValue>{cancelledOrdersLoading ? <Bone w={65} h={22} /> : cancelledOrdersFailure ? '0' : fmtNum(cancelledOrders.length)}</TileValue>
                            {(data.isToday || showDelta) && deltaLoading ? <Bone w={80} h={14} r={4} /> : (data.isToday || showDelta) && snapshotComparison != null ? getDeltaNode(snapshotComparison.cancelledOrders, true, true) : null}
                        </div>
                        {/* REF-2 — opens the ONE shared Refunds drill-in instead of this report's
                            own retired refund drill. */}
                        <div className="snapshot-tile snapshot-tile--small snapshot-tile--drill" onClick={() => navigate(refundsPath({ origin: 'snapshot', dateFilter: activeDate, startDate: dateRange.startDate, endDate: dateRange.endDate, source: waterfallSource }))}>
                            <DrillArrow />
                            {/* REF-3 — total cash returned across all components, same definition
                                on all three reports. */}
                            <div className="snapshot-tile-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Refund<KpiTooltip text={REFUND_KPI_TOOLTIP} /></div>
                            <TileValue>
                                {kpiLoading
                                    ? <Bone w={65} h={22} />
                                    : fmtCurrencyKpi(refundStampTotal)
                                }
                            </TileValue>
                            {(data.isToday || showDelta) && deltaLoading ? <Bone w={80} h={14} r={4} /> : (data.isToday || showDelta) && snapshotComparison != null ? getDeltaNode(snapshotComparison.refund, true, true) : null}
                        </div>
                        {/* Voids: always drillable; Today shows total voided amount, Historical shows count.
                            Not relevant to 3rd-party orders — hidden under that filter. */}
                        {!isThirdParty && (
                        <div className="snapshot-tile snapshot-tile--small snapshot-tile--drill" onClick={() => setDrillView('voids')}>
                            <DrillArrow />
                            <div className="snapshot-tile-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Voided items<KpiTooltip text={VOIDED_ITEMS_KPI_TOOLTIP} /></div>
                            <TileValue className="snapshot-tile-value snapshot-tile-value--no-margin">
                                {liveVoidSummaryLoading ? <Bone w={65} h={22} /> : liveVoidSummaryFailure ? '0' : fmtNum(voidedItemsCount)}
                            </TileValue>
                            {!liveVoidSummaryLoading && !liveVoidSummaryFailure && (
                                <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>{fmtCurrencyKpi(voidAmount)} · {fmtNum(voidCount)} {voidCount === 1 ? 'order' : 'orders'}</div>
                            )}
                            {(data.isToday || showDelta) && liveVoidSummaryLoading ? <Bone w={80} h={14} r={4} /> : (data.isToday || showDelta) && !liveVoidSummaryFailure ? getDeltaNode(voidDeltaPct, true, true) : null}
                        </div>
                        )}
                        {/* Sales/Hr tile hidden */}
                        {/* USSE-1: Check-Ins hidden in historical view
                        {!data.isToday && !isThirdParty && (
                            <div className="rpt-card" style={{ padding: '20px 22px', flex: 1, minWidth: 110 }}>
                                <div className="snapshot-tile-label">Check-Ins</div>
                                <TileValue className="snapshot-tile-value snapshot-tile-value--no-margin">{kpiLoading ? <Bone w={65} h={22} /> : fmtNum(checkInCount)}</TileValue>
                            </div>
                        )}
                        */}
                    </div>

                    <div className="snapshot-stat-row">
                        {/* ZAR-1762 / TG-1 — unified Tips & Gratuity card: combined total + combined
                            delta only. The Tips/Gratuity split lives in the drill-in (TG-2). */}
                        <div className="snapshot-tile snapshot-tile--small snapshot-tile--drill" onClick={() => setDrillView('tips')}>
                            <DrillArrow />
                            <div className="snapshot-tile-label">{TG_CARD_LABEL}</div>
                            <div className="snapshot-tile-value">
                                {kpiLoading ? <Bone w={90} h={28} mb={6} /> : fmtCurrencyKpi(tipsAndGratuity)}
                            </div>
                            {(data.isToday || showDelta) && deltaLoading ? <Bone w={80} h={14} r={4} /> : (data.isToday || showDelta) ? getDeltaNode(tipsGratuityPct) : null}
                        </div>
                        <div className="snapshot-tile snapshot-tile--small snapshot-tile--drill" onClick={() => setDrillView('discounts')}>
                            <DrillArrow />
                            <div className="snapshot-tile-label">Discounts</div>
                            <TileValue>{kpiLoading ? <Bone w={65} h={22} /> : fmtCurrencyKpi(discounts)}</TileValue>
                            {(data.isToday || showDelta) && deltaLoading ? <Bone w={80} h={14} r={4} /> : (data.isToday || showDelta) && snapshotComparison != null ? getDeltaNode(snapshotComparison.discount) : null}
                        </div>
                        {/* Complimentary — not relevant to 3rd-party orders — hidden under that filter; the flex row reflows to fill the gap. */}
                        {!isThirdParty && (
                        <div className="snapshot-tile snapshot-tile--small snapshot-tile--drill" onClick={() => setDrillView('complimentary')}>
                            <DrillArrow />
                            <div className="snapshot-tile-label">Complimentary</div>
                            <TileValue className="snapshot-tile-value snapshot-tile-value--no-margin">
                                {liveCompOrderLogLoading ? <Bone w={65} h={22} /> : liveCompOrderLogFailure ? '0' : fmtNum(compCount)}
                            </TileValue>
                            {!liveCompOrderLogLoading && compAmount > 0 && <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>{fmtCurrencyKpi(compAmount)}</div>}
                            {(data.isToday || showDelta) && (deltaLoading ? <Bone w={80} h={14} r={4} /> : snapshotComparison != null ? getDeltaNode(snapshotComparison.compleOrder) : null)}
                        </div>
                        )}
                        {/* Staff Worked — /sales/live/v2/staff-active-clocking-v2 (staffWorked field), Today + historical.
                            Not relevant to 3rd-party orders — hidden under that filter. */}
                        {!isThirdParty && (
                        <div className="snapshot-tile snapshot-tile--small snapshot-tile--drill" onClick={() => setDrillView('staff')}>
                            <DrillArrow />
                            <div className="snapshot-tile-label">Staff Worked</div>
                            <TileValue className="snapshot-tile-value snapshot-tile-value--no-margin">
                                {staffRosterV2Loading ? <Bone w={65} h={22} /> : staffRosterV2Failure ? '0' : fmtNum(staffRosterV2StaffWorked)}

                            </TileValue>
                        </div>
                        )}
                        {/* Guests — value from snapshot-details guestCount, % delta from snapshot-details/comparison (Today + historical); not relevant to 3rd-party orders — hidden under that filter */}
                        {!isThirdParty && (
                        <div className="snapshot-tile snapshot-tile--small">
                            <div className="snapshot-tile-label">Guests</div>
                            <TileValue>{kpiLoading ? <Bone w={65} h={22} /> : fmtNum(guests)}</TileValue>
                            {(data.isToday || showDelta) && deltaLoading ? <Bone w={80} h={14} r={4} /> : (data.isToday || showDelta) && snapshotComparison != null ? getDeltaNode(snapshotComparison.guestCount) : null}
                        </div>
                        )}
                        {/* SPG-1: Spend/Guest — value from snapshot-details spendPerGuest (Total Collected ÷ guest-bearing
                            orders), % delta from snapshot-details/comparison. Shows "—" (not $0.00) when there are no
                            guests in the active period/source — expected under a 3rd-party filter, so unlike Guests
                            this tile is not hidden there. */}
                        <div className="snapshot-tile snapshot-tile--small">
                            <div className="snapshot-tile-label">Spend / Guest</div>
                            <div className="snapshot-tile-value">{kpiLoading ? <Bone w={65} h={22} /> : spendPerGuest == null ? '—' : fmtCurrencyKpi(spendPerGuest)}</div>
                            {(data.isToday || showDelta) && deltaLoading ? <Bone w={80} h={14} r={4} /> : (data.isToday || showDelta) && snapshotComparison != null ? getDeltaNode(snapshotComparison.spendPerGuest) : null}
                        </div>
                        {/* USSE-1: Guests hidden in historical view
                        {!data.isToday && !isThirdParty && (
                            <div className="rpt-card" style={{ padding: '20px 22px', flex: 1, minWidth: 110 }}>
                                <div className="snapshot-tile-label">Guests</div>
                                <TileValue className="snapshot-tile-value snapshot-tile-value--no-margin">{kpiLoading ? <Bone w={65} h={22} /> : fmtNum(guestCount)}</TileValue>
                            </div>
                        )}
                        */}
                    </div>

                    <div className="snapshot-chart-row">
                        <div className="rpt-card" style={{ flex: 2, display: 'flex', flexDirection: 'column' }}>
                            <div style={{ marginBottom: 14 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Hourly Revenue</div>
                                <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>Revenue distribution across service hours</div>
                            </div>
                            <HourlyBarChart
                                data={hourlyRevenueChartData}
                                loading={hourlySalesLoading}
                                error={hourlySalesFailure}
                                onRetry={() => dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }))}
                                kpiName="hourly revenue"
                                barColor={T.accent}
                                chartHeight={160}
                                formatValue={(v) => {
                                    const n = Number(v) || 0;
                                    const sym = getCurrencySymbol();
                                    if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`;
                                    if (n >= 1_000) return `${sym}${(n / 1_000).toFixed(0)}K`;
                                    return `${sym}${n.toFixed(0)}`;
                                }}
                                showCurrency={true}
                                currencySymbol={getCurrencySymbol()}
                                minHeight={200}
                                avgOverride={avgRevenuePerHour}
                            />
                        </div>
                        {activeDate !== 'Today' && <div className="rpt-card" style={{ flex: 1 }}>
                            <div className="snapshot-card-title-wrap">
                                <div className="snapshot-card-title">Staff Sales Leaderboard</div>
                                <div className="snapshot-card-subtitle">{activeDate === 'Yesterday' ? "Yesterday's top performers" : activeDate === 'Today' ? "Today's top performers" : activeDate === '7D' ? "Last 7 days top performers" : activeDate === '30D' ? "Last 30 days top performers" : activeDate === 'This Month' ? "This month's top performers" : "Top performers"}</div>
                            </div>
                            {liveStaffPerformanceFailure ? (
                                <SectionError kpiName="staff leaderboard" onRetry={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={200} />
                            ) : staffLeaderboard.length ? (() => {
                                const totalPages = Math.ceil(staffLeaderboard.length / STAFF_LEADER_PAGE_SIZE);
                                const pageItems = staffLeaderboard.slice(
                                    staffLeaderPage * STAFF_LEADER_PAGE_SIZE,
                                    (staffLeaderPage + 1) * STAFF_LEADER_PAGE_SIZE
                                );
                                return (
                                    <>
                                        {pageItems.map((item, index) => {
                                            const rank = staffLeaderPage * STAFF_LEADER_PAGE_SIZE + index;
                                            return (
                                                <div key={`${item.name}-${rank}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: index < pageItems.length - 1 ? `1px solid ${T.divider}` : 'none' }}>
                                                    <div style={{ width: 20, height: 20, borderRadius: 6, background: rank === 0 ? T.accentSoft : T.hover, color: rank === 0 ? T.accent : T.t3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{rank + 1}</div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 12, fontWeight: 600, color: T.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                                                        <div style={{ fontSize: 9.5, color: T.t2, fontWeight: 600 }}>{item.role}{item.hoursLabel ? ` · ${item.hoursLabel}` : ''}</div>
                                                    </div>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{fmtCurrency(item.sales)}</div>
                                                </div>
                                            );
                                        })}
                                        {totalPages > 1 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
                                                <div style={{ fontSize: 11, color: T.t3 }}>
                                                    Showing {staffLeaderPage * STAFF_LEADER_PAGE_SIZE + 1}–{Math.min((staffLeaderPage + 1) * STAFF_LEADER_PAGE_SIZE, staffLeaderboard.length)} of {staffLeaderboard.length}
                                                </div>
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <button onClick={() => setStaffLeaderPage((p) => Math.max(0, p - 1))} disabled={staffLeaderPage === 0} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: staffLeaderPage === 0 ? T.hover : T.card, color: staffLeaderPage === 0 ? T.t3 : T.t1, fontSize: 11, fontWeight: 600, cursor: staffLeaderPage === 0 ? 'default' : 'pointer' }}>← Prev</button>
                                                    <span style={{ fontSize: 11, color: T.t2, padding: '4px 6px' }}>{staffLeaderPage + 1} / {totalPages}</span>
                                                    <button onClick={() => setStaffLeaderPage((p) => Math.min(totalPages - 1, p + 1))} disabled={staffLeaderPage >= totalPages - 1} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: staffLeaderPage >= totalPages - 1 ? T.hover : T.card, color: staffLeaderPage >= totalPages - 1 ? T.t3 : T.t1, fontSize: 11, fontWeight: 600, cursor: staffLeaderPage >= totalPages - 1 ? 'default' : 'pointer' }}>Next →</button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                );
                            })() : (loading || liveStaffPerformanceLoading) ? (
                <div style={{ padding: '4px 0' }}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: i < 4 ? `1px solid ${T.divider}` : 'none' }}>
                            <Bone w={20} h={20} r={6} />
                            <div style={{ flex: 1 }}><Bone w={null} h={12} r={4} style={{ maxWidth: 120 }} /></div>
                            <Bone w={50} h={12} r={4} />
                        </div>
                    ))}
                </div>
            ) : <SectionEmpty kpiName="staff leaderboard" minHeight={200} />}
                        </div>}
                    </div>

                    {/* alignItems: flex-start — Sales by Order Source (Source view) can run to 5+
                        rows and grow taller than Items Overview's fixed 10-row table; default flex
                        stretch was matching Items Overview's height to it, leaving a large blank
                        gap below its pagination controls. */}
                    <div className="snapshot-stack-row" style={{ alignItems: 'flex-start' }}>
                        <div className="rpt-card" style={{ flex: 1 }}>
                            <SnapshotItemsOverview
                                variant="desktop"
                                tk={T}
                                title="Items Overview"
                                titleInfo={REPORT_BASIS_NOTE}
                                exportSlot={(disabled, fetchAll) => !disabled && (
                                    <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                        variant="desktop"
                                        title="Items Overview"
                                        filename="items_overview"
                                        buildExport={async (_format, opts) => {
                                            const res = await fetchAll(opts?.locationId);
                                            const items = mapApiResponseToExportItems(res, true);
                                            return {
                                                headers: ['#', 'Item', 'Qty', 'Revenue'],
                                                rows: items.map((item) => [String(item.rank), item.name, String(item.qty), formatMoneyForExport(item.rev)]),
                                            };
                                        }}
                                    />
                                )}
                                startDate={activeDate === 'Today' ? undefined : dateRange.startDate}
                                endDate={activeDate === 'Today' ? undefined : dateRange.endDate}
                                datasetKey={`${activeDate === 'Today'}|${dateRange.startDate}|${dateRange.endDate}|${channel}`}
                                userId={auth?.userId ?? ''}
                                outletId={locationId}
                                token={token}
                                orderSource={orderSourceVal}
                                fmtQty={fmtQty}
                                fmtCurrencyCell={fmtCurrencyCell}
                                tracker={itemsTracker}
                            />
                        </div>

                        <div className="rpt-card" style={{ flex: 1 }}>
                            {/* Sales by Channel / Order Source (ZAR-2371) — one shared card; Channel|Source toggle persisted in Redux */}
                            <SalesByChannelSourceCard
                                variant="desktop"
                                items={liveByChannel}
                                loading={liveByChannelLoading}
                                error={liveByChannelFailure}
                                onRetry={() => dispatch(fetchLiveByChannelRequest({ locationId, ...chDatesView, ...orderSourceParam }))}
                                locationId={locationId}
                                startDate={chDatesView.startDate}
                                endDate={chDatesView.endDate}
                                orderSource={orderSourceParam.orderSource}
                                formatMoney={fmtCurrency}
                                formatCenter={fmtCurrencyShort}
                                comparisonHint={comparisonTooltip(activeDate, storeTz)}
                                exportDateRangeLabel={exportDateRangeLabel}
                                exportFilename="snapshot-sales-by"
                                fetchOutletItems={async (outletId, view) => {
                                    const res: any = await getLiveByChannelApi({ locationId: outletId, ...chDatesView, ...orderSourceParam, ...(view === 'source' ? { type: 'source' as const } : {}), token });
                                    return Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
                                }}
                                countryCode={branch?.country ?? branch?.countryCode ?? branch?.countryCd}
                            />
                        </div>
                    </div>

                    {/* Sales by Tender Type — desktop (ClickHouse Redux) */}
                    {(() => {
                        const sortedPayments = [...livePayments].sort((a, b) => b.tenderAmount - a.tenderAmount);
                        const shares = distribute100At2dp(sortedPayments.map((item) => item.tenderAmount));
                        return (
                            <div style={{ marginTop: 16 }}>
                                <div className="rpt-card">
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                                        <div className="snapshot-card-title-wrap">
                                            <div className="snapshot-card-title">Sales by Tender Type</div>
                                            <div className="snapshot-card-subtitle">Payment method breakdown</div>
                                        </div>
                                        {sortedPayments.length > 0 && (
                                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                                variant="desktop"
                                                title="Sales by Tender Type"
                                                filename="snapshot-sales-by-tender-type"
                                                buildExport={async (format, opts) => {
                                                    let payments = sortedPayments;
                                                    if (opts?.locationId) {
                                                        const res: any = await getLivePaymentsApi({ locationId: opts.locationId, ...chDatesView, ...orderSourceParam, token });
                                                        const list: any[] = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
                                                        payments = [...list].sort((a: any, b: any) => b.tenderAmount - a.tenderAmount);
                                                    }
                                                    const paymentShares = distribute100At2dp(payments.map((item: any) => item.tenderAmount));
                                                    return {
                                                        headers: ['Type', 'Transactions', 'Amount', 'Share'],
                                                        rows: payments.map((item: LivePaymentsItem, i: number) => [item.tenderType.toUpperCase(), item.transactionCount, formatMoneyForExport(item.tenderAmount), `${paymentShares[i].toFixed(2)}%`]),
                                                    };
                                                }}
                                            />
                                        )}
                                    </div>
                                    {livePaymentsLoading ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {[1, 2, 3].map((i) => (
                                                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                                    <Bone w={100} h={12} /> <Bone w={60} h={12} /> <Bone w={70} h={12} /> <Bone w={50} h={12} />
                                                </div>
                                            ))}
                                        </div>
                                    ) : livePaymentsFailure ? (
                                        <SectionError kpiName="sales by tender type" onRetry={() => dispatch(fetchLivePaymentsRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={140} />
                                    ) : sortedPayments.length === 0 ? (
                                        <SectionEmpty kpiName="sales by tender type" minHeight={140} />
                                    ) : (
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    {['TYPE', 'TRANSACTIONS', 'AMOUNT', 'SHARE'].map((col, ci) => (
                                                        <th key={col} style={{ fontSize: 10, fontWeight: 600, color: T.t3, textAlign: ci === 0 ? 'left' : 'right', padding: '0 0 10px', letterSpacing: 0.5 }}>{col}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedPayments.map((item: LivePaymentsItem, i: number) => (
                                                    <tr key={i} style={{ borderTop: `1px solid ${T.divider}` }}>
                                                        <td style={{ padding: '11px 0', fontSize: 12, fontWeight: 600, color: T.t1 }}>{item.tenderType.toUpperCase()}</td>
                                                        <td style={{ padding: '11px 0', fontSize: 12, color: T.t2, textAlign: 'right' }}>{item.transactionCount}</td>
                                                        <td style={{ padding: '11px 0', fontSize: 12, fontWeight: 600, color: T.accent, textAlign: 'right' }}>{fmtCurrency(item.tenderAmount)}</td>
                                                        <td style={{ padding: '11px 0', fontSize: 12, color: T.t2, textAlign: 'right' }}>{shares[i].toFixed(2)}%</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* <div className="snapshot-stack-row">
                        <div className="rpt-card" style={{ flex: 1 }}>
                            <div className="snapshot-card-title-wrap">
                                <div className="snapshot-card-title">Tips by Channel</div>
                                <div className="snapshot-card-subtitle">Tip distribution across order sources</div>
                            </div>
                            {tipsByChannelData.length ? (
                                <>
                                    {tipsByChannelData.map((item, index) => (
                                            <div key={`tip-${item.name}-${index}`} style={{ marginBottom: index < tipsByChannelData.length - 1 ? 10 : 0 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                                    <span style={{ fontSize: 12, fontWeight: 500, color: T.t1 }}>{item.name}</span>
                                                    <div style={{ display: 'flex', gap: 10 }}>
                                                        <span style={{ fontSize: 11, color: T.t3 }}>{item.tipPct}%</span>
                                                        <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, width: 42, textAlign: 'right' }}>{fmtCurrency(item.tipValue)}</span>
                                                    </div>
                                                </div>
                                                <div style={{ height: 6, background: T.hover, borderRadius: 3, overflow: 'hidden' }}>
                                                    <div style={{ height: 6, borderRadius: 3, width: `${item.tipPct}%`, background: item.color, opacity: 0.7 }} />
                                                </div>
                                            </div>
                                    ))}
                                    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.t2 }}>
                                        <span style={{ color:'#000000' }}>Avg/Order: <span style={{ color: '#000000', fontWeight: 600 }}>{fmtCurrency(avgPerOrderTip)}</span></span>
                                        <span>Tip Rate: <span style={{ color: T.green, fontWeight: 600 }}>{tipRatePct.toFixed(1)}%</span></span>
                                    </div>
                                </>
                            ) : <div style={{ fontSize: 12, color: T.t3 }}>{loading ? '...' : 'No tips by channel'}</div>}
                        </div>
                    </div> */}
        </div>
    );
};

// =========================
// Mobile implementation
// =========================

const findTokenMobile = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    const tokenKeys = ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'bearerToken', 'authToken', 'userToken', 'sessionToken', 'Authorization', 'authorization', 'idToken', 'id_token'];
    for (const key of tokenKeys) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findTokenMobile(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const findLocationIdMobile = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['locationId', 'location_id', 'locationID', 'locId', 'outletId', 'outlet_id']) {
        if (obj[key] && typeof obj[key] === 'string') return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findLocationIdMobile(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const unwrapResMobile = (res: any) => {
    if (res == null) return res;
    const d = res.metaDataInfo?.data ?? res.responseBody ?? res.content ?? res.data ?? res.result ?? res.response;
    return d != null && d !== res ? d : res;
};

const pickMobile = (res: any, ...keys: any[]) => {
    if (res == null) return null;
    for (const key of keys) {
        const v = res?.[key];
        if (v !== undefined && v !== null) return v;
    }
    const r = unwrapResMobile(res);
    for (const key of keys) {
        const v = r?.[key];
        if (v !== undefined && v !== null) return v;
    }
    const raw = Array.isArray(r) ? r : (r.content ?? r.data ?? r);
    const obj = Array.isArray(raw) ? raw[0] : raw;
    if (!obj || typeof obj !== 'object') return null;
    for (const key of keys) {
        const v = obj[key];
        if (v !== undefined && v !== null) return v;
    }
    // Fuzzy key matching removed — same fix as desktop pick() above.
    return null;
};

const pickFromListMobile = (res: any, key: any) => {
    if (res == null) return null;
    const r = unwrapResMobile(res);
    const raw = r?.content ?? r?.data ?? r;
    const list = Array.isArray(raw) ? raw : (raw?.content ?? raw?.data);
    if (!Array.isArray(list) || list.length === 0) return null;
    const first = list[0];
    return first?.[key] ?? first?.value ?? first?.amount ?? first?.total ?? null;
};

const sumFromListMobile = (res: any, key: any) => {
    if (res == null) return 0;
    const r = unwrapResMobile(res);
    const raw = r?.content ?? r?.data ?? r;
    const list = Array.isArray(raw) ? raw : (raw?.content ?? raw?.data);
    if (!Array.isArray(list)) return 0;
    return list.reduce((s, i) => s + (Number(i[key] ?? i.amount ?? i.value ?? i.total ?? 0) || 0), 0);
};

const fmtCurrencyMobile = (v: any) => {
    const sym = getCurrencySymbol();
    const n = Number(v ?? 0) || 0;
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 100_000) return `${sym}${(n / 1_000).toFixed(1)}K`;
    return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
// Table-cell amount (mobile): whole numbers drop decimals, fractional keep 2.
// Preserves the K/M abbreviation behaviour of fmtCurrencyMobile for large values.
const fmtCurrencyCellMobile = (v: any) => {
    const sym = getCurrencySymbol();
    const n = Number(v ?? 0) || 0;
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 100_000) return `${sym}${(n / 1_000).toFixed(1)}K`;
    return Number.isInteger(n)
        ? `${sym}${n.toLocaleString('en-US')}`
        : `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtCurrencyMobileFull = (v: any) =>
    `${getCurrencySymbol()}${(Number(v ?? 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtNumMobile = (v: any) => {
    const n = Number(v ?? 0) || 0;
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 100_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString('en-US');
};
const fmtQtyMobile = (v: any, isWeight: any) =>
    Number(isWeight) === 1
        ? (Number(v ?? 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
        : fmtNumMobile(v);

const STORAGE_BUCKET_URL_MOBILE = 'https://storage.googleapis.com/mhd-media/';
const getBranchLogoUrlMobile = (b: any) => {
    if (!b) return null;
    if (b.logoUrl) return b.logoUrl;
    const media = b.media && Array.isArray(b.media) ? b.media : [];
    const logoMedia = media.find((m) => m && (m.entityType === 'LOGO' || m.entityType === 'logo'));
    if (!logoMedia || !logoMedia.id) return null;
    const ext = (logoMedia.mimeType || '').split('/')[1] || 'png';
    return `${STORAGE_BUCKET_URL_MOBILE}${logoMedia.id}.${ext}`;
};

const getBranchInitialsMobile = (branch: any, auth: any) => {
    const name = branch?.locationName ?? branch?.name ?? branch?.branchName ?? '';
    const str = (typeof name === 'string' && name.trim()) ? name.trim() : (auth?.fullName ?? auth?.userId ?? '').toString().trim();
    if (!str) return '—';
    const parts = str.split(/[\s,]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
    return str.slice(0, 2).toUpperCase();
};

const MobileSnapshotScreen = () => {
    // Outlet captures a service charge? (location-details uiFeatureFlags) — gates the KPI and waterfall line.
    const scEnabled = useServiceChargeEnabled();
    const navigate = useNavigate();
    const location = useLocation();
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const setTopBarProps = useDesktopTopBar();
    const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? findLocationIdMobile(auth) ?? '', [auth, branch]);
    const token = useMemo(() => (auth ? findTokenMobile(auth) : null), [auth]);
    const itemsTracker = useMemo(() => makeReportTracker({ location_id: locationId, outlet_id: locationId, user_id: auth?.userId ?? '', user_role: auth?.role ?? '' }), [locationId, auth]);
    const dispatch = useAppDispatch();
    const snapshotDetails: SnapshotDetailsResponse | null = useAppSelector((s) => s.clickHouse.snapshotDetailsSuccess);
    const snapshotDetailsLoadingM: boolean = useAppSelector((s) => s.clickHouse.snapshotDetailsLoading);
    const snapshotComparisonM: SnapshotComparisonResponse | null = useAppSelector((s) => s.clickHouse.snapshotComparisonSuccess);
    const snapshotComparisonLoadingM: boolean = useAppSelector((s) => s.clickHouse.snapshotComparisonLoading);
    const liveItemSalesV2: LiveItemSalesV2Item[] = useAppSelector((s) => s.clickHouse.liveItemSalesV2Success);
    const liveItemSalesV2Loading: boolean = useAppSelector((s) => s.clickHouse.liveItemSalesV2Loading);
    const liveItemSalesV2Failure: boolean = useAppSelector((s) => s.clickHouse.liveItemSalesV2Failure);
    const liveItemSalesV2TotalElementsM: number = useAppSelector((s) => s.clickHouse.liveItemSalesV2TotalElements);
    const liveItemSalesV2TotalPagesM: number = useAppSelector((s) => s.clickHouse.liveItemSalesV2TotalPages);
    const liveRefundSummaryM: LiveRefundSummaryItem[] = useAppSelector((s) => s.clickHouse.liveRefundSummarySuccess);
    const liveRefundSummaryLoadingM: boolean = useAppSelector((s) => s.clickHouse.liveRefundSummaryLoading);
    const liveRefundSummaryFailureM: boolean = useAppSelector((s) => s.clickHouse.liveRefundSummaryFailure);
    const liveRefundDetailsM: LiveRefundDetailsItem[] = useAppSelector((s) => s.clickHouse.liveRefundDetailsSuccess);
    const liveRefundDetailsLoadingM: boolean = useAppSelector((s) => s.clickHouse.liveRefundDetailsLoading);
    const liveRefundDetailsFailureM: boolean = useAppSelector((s) => s.clickHouse.liveRefundDetailsFailure);
    const liveRefundDetailsTotalPagesM: number = useAppSelector((s) => s.clickHouse.liveRefundDetailsTotalPages);
    const liveRefundDetailsTotalElementsM: number = useAppSelector((s) => s.clickHouse.liveRefundDetailsTotalElements);
    const liveStaffRefundsM = useAppSelector((s) => s.clickHouse.liveStaffRefundsSuccess) as LiveStaffRefundsItem[];
    const liveStaffRefundsLoadingM = useAppSelector((s) => s.clickHouse.liveStaffRefundsLoading);
    const liveStaffRefundsFailureM = useAppSelector((s) => s.clickHouse.liveStaffRefundsFailure);
    const cancelledOrdersM: OrderLogItem[] = useAppSelector((s) => s.clickHouse.cancelledOrderLogOrders);
    const cancelledOrdersLoadingM: boolean = useAppSelector((s) => s.clickHouse.cancelledOrderLogLoading);
    const cancelledOrdersFailureM: boolean = useAppSelector((s) => s.clickHouse.cancelledOrderLogFailure);
    const liveVoidSummaryM: LiveVoidSummaryItem[] = useAppSelector((s) => s.clickHouse.liveVoidSummarySuccess);
    const liveVoidSummaryLoadingM: boolean = useAppSelector((s) => s.clickHouse.liveVoidSummaryLoading);
    const liveVoidSummaryFailureM: boolean = useAppSelector((s) => s.clickHouse.liveVoidSummaryFailure);
    // Voids by Staff ranked bars — NOT YET DEPLOYED backend endpoint (see getVoidByStaffApi).
    const voidByStaffM: LiveVoidByStaffItem[] = useAppSelector((s) => s.clickHouse.voidByStaffSuccess);
    const voidByStaffLoadingM: boolean = useAppSelector((s) => s.clickHouse.voidByStaffLoading);
    const voidByStaffFailureM: boolean = useAppSelector((s) => s.clickHouse.voidByStaffFailure);
    // Void drill-in Mini-KPIs row (Voided Items · Void Value · Void Rate · Avg Void Value).
    const voidKpisM: LiveVoidKpisResponse | null = useAppSelector((s) => s.clickHouse.voidKpisSuccess);
    const voidKpisLoadingM: boolean = useAppSelector((s) => s.clickHouse.voidKpisLoading);
    const voidKpisFailureM: boolean = useAppSelector((s) => s.clickHouse.voidKpisFailure);
    const liveVoidDetailsM: LiveVoidDetailsItem[] = useAppSelector((s) => s.clickHouse.liveVoidDetailsSuccess);
    const liveVoidDetailsLoadingM: boolean = useAppSelector((s) => s.clickHouse.liveVoidDetailsLoading);
    const liveVoidDetailsFailureM: boolean = useAppSelector((s) => s.clickHouse.liveVoidDetailsFailure);
    const liveVoidDetailsTotalPagesM: number = useAppSelector((s) => s.clickHouse.liveVoidDetailsTotalPages);
    const liveVoidDetailsTotalElementsM: number = useAppSelector((s) => s.clickHouse.liveVoidDetailsTotalElements);
    const hourlySalesDataM: HourlySalesResponse | null = useAppSelector((s) => s.clickHouse.hourlySalesData);
    const hourlySalesLoadingM: boolean = useAppSelector((s) => s.clickHouse.hourlySalesLoading);
    const hourlySalesFailureM: boolean = useAppSelector((s) => s.clickHouse.hourlySalesFailure);
    const liveByChannelM: LiveByChannelItem[] = useAppSelector((s) => s.clickHouse.liveByChannelSuccess);
    const liveByChannelLoadingM: boolean = useAppSelector((s) => s.clickHouse.liveByChannelLoading);
    const liveByChannelFailureM: boolean = useAppSelector((s) => s.clickHouse.liveByChannelFailure);
    const livePaymentsM: LivePaymentsItem[] = useAppSelector((s) => s.clickHouse.livePaymentsSuccess);
    const livePaymentsLoadingM: boolean = useAppSelector((s) => s.clickHouse.livePaymentsLoading);
    const livePaymentsFailureM: boolean = useAppSelector((s) => s.clickHouse.livePaymentsFailure);
    const liveStaffPerformanceM: LiveStaffPerformanceItem[] = useAppSelector((s) => s.clickHouse.liveStaffPerformanceSuccess);
    const liveStaffPerformanceLoadingM: boolean = useAppSelector((s) => s.clickHouse.liveStaffPerformanceLoading);
    const liveStaffPerformanceFailureM: boolean = useAppSelector((s) => s.clickHouse.liveStaffPerformanceFailure);
    const liveStaffActiveClockingM: LiveStaffActiveClockingItem[] = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingSuccess);
    const liveStaffActiveClockingLoadingM: boolean = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingLoading);
    const liveStaffActiveClockingFailureM: boolean = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingFailure);
    // Staff Worked KPI — /sales/live/v2/staff-active-clocking-v2 (staffWorked field)
    const staffRosterV2StaffWorkedM: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2StaffWorked);
    const staffRosterV2LoadingM: boolean = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2Loading);
    const staffRosterV2FailureM: boolean = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2Failure);
    // Unpaid Orders (Today only) — mobile
    const liveUnpaidOrdersM: UnpaidOrderItem[] = useAppSelector((s) => s.clickHouse.liveUnpaidOrders);
    const liveUnpaidOrdersLoadingM: boolean = useAppSelector((s) => s.clickHouse.liveUnpaidOrdersLoading);
    const liveUnpaidOrdersFailureM: boolean = useAppSelector((s) => s.clickHouse.liveUnpaidOrdersFailure);
    const liveUnpaidOrdersTotalElementsM: number = useAppSelector((s) => s.clickHouse.liveUnpaidOrdersTotalElements);
    const liveUnpaidLogM: UnpaidOrderItem[] = useAppSelector((s) => s.clickHouse.liveUnpaidLog);
    const liveUnpaidLogLoadingM: boolean = useAppSelector((s) => s.clickHouse.liveUnpaidLogLoading);
    const liveUnpaidLogFailureM: boolean = useAppSelector((s) => s.clickHouse.liveUnpaidLogFailure);
    // Comp Orders (Today only) — mobile
    const liveCompOrderLogM: CompOrderLogItem[] = useAppSelector((s) => s.clickHouse.liveCompOrderLog);
    const liveCompOrderLogLoadingM: boolean = useAppSelector((s) => s.clickHouse.liveCompOrderLogLoading);
    const liveCompOrderLogFailureM: boolean = useAppSelector((s) => s.clickHouse.liveCompOrderLogFailure);
    const liveCompOrderLogTotalElementsM: number = useAppSelector((s) => s.clickHouse.liveCompOrderLogTotalElements);
    const liveCompOrderLogTotalPagesM: number = useAppSelector((s) => s.clickHouse.liveCompOrderLogTotalPages);
    const liveCompReasonsM: CompReasonItem[] = useAppSelector((s) => s.clickHouse.liveCompReasons);
    const liveCompReasonsLoadingM: boolean = useAppSelector((s) => s.clickHouse.liveCompReasonsLoading);
    const liveCompReasonsFailureM: boolean = useAppSelector((s) => s.clickHouse.liveCompReasonsFailure);
    const liveCompByStaffM: CompByStaffItem[] = useAppSelector((s) => s.clickHouse.liveCompByStaff);
    const liveCompByStaffLoadingM: boolean = useAppSelector((s) => s.clickHouse.liveCompByStaffLoading);
    const liveCompByStaffFailureM: boolean = useAppSelector((s) => s.clickHouse.liveCompByStaffFailure);
    const liveUnpaidLogTotalElementsM: number = useAppSelector((s) => s.clickHouse.liveUnpaidLogTotalElements);
    const liveUnpaidLogTotalPagesM: number = useAppSelector((s) => s.clickHouse.liveUnpaidLogTotalPages);
    const liveUnpaidLogDineInAvailableM: boolean = useAppSelector((s) => s.clickHouse.liveUnpaidLogDineInAvailable);
    const channelNamesM = useAppSelector((s) => s.clickHouse.channelNames);
    // Unpaid drill → Cancelled segment. Same /order-log?type=cancel slice the Paid drill
    // uses; only one drill is open at a time, and each refetches when it opens.
    const orderLogOrdersM = useAppSelector((s) => s.clickHouse.orderLogOrders) as OrderLogItem[];
    const orderLogLoadingM: boolean = useAppSelector((s) => s.clickHouse.orderLogLoading);
    const orderLogFailureM: boolean = useAppSelector((s) => s.clickHouse.orderLogFailure);
    const orderLogTotalElementsM: number = useAppSelector((s) => s.clickHouse.orderLogTotalElements);
    const orderLogTotalPagesM: number = useAppSelector((s) => s.clickHouse.orderLogTotalPages);
    const orderLogDineInAvailableM: boolean = useAppSelector((s) => s.clickHouse.orderLogDineInAvailable);
    const orderDetailDataM = useAppSelector((s) => s.clickHouse.orderDetailData) as OrderDetailResponse | null;
    const orderDetailLoadingM: boolean = useAppSelector((s) => s.clickHouse.orderDetailLoading);
    const orderDetailErrorM: boolean = useAppSelector((s) => s.clickHouse.orderDetailError);
    const isSuperUser = useMemo(() => {
        const SUPER_ROLES = ['Restaurant_Owner', 'Regional_Employee', 'Magil_Admin'];
        return SUPER_ROLES.includes(auth?.role);
    }, [auth]);
    // SW3 — deep-link into a specific waterfall deduction drill via ?drill=<key>, e.g. from
    // Sales/EOD's waterfall (which don't have their own Voids/Complimentary drill content).
    // 'refunds' (the waterfall row's own key) maps to this screen's local 'refund' drill.
    // See the matching desktop comment above drillFromUrlRef — a self-consuming flag can't
    // survive the "always open on Today" mount cascade below if the shared date filter arrives
    // non-Today, since [activeDateFilter]'s reset effect further down would fire a second time
    // after the flag's already been consumed. Clears itself on the next tick instead.
    const [searchParams] = useSearchParams();
    const drillFromUrlRef = useRef(false);
    // See the matching desktop comment above the equivalent ref — where to send the user when
    // they back out of a deep-linked drill, set from location.state.returnTo.
    const deepLinkReturnToRef = useRef<string | null>(null);
    const [drillView, setDrillView] = useState<string | null>(() => {
        const raw = searchParams.get('drill');
        if (!raw) return null;
        drillFromUrlRef.current = true;
        deepLinkReturnToRef.current = (location.state as { returnTo?: string } | null)?.returnTo ?? null;
        return raw === 'refunds' ? 'refund' : raw;
    });
    // Where a deduction click was opened FROM, when it was a LOCAL jump (net-sales's own Voids/
    // Discounts/Complimentary/Refunds links) rather than a fresh entry from a main-page KPI tile.
    // Auto-resets whenever drillView closes through ANY path — see the matching desktop comment.
    const [drillOrigin, setDrillOrigin] = useState<'net-sales' | null>(null);
    useEffect(() => { if (!drillView) setDrillOrigin(null); }, [drillView]);
    // Read once by DrillNetSales at its own mount (it fully remounts each time drillView
    // returns to 'net-sales', since a different sibling component occupies that slot in
    // between) — explicitly reset to false wherever net-sales is entered any OTHER way, so a
    // stale true from an earlier deduction-return cycle can't leak into an unrelated later visit.
    const scrollToNetSalesKpiRef = useRef(false);
    const goBack = useCallback(() => {
        if (deepLinkReturnToRef.current) {
            const returnTo = deepLinkReturnToRef.current;
            deepLinkReturnToRef.current = null;
            navigate(returnTo, { replace: true });
            return;
        }
        if (drillOrigin === 'net-sales') {
            setDrillOrigin(null);
            scrollToNetSalesKpiRef.current = true;
            setDrillView('net-sales');
            return;
        }
        setDrillView(null);
    }, [navigate, drillOrigin]);
    useDrillHistoryBack(!!drillView, goBack);
    useSwipeBack();
    useEffect(() => {
        if (!drillFromUrlRef.current) return;
        const t = setTimeout(() => { drillFromUrlRef.current = false; }, 0);
        return () => clearTimeout(t);
    }, []);

    // Memoize today/yesterday, anchored on the store's timezone. Recompute when the
    // outlet's timezone resolves (it can arrive after first paint on a cold load).
    const storeTz = useStoreTimeZone();
    const today = useMemo(() => todayStr(storeTz), [storeTz]);
    const yesterday = useMemo(() => yesterdayStr(storeTz), [storeTz]);
    const [selectedDate, setSelectedDate] = useState(today);
    const [activeDateFilter, setActiveDateFilter] = useSharedReportDateFilter('Today');
    // Global sales-channel filter — per-report, defaults to Overall.
    const [channel, setChannel] = useState('Overall sales');
    const orderSourceVal = channelToOrderSource(channel);
    const orderSourceParam: { orderSource?: 'direct' | 'thirdparty' } = orderSourceVal ? { orderSource: orderSourceVal } : {};
    const isThirdParty = orderSourceVal === 'thirdparty';

    const dateRange = useMemo(() => {
        if (activeDateFilter === 'Custom') {
            return { startDate: selectedDate, endDate: selectedDate };
        }
        const range = getStartEndFromFilter(activeDateFilter, today, yesterday, storeTz);
        return range ?? { startDate: selectedDate, endDate: selectedDate };
    }, [activeDateFilter, selectedDate, today, yesterday, storeTz]);

    /*
     * REF-1 / REF-3 — the period's refund component vector. Mobile and desktop read the same
     * real hook (GET /sales/live/v2/refund-log), so the REFUND KPI can never mean one thing
     * on a phone and another on a laptop.
     */
    const { summary: refundSummaryM } = useRefundLogPeriod({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        source: orderSourceVal === 'direct' ? 'direct' : orderSourceVal === 'thirdparty' ? 'thirdparty' : 'overall',
    });

    // Human-readable active date range for the "All outlets" export file header.
    const exportDateRangeLabel = exportPeriodFor(activeDateFilter, dateRange)?.label ?? activeDateFilter;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<any>(null);
    useEffect(() => {
        // Skip while the deep-link flag is still active — see drillFromUrlRef above.
        if (drillFromUrlRef.current) return;
        setDrillView(null);
    }, [location.key]);
    const [topItemsPageM, setTopItemsPageM] = useState(0);
    const TOP_ITEMS_PAGE_SIZE_M = 5;
    const allTopItemsMobileRef = useRef<any[]>([]);
    const kpiExportRefM = useRef<Record<string, any>>({});
    const [topItemsLoadingM, setTopItemsLoadingM] = useState(false);
    const REFUND_PAGE_SIZE_M = 5;
    const [refundLogPageM, setRefundLogPageM] = useState(1);
    // Staff Sales Leaderboard pagination (historical, mobile) — client-side, 5/page
    const STAFF_LEADER_PAGE_SIZE_M = 5;
    const [staffLeaderPageM, setStaffLeaderPageM] = useState(0);
    // Void details pagination for mobile — Redux, 0-based display, size 5 (Today + historical)
    const VOID_DETAILS_PAGE_SIZE_M = 5;
    const [voidDetailsPageM, setVoidDetailsPageM] = useState(0);
    // Historical discount summary (offer + staff breakdown) — loading / failure / retry for mobile
    const [discountsLoadingM, setDiscountsLoadingM] = useState(false);
    const [discountsFailureM, setDiscountsFailureM] = useState(false);
    const discountsRetryRefM = useRef<() => void>(() => {});
    const [discountsStaffPageM, setDiscountsStaffPageM] = useState(0);
    // Comp log state for mobile
    const [compLogItemsM, setCompLogItemsM] = useState<any[]>([]);
    const COMP_PAGE_SIZE_M = 5;
    const [compLogPageTodayM, setCompLogPageTodayM] = useState(0);
    // Unpaid Orders (SNAP-14/15) — mobile state; Today uses Redux
    const UNPAID_PAGE_SIZE = 25;
    const [unpaidCount, setUnpaidCount] = useState<number | null>(null);
    const [unpaidValue, setUnpaidValue] = useState<number | null>(null);
    const [unpaidAvg, setUnpaidAvg] = useState<number | null>(null);
    const [unpaidPriorCount, setUnpaidPriorCount] = useState<number | null>(null);
    const [unpaidServers, setUnpaidServers] = useState<any[]>([]);
    const [unpaidLogItems, setUnpaidLogItems] = useState<any[]>([]);
    // Today unpaid log pagination (Redux) — mobile
    const [unpaidLogPageTodayM, setUnpaidLogPageTodayM] = useState(1);
    const UNPAID_LOG_PAGE_SIZE_TODAY_M = 5;
    const [unpaidExportLoadingM, setUnpaidExportLoadingM] = useState(false);
    // Today unpaid — full fetch for By Server KPI (decoupled from Order Log pagination) — mobile
    const [allUnpaidForByServerM, setAllUnpaidForByServerM] = useState<UnpaidOrderItem[]>([]);
    const [allUnpaidForByServerLoadingM, setAllUnpaidForByServerLoadingM] = useState(false);
    const [unpaidLogTotal, setUnpaidLogTotal] = useState(0);
    const [unpaidLogPage, setUnpaidLogPage] = useState(0);
    const [unpaidSortByM, setUnpaidSortByM] = useState<string>('amount');
    const [unpaidSortDirM, setUnpaidSortDirM] = useState<'asc' | 'desc'>('desc');
    const [unpaidSearchM, setUnpaidSearchM] = useState('');
    const [unpaidDebouncedSearchM, setUnpaidDebouncedSearchM] = useState('');
    const [unpaidOrderTypesM, setUnpaidOrderTypesM] = useState<string[]>([]);
    const [unpaidTypeMenuOpenM, setUnpaidTypeMenuOpenM] = useState(false);
    const [unpaidLogLoading, setUnpaidLogLoading] = useState(false);
    const [unpaidSelectedOrderM, setUnpaidSelectedOrderM] = useState<UnpaidOrderItem | OrderLogItem | null>(null);
    // ORD-1.6 — card density for the Unpaid log, stored separately from the Paid one.
    // Both segments share it: the card renders whichever fields its row carries.
    const [unpaidCardDensityM, setUnpaidCardDensityM] = useOrderLogCardDensity('unpaid');
    // ── Unpaid log — Unpaid / Cancelled segments (desktop twin above) ──
    // The Cancelled side reads /order-log?type=cancel, the Paid log's own cancelled
    // dataset, so every surface shows one identical set of cancelled orders.
    const [unpaidStatusM, setUnpaidStatusM] = useState<'unpaid' | 'cancelled'>('unpaid');
    const [unpaidCancelPageM, setUnpaidCancelPageM] = useState(1);


    // Hardware / swipe back closes the Unpaid Orders slider before exiting the drill
    const closeUnpaidSliderM = useCallback(() => {
        setUnpaidSelectedOrderM(null);
        dispatch(clearOrderDetail());
    }, [dispatch]);
    useDrillHistoryBack(drillView === 'unpaid' && !!unpaidSelectedOrderM, closeUnpaidSliderM);

    const fetchIdRefMobile = useRef(0);
    const snapshotFetchKeyRefM = useRef<string | null>(null);
    const [refreshNonce, setRefreshNonce] = useState(0);
    const [refreshedTimeM, setRefreshedTimeM] = useState<string | null>(() => localStorage.getItem(SNAPSHOT_REFRESH_TS_KEY));
    const recordRefreshedTimeM = useCallback(() => {
        const ts = new Date().toISOString();
        localStorage.setItem(SNAPSHOT_REFRESH_TS_KEY, ts);
        setRefreshedTimeM(ts);
    }, []);
    const handlePtrRefresh = useCallback(() => {
        setRefreshNonce(n => n + 1);
        recordRefreshedTimeM();
    }, [recordRefreshedTimeM]);
    const [isSpinningM, setIsSpinningM] = useState(false);
    const handleManualRefreshM = useCallback(() => {
        setRefreshNonce(n => n + 1);
        recordRefreshedTimeM();
        setIsSpinningM(true);
    }, [recordRefreshedTimeM]);
    const { scrollRef: ptrScrollRef, pullDistance, isRefreshing: ptrRefreshing } = usePullToRefresh({ onRefresh: handlePtrRefresh, disabled: drillView === 'unpaid' && !!unpaidSelectedOrderM });
    const liveItemSalesFetchKeyRefM = useRef<string | null>(null);
    const [data, setData] = useState<any>({
        channel: null,
        voidSummary: null,
        voidDetails: null,
        checkIn: null,
        guests: null,
        peak: null,
        hourly: null,
        categoryItemSales: null,
        categoryItemSalesTotalElements: null,
        // Live today-only fields
        liveBilledOrders: null,
        liveBilledOrdersCount: null,
        liveCancelledOrders: null,
        liveVoidOrders: null,
        liveCompOrders: null,
        liveItemSales: null,
        liveTracking: null,
        liveTrackingPaid: null,
        liveTables: null,
        tenderPayment: null,
        isToday: false,
    });

    // ClickHouse date params for drill effects / retries — omitted for Today (live path), passed for historical.
    // Resolved from the range itself, not `data.isToday` — that flag only flips inside the date-change
    // effect, so on the first render after a date change it still describes the previous range.
    const chDatesView: { startDate?: string; endDate?: string } = isTodayRange(dateRange.startDate, dateRange.endDate, today) ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };



    useEffect(() => {
        if (!locationId || !token) {
            setLoading(false);
            return;
        }

        // Guard against duplicate/stale fetches (StrictMode double-mount, rapid date changes)
        const id = ++fetchIdRefMobile.current;
        // Resolved-range check, mirroring the desktop effect — the date picker's today
        // must take the same live path as the Today pill. See isTodayRange (ZAR-1762).
        const isToday = isTodayRange(dateRange.startDate, dateRange.endDate, today);

        const opts = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
        // ClickHouse date params — omitted for Today (live path), passed only for historical ranges
        const chDates: { startDate?: string; endDate?: string } = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
        setLoading(true);
        setError(null);

        // Helper: update a single data field progressively (skip if stale fetch)
        const patchMobile = (fields: Record<string, any>) => {
            if (id !== fetchIdRefMobile.current) return;
            setData((prev: any) => ({ ...prev, ...fields }));
        };

        if (isToday) {
            const fetchKey = `${locationId}-${dateRange.startDate}-${refreshNonce}-${channel}`;
            if (snapshotFetchKeyRefM.current !== fetchKey) {
                snapshotFetchKeyRefM.current = fetchKey;
                recordRefreshedTimeM();
                dispatch(fetchSnapshotDetailsRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchSnapshotComparisonRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchLiveByChannelRequest({ locationId, ...chDates, ...orderSourceParam }));
                // ZAR-2371 / SBS-03 — Source rows fetched alongside Channel, so the card's toggle only swaps data.
                dispatch(fetchLiveBySourceRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveRefundSummaryRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchLiveStaffRefundsRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveVoidSummaryRequest(locationId, undefined, undefined, orderSourceVal));
                dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }));
                dispatch(fetchLivePaymentsRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveStaffActiveClockingRequest(locationId, orderSourceVal));
                dispatch(fetchLiveStaffActiveClockingV2Request({ locationId, ...chDates, search: '', page: 1, size: 10, sortBy: 'date', sortAsc: false, ...orderSourceParam }));
                dispatch(fetchLiveUnpaidOrdersRequest({ locationId, size: 10, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveCompOrderLogRequest({ locationId, page: 1, size: COMP_PAGE_SIZE_M, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveCompReasonsRequest({ locationId, ...chDates, ...orderSourceParam }));
                dispatch(fetchLiveCompByStaffRequest({ locationId, ...chDates, ...orderSourceParam }));
            }
            // Reset to today shape immediately
            setTopItemsPageM(0);
            setData((prev: any) => ({ ...prev, isToday: true, channel: null, voidSummary: null, voidDetails: null, guests: null, peak: null, hourly: null, categoryItemSales: null, categoryItemSalesTotalElements: null, cancelDetail: null, voidDetail: null, liveTrackingPaid: null, checkIn: null, tenderPayment: null }));

            setLoading(false);
        } else {
            // Reset so switching back to Today re-triggers snapshot details fetch
            snapshotFetchKeyRefM.current = null;
            // Reset to historical shape immediately
            setData((prev: any) => ({ ...prev, isToday: false, liveBilledOrders: null, liveBilledOrdersCount: null, liveCancelledOrders: null, liveVoidOrders: null, liveItemSales: null, liveTracking: null, liveTrackingPaid: null, liveVoidedByCategory: null, guests: null, peak: null, hourly: null, tenderPayment: null }));
            setStaffLeaderPageM(0);

            // ClickHouse historical dispatches — same endpoints as Today, with startDate/endDate
            dispatch(fetchSnapshotDetailsRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchSnapshotComparisonRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchLiveByChannelRequest({ locationId, ...chDates, ...orderSourceParam }));
            // ZAR-2371 / SBS-03 — Source rows fetched alongside Channel, so the card's toggle only swaps data.
            dispatch(fetchLiveBySourceRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveRefundSummaryRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchLiveStaffRefundsRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveStaffActiveClockingRequest(locationId, orderSourceVal, chDates.startDate, chDates.endDate));
            dispatch(fetchLiveStaffActiveClockingV2Request({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, search: '', page: 1, size: 10, sortBy: 'date', sortAsc: false, ...orderSourceParam }));
            dispatch(fetchLivePaymentsRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveUnpaidOrdersRequest({ locationId, size: 10, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveCompOrderLogRequest({ locationId, page: 1, size: COMP_PAGE_SIZE_M, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveCompReasonsRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveCompByStaffRequest({ locationId, ...chDates, ...orderSourceParam }));

            // USSE-3: legacy /sales/snapshot-summary calls removed — Today never calls it;
            // historical runs on the same ClickHouse endpoints as Today (values from
            // snapshot-details, deltas from snapshot-details/comparison, leaderboard from
            // staff-performance). Redux loading flags drive the shimmers, same as Today.
            setLoading(false);

            dispatch(fetchLiveVoidSummaryRequest(locationId, opts.startDate, opts.endDate, orderSourceVal));
            dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }));
        }
    }, [locationId, token, dateRange.startDate, dateRange.endDate, today, activeDateFilter, orderSourceVal, refreshNonce]);

    // Today item-sales fetch (Mobile) — ref guard prevents React StrictMode double-fire.
    // No dateRange dep so a dateRange recalculation on the same day never fires a second request.
    useEffect(() => {
        if (!locationId || !token || activeDateFilter !== 'Today') return;
        const fetchKey = `${locationId}-${topItemsPageM}-${refreshNonce}-${channel}`;
        if (liveItemSalesFetchKeyRefM.current === fetchKey) return;
        liveItemSalesFetchKeyRefM.current = fetchKey;
        const pins = readLocalPins(auth?.userId ?? '', locationId);
        dispatch(fetchLiveItemSalesV2Request(locationId, topItemsPageM, TOP_ITEMS_PAGE_SIZE_M, pins.length ? pins : undefined, undefined, undefined, orderSourceVal));
    }, [locationId, token, activeDateFilter, topItemsPageM, refreshNonce, orderSourceVal]);

    // Historical (Mobile) — reset the Today ref so switching back to Today re-fetches.
    // USSE-2: the legacy /sales/category/getCategorySales fetch that lived here was removed —
    // data.categoryItemSales no longer backs any visible UI (the Items Overview card fetches
    // /sales/live/v2/item-sales itself for both Today and historical).
    useEffect(() => {
        if (!locationId || !token || activeDateFilter === 'Today') return;
        liveItemSalesFetchKeyRefM.current = null; // allow fresh fetch on return to Today
    }, [locationId, token, activeDateFilter]);

    // Reset void details page when leaving the voids drill
    useEffect(() => {
        if (drillView !== 'voids') { setVoidDetailsPageM(0); }
    }, [drillView]);

    // Void details — Redux, fetch all at once (FE pagination). Fires lazily when drill opens (Today + historical).
    useEffect(() => {
        if (drillView !== 'voids' || !locationId) return;
        dispatch(fetchLiveVoidDetailsRequest(locationId, 1, 1000, chDatesView.startDate, chDatesView.endDate, orderSourceVal));
    }, [drillView, locationId, dateRange.startDate, dateRange.endDate, dispatch, refreshNonce, orderSourceVal]);

    // Voids by Staff — lazily fetched when the voids drill opens. NOT YET DEPLOYED backend
    // endpoint; the mobile DrillVoids section shows its own failure/retry state until it ships.
    useEffect(() => {
        if (drillView !== 'voids' || !locationId) return;
        dispatch(fetchVoidByStaffRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal));
    }, [drillView, locationId, dateRange.startDate, dateRange.endDate, dispatch, refreshNonce, orderSourceVal]);

    // Void drill-in Mini-KPIs — lazily fetched when the voids drill opens.
    useEffect(() => {
        if (drillView !== 'voids' || !locationId) return;
        dispatch(fetchVoidKpisRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal));
    }, [drillView, locationId, dateRange.startDate, dateRange.endDate, dispatch, refreshNonce, orderSourceVal]);

    // Debounce unpaid search — 3 s after last keystroke (mobile)
    useEffect(() => {
        const t = setTimeout(() => setUnpaidDebouncedSearchM(unpaidSearchM), 500);
        return () => clearTimeout(t);
    }, [unpaidSearchM]);

    // Reset to page 1 when debounced search or order type filter changes (mobile)
    useEffect(() => { setUnpaidLogPageTodayM(1); setUnpaidCancelPageM(1); }, [unpaidDebouncedSearchM, unpaidOrderTypesM]); // eslint-disable-line react-hooks/exhaustive-deps

    // Channel Names — fetch once per location for Order Type dropdown (mobile)
    useEffect(() => {
        if (!locationId) return;
        dispatch(fetchChannelNamesRequest(locationId));
    }, [locationId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    // Unpaid Order Log — always uses Redux ClickHouse (supports both Today and historical, mobile)
    useEffect(() => {
        // Only the Unpaid segment reads this endpoint; the Cancelled one has its own fetch below.
        if (drillView !== 'unpaid' || unpaidStatusM !== 'unpaid' || !locationId) return;
        dispatch(fetchLiveUnpaidLogRequest({ locationId, page: unpaidLogPageTodayM, size: UNPAID_LOG_PAGE_SIZE_TODAY_M, sortBy: unpaidSortByM, sortDir: unpaidSortDirM, search: unpaidDebouncedSearchM || undefined, orderType: unpaidOrderTypesM.length ? unpaidOrderTypesM.join(',') : undefined, ...chDatesView, ...orderSourceParam }));
    }, [drillView, unpaidStatusM, unpaidLogPageTodayM, unpaidSortByM, unpaidSortDirM, unpaidDebouncedSearchM, unpaidOrderTypesM, locationId, dateRange.startDate, dateRange.endDate, dispatch, orderSourceVal]);

    // Unpaid drill → Cancelled segment (mobile) — /order-log?type=cancel, the same call the
    // desktop twin makes. The card view has no sortable headers, so it keeps the log's
    // default Time-desc ordering.
    useEffect(() => {
        if (drillView !== 'unpaid' || unpaidStatusM !== 'cancelled' || !locationId) return;
        dispatch(fetchOrderLogRequest({
            locationId,
            page: unpaidCancelPageM,
            size: UNPAID_LOG_PAGE_SIZE_TODAY_M,
            sortBy: 'orderTime',
            sortDir: 'desc',
            search: unpaidDebouncedSearchM || undefined,
            channelName: unpaidOrderTypesM.length ? unpaidOrderTypesM.join(',') : undefined,
            ...chDatesView,
            ...orderSourceParam,
            type: 'cancel' as const,
        }));
    }, [drillView, unpaidStatusM, unpaidCancelPageM, unpaidDebouncedSearchM, unpaidOrderTypesM, locationId, dateRange.startDate, dateRange.endDate, dispatch, orderSourceVal]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch ALL unpaid orders for By Server KPI + Total Value/Avg — always ClickHouse (Today + historical, mobile).
    useEffect(() => {
        // Wait for the count of the CURRENT scope — on a date/outlet/channel change this effect first runs
        // with the previous scope's count. Clear the list so a 0 count never sums a previous scope's orders.
        if (!locationId || liveUnpaidOrdersLoadingM || liveUnpaidOrdersTotalElementsM <= 0) {
            setAllUnpaidForByServerM([]);
            setAllUnpaidForByServerLoadingM(false);
            return;
        }
        let stale = false;
        setAllUnpaidForByServerLoadingM(true);
        getLiveUnpaidOrdersApi({ locationId, page: 1, size: liveUnpaidOrdersTotalElementsM, ...chDatesView, ...orderSourceParam })
            .then((res: any) => {
                if (!stale) setAllUnpaidForByServerM(Array.isArray(res?.orders) ? res.orders : []);
            })
            .catch(() => { if (!stale) setAllUnpaidForByServerM([]); })
            .finally(() => { if (!stale) setAllUnpaidForByServerLoadingM(false); });
        // Drop the response if the scope changed while it was in flight.
        return () => { stale = true; };
    }, [locationId, liveUnpaidOrdersLoadingM, liveUnpaidOrdersTotalElementsM, dateRange.startDate, dateRange.endDate, orderSourceVal]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch Order Detail when an unpaid order row is clicked (mobile)
    useEffect(() => {
        if (!unpaidSelectedOrderM || !locationId) return;
        dispatch(fetchOrderDetailRequest({ locationId, orderId: unpaidSelectedOrderM.orderId ?? '', ...(isHistoricalReportDateFilter(activeDateFilter) ? { type: 'historical' } : {}) }));
    }, [unpaidSelectedOrderM, locationId, activeDateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Mobile comp log — lazy fetch when complimentary drill is opened, re-fetches on page change (always ClickHouse)
    useEffect(() => {
        if (drillView !== 'complimentary') { setCompLogPageTodayM(0); return; }
        if (!locationId || !token) return;
        dispatch(fetchLiveCompOrderLogRequest({ locationId, page: compLogPageTodayM + 1, size: COMP_PAGE_SIZE_M, ...chDatesView, ...orderSourceParam }));
        if (compLogPageTodayM === 0) {
            dispatch(fetchLiveCompReasonsRequest({ locationId, ...chDatesView, ...orderSourceParam }));
            dispatch(fetchLiveCompByStaffRequest({ locationId, ...chDatesView, ...orderSourceParam }));
        }
    }, [drillView, compLogPageTodayM, locationId, token, dateRange.startDate, dateRange.endDate, orderSourceVal]);

    const branchLogoUrl = useMemo(() => getBranchLogoUrlMobile(branch), [branch]);
    const branchInitials = useMemo(() => getBranchInitialsMobile(branch, auth), [branch, auth]);
    const [logoError, setLogoError] = useState(false);
    useEffect(() => { setLogoError(false); }, [branchLogoUrl]);

    const handleDateFilterChange = useCallback((opt) => {
        setActiveDateFilter(opt);
        if (opt === 'Today') setSelectedDate(today);
        else if (opt === 'Yesterday') setSelectedDate(yesterday);
    }, [today, yesterday]);

    const DRILL_TITLES_MOBILE: Record<string, string> = { 'net-sales': 'Net Sales', orders: 'Paid Orders', refund: 'Refund Orders', 'cancelled-orders': 'Cancelled Orders', staff: 'Staff Worked', voids: 'Voids', tips: TG_DRILL_TITLE, unpaid: 'Unpaid Orders', complimentary: 'Complimentary Orders', discounts: 'Discounts' };

    // CANC-1 — Cancelled Orders KPI + drill (/order-log, type=cancel). Same single fetch feeds
    // the card count and the drill log, so they can never disagree.
    const fetchCancelledOrdersM = useCallback(() => {
        if (!locationId) return;
        dispatch(fetchCancelledOrderLogRequest({
            locationId,
            page: 1,
            size: CANCELLED_LOG_FETCH_SIZE,
            sortBy: 'orderTime',
            sortDir: 'desc',
            ...chDatesView,
            ...orderSourceParam,
            type: 'cancel',
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal]);
    useEffect(() => { fetchCancelledOrdersM(); }, [fetchCancelledOrdersM, refreshNonce]);

    // CANC-1 — one outlet's cancelled orders, for the "All outlets" export scope. The export
    // button calls this per accessible outlet, so it must NOT reuse the on-screen redux rows.
    const fetchCancelledOrdersForExportM = useCallback(async (loc: string): Promise<OrderLogItem[]> => {
        const res: any = await getOrderLogApi({
            locationId: loc,
            page: 1,
            size: CANCELLED_LOG_FETCH_SIZE,
            sortBy: 'orderTime',
            sortDir: 'desc',
            ...chDatesView,
            ...orderSourceParam,
            type: 'cancel',
        });
        return Array.isArray(res?.orders) ? res.orders : (Array.isArray(res) ? res : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chDatesView.startDate, chDatesView.endDate, orderSourceVal]);


    const buildSnapshotKpiExportM = useCallback(async (_format?: ExportFormat, opts?: { locationId?: string }) => {
        let k: Record<string, any> = kpiExportRefM.current;
        if (opts?.locationId) {
            const loc = opts.locationId;
            const { chDates, orderSourceVal: os, token: tok } = kpiExportRefM.current;
            // ZAR-1762 — Tips & Grat now comes straight off snapshot-details' tipAndGratuity,
            // so this refetch no longer needs the staff-performance call it only summed tips from.
            const [snap, refundRes, staffV2Res, voidRes, compRes, cancelledRes]: any[] = await Promise.all([
                getSnapshotDetails({ locationId: loc, ...chDates, orderSource: os, token: tok }),
                getLiveRefundSummaryApi({ locationId: loc, ...chDates, orderSource: os, token: tok }),
                getLiveStaffActiveClockingV2Api({ locationId: loc, ...chDates, page: 1, size: 1, sortBy: 'date', sortAsc: false, orderSource: os }),
                getLiveVoidSummaryApi(loc, tok, chDates?.startDate, chDates?.endDate, os),
                getLiveCompOrderLogApi({ locationId: loc, page: 1, size: COMP_PAGE_SIZE_M, ...chDates, orderSource: os }),
                // CANC-1 — Cancelled orders for the override outlet, same call as fetchCancelledOrdersForExportM.
                getOrderLogApi({ locationId: loc, page: 1, size: CANCELLED_LOG_FETCH_SIZE, sortBy: 'orderTime', sortDir: 'desc', ...chDates, orderSource: os, type: 'cancel' }),
            ]);
            const refundItems: any[] = Array.isArray(refundRes) ? refundRes : (refundRes?.data ?? []);
            const voidItems: any[] = Array.isArray(voidRes) ? voidRes : (voidRes?.data ?? []);
            const compItems: any[] = Array.isArray(compRes?.orders) ? compRes.orders : (Array.isArray(compRes?.content) ? compRes.content : (Array.isArray(compRes) ? compRes : []));
            const cancelledCountOther = Array.isArray(cancelledRes?.orders) ? cancelledRes.orders.length : (Array.isArray(cancelledRes) ? cancelledRes.length : 0);
            const unpaidProbe: any = await getLiveUnpaidOrdersApi({ locationId: loc, page: 1, size: 1, ...chDates, orderSource: os });
            const unpaidTotal = num(unpaidProbe?.totalElements ?? unpaidProbe?.total ?? 0);
            let unpaidAmountOther = 0;
            if (unpaidTotal > 0) {
                const unpaidAll: any = await getLiveUnpaidOrdersApi({ locationId: loc, page: 1, size: unpaidTotal, ...chDates, orderSource: os });
                const unpaidList: any[] = Array.isArray(unpaidAll?.orders) ? unpaidAll.orders : [];
                unpaidAmountOther = unpaidList.reduce((s: number, o: any) => s + (Number(o.amount) || 0), 0);
            }
            const netSalesOther = num(snap?.netSales);
            const totalOrdersOther = num(snap?.orderCount);
            k = {
                ...kpiExportRefM.current,
                netSales: netSalesOther,
                spendPerGuest: num(snap?.guestCount) > 0 ? num(snap?.spendPerGuest) : null,
                totalOrders: totalOrdersOther,
                unpaidCount: unpaidTotal,
                unpaidAmount: unpaidAmountOther,
                avgOrder: totalOrdersOther > 0 ? netSalesOther / totalOrdersOther : 0,
                serviceCharge: readServiceCharge(snap, scEnabled)?.amount ?? null,
                refund: parseFloat(refundItems.reduce((s: number, item: any) => s + (item.cancelledAmt || 0), 0).toFixed(2)),
                // ZAR-1762 / TG-1 — export mirrors the on-screen card: the backend's own combined figure.
                tipsAndGratuity: num(snap?.tipAndGratuity),
                staffWorked: num(staffV2Res?.staffWorked),
                voidCount: voidOrdersFrom(voidItems),
                compCount: num(compRes?.totalElements ?? compRes?.total ?? compItems.length),
                compAmount: compItems.reduce((s: number, o: any) => s + (Number(o.amount) || 0), 0),
                cancelledCount: cancelledCountOther,
                discounts: num(snap?.discount),
                guestCount: num(snap?.guestCount),
            };
        }
        const sym = getCurrencySymbol();
        const fmtMoney = (v: number) => `${sym}${formatMonetaryExport(v)}`;
        const rows: ExportCell[][] = [
            ['From', fmtExportDate(k.dateRange?.startDate)],
            ['To', fmtExportDate(k.dateRange?.endDate)],
            ['Channel Filter', k.channel],
            ['Net Sales', fmtMoney(num(k.netSales))],
            ['Paid orders', fmtNumMobile(k.totalOrders)],
            ['Unpaid orders', k.unpaidCount ? `${fmtNumMobile(k.unpaidCount)} (${fmtMoney(num(k.unpaidAmount))})` : fmtNumMobile(k.unpaidCount)],
            ['Avg Order', fmtMoney(num(k.avgOrder))],
            ...(k.serviceCharge != null ? [['Service Charge', fmtMoney(num(k.serviceCharge))] as ExportCell[]] : []),
            ['Refund', fmtMoney(num(k.refund))],
        ];
        // Staff Worked / Void / Complimentary cards are hidden on screen under the
        // 3rd-party channel filter — match that on export.
        if (!k.isThirdParty) {
            rows.push(['Staff Worked', fmtNumMobile(k.staffWorked)]);
            rows.push(['Voided Items', fmtNumMobile(k.voidCount)]);
        }
        rows.push(['Tips & Grat', fmtMoney(num(k.tipsAndGratuity))]);
        if (!k.isThirdParty) {
            rows.push(['Complimentary', k.compAmount > 0 ? `${fmtNumMobile(k.compCount)} (${fmtMoney(num(k.compAmount))})` : fmtNumMobile(k.compCount)]);
        }
        // CANC-1 — Cancelled orders. Shown under every source (unlike Staff Worked / Void /
        // Complimentary), matching the on-screen "Cancelled" tile which is never hidden.
        rows.push(['Cancelled', fmtNumMobile(k.cancelledCount)]);
        rows.push(['Discounts', fmtMoney(num(k.discounts))]);
        if (!k.isThirdParty) {
            rows.push(['Guests', fmtNumMobile(k.guestCount)]);
        }
        rows.push(['Spend / Guest', k.spendPerGuest == null ? '—' : fmtMoney(num(k.spendPerGuest))]);
        return { headers: ['KPI', 'Value'], rows };
    }, []);

    // Global sales-channel change also clears the Unpaid Orders drill search + filters so they don't
    // leak across channels (mirrors desktop handleChannelChange; done in the handler so the unpaid-log
    // fetch fires once with the new channel and clean filters). The Paid Orders drill clears itself —
    // DrillOrders watches its orderSource prop.
    const handleChannelChangeM = useCallback((c: string) => {
        setChannel(c);
        setUnpaidSearchM(''); setUnpaidDebouncedSearchM(''); setUnpaidOrderTypesM([]); setUnpaidTypeMenuOpenM(false); setUnpaidSortByM('amount'); setUnpaidSortDirM('desc'); setUnpaidLogPageTodayM(1); setUnpaidStatusM('unpaid'); setUnpaidCancelPageM(1); setUnpaidSelectedOrderM(null); dispatch(clearOrderDetail());
    }, [dispatch]);

    useEffect(() => {
        setTopBarProps({
            title: drillView ? (DRILL_TITLES_MOBILE[drillView] ?? 'Snapshot') : 'Snapshot',
            dateFilters: drillView ? undefined : { value: activeDateFilter, onChange: handleDateFilterChange },
            // Active Staff / Voids / Complimentary drills: hide the global sales-channel filter (mirrors desktop).
            sourceFilters: (drillView === 'staff' || drillView === 'voids' || drillView === 'complimentary') ? undefined : { options: CHANNEL_OPTIONS, value: channel, onChange: handleChannelChangeM },
            onBack: drillView ? goBack : (isSuperUser && (getOwnerLocationCount() ?? 0) > 1 ? () => navigate('/owner-view') : undefined),
            outletRightAddon: !drillView ? (
                <>
                    {(false && activeDateFilter === 'Today') ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.t2, fontSize: 10, flexShrink: 0 }}>
                            {refreshedTimeM && <span>As of {formatRefreshedTime(refreshedTimeM)}</span>}
                            <button type="button" onClick={handleManualRefreshM} aria-label="Refresh" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}>
                                <span className="material-symbols-rounded" onAnimationEnd={() => setIsSpinningM(false)} style={{ fontSize: 15, color: isSpinningM ? 'var(--accent)' : C.t2, display: 'inline-block', animation: isSpinningM ? 'spin 0.5s linear' : 'none' }}>refresh</span>
                            </button>
                        </div>
                    ) : null}
                    <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                        title="Snapshot KPIs"
                        filename={kpiExportFilename('snapshot-kpis', dateRange.startDate, dateRange.endDate, orderSourceVal)}
                        buildExport={buildSnapshotKpiExportM}
                        variant="mobile"
                        iconOnly
                    />
                </>
            ) : undefined,
        });
    }, [setTopBarProps, activeDateFilter, channel, handleDateFilterChange, handleChannelChangeM, isSuperUser, navigate, drillView, goBack, refreshedTimeM, isSpinningM, handleManualRefreshM, buildSnapshotKpiExportM, dateRange.startDate, dateRange.endDate, orderSourceVal]);

    // SW3 — fires on mount too (and can fire a second time on mount if the "always open on
    // Today" effect above forces activeDateFilter to change), which would otherwise stomp a
    // ?drill=<key> deep link before the user ever sees it open. See drillFromUrlRef above.
    useEffect(() => { if (!drillFromUrlRef.current) setDrillView(null); setTopItemsPageM(0); setRefundLogPageM(1); setVoidDetailsPageM(0); setUnpaidLogPageTodayM(1); setUnpaidStatusM('unpaid'); setUnpaidCancelPageM(1); setUnpaidSortByM('amount'); setUnpaidSortDirM('desc'); setAllUnpaidForByServerM([]); setAllUnpaidForByServerLoadingM(false); allTopItemsMobileRef.current = []; setUnpaidSelectedOrderM(null); dispatch(clearOrderDetail()); }, [activeDateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Under 3rd party, the Active Staff / Void / Complimentary KPIs are hidden — close their drills if open.
    useEffect(() => { if (isThirdParty && (drillView === 'staff' || drillView === 'voids' || drillView === 'complimentary')) setDrillView(null); }, [isThirdParty, drillView]);
    useEffect(() => { if (drillView !== 'unpaid') { setUnpaidSelectedOrderM(null); setUnpaidSearchM(''); setUnpaidDebouncedSearchM(''); setUnpaidOrderTypesM([]); setUnpaidTypeMenuOpenM(false); setUnpaidStatusM('unpaid'); setUnpaidCancelPageM(1); dispatch(clearOrderDetail()); } }, [drillView]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setDiscountsStaffPageM(0); }, [liveStaffPerformanceM]); // eslint-disable-line react-hooks/exhaustive-deps

    // Refund Order Log (mobile) — same ClickHouse endpoint for Today and historical;
    // historical adds startDate/endDate.
    useEffect(() => {
        if (drillView !== 'refund' || !locationId) return;
        dispatch(fetchLiveRefundDetailsRequest(locationId, refundLogPageM, REFUND_PAGE_SIZE_M, orderSourceVal, chDatesView.startDate, chDatesView.endDate));
    }, [drillView, activeDateFilter, locationId, refundLogPageM, dispatch, refreshNonce, orderSourceVal, dateRange.startDate, dateRange.endDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Stamp KPIs — always from snapshotDetails (ClickHouse, dispatched with startDate/endDate for historical)
    const billedSrcM = unwrapResMobile(data.liveBilledOrders);
    const netSales = snapshotDetails?.netSales ?? pickMobile(billedSrcM, 'totalNetSales', 'netSales', 'totalSales', 'net_sales', 'total_sales', 'revenue');
    const netSalesYesterday = pickMobile(billedSrcM, 'netSalesYesterday');

    const totalOrders = snapshotDetails?.orderCount
        ?? pickMobile(billedSrcM, 'totalTransactions', 'totalOrders', 'orders', 'total_orders', 'orderCount', 'totalOrderCount', 'number_of_orders');
    const avgOrder = snapshotDetails?.orderCount
        ? num(snapshotDetails.netSales) / num(snapshotDetails.orderCount)
        : Number(pickMobile(billedSrcM, 'avgOrderValue') || 0);
    const staffPerfTotalM = num(snapshotDetails?.tip);
    const tips = staffPerfTotalM;
    // ZAR-1762 / TG-1 — unified card: the backend's own combined total and combined delta
    // (`tipAndGratuity` on snapshot-details and on its /comparison twin).
    const gratuityM = num(snapshotDetails?.gratuity);
    const tipsAndGratuityM = num(snapshotDetails?.tipAndGratuity);
    const tipsGratuityPctM = snapshotComparisonM?.tipAndGratuity != null && Number.isFinite(Number(snapshotComparisonM.tipAndGratuity))
        ? Number(snapshotComparisonM.tipAndGratuity)
        : null;
    // Service Charge stamp KPI — see the desktop twin.
    const serviceChargeM = readServiceCharge(snapshotDetails, scEnabled);
    const serviceChargePctM = snapshotComparisonM?.serviceFee != null && Number.isFinite(Number(snapshotComparisonM.serviceFee))
        ? Number(snapshotComparisonM.serviceFee)
        : null;
    const avgTipPerOrder = num(pickMobile(billedSrcM, 'avgTipPerOrder'));
    const tipRatePercent = num(pickMobile(billedSrcM, 'tipRatePercent'));
    const tax = snapshotDetails?.tax ?? pickMobile(billedSrcM, 'tax', 'totalTax', 'taxAmount');
    const discounts = snapshotDetails?.discount ?? pickMobile(billedSrcM, 'discounts', 'totalDiscount', 'totalDiscounts');
    // Guests stamp KPI — value from snapshot-details guestCount (Today + historical)
    const guests = snapshotDetails?.guestCount ?? 0;
    // SPG-1: Spend per Guest stamp KPI — value from snapshot-details spendPerGuest, scoped to
    // guest-bearing orders. "—" when there are no guests in the active period/source (incalculable).
    const spendPerGuest = guests > 0 ? num(snapshotDetails?.spendPerGuest) : null;

    // ── Historical discount breakdown (offers + staff) — sourced from getDiscountSummary ──
    const discountListM = listFrom(data.discounts);
    const discountOffersM = discountListM
        .map((d: any) => ({
            name: d.offerName ?? d.discountName ?? d.name ?? d.offer ?? '—',
            type: d.discountType ?? d.type ?? '—',
            rate: Number(d.discountRate ?? d.rate ?? d.percentage ?? 0) || 0,
            sales: Number(d.salesAmount ?? d.grossSales ?? d.sales ?? 0) || 0,
            amount: Number(d.discountAmount ?? d.amount ?? d.totalDiscount ?? d.value ?? 0) || 0,
            count: Number(d.orderCount ?? d.count ?? d.orders ?? 0) || 0,
        }))
        .filter((d: any) => d.amount > 0)
        .sort((a: any, b: any) => b.amount - a.amount)
        .slice(0, 10);
    const staffDiscountMapM: Record<string, { name: string; role: string; total: number; count: number }> = {};
    discountListM.forEach((d: any) => {
        const staffName = d.stew ?? d.staffName ?? d.employeeName ?? d.staff ?? '';
        if (staffName) {
            if (!staffDiscountMapM[staffName]) staffDiscountMapM[staffName] = { name: staffName, role: d.role ?? d.staffRole ?? '', total: 0, count: 0 };
            staffDiscountMapM[staffName].total += Number(d.discountAmount ?? d.amount ?? 0) || 0;
            staffDiscountMapM[staffName].count += Number(d.orderCount ?? d.count ?? 1) || 0;
        }
    });
    const staffDiscountsM = Object.values(staffDiscountMapM).sort((a, b) => b.total - a.total).slice(0, 6);

    // Channel data: Today → liveByChannel Redux (from /sales/live/v2/by-channel), Historical → getSalesByChannel
    // Complimentary orders (mobile) — always from Redux ClickHouse
    const compCount = liveCompOrderLogTotalElementsM;
    const compAmount = liveCompOrderLogM.reduce((s, o) => s + (Number(o.amount) || 0), 0);

    // Channel data: Today → derive from liveTracking orders + liveTables for Dine-In, Historical → getSalesByChannel
    const liveTablesOrdersM = (() => {
        const raw = unwrapResMobile(data.liveTables);
        const arr = raw?.content ?? raw?.data ?? (Array.isArray(raw) ? raw : []);
        return Array.isArray(arr) ? arr : [];
    })();
    const dineInFromTablesM = data.isToday && liveTablesOrdersM.length > 0
        ? Math.round(liveTablesOrdersM.reduce((sum: number, o: any) => sum + (Number(o.orderAmount) || 0), 0) * 100) / 100
        : null;
    const trackingListM = (() => {
        const raw = unwrapResMobile(data.liveTracking);
        const arr = raw?.content ?? raw?.data ?? (Array.isArray(raw) ? raw : []);
        return Array.isArray(arr) ? arr : [];
    })();
    const channelRaw = (() => {
        const raw = unwrapResMobile(data.channel);
        return Array.isArray(raw) ? raw : (raw?.content ?? raw?.data ?? []);
    })();
    const channelArr = Array.isArray(channelRaw) ? channelRaw : [];
    const channelDataM = channelArr.map((item: any) => ({
        name: item.channelName ?? item.channel ?? item.name ?? 'Channel',
        value: Number(item.totalSales ?? item.sales ?? item.amount ?? item.value ?? item.netSales ?? 0) || 0,
    })).filter((item: any) => item.value > 0).sort((a: any, b: any) => b.value - a.value);
    const dineIn = dineInFromTablesM ?? (num(pickMobile(billedSrcM, 'maghilSales')) || (channelDataM.find((item: any) => /dine|in.?house|in-house/i.test(item.name))?.value ?? channelDataM[0]?.value ?? 0));
    const offPremise = num(pickMobile(billedSrcM, 'thirdPartySales')) || channelDataM.filter((item: any) => !(/dine|in.?house|in-house/i.test(item.name))).reduce((sum: number, item: any) => sum + item.value, 0);

    // Net Sales channel mini-points for mobile hero — Today only, Dine-In & Pickup from liveByChannelM Redux
    const netSalesMobileChannels = useMemo((): { label: string; netSales: number }[] => {
        if (!data.isToday) return [];
        const dineInSales = liveByChannelM
            .filter((item: LiveByChannelItem) => getCanonicalChannel(item.channel) === 'Dine-In')
            .reduce((s: number, item: LiveByChannelItem) => s + item.netSales, 0);
        const pickupSales = liveByChannelM
            .filter((item: LiveByChannelItem) => getCanonicalChannel(item.channel) === 'Pickup')
            .reduce((s: number, item: LiveByChannelItem) => s + item.netSales, 0);
        return [
            { label: 'Dine-In', netSales: dineInSales },
            { label: 'Pickup', netSales: pickupSales },
        ];
    }, [liveByChannelM, data.isToday]);

    // Historical Sales by Channel (Mobile) — derived from channelDataM (Yesterday / 7D / 30D / Custom)
    const channelTotalM = channelDataM.reduce((s: number, item: any) => s + item.value, 0);
    const channelBreakdownM: DoughnutSlice[] = (() => {
        if (!channelDataM.length) return [];
        const pcts = distribute100(channelDataM.map((item: any) => item.value));
        return channelDataM.map((item: any, i: number) => ({
            name: item.name,
            value: item.value,
            color: getChannelColor(item.name),
            pct: pcts[i],
        }));
    })();

    // Cancel data — ClickHouse refund-summary for BOTH Today and historical (historical is
    // dispatched with startDate/endDate). No legacy cancelSummary.
    const liveRefundTotalAmtM = liveRefundSummaryM.reduce((sum, item) => sum + (item.cancelledAmt ?? 0), 0);
    const liveRefundOrderCountM = liveRefundSummaryM.reduce((sum, item) => sum + item.orderCount, 0);
    const cancelledAmount = liveRefundTotalAmtM;
    const cancelledCount = liveRefundOrderCountM;
    // REF-3 — the Refund stamp KPI reads item + fee refund straight off snapshot-details now
    // (real data), rather than the refund-summary sum above. This only replaces the stamp tile.
    const refundStampItemM = num(snapshotDetails?.refund);
    const refundStampTotalM = refundStampItemM + num(snapshotDetails?.feeRefund);

    // Active Staff KPI: /sales/live/v2/staff-active-clocking redux for BOTH Today and historical
    // (historical dispatches with startDate/endDate). Count of items where currentStatus === 'active'.
    const isStatusActiveM = (s: string | null | undefined) => String(s ?? '').trim().toLowerCase() === 'active';
    const isStatusBreakM = (s: string | null | undefined) => String(s ?? '').trim().toLowerCase() === 'break';
    const activeStaffActiveCountM = liveStaffActiveClockingM.reduce((n, it) => n + (isStatusActiveM(it.currentStatus) ? 1 : 0), 0);
    const activeStaffOnBreakCountM = liveStaffActiveClockingM.reduce((n, it) => n + (isStatusBreakM(it.currentStatus) ? 1 : 0), 0);
    const activeStaffTotalHoursDecimalM = liveStaffActiveClockingM.reduce((s, it) => s + parseClockHours(it.totalHours), 0);
    const activeStaffNetSalesSumM = liveStaffActiveClockingM.reduce((s, it) => s + (Number(it.netSales) || 0), 0);
    const activeStaffAvgSalesM = liveStaffActiveClockingM.length > 0
        ? activeStaffNetSalesSumM / liveStaffActiveClockingM.length
        : 0;

    // LBR-11: stamp KPI = total headcount (response array length), not the 'active' subset.
    const activeStaffTotalCountM = liveStaffActiveClockingM.length;
    const activeStaff = activeStaffTotalCountM;

    // Void data: /sales/live/v2/void-summary for both Today and historical (with startDate/endDate for historical).
    const liveVoidTotalCountM = voidOrdersFrom(liveVoidSummaryM);
    const liveVoidTotalAmtM = liveVoidSummaryM.reduce((s, item) => s + item.cancelledAmt, 0);
    const voidCount = liveVoidTotalCountM;
    const voidAmount = liveVoidTotalAmtM;
    // Voided Items (stamp KPI value) — see the matching desktop comment above voidedItemsCount.
    const voidedItemsCountM = liveVoidSummaryM.reduce((s, item) => s + (item.removedQty ?? 0), 0);
    // Voided Items delta — see the matching desktop comment above voidDeltaPct.
    const voidDeltaPctM = liveVoidSummaryM.length > 0 && liveVoidSummaryM[0].delta != null ? Number(liveVoidSummaryM[0].delta) : null;

    const checkInCount = pickMobile(data.checkIn, 'totalCheckin', 'totalCheckIns', 'checkInCount', 'count', 'totalCheckIn', 'checkIns') ?? pickFromListMobile(data.checkIn, 'checkInCount') ?? (Array.isArray(data.checkIn?.content ?? data.checkIn?.data) ? (data.checkIn.content ?? data.checkIn.data).length : 0);
    const guestCount = Number(pickMobile(billedSrcM, 'guestsCount') ?? pickMobile(data.guests, 'totalGuests', 'guestCount', 'count', 'totalGuestCount', 'total') ?? 0) || 0;
    const avgWait = Number(pickMobile(billedSrcM, 'avgWaitTime') ?? pickMobile(data.peak, 'avgWaitTime', 'averageWait', 'avgWait', 'avgWaitTimeMinutes', 'averageWaitTime') ?? 0) || 0;

    // Hourly chart data (mobile) — unified from /sales/live/v2/hourly-sales?startDate=&endDate=
    // Shares the same Redux state as desktop; same shared time axis for Revenue and Orders.
    const hourlyHoursM = hourlySalesDataM?.hours ?? [];
    const operatingStartHourM = hourlySalesDataM?.operatingStartHour ?? 6;
    const operatingEndHourM = hourlySalesDataM?.operatingEndHour ?? 23;
    const operatingHourCountM = hourlySalesDataM?.operatingHourCount ?? 0;

    // salesPerHour: derived from operatingHourCount returned by the hourly-sales API
    const salesPerHour = netSales != null && operatingHourCountM > 0
        ? Number(netSales) / operatingHourCountM
        : null;
    const buildHourlyChartDataM = (valueKey: 'itemTotal' | 'orderCount'): { label: string; value: number }[] =>
        hourlyHoursM.map(h => {
            const hv = h.hourValue % 24;
            const suffix = hv >= 12 ? ' PM' : ' AM';
            const disp = hv % 12 || 12;
            return { label: `${disp}${suffix}`, value: h[valueKey] };
        });

    const hourlyRevenueChartDataM = buildHourlyChartDataM('itemTotal');
    const hourlyOrdersChartDataM = buildHourlyChartDataM('orderCount');
    const windowHoursM = hourlyHoursM.filter(h => h.hourValue >= operatingStartHourM && h.hourValue <= operatingEndHourM);
    const avgRevenuePerHourM = operatingHourCountM > 0
        ? windowHoursM.reduce((s, h) => s + h.itemTotal, 0) / operatingHourCountM
        : 0;
    const avgOrdersPerHourM = operatingHourCountM > 0
        ? windowHoursM.reduce((s, h) => s + h.orderCount, 0) / operatingHourCountM
        : 0;

    // Staff Sales Leaderboard (historical-only card, mobile) — USSE-3: ClickHouse
    // staff-performance (same redux the Tips KPI reads), aggregated per staff since the
    // endpoint may return one row per staff × channel; hours enriched from the
    // staff-active-clocking roster. Replaces the legacy /sales/snapshot-summary leaderboard.
    const staffLeaderboardM = (() => {
        if (data.isToday) return [];
        const hoursByNameM: Record<string, number> = {};
        for (const c of liveStaffActiveClockingM) {
            const key = String(c.staffName ?? '').trim().toLowerCase();
            if (key) hoursByNameM[key] = (hoursByNameM[key] || 0) + parseClockHours(c.totalHours);
        }
        const aggM: Record<string, { name: string; sales: number; qty: number }> = {};
        for (const item of liveStaffPerformanceM) {
            const name = String(item.staff_name ?? '').trim();
            if (!name) continue;
            const key = name.toLowerCase();
            if (!aggM[key]) aggM[key] = { name, sales: 0, qty: 0 };
            aggM[key].sales += Number(item.netSales) || 0;
            aggM[key].qty += Number(item.orderCount) || 0;
        }
        // REF-6 — item refunds reduce Staff Sales Leaderboard revenue; fee refunds do not.
        const itemRefundsByStaffM = itemRefundsByCheckOwner(refundSummaryM.records);
        return Object.values(aggM)
            .map(({ name, sales, qty }) => {
                const totalHrs = hoursByNameM[name.toLowerCase()] ?? 0;
                return {
                    name,
                    role: '',
                    hoursLabel: totalHrs > 0 ? `${totalHrs.toFixed(1)}h` : '',
                    sales: Number((sales - (itemRefundsByStaffM[name] ?? 0)).toFixed(2)),
                    qty,
                };
            })
            .filter((item) => item.sales > 0 || item.qty > 0)
            .sort((a, b) => b.sales - a.sales);
    })();

    // ZAR-1661 — see the desktop branch: delta visibility follows the shape of the period.
    const showDelta = hasComparison(activeDateFilter, storeTz);

    // Today unpaid derived values — sum the FULL all-orders fetch (same dataset as By Server)
    // so Total Value matches the By Server total (mobile). Fall back to the size-10 stats
    // fetch only until the all-orders fetch resolves.
    const todayUnpaidValueM = useMemo(() => {
        const source = allUnpaidForByServerM.length > 0 ? allUnpaidForByServerM : liveUnpaidOrdersM;
        return source.reduce((s, o) => s + (Number(o.amount) || 0), 0);
    }, [allUnpaidForByServerM, liveUnpaidOrdersM]);
    const todayUnpaidAvgM = liveUnpaidOrdersTotalElementsM > 0
        ? todayUnpaidValueM / liveUnpaidOrdersTotalElementsM
        : 0;
    const todayUnpaidServersM = useMemo(() => {
        const source = allUnpaidForByServerM.length > 0 ? allUnpaidForByServerM : liveUnpaidOrdersM;
        const map = new Map<string, { name: string; count: number; amount: number }>();
        source.forEach((o) => {
            const key = o.serverName?.trim() || 'Unknown';
            const existing = map.get(key) ?? { name: key, count: 0, amount: 0 };
            map.set(key, { name: existing.name, count: existing.count + 1, amount: existing.amount + (Number(o.amount) || 0) });
        });
        return [...map.values()].sort((a, b) => b.amount - a.amount);
    }, [allUnpaidForByServerM, liveUnpaidOrdersM]);

    // Unified unpaid values — always from Redux ClickHouse (mobile)
    const displayUnpaidCountM = liveUnpaidOrdersTotalElementsM;
    const displayUnpaidValueM = todayUnpaidValueM;
    const displayUnpaidAvgM = todayUnpaidAvgM;
    const displayUnpaidServersM = todayUnpaidServersM;

    // Snapshot report-level "Export KPIs" — mirrors exactly what each stamp KPI card shows on screen.
    kpiExportRefM.current = {
        activeDate: activeDateFilter,
        dateRange,
        chDates: chDatesView,
        orderSourceVal,
        token,
        channel,
        isToday: data.isToday,
        isThirdParty,
        netSales, totalOrders,
        unpaidCount: liveUnpaidOrdersFailureM ? 0 : displayUnpaidCountM,
        unpaidAmount: displayUnpaidValueM,
        avgOrder,
        serviceCharge: serviceChargeM?.amount ?? null,
        refund: data.isToday ? (liveRefundSummaryFailureM ? 0 : liveRefundTotalAmtM) : cancelledAmount,
        tipsAndGratuity: liveStaffPerformanceFailureM ? 0 : tipsAndGratuityM,
        // Staff Worked — same source as the on-screen "Staff Worked" tile
        // (/sales/live/v2/staff-active-clocking-v2's staffWorked field), for both
        // Today and historical, not the legacy staff-active-clocking roster length.
        staffWorked: staffRosterV2FailureM ? 0 : staffRosterV2StaffWorkedM,
        voidCount: liveVoidSummaryFailureM ? 0 : voidCount,
        compCount: liveCompOrderLogFailureM ? 0 : compCount,
        compAmount,
        // CANC-1 — Cancelled orders, same source as the on-screen "Cancelled" tile.
        cancelledCount: cancelledOrdersFailureM ? 0 : cancelledOrdersM.length,
        discounts,
        guestCount: guests,
        spendPerGuest,
    };

    const unpaidPct = (displayUnpaidCountM > 0 && unpaidPriorCount != null && unpaidPriorCount > 0)
        ? ((displayUnpaidCountM - unpaidPriorCount) / unpaidPriorCount) * 100
        : null;

    const itemsTotalElementsM = data.isToday
        ? liveItemSalesV2TotalElementsM
        : (Number(data.categoryItemSalesTotalElements) || 0);


    const topItemsMobile = useMemo(() => {
        // Today: v2 Redux only, Historical: categoryItemSales
        let list: any[] | null = null;
        if (data.isToday && liveItemSalesV2.length > 0) {
            list = liveItemSalesV2;
        }
        if (!list) {
            const unwrapped = unwrapResMobile(data.categoryItemSales);
            list = Array.isArray(unwrapped) ? unwrapped : (
                unwrapped?.content ?? unwrapped?.data ?? unwrapped?.items ?? unwrapped?.records ??
                unwrapped?.categories ?? unwrapped?.channelList ?? unwrapped?.orders ?? null
            );
            if (!Array.isArray(list) && unwrapped && typeof unwrapped === 'object') {
                for (const val of Object.values(unwrapped)) {
                    if (Array.isArray(val)) { list = val; break; }
                }
            }
        }
        if (!Array.isArray(list) || list.length === 0) return [];

        // Deep pick for numeric values (matches desktop pickNumDeep)
        const pickNum = (obj: any, keys: any) => {
            if (!obj || typeof obj !== 'object') return 0;
            for (const key of keys) {
                const v = obj[key];
                if (v != null && v !== '' && !Number.isNaN(Number(v))) return Number(v);
            }
            for (const v of Object.values(obj)) {
                if (v && typeof v === 'object' && !Array.isArray(v)) {
                    const nested = pickNum(v, keys);
                    if (nested > 0) return nested;
                }
            }
            return 0;
        };

        const mapped = list
            .map((item) => ({
                name: item.itemName ?? item.name ?? item.menuItemName ?? item.label ?? '—',
                qty: pickNum(item, ['quantity', 'salesQty', 'sales_qty', 'itemCount', 'quantitySold', 'qty', 'count']),
                rev: pickNum(item, ['price', 'totalItemPrice', 'grossSales', 'gross_sales', 'itemTotal', 'totalSales', 'amount', 'revenue']),
                isWeight: Number(item.isWeight ?? 0),
            }))
            .filter((item) => item.name !== '—' || item.rev > 0 || item.qty > 0)
            .sort((a, b) => b.rev - a.rev);
        return mapped;
    }, [data.categoryItemSales, liveItemSalesV2, data.isToday]);

    // Accumulate mobile items across pages for export consistency
    useEffect(() => {
        if (topItemsMobile.length === 0) return;
        const existingNames = new Set(allTopItemsMobileRef.current.map((i) => i.name));
        const newItems = topItemsMobile.filter((i) => !existingNames.has(i.name));
        if (newItems.length > 0) {
            allTopItemsMobileRef.current = [...allTopItemsMobileRef.current, ...newItems];
        }
    }, [topItemsMobile]);

    // grandTotal + compAmount + otherCharges feed the Net Sales drill's sales-breakdown
    // waterfall (ZAR-1462) — grandTotal is its "Total Collected" end row and otherCharges
    // its "Others" row, both taken straight from the stamp KPI sources. `gratuityM` is the
    // one already declared for the ZAR-1762 unified Tips & Gratuity card above.
    const grandTotalM = num(snapshotDetails?.grandTotal);
    const otherChargesM = num(snapshotDetails?.otherCharges);
    const snapshotValues = useMemo(() => ({
        netSales, totalOrders, avgOrder, cancelledAmount, cancelledCount, activeStaff,
        voidCount, voidAmount, tips, avgTipPerOrder, tipRatePercent, tax, discounts, checkInCount, avgWait, guestCount, salesPerHour,
        compAmount, gratuity: gratuityM, grandTotal: grandTotalM, otherCharges: otherChargesM,
        serviceCharge: serviceChargeM ? serviceChargeM.amount : null, serviceChargeRetained: serviceChargeM?.retained ?? false,
        // item / fee refund split off snapshot-details — feeds the Net Sales drill's waterfall Refunds row.
        refund: snapshotDetails?.refund ?? null, feeRefund: snapshotDetails?.feeRefund ?? null,
    }), [netSales, totalOrders, avgOrder, cancelledAmount, cancelledCount, activeStaff, voidCount, voidAmount, tips, avgTipPerOrder, tipRatePercent, tax, discounts, checkInCount, avgWait, guestCount, salesPerHour, compAmount, gratuityM, grandTotalM, otherChargesM, serviceChargeM, snapshotDetails]);

    // snapshotKpisLoading: snapshotValues above fall back to legacy `billedSrcM` while
    // snapshot-details refetches (date/channel change) — drills must shimmer their
    // snapshotValues-fed slots instead of flashing the previous range's numbers (SSE-18).
    // SW3 — lets the embedded Net Sales drill's waterfall switch this screen's own drillView
    // directly (same-page, no navigation needed since we're already on Snapshot mobile), and
    // remembers to return here (scrolled to the KPI row) when the user backs out.
    const onWaterfallDeductionClick = (key: string) => {
        // REF-2 — the waterfall's own "Refunds ›" row opens the ONE shared Refunds
        // drill-in too, same destination as the Refund KPI tile.
        if (key === 'refunds') {
            navigate(refundsPath({ origin: 'snapshot', dateFilter: activeDateFilter, startDate: dateRange.startDate, endDate: dateRange.endDate, source: orderSourceVal === 'direct' ? 'direct' : orderSourceVal === 'thirdparty' ? 'thirdparty' : 'overall' }));
            return;
        }
        setDrillOrigin('net-sales');
        setDrillView(key);
    };
    const drillProps = { onBack: goBack, initialDateFilter: activeDateFilter, initialDate: selectedDate, snapshotValues, orderSource: orderSourceVal, snapshotKpisLoading: loading || snapshotDetailsLoadingM, onDeductionClick: onWaterfallDeductionClick, scrollToKpiOnMount: scrollToNetSalesKpiRef.current };
    const refundDrillProps = {
        prefetchedRefundSummary: liveRefundSummaryM,
        refundSummaryLoading: liveRefundSummaryLoadingM,
        refundSummaryFailure: liveRefundSummaryFailureM,
        prefetchedRefundDetails: liveRefundDetailsM,
        refundDetailsLoading: liveRefundDetailsLoadingM,
        refundDetailsFailure: liveRefundDetailsFailureM,
        refundDetailsPagination: {
            page: refundLogPageM,
            totalPages: liveRefundDetailsTotalPagesM,
            totalElements: liveRefundDetailsTotalElementsM,
            pageSize: REFUND_PAGE_SIZE_M,
            onPageChange: setRefundLogPageM,
            onRetry: () => dispatch(fetchLiveRefundDetailsRequest(locationId, refundLogPageM, REFUND_PAGE_SIZE_M, orderSourceVal, chDatesView.startDate, chDatesView.endDate)),
        },
        prefetchedStaffRefunds: liveStaffRefundsM,
        staffRefundsLoading: liveStaffRefundsLoadingM,
        staffRefundsFailure: liveStaffRefundsFailureM,
    };

    // Void log rows and reasons for DrillVoids — FE-paginated slice from all Redux items.
    // DrillVoids expects { voidedReasons, orderCount, removedQty, voidedAmount } — removedQty
    // (voided item quantity) drives the Void Reasons count, same as the desktop drill.
    // Paginate by ORDER (not by raw item row), like the desktop log: DrillVoids collapses rows
    // sharing an order number into one card, so slicing raw rows could split a multi-item order
    // across pages and show only some of its items.
    const voidOrderGroupsM = (() => {
        const map = new Map<string, typeof liveVoidDetailsM>();
        liveVoidDetailsM.forEach((v, i) => {
            const key = v.orderNo || `_${i}`;
            const g = map.get(key);
            if (g) g.push(v); else map.set(key, [v]);
        });
        return Array.from(map.values());
    })();
    const feVoidTotalPagesM = Math.max(1, Math.ceil(voidOrderGroupsM.length / VOID_DETAILS_PAGE_SIZE_M));
    const voidLogRowsM = voidOrderGroupsM
        .slice(voidDetailsPageM * VOID_DETAILS_PAGE_SIZE_M, (voidDetailsPageM + 1) * VOID_DETAILS_PAGE_SIZE_M)
        .flat();
    const voidReasonRowsM = liveVoidSummaryM.map((item) => ({
        voidedReasons: item.cancelReason,
        orderCount: item.orderCount,
        removedQty: item.removedQty,
        voidedAmount: item.cancelledAmt,
    }));
    const voidPaginationProps = {
        page: voidDetailsPageM,
        totalPages: feVoidTotalPagesM,
        totalElements: voidOrderGroupsM.length,
        pageSize: VOID_DETAILS_PAGE_SIZE_M,
        loading: liveVoidDetailsLoadingM,
        onPageChange: setVoidDetailsPageM,
    };
    // Voids by Staff alert — aggregated from the FULL void log (liveVoidDetailsM, not the
    // paginated voidLogRowsM slice above) so a staff member who voided ≥2 items anywhere in
    // the period is flagged, matching the desktop drill's alertStaffVoids.
    const voidStaffAlertsM = useMemo(() => {
        const map: Record<string, { name: string; count: number; amount: number }> = {};
        liveVoidDetailsM.forEach((v) => {
            const name = (v.staffName ?? '').trim();
            if (!name) return;
            if (!map[name]) map[name] = { name, count: 0, amount: 0 };
            map[name].count += Number(v.voidedQty) || 1; // voided item quantity, not log rows
            map[name].amount += Number(v.cancelledAmt) || 0;
        });
        return Object.values(map).filter((s) => s.count >= 2).sort((a, b) => b.count - a.count);
    }, [liveVoidDetailsM]);

    const logLoadingM = liveUnpaidLogLoadingM;
    const logItemsM = liveUnpaidLogM;
    const logTotalPagesM = liveUnpaidLogTotalPagesM;
    const logPageM = unpaidLogPageTodayM - 1;
    const setLogPageM = (p: number) => setUnpaidLogPageTodayM(p + 1);
    // One view model for whichever Unpaid-log segment is on screen, so the card list,
    // header and pagination below render through a single path.
    const unpaidIsCancelledM = unpaidStatusM === 'cancelled';
    const segItemsM: (UnpaidOrderItem | OrderLogItem)[] = unpaidIsCancelledM ? orderLogOrdersM : logItemsM;
    const segLoadingM = unpaidIsCancelledM ? orderLogLoadingM : logLoadingM;
    const segFailureM = unpaidIsCancelledM ? orderLogFailureM : liveUnpaidLogFailureM;
    const segTotalElementsM = unpaidIsCancelledM ? orderLogTotalElementsM : liveUnpaidLogTotalElementsM;
    const segTotalPagesM = unpaidIsCancelledM ? orderLogTotalPagesM : logTotalPagesM;
    const segPageM = unpaidIsCancelledM ? unpaidCancelPageM - 1 : logPageM; // 0-based display
    const setSegPageM = (p: number) => (unpaidIsCancelledM ? setUnpaidCancelPageM(p + 1) : setLogPageM(p));
    // By Server: always from Redux ClickHouse (mobile)
    const pageUnpaidServersM = todayUnpaidServersM;
    const maxUnpaidValM = pageUnpaidServersM.reduce((m, s) => Math.max(m, s.amount), 0) || 1;
    const UnpaidDrillMobile = () => {
        if (liveUnpaidOrdersLoadingM) {
            return (
                <div style={{ padding: '12px 16px', flex: 1, overflowY: 'auto' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                        {['Total Unpaid', 'Total Value', 'Avg Unpaid'].map(label => (
                            <div key={label} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '10px 12px' }}>
                                <div style={{ fontSize: 10, color: C.t3, marginBottom: 4 }}>{label}</div>
                                <Bone w={70} h={16} />
                            </div>
                        ))}
                    </div>
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '12px', marginBottom: 12 }}>
                        <Bone w={80} h={14} mb={10} />
                        {[1, 2, 3].map(i => <Bone key={i} w={'100%' as any} h={10} mb={8} />)}
                    </div>
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '12px' }}>
                        <Bone w={80} h={14} mb={10} />
                        {[1, 2, 3].map(i => <Bone key={i} w={'100%' as any} h={32} mb={8} />)}
                    </div>
                </div>
            );
        }
        // SNAP-DRILL-ERR-1 — a failed top-level fetch used to blank the whole drill for one
        // Retry button. By Server now gets its own scoped SectionError (Order Log already had
        // one below), mirroring the Cancelled Orders drill shell; displayUnpaidCountM/ValueM/AvgM
        // naturally read 0 on failure since the reducer resets liveUnpaidOrdersTotalElements.
        return (
            <div style={{ padding: '12px 16px', flex: 1, overflowY: 'auto' }}>
                <div style={{ fontSize: 11, color: C.t3, marginBottom: 12 }}>
                    {fmtNumMobile(displayUnpaidCountM)} orders · {fmtCurrencyMobile(displayUnpaidValueM)} total
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ fontSize: 10, color: C.t3, marginBottom: 4 }}>Total Unpaid</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: C.red }}>{fmtNumMobile(displayUnpaidCountM)}</div>
                    </div>
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ fontSize: 10, color: C.t3, marginBottom: 4 }}>Total Value</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: C.orange }}>{fmtCurrencyMobile(displayUnpaidValueM)}</div>
                    </div>
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '10px 12px', gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: C.t3, marginBottom: 4 }}>Avg Unpaid</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: C.t1 }}>{fmtCurrencyMobile(displayUnpaidAvgM)}</div>
                    </div>
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '12px', marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.t1, marginBottom: 10 }}>By Server</div>
                    {liveUnpaidOrdersFailureM ? (
                        <SectionError
                            kpiName="unpaid by server"
                            size="small"
                            onRetry={() => dispatch(fetchLiveUnpaidOrdersRequest({ locationId, size: 10, ...chDatesView, ...orderSourceParam }))}
                        />
                    ) : allUnpaidForByServerLoadingM ? (
                        <div>
                            {[1, 2, 3].map(i => <Bone key={i} w={'100%' as any} h={10} mb={8} />)}
                        </div>
                    ) : pageUnpaidServersM.length === 0 ? (
                        <SectionEmpty kpiName="unpaid by server" size="small" />
                    ) : (
                        [...pageUnpaidServersM]
                            .sort((a: any, b: any) => Number(b?.amount ?? b?.totalAmount ?? 0) - Number(a?.amount ?? a?.totalAmount ?? 0))
                            .map((s: any, i: number) => {
                                const val = Number(s?.amount ?? s?.totalAmount ?? 0);
                                const count = Number(s?.count ?? s?.orderCount ?? 0);
                                const name = s?.name ?? s?.serverName ?? s?.staffName ?? `Server ${i + 1}`;
                                const pct = Math.min((val / maxUnpaidValM) * 100, 100);
                                return (
                                    <div key={i} style={{ marginBottom: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                            <span style={{ fontSize: 11, color: C.t1 }}>{name}</span>
                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                {count > 0 && <span style={{ fontSize: 10, color: C.t3 }}>{count} orders</span>}
                                                <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{fmtCurrencyMobile(val)}</span>
                                            </div>
                                        </div>
                                        <div style={{ height: 4, background: C.hover, borderRadius: 2 }}>
                                            <div style={{ height: 4, borderRadius: 2, width: `${pct}%`, background: C.orange, opacity: 0.7 }} />
                                        </div>
                                    </div>
                                );
                            })
                    )}
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>{unpaidIsCancelledM ? 'Order Log (Cancelled Orders)' : 'Order Log (Unpaid Orders)'}</div>
                            {segTotalElementsM > 0
                                ? <div style={{ fontSize: 10, color: C.t3, marginTop: 2 }}>{segTotalElementsM} orders</div>
                                : null}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <OrderLogDensityButton value={unpaidCardDensityM} onChange={setUnpaidCardDensityM} />
                        {segItemsM.length > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                variant="mobile"
                                title={unpaidIsCancelledM ? orderLogExportTitle('cancelled') : 'Order Log (Unpaid Orders)'}
                                filename={unpaidIsCancelledM ? 'order-log-cancelled-orders' : 'order-log-unpaid-orders'}
                                buildExport={async (format, opts) => {
                                    const loc = opts?.locationId ?? locationId;
                                    // Each segment exports its own endpoint, under the active
                                    // search and Order Type filter.
                                    if (unpaidIsCancelledM) {
                                        const params = { locationId: loc, sortBy: 'orderTime', sortDir: 'desc' as const, search: unpaidDebouncedSearchM || undefined, channelName: unpaidOrderTypesM.length ? unpaidOrderTypesM.join(',') : undefined, ...chDatesView, ...orderSourceParam, type: 'cancel' as const };
                                        let cancelSize = orderLogTotalElementsM || 1;
                                        if (opts?.locationId) {
                                            const probe: any = await getOrderLogExportApi({ ...params, page: 1, size: 1 });
                                            cancelSize = Number(probe?.totalElements) || 1;
                                        }
                                        const res: any = await getOrderLogExportApi({ ...params, page: 1, size: cancelSize });
                                        // ORD-1.6 — the selected card view decides the columns.
                                        return orderLogRowsToExportTable(toOrderLogRows(res?.orders ?? (Array.isArray(res) ? res : [])), cardViewExportColumns(unpaidCardDensityM, { hasTableService: orderLogDineInAvailableM, status: 'cancelled', hasServiceCharge: scEnabled }));
                                    }
                                    let size = liveUnpaidLogTotalElementsM || 1;
                                    if (opts?.locationId) {
                                        const probe: any = await getLiveUnpaidOrdersApi({ locationId: loc, page: 1, size: 1, sortBy: unpaidSortByM, sortDir: unpaidSortDirM, search: unpaidDebouncedSearchM || undefined, orderType: unpaidOrderTypesM.length ? unpaidOrderTypesM.join(',') : undefined, ...chDatesView, ...orderSourceParam });
                                        size = Number(probe?.totalElements) || 1;
                                    }
                                    const response: any = await getLiveUnpaidOrdersApi({ locationId: loc, page: 1, size, sortBy: unpaidSortByM, sortDir: unpaidSortDirM, search: unpaidDebouncedSearchM || undefined, orderType: unpaidOrderTypesM.length ? unpaidOrderTypesM.join(',') : undefined, ...chDatesView, ...orderSourceParam });
                                    // ORD-1.6 — the selected card view decides the columns.
                                    return orderLogRowsToExportTable(toOrderLogRows(response?.orders ?? []), cardViewExportColumns(unpaidCardDensityM, { hasTableService: liveUnpaidLogDineInAvailableM, hasServiceCharge: scEnabled }));
                                }}
                            />
                        )}
                        </div>
                    </div>

                    {/* Unpaid / Cancelled segments — same control as the Paid log, full-width
                        on mobile. Both counts are the period totals behind the Unpaid and
                        Cancelled KPI cards, so the pills can't disagree with the cards. */}
                    <OrderLogStatusSegments
                        value={unpaidStatusM}
                        onChange={(next: 'unpaid' | 'cancelled') => { setUnpaidStatusM(next); setUnpaidSelectedOrderM(null); setUnpaidLogPageTodayM(1); setUnpaidCancelPageM(1); }}
                        primaryKey="unpaid"
                        primaryLabel="Unpaid"
                        paidCount={liveUnpaidOrdersFailureM ? 0 : displayUnpaidCountM}
                        cancelledCount={cancelledOrdersFailureM ? 0 : cancelledOrdersM.length}
                        fullWidth
                    />

                    {/* Filter row: search + Order Type */}
                    <div style={{ position: 'relative', marginBottom: 8 }}>
                        <input
                            value={unpaidSearchM}
                            onChange={e => setUnpaidSearchM(e.target.value)}
                            placeholder="Search by order ID, table no., customer name"
                            style={{ width: '100%', height: 32, padding: unpaidSearchM ? '0 28px 0 10px' : '0 10px', border: `1px solid ${C.cardBorder}`, borderRadius: 6, fontSize: 10, color: C.t1, background: C.surface, outline: 'none', fontFamily: C.font, boxSizing: 'border-box' }}
                        />
                        {unpaidSearchM && (
                            <button onClick={() => setUnpaidSearchM('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', color: C.t3 }} aria-label="Clear search">
                                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>close</span>
                            </button>
                        )}
                    </div>
                    {/* Order Type multi-select (mobile) */}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                        {unpaidTypeMenuOpenM && <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setUnpaidTypeMenuOpenM(false)} />}
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => setUnpaidTypeMenuOpenM(v => !v)}
                                style={{ height: 28, padding: '0 10px', border: `1px solid ${unpaidOrderTypesM.length ? C.accent : C.cardBorder}`, borderRadius: 6, fontSize: 11, color: unpaidOrderTypesM.length ? C.accent : C.t2, background: unpaidOrderTypesM.length ? `${C.accent}10` : C.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', fontFamily: C.font }}
                            >
                                Order Type {unpaidOrderTypesM.length > 0 ? `(${unpaidOrderTypesM.length})` : ''}<span style={{ fontSize: 9 }}>▾</span>
                            </button>
                            {unpaidTypeMenuOpenM && (
                                <div style={{ position: 'absolute', top: 32, left: 0, zIndex: 200, background: C.surface, border: `1px solid ${C.cardBorder}`, borderRadius: 8, boxShadow: C.shadow, padding: '6px 0', minWidth: 170, maxHeight: 220, overflowY: 'auto' }}>
                                    {(channelNamesM.length > 0 ? channelNamesM : []).map(ot => (
                                        <div key={ot} onClick={() => setUnpaidOrderTypesM(prev => prev.includes(ot) ? prev.filter(x => x !== ot) : [...prev, ot])} style={{ padding: '7px 12px', fontSize: 12, color: unpaidOrderTypesM.includes(ot) ? C.accent : C.t1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: unpaidOrderTypesM.includes(ot) ? `${C.accent}08` : 'transparent' }}>
                                            <span style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${unpaidOrderTypesM.includes(ot) ? C.accent : C.t3}`, background: unpaidOrderTypesM.includes(ot) ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                {unpaidOrderTypesM.includes(ot) && <span style={{ color: '#fff', fontSize: 9, lineHeight: 1 }}>✓</span>}
                                            </span>
                                            {ot}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {unpaidOrderTypesM.map(ot => (
                            <span key={ot} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px 2px 8px', background: `${C.accent}12`, borderRadius: 12, fontSize: 10, color: C.accent, fontWeight: 500 }}>
                                {ot}<span onClick={() => setUnpaidOrderTypesM(prev => prev.filter(x => x !== ot))} style={{ cursor: 'pointer', fontSize: 11, lineHeight: 1, opacity: 0.7 }}>×</span>
                            </span>
                        ))}
                        {(unpaidSearchM || unpaidOrderTypesM.length > 0) && (
                            <button onClick={() => { setUnpaidSearchM(''); setUnpaidOrderTypesM([]); }} style={{ height: 28, padding: '0 8px', border: 'none', borderRadius: 6, fontSize: 11, color: C.accent, background: 'transparent', cursor: 'pointer', fontFamily: C.font, textDecoration: 'underline' }}>Clear</button>
                        )}
                    </div>
                    {segLoadingM
                        ? Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} style={{ padding: '10px 0', borderBottom: `1px solid ${C.divider}` }}>
                                <Bone w={'70%' as any} h={12} mb={6} />
                                <Bone w={'40%' as any} h={10} />
                            </div>
                        ))
                        : segFailureM
                            ? <SectionError
                                kpiName="order log"
                                size="small"
                                onRetry={() => (unpaidIsCancelledM
                                    ? dispatch(fetchOrderLogRequest({ locationId, page: unpaidCancelPageM, size: UNPAID_LOG_PAGE_SIZE_TODAY_M, sortBy: 'orderTime', sortDir: 'desc', search: unpaidDebouncedSearchM || undefined, channelName: unpaidOrderTypesM.length ? unpaidOrderTypesM.join(',') : undefined, ...chDatesView, ...orderSourceParam, type: 'cancel' as const }))
                                    : dispatch(fetchLiveUnpaidLogRequest({ locationId, page: unpaidLogPageTodayM, size: UNPAID_LOG_PAGE_SIZE_TODAY_M, sortBy: unpaidSortByM, sortDir: unpaidSortDirM, search: unpaidDebouncedSearchM || undefined, orderType: unpaidOrderTypesM.length ? unpaidOrderTypesM.join(',') : undefined, ...chDatesView, ...orderSourceParam })))}
                            />
                            : segItemsM.length === 0
                                ? <SectionEmpty kpiName={unpaidIsCancelledM ? 'cancelled orders' : 'unpaid orders'} size="small" />
                                : toOrderLogRows(segItemsM).map((row, i, arr) => (
                                    <OrderLogCard
                                        key={row.key}
                                        row={row}
                                        density={unpaidCardDensityM}
                                        fmtMoney={fmtCurrencyCellMobile}
                                        last={i === arr.length - 1}
                                        onOpen={r => setUnpaidSelectedOrderM(r.raw)}
                                    />
                                ))
                    }
                    {segItemsM.length > 0 && (() => {
                        const logPageSizeM = UNPAID_LOG_PAGE_SIZE_TODAY_M;
                        const logStartM = segPageM * logPageSizeM + 1;
                        const logEndM = Math.min(segPageM * logPageSizeM + segItemsM.length, segTotalElementsM);
                        const totalM = segTotalPagesM || 1;
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.divider}` }}>
                                <span style={{ fontSize: 11, color: C.t3 }}>Showing {logStartM}–{logEndM} of {segTotalElementsM} orders</span>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <button onClick={() => setSegPageM(segPageM - 1)} disabled={segPageM === 0}
                                        style={{ height: 28, padding: '0 10px', border: `1px solid ${C.cardBorder}`, borderRadius: 6, fontSize: 12, color: segPageM === 0 ? C.t3 : C.t1, background: segPageM === 0 ? C.hover : C.surface, cursor: segPageM === 0 ? 'default' : 'pointer' }}>‹</button>
                                    <span style={{ fontSize: 11, color: C.t3 }}>{segPageM + 1} / {totalM}</span>
                                    <button onClick={() => setSegPageM(segPageM + 1)} disabled={segPageM >= totalM - 1}
                                        style={{ height: 28, padding: '0 10px', border: `1px solid ${C.cardBorder}`, borderRadius: 6, fontSize: 12, color: segPageM >= totalM - 1 ? C.t3 : C.t1, background: segPageM >= totalM - 1 ? C.hover : C.surface, cursor: segPageM >= totalM - 1 ? 'default' : 'pointer' }}>›</button>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>
        );
    };

    if (drillView) {
        // ── Build compDrillView based on Today vs Historical ─────────────────
        let compDrillView: React.ReactNode;
        if (data.isToday) {
            if (liveCompOrderLogLoadingM) {
                compDrillView = (
                    <div style={{ padding: '12px 16px 80px', flex: 1, overflowY: 'auto' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                            {['Total Comps', 'Total Value', 'Avg Comp', 'Top Reason'].map(label => (
                                <div key={label} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '10px 12px' }}>
                                    <div style={{ fontSize: 9, fontWeight: 600, color: C.t3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{label}</div>
                                    <Bone w={70} h={16} />
                                </div>
                            ))}
                        </div>
                        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Comp Reasons</div>
                            {[1, 2, 3, 4].map(i => <Bone key={i} w={'100%' as any} h={10} mb={8} />)}
                        </div>
                        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Comp by Staff</div>
                            {[1, 2, 3].map(i => <Bone key={i} w={'100%' as any} h={10} mb={8} />)}
                        </div>
                        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px' }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Comp Order Log</div>
                            {[1, 2, 3, 4, 5].map(i => <Bone key={i} w={'100%' as any} h={32} mb={8} />)}
                        </div>
                    </div>
                );
            } else if (liveCompOrderLogFailureM) {
                compDrillView = (
                    <div style={{ padding: '12px 16px', flex: 1, overflowY: 'auto' }}>
                        <SectionError
                            kpiName="complimentary orders"
                            size="medium"
                            onRetry={() => {
                                dispatch(fetchLiveCompOrderLogRequest({ locationId, page: compLogPageTodayM + 1, size: COMP_PAGE_SIZE_M, ...chDatesView, ...orderSourceParam }));
                                dispatch(fetchLiveCompReasonsRequest({ locationId, ...chDatesView, ...orderSourceParam }));
                                dispatch(fetchLiveCompByStaffRequest({ locationId, ...chDatesView, ...orderSourceParam }));
                            }}
                        />
                    </div>
                );
            } else {
            const todayTotalM = liveCompOrderLogM.reduce((s, o) => s + (Number(o.amount) || 0), 0);
            const todayCountM = liveCompOrderLogTotalElementsM;
            const avgCompM = todayCountM > 0 ? todayTotalM / todayCountM : 0;
            const topReasonTodayM = [...liveCompReasonsM].sort((a, b) => b.occurrenceCount - a.occurrenceCount)[0]?.cancelReason ?? '—';
            const maxOccurrenceM = liveCompReasonsM.reduce((m, r) => Math.max(m, r.occurrenceCount), 1);
            const alertStaffTodayM = [...liveCompByStaffM].sort((a, b) => b.compCount - a.compCount).slice(0, 1).filter(s => s.compCount >= 5);
            const maxStaffValueM = liveCompByStaffM.reduce((m, s) => Math.max(m, s.totalCompValue), 1);
            compDrillView = (
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 80px' }}>
                    <div style={{ fontSize: 11, color: C.t3, marginBottom: 12 }}>
                        {fmtNumMobile(todayCountM)} comps · {fmtCurrencyMobile(todayTotalM)} total
                    </div>
                    {alertStaffTodayM.map(s => (
                        <div key={s.staffName} style={{ background: `${C.orange ?? '#f97316'}18`, border: `1px solid ${(C.orange ?? '#f97316')}44`, borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.t1 }}>
                            <span style={{ fontWeight: 600, color: C.orange ?? '#f97316' }}>⚠ {s.staffName}</span> has applied {s.compCount} of {todayCountM} comps ({fmtCurrencyMobile(s.totalCompValue)} total).
                        </div>
                    ))}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                        {[
                            { label: 'Total Comps', value: fmtNumMobile(todayCountM) },
                            { label: 'Total Value', value: fmtCurrencyMobile(todayTotalM) },
                            { label: 'Avg Comp', value: fmtCurrencyMobile(avgCompM) },
                            { label: 'Top Reason', value: liveCompReasonsLoadingM ? <Bone w={70} h={13} r={4} /> : topReasonTodayM },
                        ].map(k => (
                            <div key={k.label} style={{ background: C.card, borderRadius: 10, padding: '10px 12px', border: `1px solid ${C.cardBorder}` }}>
                                <div style={{ fontSize: 9, fontWeight: 600, color: C.t3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{k.label}</div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.value}</div>
                            </div>
                        ))}
                    </div>
                    {!liveCompOrderLogLoadingM && todayCountM === 0 ? (
                        <SectionEmpty kpiName="complimentary orders" />
                    ) : (
                        <>
                            {/* Comp Reasons */}
                            <div style={{ background: C.card, borderRadius: 12, padding: '12px 14px', border: `1px solid ${C.cardBorder}`, marginBottom: 10 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Comp Reasons</div>
                                {liveCompReasonsLoadingM ? (
                                    <div>
                                        {[1, 2, 3, 4].map(i => (
                                            <div key={i} style={{ marginBottom: 8 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                                    <Bone h={11} r={3} style={{ width: `${45 + i * 10}%` }} />
                                                    <Bone w={24} h={11} r={3} />
                                                </div>
                                                <Bone h={4} r={2} />
                                            </div>
                                        ))}
                                    </div>
                                ) : liveCompReasonsFailureM ? (
                                    <SectionError kpiName="comp reasons" onRetry={() => dispatch(fetchLiveCompReasonsRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={60} />
                                ) : liveCompReasonsM.length === 0 ? (
                                    <SectionEmpty kpiName="comp reasons" size="small" minHeight={60} />
                                ) : liveCompReasonsM.slice(0, 6).map((r, i) => (
                                    <div key={i} style={{ marginBottom: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                            <span style={{ fontSize: 11, color: C.t2 }}>{r.cancelReason}</span>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <span style={{ fontSize: 10, color: C.t3 }}>{r.occurrenceCount}x</span>
                                                <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{fmtCurrencyMobile(r.totalAmount)}</span>
                                            </div>
                                        </div>
                                        <div style={{ height: 4, background: C.hover ?? '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${Math.round((r.occurrenceCount / maxOccurrenceM) * 100)}%`, background: C.green ?? '#22c55e', borderRadius: 2, opacity: 0.7 }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Comp by Staff */}
                            <div style={{ background: C.card, borderRadius: 12, padding: '12px 14px', border: `1px solid ${C.cardBorder}`, marginBottom: 10 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Comp by Staff</div>
                                {liveCompByStaffLoadingM ? (
                                    <div>
                                        {[1, 2, 3, 4].map(i => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < 4 ? `1px solid ${C.divider}` : 'none' }}>
                                                <Bone h={12} r={3} style={{ width: `${45 + i * 10}%` }} />
                                                <Bone w={50} h={12} r={3} />
                                            </div>
                                        ))}
                                    </div>
                                ) : liveCompByStaffFailureM ? (
                                    <SectionError kpiName="comp by staff" onRetry={() => dispatch(fetchLiveCompByStaffRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={60} />
                                ) : liveCompByStaffM.length === 0 ? (
                                    <SectionEmpty kpiName="comp by staff" size="small" minHeight={60} />
                                ) : liveCompByStaffM.slice(0, 5).map((s, i) => (
                                    <div key={i} style={{ padding: '6px 0', borderBottom: i < liveCompByStaffM.slice(0, 5).length - 1 ? `1px solid ${C.divider}` : 'none' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                            <span style={{ fontSize: 12, color: C.accent, fontWeight: 500 }}>{s.staffName}</span>
                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                <span style={{ fontSize: 10, color: C.t3 }}>{s.compCount}x</span>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{fmtCurrencyMobile(s.totalCompValue)}</span>
                                            </div>
                                        </div>
                                        <div style={{ height: 3, background: C.hover ?? '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${Math.round((s.totalCompValue / maxStaffValueM) * 100)}%`, background: C.accent, borderRadius: 2, opacity: 0.45 }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Comp Order Log */}
                            <div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Comp Order Log</div>
                                    {liveCompOrderLogM.length > 0 && (
                                        <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                            variant="mobile"
                                            title="Comp Order Log"
                                            filename="snapshot-comp-order-log-today"
                                            buildExport={async (format, opts) => {
                                                const loc = opts?.locationId ?? locationId;
                                                let total = liveCompOrderLogTotalElementsM || 1;
                                                if (opts?.locationId) {
                                                    const probe: any = await getLiveCompOrderLogApi({ locationId: loc, page: 1, size: 1, ...chDatesView, ...orderSourceParam });
                                                    total = probe?.totalElements ?? probe?.total ?? 1;
                                                }
                                                const res: any = await getLiveCompOrderLogApi({ locationId: loc, page: 1, size: total || 1, ...chDatesView, ...orderSourceParam });
                                                const list: any[] = Array.isArray(res?.orders) ? res.orders : (Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : liveCompOrderLogM));
                                                return {
                                                    headers: ['Order ID', 'Time', 'Staff', 'Reason', 'Items', 'Amount'],
                                                    rows: list.map((row: any) => {
                                                        const displayTime = (() => { const m = String(row.orderTime).match(/(\d{1,2}:\d{2}\s*(?:[AaPp][Mm])?)/); return m ? m[1].trim() : row.orderTime; })();
                                                        const itemDisplay = Array.isArray(row.items) ? row.items[0] ?? '—' : String(row.items ?? '—');
                                                        return [row.orderNo, displayTime, row.staffName, row.cancelReason, itemDisplay, formatMoneyForExport(row.amount)];
                                                    }),
                                                };
                                            }}
                                        />
                                    )}
                                </div>
                                {liveCompOrderLogLoadingM ? (
                                    <div>
                                        {[1, 2, 3, 4].map(i => (
                                            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '8px 0', borderBottom: i < 4 ? `1px solid ${C.divider}` : 'none' }}>
                                                <Bone w={50} h={11} r={3} />
                                                <Bone h={11} r={3} style={{ flex: 1 }} />
                                                <Bone w={45} h={11} r={3} />
                                            </div>
                                        ))}
                                    </div>
                                ) : liveCompOrderLogFailureM ? (
                                    <SectionError kpiName="comp order log" onRetry={() => dispatch(fetchLiveCompOrderLogRequest({ locationId, page: compLogPageTodayM + 1, size: COMP_PAGE_SIZE_M, ...chDatesView, ...orderSourceParam }))} minHeight={80} />
                                ) : liveCompOrderLogM.length === 0 ? (
                                    <SectionEmpty kpiName="comp order log" minHeight={80} />
                                ) : (
                                    <>
                                        {liveCompOrderLogM.map((row, i) => {
                                            const displayTime = (() => { const m = String(row.orderTime).match(/(\d{1,2}:\d{2}\s*(?:[AaPp][Mm])?)/); return m ? m[1].trim() : row.orderTime; })();
                                            const itemDisplay = Array.isArray(row.items) ? row.items[0] ?? '—' : String(row.items ?? '—');
                                            return (
                                                <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '12px 12px', marginBottom: 5, boxShadow: C.shadow, overflow: 'hidden' }}>
                                                    {row.orderNo && <div style={{ fontSize: 10, fontWeight: 600, color: C.t2, marginBottom: 4 }}>#{row.orderNo}</div>}
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 3 }}>
                                                        <span style={{ fontSize: 12, fontWeight: 600, color: C.accent, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.staffName}</span>
                                                        <span style={{ fontSize: 12, fontWeight: 600, color: C.orange, flexShrink: 0 }}>{fmtCurrencyMobile(row.amount)}</span>
                                                    </div>
                                                    <div style={{ fontSize: 10, color: C.t2, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.cancelReason}</div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                                        <span style={{ fontSize: 11, fontWeight: 600, color: C.t1, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{itemDisplay !== '—' ? itemDisplay : ''}</span>
                                                        {displayTime && <span style={{ fontSize: 9, color: C.t3, padding: '2px 6px', borderRadius: 4, background: C.hover, flexShrink: 0 }}>{displayTime}</span>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {liveCompOrderLogTotalPagesM > 1 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.divider}` }}>
                                                <span style={{ fontSize: 10, color: C.t3 }}>
                                                    {compLogPageTodayM * COMP_PAGE_SIZE_M + 1}–{Math.min((compLogPageTodayM + 1) * COMP_PAGE_SIZE_M, liveCompOrderLogTotalElementsM)} of {liveCompOrderLogTotalElementsM}
                                                </span>
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <button onClick={() => setCompLogPageTodayM(p => Math.max(0, p - 1))} disabled={compLogPageTodayM === 0} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.cardBorder}`, background: 'none', cursor: compLogPageTodayM === 0 ? 'not-allowed' : 'pointer', opacity: compLogPageTodayM === 0 ? 0.4 : 1 }}>Prev</button>
                                                    <span style={{ fontSize: 10, color: C.t2, padding: '3px 4px' }}>{compLogPageTodayM + 1}/{liveCompOrderLogTotalPagesM}</span>
                                                    <button onClick={() => setCompLogPageTodayM(p => Math.min(liveCompOrderLogTotalPagesM - 1, p + 1))} disabled={compLogPageTodayM >= liveCompOrderLogTotalPagesM - 1} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.cardBorder}`, background: 'none', cursor: compLogPageTodayM >= liveCompOrderLogTotalPagesM - 1 ? 'not-allowed' : 'pointer', opacity: compLogPageTodayM >= liveCompOrderLogTotalPagesM - 1 ? 0.4 : 1 }}>Next</button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            );
            } // end else (data loaded)
        } else {
            // Historical comp drill — same Redux selectors, data fetched with startDate/endDate
            if (liveCompOrderLogLoadingM) {
                compDrillView = (
                    <div style={{ padding: '12px 16px 80px', flex: 1, overflowY: 'auto' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                            {['Total Comps', 'Total Value', 'Avg Comp', 'Top Reason'].map(label => (
                                <div key={label} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '10px 12px' }}>
                                    <div style={{ fontSize: 9, fontWeight: 600, color: C.t3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{label}</div>
                                    <Bone w={70} h={16} />
                                </div>
                            ))}
                        </div>
                        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Comp Reasons</div>
                            {[1, 2, 3, 4].map(i => <Bone key={i} w={'100%' as any} h={10} mb={8} />)}
                        </div>
                        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Comp by Staff</div>
                            {[1, 2, 3].map(i => <Bone key={i} w={'100%' as any} h={10} mb={8} />)}
                        </div>
                        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px' }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Comp Order Log</div>
                            {[1, 2, 3, 4, 5].map(i => <Bone key={i} w={'100%' as any} h={32} mb={8} />)}
                        </div>
                    </div>
                );
            } else if (liveCompOrderLogFailureM) {
                compDrillView = (
                    <div style={{ padding: '12px 16px', flex: 1, overflowY: 'auto' }}>
                        <SectionError
                            kpiName="complimentary orders"
                            size="medium"
                            onRetry={() => {
                                dispatch(fetchLiveCompOrderLogRequest({ locationId, page: compLogPageTodayM + 1, size: COMP_PAGE_SIZE_M, ...chDatesView, ...orderSourceParam }));
                                dispatch(fetchLiveCompReasonsRequest({ locationId, ...chDatesView, ...orderSourceParam }));
                                dispatch(fetchLiveCompByStaffRequest({ locationId, ...chDatesView, ...orderSourceParam }));
                            }}
                        />
                    </div>
                );
            } else {
                const histTotalM = liveCompOrderLogM.reduce((s, o) => s + (Number(o.amount) || 0), 0);
                const histCountM = liveCompOrderLogTotalElementsM;
                const avgCompHistM = histCountM > 0 ? histTotalM / histCountM : 0;
                const topReasonHistM = [...liveCompReasonsM].sort((a, b) => b.occurrenceCount - a.occurrenceCount)[0]?.cancelReason ?? '—';
                const maxOccurrenceHistM = liveCompReasonsM.reduce((m, r) => Math.max(m, r.occurrenceCount), 1);
                const alertStaffHistM = [...liveCompByStaffM].sort((a, b) => b.compCount - a.compCount).slice(0, 1).filter(s => s.compCount >= 5);
                const maxStaffValueHistM = liveCompByStaffM.reduce((m, s) => Math.max(m, s.totalCompValue), 1);
                compDrillView = (
                    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 80px' }}>
                        <div style={{ fontSize: 11, color: C.t3, marginBottom: 12 }}>
                            {fmtNumMobile(histCountM)} comps · {fmtCurrencyMobile(histTotalM)} total
                        </div>
                        {alertStaffHistM.map(s => (
                            <div key={s.staffName} style={{ background: `${C.orange ?? '#f97316'}18`, border: `1px solid ${(C.orange ?? '#f97316')}44`, borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.t1 }}>
                                <span style={{ fontWeight: 600, color: C.orange ?? '#f97316' }}>⚠ {s.staffName}</span> has applied {s.compCount} of {histCountM} comps ({fmtCurrencyMobile(s.totalCompValue)} total).
                            </div>
                        ))}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                            {[
                                { label: 'Total Comps', value: fmtNumMobile(histCountM) },
                                { label: 'Total Value', value: fmtCurrencyMobile(histTotalM) },
                                { label: 'Avg Comp', value: fmtCurrencyMobile(avgCompHistM) },
                                { label: 'Top Reason', value: liveCompReasonsLoadingM ? <Bone w={70} h={13} r={4} /> : topReasonHistM },
                            ].map(k => (
                                <div key={k.label} style={{ background: C.card, borderRadius: 10, padding: '10px 12px', border: `1px solid ${C.cardBorder}` }}>
                                    <div style={{ fontSize: 9, fontWeight: 600, color: C.t3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{k.label}</div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.value}</div>
                                </div>
                            ))}
                        </div>
                        {!liveCompOrderLogLoadingM && histCountM === 0 ? (
                            <SectionEmpty kpiName="complimentary orders" />
                        ) : (
                            <>
                                {/* Comp Reasons */}
                                <div style={{ background: C.card, borderRadius: 12, padding: '12px 14px', border: `1px solid ${C.cardBorder}`, marginBottom: 10 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Comp Reasons</div>
                                    {liveCompReasonsLoadingM ? (
                                        <div>
                                            {[1, 2, 3, 4].map(i => (
                                                <div key={i} style={{ marginBottom: 8 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                                        <Bone h={11} r={3} style={{ width: `${45 + i * 10}%` }} />
                                                        <Bone w={24} h={11} r={3} />
                                                    </div>
                                                    <Bone h={4} r={2} />
                                                </div>
                                            ))}
                                        </div>
                                    ) : liveCompReasonsFailureM ? (
                                        <SectionError kpiName="comp reasons" onRetry={() => dispatch(fetchLiveCompReasonsRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={60} />
                                    ) : liveCompReasonsM.length === 0 ? (
                                        <SectionEmpty kpiName="comp reasons" size="small" minHeight={60} />
                                    ) : liveCompReasonsM.slice(0, 6).map((r, i) => (
                                        <div key={i} style={{ marginBottom: 8 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                                <span style={{ fontSize: 11, color: C.t2 }}>{r.cancelReason}</span>
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <span style={{ fontSize: 10, color: C.t3 }}>{r.occurrenceCount}x</span>
                                                    <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{fmtCurrencyMobile(r.totalAmount)}</span>
                                                </div>
                                            </div>
                                            <div style={{ height: 4, background: C.hover ?? '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: `${Math.round((r.occurrenceCount / maxOccurrenceHistM) * 100)}%`, background: C.green ?? '#22c55e', borderRadius: 2, opacity: 0.7 }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {/* Comp by Staff */}
                                <div style={{ background: C.card, borderRadius: 12, padding: '12px 14px', border: `1px solid ${C.cardBorder}`, marginBottom: 10 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Comp by Staff</div>
                                    {liveCompByStaffLoadingM ? (
                                        <div>
                                            {[1, 2, 3, 4].map(i => (
                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < 4 ? `1px solid ${C.divider}` : 'none' }}>
                                                    <Bone h={12} r={3} style={{ width: `${45 + i * 10}%` }} />
                                                    <Bone w={50} h={12} r={3} />
                                                </div>
                                            ))}
                                        </div>
                                    ) : liveCompByStaffFailureM ? (
                                        <SectionError kpiName="comp by staff" onRetry={() => dispatch(fetchLiveCompByStaffRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={60} />
                                    ) : liveCompByStaffM.length === 0 ? (
                                        <SectionEmpty kpiName="comp by staff" size="small" minHeight={60} />
                                    ) : liveCompByStaffM.slice(0, 5).map((s, i) => (
                                        <div key={i} style={{ padding: '6px 0', borderBottom: i < liveCompByStaffM.slice(0, 5).length - 1 ? `1px solid ${C.divider}` : 'none' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                                <span style={{ fontSize: 12, color: C.accent, fontWeight: 500 }}>{s.staffName}</span>
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                    <span style={{ fontSize: 10, color: C.t3 }}>{s.compCount}x</span>
                                                    <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{fmtCurrencyMobile(s.totalCompValue)}</span>
                                                </div>
                                            </div>
                                            <div style={{ height: 3, background: C.hover ?? '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: `${Math.round((s.totalCompValue / maxStaffValueHistM) * 100)}%`, background: C.accent, borderRadius: 2, opacity: 0.45 }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {/* Comp Order Log */}
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Comp Order Log</div>
                                        {liveCompOrderLogM.length > 0 && (
                                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                                variant="mobile"
                                                title="Comp Order Log"
                                                filename="snapshot-comp-order-log"
                                                buildExport={async (format, opts) => {
                                                    const loc = opts?.locationId ?? locationId;
                                                    let total = liveCompOrderLogTotalElementsM || 1;
                                                    if (opts?.locationId) {
                                                        const probe: any = await getLiveCompOrderLogApi({ locationId: loc, page: 1, size: 1, ...chDatesView, ...orderSourceParam });
                                                        total = probe?.totalElements ?? probe?.total ?? 1;
                                                    }
                                                    const res: any = await getLiveCompOrderLogApi({ locationId: loc, page: 1, size: total || 1, ...chDatesView, ...orderSourceParam });
                                                    const list: any[] = Array.isArray(res?.orders) ? res.orders : (Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : liveCompOrderLogM));
                                                    return {
                                                        headers: ['Order ID', 'Date', 'Time', 'Staff', 'Reason', 'Items', 'Amount'],
                                                        rows: list.map((row: any) => {
                                                            const displayTime = (() => { const m = String(row.orderTime).match(/(\d{1,2}:\d{2}\s*(?:[AaPp][Mm])?)/); return m ? m[1].trim() : row.orderTime; })();
                                                            const itemDisplay = Array.isArray(row.items) ? row.items[0] ?? '—' : String(row.items ?? '—');
                                                            return [row.orderNo, row.orderDate ?? '—', displayTime, row.staffName, row.cancelReason, itemDisplay, formatMoneyForExport(row.amount)];
                                                        }),
                                                    };
                                                }}
                                            />
                                        )}
                                    </div>
                                    {liveCompOrderLogLoadingM ? (
                                        <div>
                                            {[1, 2, 3, 4].map(i => (
                                                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '8px 0', borderBottom: i < 4 ? `1px solid ${C.divider}` : 'none' }}>
                                                    <Bone w={50} h={11} r={3} />
                                                    <Bone h={11} r={3} style={{ flex: 1 }} />
                                                    <Bone w={45} h={11} r={3} />
                                                </div>
                                            ))}
                                        </div>
                                    ) : liveCompOrderLogFailureM ? (
                                        <SectionError kpiName="comp order log" onRetry={() => dispatch(fetchLiveCompOrderLogRequest({ locationId, page: compLogPageTodayM + 1, size: COMP_PAGE_SIZE_M, ...chDatesView, ...orderSourceParam }))} minHeight={80} />
                                    ) : liveCompOrderLogM.length === 0 ? (
                                        <SectionEmpty kpiName="comp order log" minHeight={80} />
                                    ) : (
                                        <>
                                            {liveCompOrderLogM.map((row, i) => {
                                                const displayTime = (() => { const m = String(row.orderTime).match(/(\d{1,2}:\d{2}\s*(?:[AaPp][Mm])?)/); return m ? m[1].trim() : row.orderTime; })();
                                                const itemDisplay = Array.isArray(row.items) ? row.items[0] ?? '—' : String(row.items ?? '—');
                                                return (
                                                    <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '12px 12px', marginBottom: 5, boxShadow: C.shadow, overflow: 'hidden' }}>
                                                        {row.orderNo && <div style={{ fontSize: 10, fontWeight: 600, color: C.t2, marginBottom: 4 }}>#{row.orderNo}</div>}
                                                        {(row as any).orderDate && <div style={{ fontSize: 10, color: C.t3, marginBottom: 4 }}>{(row as any).orderDate}</div>}
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 3 }}>
                                                            <span style={{ fontSize: 12, fontWeight: 600, color: C.accent, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.staffName}</span>
                                                            <span style={{ fontSize: 12, fontWeight: 600, color: C.orange, flexShrink: 0 }}>{fmtCurrencyMobile(row.amount)}</span>
                                                        </div>
                                                        <div style={{ fontSize: 10, color: C.t2, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.cancelReason}</div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                                            <span style={{ fontSize: 11, fontWeight: 600, color: C.t1, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{itemDisplay !== '—' ? itemDisplay : ''}</span>
                                                            {displayTime && <span style={{ fontSize: 9, color: C.t3, padding: '2px 6px', borderRadius: 4, background: C.hover, flexShrink: 0 }}>{displayTime}</span>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {liveCompOrderLogTotalPagesM > 1 && (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.divider}` }}>
                                                    <span style={{ fontSize: 10, color: C.t3 }}>
                                                        {compLogPageTodayM * COMP_PAGE_SIZE_M + 1}–{Math.min((compLogPageTodayM + 1) * COMP_PAGE_SIZE_M, liveCompOrderLogTotalElementsM)} of {liveCompOrderLogTotalElementsM}
                                                    </span>
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <button onClick={() => setCompLogPageTodayM(p => Math.max(0, p - 1))} disabled={compLogPageTodayM === 0} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.cardBorder}`, background: 'none', cursor: compLogPageTodayM === 0 ? 'not-allowed' : 'pointer', opacity: compLogPageTodayM === 0 ? 0.4 : 1 }}>Prev</button>
                                                        <span style={{ fontSize: 10, color: C.t2, padding: '3px 4px' }}>{compLogPageTodayM + 1}/{liveCompOrderLogTotalPagesM}</span>
                                                        <button onClick={() => setCompLogPageTodayM(p => Math.min(liveCompOrderLogTotalPagesM - 1, p + 1))} disabled={compLogPageTodayM >= liveCompOrderLogTotalPagesM - 1} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.cardBorder}`, background: 'none', cursor: compLogPageTodayM >= liveCompOrderLogTotalPagesM - 1 ? 'not-allowed' : 'pointer', opacity: compLogPageTodayM >= liveCompOrderLogTotalPagesM - 1 ? 0.4 : 1 }}>Next</button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                );
            }
        }

        const MStatCard = ({ label, value, color, sub }: { label: string; value: string; color?: string; sub?: string }) => (
            <div style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow }}>
                <div style={{ fontSize: 9, fontWeight: 600, color: C.t3, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>{label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: color ?? C.t1, letterSpacing: -0.5 }}>{value}</div>
                {sub && <div style={{ fontSize: 10, color: C.t3, marginTop: 2 }}>{sub}</div>}
            </div>
        );

        // Discount drill (mobile) — ClickHouse liveStaffPerformanceM for both Today and historical
        const totalDiscountsPfM = liveStaffPerformanceM.reduce((s, item) => s + (item.discount || 0), 0);
        const discOrdersPfM = liveStaffPerformanceM.reduce((s, item) => s + (item.orderCount || 0), 0);
        const totalGrandTotalPfM = liveStaffPerformanceM.reduce((s, item) => s + (item.grandTotal || 0), 0);
        const sortedByDiscountM = [...liveStaffPerformanceM].filter(s => s.discount > 0).sort((a, b) => b.discount - a.discount);
        const topDiscounterPfM: LiveStaffPerformanceItem | null = sortedByDiscountM[0] ?? null;
        const DISC_STAFF_PAGE_SIZE_M = 5;
        const discTotalPagesM = data.isToday ? 1 : Math.ceil(sortedByDiscountM.length / DISC_STAFF_PAGE_SIZE_M);
        const discSafePageM = data.isToday ? 0 : Math.min(discountsStaffPageM, Math.max(0, discTotalPagesM - 1));
        const discPageSliceM = data.isToday
            ? sortedByDiscountM.slice(0, DISC_STAFF_PAGE_SIZE_M)
            : sortedByDiscountM.slice(discSafePageM * DISC_STAFF_PAGE_SIZE_M, discSafePageM * DISC_STAFF_PAGE_SIZE_M + DISC_STAFF_PAGE_SIZE_M);
        const abbreviateDiscNameM = (name: string) => {
            const parts = name.trim().split(/\s+/);
            if (parts.length === 1) return parts[0];
            return `${parts[0]} ${parts[parts.length - 1][0]}.`;
        };
        // SNAP-DSC-1 — shared Discounts Drill-Down (identical in Snapshot & Sales).
        const discountsDrillMobile = (
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 14px 16px' }}>
                {liveStaffPerformanceLoadingM ? (
                    <>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                            {[1, 2].map(i => (
                                <div key={i} style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow }}>
                                    <Bone w={60} h={9} mb={8} />
                                    <Bone w={90} h={22} mb={6} />
                                    <Bone w={50} h={9} />
                                </div>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                            <div style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow }}>
                                <Bone w={60} h={9} mb={8} />
                                <Bone w={90} h={22} mb={6} />
                                <Bone w={50} h={9} />
                            </div>
                        </div>
                    </>
                ) : liveStaffPerformanceFailureM ? (
                    <SectionError kpiName="discounts" onRetry={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDatesView, ...orderSourceParam }))} size="small" minHeight={120} />
                ) : (
                    <>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                            <MStatCard label="Total Discounts" value={fmtCurrencyMobile(totalDiscountsPfM)} color={C.orange} sub={totalGrandTotalPfM > 0 ? `${((totalDiscountsPfM / totalGrandTotalPfM) * 100).toFixed(1)}% of Gross` : undefined} />
                            <MStatCard label="Disc. Orders" value={fmtNumMobile(discOrdersPfM)} sub={num(totalOrders) > 0 ? `${((discOrdersPfM / num(totalOrders)) * 100).toFixed(1)}% of all` : undefined} />
                        </div>
                    </>
                )}
                <DiscountBreakdownAndLog
                    sym={getCurrencySymbol()}
                    startISO={data.isToday ? today : (dateRange.startDate || today)}
                    endISO={data.isToday ? today : (dateRange.endDate || today)}
                    outletId={null}
                    locationId={locationId}
                    isToday={data.isToday}
                    orderSource={orderSourceVal || undefined}
                    token={token}
                    onOpenOrder={(r) => setUnpaidSelectedOrderM({ orderId: r.orderId, orderNo: r.orderNo } as any)}
                >
                    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', boxShadow: C.shadow, marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Discounts by Staff/ Platform</div>
                        {liveStaffPerformanceLoadingM ? (
                            <div>
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < 5 ? `1px solid ${C.divider}` : 'none' }}>
                                        <Bone w={22} h={22} r={6} />
                                        <div style={{ flex: 1 }}><Bone w={110} h={12} mb={4} /><Bone w={70} h={10} /></div>
                                        <div style={{ textAlign: 'right' }}><Bone w={56} h={13} mb={4} /><Bone w={80} h={9} /></div>
                                    </div>
                                ))}
                            </div>
                        ) : liveStaffPerformanceFailureM ? (
                            <SectionError kpiName="discounts by staff" onRetry={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDatesView, ...orderSourceParam }))} size="small" minHeight={80} />
                        ) : sortedByDiscountM.length ? (
                            <>
                                {discPageSliceM.map((s: LiveStaffPerformanceItem, i: number) => {
                                    const globalRankM = discSafePageM * DISC_STAFF_PAGE_SIZE_M + i;
                                    const pct = totalDiscountsPfM > 0 ? ((s.discount / totalDiscountsPfM) * 100).toFixed(1) : '0.0';
                                    return (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < discPageSliceM.length - 1 ? `1px solid ${C.divider}` : 'none' }}>
                                            <div style={{ width: 22, height: 22, borderRadius: 6, background: globalRankM === 0 ? C.orangeSoft : C.hover, color: globalRankM === 0 ? C.orange : C.t3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{globalRankM + 1}</div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13, color: C.t1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.staff_name}</div>
                                                <div style={{ fontSize: 11, color: C.t3, marginTop: 1 }}>{s.orderCount} orders · {pct}%</div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: C.orange }}>{fmtCurrencyMobile(s.discount)}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {!data.isToday && discTotalPagesM > 1 && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, paddingTop: 4 }}>
                                        <button onClick={() => setDiscountsStaffPageM((p) => Math.max(0, p - 1))} disabled={discSafePageM === 0} style={{ fontSize: 11, color: discSafePageM === 0 ? C.t3 : C.accent, background: 'none', border: `1px solid ${discSafePageM === 0 ? C.divider : C.accent}`, borderRadius: 4, cursor: discSafePageM === 0 ? 'default' : 'pointer', padding: '2px 8px' }}>← Prev</button>
                                        <span style={{ fontSize: 10, color: C.t3 }}>{discSafePageM + 1} / {discTotalPagesM}</span>
                                        <button onClick={() => setDiscountsStaffPageM((p) => Math.min(discTotalPagesM - 1, p + 1))} disabled={discSafePageM === discTotalPagesM - 1} style={{ fontSize: 11, color: discSafePageM === discTotalPagesM - 1 ? C.t3 : C.accent, background: 'none', border: `1px solid ${discSafePageM === discTotalPagesM - 1 ? C.divider : C.accent}`, borderRadius: 4, cursor: discSafePageM === discTotalPagesM - 1 ? 'default' : 'pointer', padding: '2px 8px' }}>Next →</button>
                                    </div>
                                )}
                                {sortedByDiscountM.length > 0 && (
                                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.t3 }}>
                                        <span>{sortedByDiscountM.length} staff</span>
                                        <span>Total: <span style={{ color: C.orange, fontWeight: 600 }}>{fmtCurrencyMobile(totalDiscountsPfM)}</span></span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <SectionEmpty kpiName="staff discounts" size="small" minHeight={80} />
                        )}
                    </div>
                </DiscountBreakdownAndLog>
            </div>
        );

        const drillMap = {
            'net-sales': <DrillNetSales key={refreshNonce} {...drillProps} />,
            orders: <DrillOrders key={refreshNonce} {...drillProps} />,
            refund: <DrillCancelled key={refreshNonce} {...drillProps} {...refundDrillProps} />,
            'cancelled-orders': (
                <CancelledOrdersDrill
                    surface="snapshot"
                    orders={cancelledOrdersM}
                    loading={cancelledOrdersLoadingM}
                    failure={cancelledOrdersFailureM}
                    onRetry={fetchCancelledOrdersM}
                    exportDateRangeLabel={exportDateRangeLabel}
                    fetchOrdersForExport={fetchCancelledOrdersForExportM}
                />
            ),
            staff: <DrillStaff key={refreshNonce} {...drillProps} />,
            voids: <DrillVoids
                {...drillProps}
                prefetchedVoidLog={voidLogRowsM}
                prefetchedVoidSummary={voidReasonRowsM}
                staffAlerts={voidStaffAlertsM}
                pagination={voidPaginationProps}
                voidReasonsLoading={liveVoidSummaryLoadingM}
                voidReasonsFailure={liveVoidSummaryFailureM}
                onRetryVoidReasons={() => dispatch(fetchLiveVoidSummaryRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal))}
                voidLogLoading={liveVoidDetailsLoadingM}
                voidLogFailure={liveVoidDetailsFailureM}
                onRetryVoidLog={() => dispatch(fetchLiveVoidDetailsRequest(locationId, 1, 1000, chDatesView.startDate, chDatesView.endDate, orderSourceVal))}
                staffVoids={voidByStaffM}
                staffVoidsLoading={voidByStaffLoadingM}
                staffVoidsFailure={voidByStaffFailureM}
                onRetryStaffVoids={() => dispatch(fetchVoidByStaffRequest(locationId, chDatesView.startDate, chDatesView.endDate, orderSourceVal))}
                voidKpis={voidKpisM}
                voidKpisLoading={voidKpisLoadingM}
                voidKpisFailure={voidKpisFailureM}
            />,
            tips: <DrillTips {...drillProps} />,
            complimentary: compDrillView,
            unpaid: UnpaidDrillMobile(),
            discounts: discountsDrillMobile,
        };
        return (
            <Phone>
                <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
                <TopBar variant="mobile" />
                <div ref={ptrScrollRef} style={{ flex: 1, overflowY: 'auto' }}>
                    {error && (
                        <div style={{ padding: '0 14px' }}>
                            <div className="snapshot-mobile-error" style={{ background: C.redSoft, border: `1px solid ${C.red}33`, color: C.red, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                                <span>{error}</span>
                                <button
                                    type="button"
                                    onClick={() => setRefreshNonce(n => n + 1)}
                                    style={{ background: 'none', border: `1px solid ${C.red}4D`, borderRadius: 6, padding: '3px 12px', fontSize: 11, fontWeight: 600, color: C.red, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                                >
                                    Try again
                                </button>
                            </div>
                        </div>
                    )}
                    {drillMap[drillView]}
                </div>
                <OrderDetailSlider
                    visible={(drillView === 'unpaid' || drillView === 'discounts') && !!unpaidSelectedOrderM}
                    onClose={() => { setUnpaidSelectedOrderM(null); dispatch(clearOrderDetail()); }}
                    orderNo={unpaidSelectedOrderM?.orderNo}
                    orderType={unpaidSelectedOrderM?.orderType}
                    loading={orderDetailLoadingM}
                    error={orderDetailErrorM}
                    onRetry={() => unpaidSelectedOrderM && dispatch(fetchOrderDetailRequest({ locationId, orderId: unpaidSelectedOrderM.orderId ?? '', ...(isHistoricalReportDateFilter(activeDateFilter) ? { type: 'historical' } : {}) }))}
                    detail={orderDetailDataM}
                    {...(() => {
                        // Fallback only — the slider derives the waterfall from `detail`.
                        // This carries the row's own table list through as well.
                        if (!unpaidSelectedOrderM) return {};
                        const row = toOrderLogRow(unpaidSelectedOrderM, 0);
                        return { financials: row.financials, tables: row.tableList };
                    })()}
                />
                <BottomNav />
            </Phone>
        );
    }

    // Stamp KPIs come from snapshot-details for BOTH Today and historical (SSE-18):
    // while it refetches, the `?? billedSrcM` fallback would show the previous range's
    // legacy values — key the shimmer off the ClickHouse loading state for all filters.
    const kpiLoadingM = loading || snapshotDetailsLoadingM;
    // USSE-3: comparison API is fetched for historical too (with startDate/endDate), so the
    // delta shimmer no longer gates on Today.
    const deltaLoadingM = kpiLoadingM || snapshotComparisonLoadingM;

    return (
        <Phone>
            <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
            <TopBar variant="mobile" />
            <div ref={ptrScrollRef} className="snapshot-mobile-scroll">
                {error && (
                    <div className="snapshot-mobile-error" style={{ background: C.redSoft, border: `1px solid ${C.red}33`, color: C.red, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <span>{error}</span>
                        <button
                            type="button"
                            onClick={() => setRefreshNonce(n => n + 1)}
                            style={{ background: 'none', border: `1px solid ${C.red}4D`, borderRadius: 6, padding: '3px 12px', fontSize: 11, fontWeight: 600, color: C.red, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                            Try again
                        </button>
                    </div>
                )}

                <div
                    onClick={() => {
                        // A fresh entry from the tile, not a return from a deduction drill —
                        // clear any stale scroll flag left over from an earlier such cycle.
                        scrollToNetSalesKpiRef.current = false;
                        setDrillView('net-sales');
                    }}
                    className="snapshot-mobile-hero"
                    style={{ background: `linear-gradient(135deg, ${C.accent}0D, ${C.purple}0A)`, border: `1px solid ${C.accent}22`, boxShadow: C.shadow }}
                >
                    <Arrow />
                    <div className="snapshot-mobile-hero-glow" style={{ background: C.accentSoft }} />
                    <div className="snapshot-mobile-hero-row">
                        <div className="snapshot-mobile-hero-left">
                            {/* Net Sales 2 */}
                            <div className="snapshot-mobile-hero-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Net Sales<KpiTooltip text="From Total collected after deducting tax, tips & gratuity. The revenue retained by you after all deductions." isMobile /></div>
                            <div className="snapshot-mobile-hero-value-row">
                                {kpiLoadingM ? <Bone w={110} h={28} mb={2} /> : <span style={{ fontSize: netSales >= 10000000 ? 20 : netSales >= 1000000 ? 24 : 30, fontWeight: 700, color: C.t1, letterSpacing: -1 }}>{fmtCurrencyMobile(netSales)}</span>}
                                {/* USSE-3: delta from the ClickHouse comparison API for Today AND historical */}
                                {(showDelta || activeDateFilter === 'Today') && (deltaLoadingM
                                    ? <Bone w={70} h={14} r={4} />
                                    : snapshotComparisonM != null ? (
                                        <span style={{ fontSize: 11, fontWeight: 600, color: snapshotComparisonM.netSales === 0 ? C.t2 : snapshotComparisonM.netSales >= 0 ? C.green : C.red }}>
                                            {snapshotComparisonM.netSales !== 0 && <>{snapshotComparisonM.netSales >= 0 ? '↑' : '↓'} {snapshotComparisonM.netSales >= 0 ? '+' : '-'}</>}{Math.abs(snapshotComparisonM.netSales).toFixed(2)}%
                                        </span>
                                    ) : null
                                )}
                            </div>
                            {/* GLOBAL-DP-4: the hero's own "vs yesterday $X" line is removed —
                                the comparison basis is stated once in the date control. */}
                        </div>
                        {data.isToday && (
                            <div className="snapshot-mobile-channel-wrap">
                                {liveByChannelLoadingM ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <Bone w={72} h={16} r={4} />
                                        <Bone w={72} h={16} r={4} />
                                    </div>
                                ) : liveByChannelFailureM ? (
                                    <div style={{ fontSize: 10, color: C.red, fontWeight: 600 }}>--</div>
                                ) : netSalesMobileChannels.every(ch => ch.netSales === 0) ? (
                                    <div style={{ fontSize: 10, color: C.t3 }}>No data</div>
                                ) : (
                                    netSalesMobileChannels.filter(ch => ch.netSales > 0).map(ch => (
                                        <div key={ch.label} className="snapshot-mobile-channel-row">
                                            {ch.label === 'Dine-In' ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 -960 960 960" fill={C.t3}>
                                                    <path d="M320-200h60v-270q26-8 43-28.5t17-49.5v-152q0-8-6-14t-14-6q-8 0-14 6t-6 14v100h-30v-100q0-8-6-14t-14-6q-8 0-14 6t-6 14v100h-30v-100q0-8-6-14t-14-6q-8 0-14 6t-6 14v152q0 29 17 49.5t43 28.5v270Zm240 0h60v-254q33-16 51.5-51t18.5-82q0-57-28.5-95T590-720q-43 0-71.5 38T490-587q0 47 18.5 82t51.5 51v254ZM160-80q-33 0-56.5-23.5T80-160v-640q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v640q0 33-23.5 56.5T800-80H160Zm0-80h640v-640H160v640Zm0 0v-640 640Z" />
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 -960 960 960" fill={C.t3}>
                                                    <path d="M120-640v-200h280v200H120Zm80-80h120v-40H200v40Zm-5 485q-35-35-35-85H80v-120q0-66 47-113t113-47h160v200h140l140-174v-106H560v-80h120q33 0 56.5 23.5T760-680v134L580-320H400q0 50-35 85t-85 35q-50 0-85-35Zm113.5-56.5Q320-303 320-320h-80q0 17 11.5 28.5T280-280q17 0 28.5-11.5ZM675-235q-35-35-35-85t35-85q35-35 85-35t85 35q35 35 35 85t-35 85q-35 35-85 35t-85-35Zm113.5-56.5Q800-303 800-320t-11.5-28.5Q777-360 760-360t-28.5 11.5Q720-337 720-320t11.5 28.5Q743-280 760-280t28.5-11.5ZM160-400h160v-120h-80q-33 0-56.5 23.5T160-440v40Zm160-320v-40 40Zm0 320Z" />
                                                </svg>
                                            )}
                                            <div style={{ fontSize: ch.netSales >= 1000000 ? 12 : 14, fontWeight: 700, color: C.t1 }}>
                                                {fmtCurrencyChannel(ch.netSales)}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {(() => {
                    // ZAR-2091 — mobile stamp KPIs re-ordered into 5 rows of 3:
                    // Paid/Unpaid/Avg, Cancelled/Refund/Void, Tips&Grat/Discount/Comp, Staff/Guests/Spend-per-guest.
                    const kpiCards = [
                        <KPI key="orders" label="Paid orders" value={kpiLoadingM ? <Bone w={55} h={20} /> : fmtNumMobile(totalOrders)} delta={(data.isToday || showDelta) && snapshotComparisonM?.orderCount != null ? `${snapshotComparisonM.orderCount >= 0 ? '+' : ''}${snapshotComparisonM.orderCount.toFixed(2)}%` : null} deltaLoading={(data.isToday || showDelta) && deltaLoadingM} up={snapshotComparisonM?.orderCount != null ? snapshotComparisonM.orderCount >= 0 : true} drill={true} onClick={() => setDrillView('orders')} />,
                        <KPI key="unpaid" label="Unpaid orders" value={liveUnpaidOrdersLoadingM ? <Bone w={55} h={20} /> : liveUnpaidOrdersFailureM ? '0' : fmtNumMobile(displayUnpaidCountM)} subtext={fmtCurrencyMobile(displayUnpaidValueM)} subtextLoading={liveUnpaidOrdersLoadingM || allUnpaidForByServerLoadingM} drill={true} onClick={() => setDrillView('unpaid')} />,
                        <KPI key="avgOrder" label="Avg Order" value={kpiLoadingM ? <Bone w={55} h={20} /> : fmtCurrencyMobile(avgOrder)} delta={(data.isToday || showDelta) && snapshotComparisonM?.avgOrderValue != null ? `${snapshotComparisonM.avgOrderValue >= 0 ? '+' : ''}${snapshotComparisonM.avgOrderValue.toFixed(2)}%` : null} deltaLoading={(data.isToday || showDelta) && deltaLoadingM} up={snapshotComparisonM?.avgOrderValue != null ? snapshotComparisonM.avgOrderValue >= 0 : true} drill={false} />,
                        // Service Charge — revenue-side, sits right after Avg Order and deliberately
                        // away from Tips & Grat. Only for outlets that capture one (0 included). Not drillable.
                        serviceChargeM && <KPI
                            key="serviceCharge"
                            label={SERVICE_CHARGE_LABEL}
                            tooltip={serviceChargeTooltip(serviceChargeM.retained)}
                            isMobile
                            value={kpiLoadingM ? <Bone w={55} h={20} /> : fmtServiceChargeMobile(serviceChargeM.amount)}
                            delta={(data.isToday || showDelta) && serviceChargePctM != null ? `${serviceChargePctM >= 0 ? '+' : ''}${serviceChargePctM.toFixed(2)}%` : null}
                            subtext={(data.isToday || showDelta) && !deltaLoadingM && serviceChargePctM == null ? '—' : null}
                            deltaLoading={(data.isToday || showDelta) && deltaLoadingM}
                            up={serviceChargePctM == null ? true : serviceChargePctM >= 0}
                            drill={false}
                        />,
                        // CANC-1 — Cancelled orders (mock). Shown under every source; the count
                        // recalculates for the active one. Delta is inverted: fewer cancellations reads green.
                        <KPI key="cancelledOrders" label="Cancelled" value={cancelledOrdersLoadingM ? <Bone w={55} h={20} /> : cancelledOrdersFailureM ? '0' : fmtNumMobile(cancelledOrdersM.length)} delta={(data.isToday || showDelta) && snapshotComparisonM?.cancelledOrders != null ? `${snapshotComparisonM.cancelledOrders >= 0 ? '+' : ''}${snapshotComparisonM.cancelledOrders.toFixed(2)}%` : null} deltaLoading={(data.isToday || showDelta) && deltaLoadingM} up={snapshotComparisonM?.cancelledOrders != null ? snapshotComparisonM.cancelledOrders < 0 : false} cancelledOrdersArrow drill={true} onClick={() => setDrillView('cancelled-orders')} />,
                        <KPI key="refund" label="Refund" tooltip={REFUND_KPI_TOOLTIP} isMobile value={kpiLoadingM ? <Bone w={55} h={20} /> : fmtCurrencyMobile(refundStampTotalM)} delta={(data.isToday || showDelta) && snapshotComparisonM?.refund != null ? `${snapshotComparisonM.refund >= 0 ? '+' : ''}${snapshotComparisonM.refund.toFixed(2)}%` : null} deltaLoading={(data.isToday || showDelta) && deltaLoadingM} up={snapshotComparisonM?.refund != null ? snapshotComparisonM.refund < 0 : false} cancelledOrdersArrow drill={true} onClick={() => navigate(refundsPath({ origin: 'snapshot', dateFilter: activeDateFilter, startDate: dateRange.startDate, endDate: dateRange.endDate, source: orderSourceVal === 'direct' ? 'direct' : orderSourceVal === 'thirdparty' ? 'thirdparty' : 'overall' }))} />,
                        // Void, Comp, Staff Worked, Guests are not relevant to 3rd-party orders — hidden under that filter (filter(Boolean) reflows the grid).
                        !isThirdParty && <KPI key="voids" label="Voided" tooltip={VOIDED_ITEMS_KPI_TOOLTIP} isMobile value={liveVoidSummaryLoadingM ? <Bone w={55} h={20} /> : liveVoidSummaryFailureM ? '0' : fmtNumMobile(voidedItemsCountM)} subtext={!liveVoidSummaryLoadingM && !liveVoidSummaryFailureM ? `${fmtCurrencyMobile(voidAmount)} · ${fmtNumMobile(voidCount)} ${voidCount === 1 ? 'order' : 'orders'}` : null} delta={(data.isToday || showDelta) && !liveVoidSummaryFailureM && voidDeltaPctM != null ? `${voidDeltaPctM >= 0 ? '+' : ''}${voidDeltaPctM.toFixed(2)}%` : null} deltaLoading={(data.isToday || showDelta) && liveVoidSummaryLoadingM} up={voidDeltaPctM == null ? false : voidDeltaPctM < 0} cancelledOrdersArrow drill={true} onClick={() => setDrillView('voids')} />,
                        <KPI key="tips" label={TG_CARD_LABEL} value={kpiLoadingM ? <Bone w={55} h={20} /> : fmtCurrencyMobile(tipsAndGratuityM)} delta={(data.isToday || showDelta) && tipsGratuityPctM != null ? `${tipsGratuityPctM >= 0 ? '+' : ''}${tipsGratuityPctM.toFixed(2)}%` : null} deltaLoading={(data.isToday || showDelta) && deltaLoadingM} up={tipsGratuityPctM != null ? tipsGratuityPctM >= 0 : true} drill={true} onClick={() => setDrillView('tips')} />,
                        <KPI key="discounts" label="Discounts" value={kpiLoadingM ? <Bone w={55} h={20} /> : fmtCurrencyMobile(discounts)} delta={(data.isToday || showDelta) && snapshotComparisonM?.discount != null ? `${snapshotComparisonM.discount >= 0 ? '+' : ''}${snapshotComparisonM.discount.toFixed(2)}%` : null} deltaLoading={(data.isToday || showDelta) && deltaLoadingM} up={snapshotComparisonM?.discount != null ? snapshotComparisonM.discount >= 0 : true} drill={true} onClick={() => setDrillView('discounts')} />,
                        !isThirdParty && <KPI key="compl" label="Comp" value={liveCompOrderLogLoadingM ? <Bone w={55} h={20} /> : liveCompOrderLogFailureM ? '0' : fmtNumMobile(compCount)} delta={(data.isToday || showDelta) && snapshotComparisonM?.compleOrder != null ? `${snapshotComparisonM.compleOrder >= 0 ? '+' : ''}${snapshotComparisonM.compleOrder.toFixed(2)}%` : null} deltaLoading={(data.isToday || showDelta) && deltaLoadingM} up={snapshotComparisonM?.compleOrder != null ? snapshotComparisonM.compleOrder >= 0 : true} drill={true} onClick={() => setDrillView('complimentary')} />,
                        // Staff Worked — /sales/live/v2/staff-active-clocking-v2 (staffWorked field), Today + historical.
                        !isThirdParty && <StatKPI key="staffWorked" label="Staff Worked" value={staffRosterV2LoadingM ? <Bone w={55} h={20} /> : staffRosterV2FailureM ? '0' : fmtNumMobile(staffRosterV2StaffWorkedM)} drill={true} onClick={() => setDrillView('staff')} />,
                        // Guests — value from snapshot-details guestCount, % delta from snapshot-details/comparison (Today + historical); not relevant to 3rd-party orders — hidden under that filter
                        !isThirdParty && <KPI key="guests" label="Guests" value={kpiLoadingM ? <Bone w={55} h={20} /> : fmtNumMobile(guests)} delta={(data.isToday || showDelta) && snapshotComparisonM?.guestCount != null ? `${snapshotComparisonM.guestCount >= 0 ? '+' : ''}${snapshotComparisonM.guestCount.toFixed(2)}%` : null} deltaLoading={(data.isToday || showDelta) && deltaLoadingM} up={snapshotComparisonM?.guestCount != null ? snapshotComparisonM.guestCount >= 0 : true} drill={false} />,
                        // SPG-1: Spend/Guest — value from snapshot-details spendPerGuest; not hidden under 3rd party
                        // (unlike Guests) since "—" is the expected/valid state there rather than an omission.
                        <KPI key="spendPerGuest" label="Spend / Guest" value={kpiLoadingM ? <Bone w={55} h={20} /> : spendPerGuest == null ? '—' : fmtCurrencyMobile(spendPerGuest)} delta={(data.isToday || showDelta) && snapshotComparisonM != null && snapshotComparisonM.spendPerGuest != null ? `${snapshotComparisonM.spendPerGuest >= 0 ? '+' : ''}${snapshotComparisonM.spendPerGuest.toFixed(2)}%` : null} deltaLoading={(data.isToday || showDelta) && deltaLoadingM} up={snapshotComparisonM != null ? snapshotComparisonM.spendPerGuest >= 0 : true} drill={false} />,
                        // USSE-1: Check-Ins/Guests hidden in historical view
                        // !data.isToday && !isThirdParty && <StatKPI key="checkIns" label="Check-Ins" value={kpiLoadingM ? <Bone w={55} h={20} /> : fmtNumMobile(checkInCount)} drill={false} />,
                        // !data.isToday && !isThirdParty && <StatKPI key="guests" label="Guests" value={kpiLoadingM ? <Bone w={55} h={20} /> : fmtNumMobile(guestCount)} drill={false} />,
                    ].filter(Boolean);
                    const rows = [];
                    for (let i = 0; i < kpiCards.length; i += 3) rows.push(kpiCards.slice(i, i + 3));
                    return rows.map((row, ri) => (
                        <div key={ri} className={`snapshot-mobile-grid${ri === rows.length - 1 ? ' snapshot-mobile-grid-last' : ''}`}>
                            {row}
                        </div>
                    ));
                })()}

                {/* Hourly Revenue — Today and Historical (unified) */}
                <div className="snapshot-mobile-top-items-card" style={{ background: C.card, border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow }}>
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>Hourly Revenue</div>
                        <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>Revenue distribution across service hours</div>
                    </div>
                    <HourlyBarChart
                        data={hourlyRevenueChartDataM}
                        loading={hourlySalesLoadingM}
                        error={hourlySalesFailureM}
                        onRetry={() => dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, ...orderSourceParam }))}
                        kpiName="hourly revenue"
                        barColor={T.accent}
                        chartHeight={100}
                        formatValue={(v) => {
                            const n = Number(v) || 0;
                            const sym = getCurrencySymbol();
                            if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`;
                            if (n >= 1_000) return `${sym}${(n / 1_000).toFixed(0)}K`;
                            return `${sym}${n.toFixed(0)}`;
                        }}
                        showCurrency={true}
                        currencySymbol={getCurrencySymbol()}
                        minHeight={160}
                        avgOverride={avgRevenuePerHourM}
                    />
                </div>

                {/* Sales by Channel / Order Source — Today + historical (mobile). marginTop/Bottom: its
                    neighbours (Hourly Revenue, Items Overview) carry no margin, so it sat flush against both (SBS-05). */}
                <div className="snapshot-mobile-top-items-card" style={{ background: C.card, border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow, marginTop: 12, marginBottom: 12 }}>
                    {/* Sales by Channel / Order Source (ZAR-2371) — one shared card; Channel|Source toggle persisted in Redux */}
                    <SalesByChannelSourceCard
                        variant="mobile"
                        items={liveByChannelM}
                        loading={liveByChannelLoadingM}
                        error={liveByChannelFailureM}
                        onRetry={() => dispatch(fetchLiveByChannelRequest({ locationId, ...chDatesView, ...orderSourceParam }))}
                        locationId={locationId}
                        startDate={chDatesView.startDate}
                        endDate={chDatesView.endDate}
                        orderSource={orderSourceParam.orderSource}
                        formatMoney={fmtCurrencyMobileFull}
                        formatCenter={fmtCurrencyMobile}
                        comparisonHint={comparisonTooltip(activeDateFilter, storeTz)}
                        exportDateRangeLabel={exportDateRangeLabel}
                        exportFilename="snapshot-sales-by"
                        fetchOutletItems={async (outletId, view) => {
                            const res: any = await getLiveByChannelApi({ locationId: outletId, ...chDatesView, ...orderSourceParam, ...(view === 'source' ? { type: 'source' as const } : {}), token });
                            return Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
                        }}
                        countryCode={branch?.country ?? branch?.countryCode ?? branch?.countryCd}
                    />
                </div>

                {/* Items Overview */}
                {!loading && (
                    <div className="snapshot-mobile-top-items-card" style={{ background: C.card, border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow }}>
                        <SnapshotItemsOverview
                            variant="mobile"
                            tk={C}
                            title="Items Overview"
                            titleInfo={REPORT_BASIS_NOTE}
                            exportSlot={(disabled, fetchAll) => !disabled && (
                                <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                    variant="mobile"
                                    title="Items Overview"
                                    filename="items_overview"
                                    buildExport={async (_format, opts) => {
                                        const res = await fetchAll(opts?.locationId);
                                        const items = mapApiResponseToExportItems(res, true);
                                        return {
                                            headers: ['#', 'Item', 'Qty', 'Revenue'],
                                            rows: items.map((item) => [String(item.rank), item.name, String(item.qty), formatMoneyForExport(item.rev)]),
                                        };
                                    }}
                                />
                            )}
                            startDate={activeDateFilter === 'Today' ? undefined : dateRange.startDate}
                            endDate={activeDateFilter === 'Today' ? undefined : dateRange.endDate}
                            datasetKey={`${activeDateFilter === 'Today'}|${dateRange.startDate}|${dateRange.endDate}|${channel}`}
                            userId={auth?.userId ?? ''}
                            outletId={locationId}
                            token={token}
                            orderSource={orderSourceVal}
                            fmtQty={fmtQtyMobile}
                            fmtCurrencyCell={fmtCurrencyCellMobile}
                            tracker={itemsTracker}
                        />
                    </div>
                )}
            {/* Staff Sales Leaderboard — Historical (mobile) */}
            {!data.isToday && (
                <div className="snapshot-mobile-top-items-card" style={{ background: C.card, border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow, marginTop: 12 }}>
                    <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>Staff Sales Leaderboard</div>
                        <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>{activeDateFilter === 'Yesterday' ? "Yesterday's top performers" : activeDateFilter === '7D' ? 'Last 7 days top performers' : activeDateFilter === '30D' ? 'Last 30 days top performers' : activeDateFilter === 'This Month' ? "This month's top performers" : 'Top performers'}</div>
                    </div>
                    {(loading || liveStaffPerformanceLoadingM) && !staffLeaderboardM.length ? (
                        <>
                            {Array.from({ length: STAFF_LEADER_PAGE_SIZE_M }).map((_, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: i < STAFF_LEADER_PAGE_SIZE_M - 1 ? `1px solid ${C.divider}` : 'none' }}>
                                    <Bone w={18} h={18} r={5} />
                                    <Bone h={11} r={4} style={{ flex: 1 }} />
                                    <Bone w={52} h={11} r={4} />
                                </div>
                            ))}
                        </>
                    ) : staffLeaderboardM.length ? (() => {
                        const totalPagesS = Math.ceil(staffLeaderboardM.length / STAFF_LEADER_PAGE_SIZE_M);
                        const safePageS = Math.min(staffLeaderPageM, totalPagesS - 1);
                        const pageItemsS = staffLeaderboardM.slice(safePageS * STAFF_LEADER_PAGE_SIZE_M, (safePageS + 1) * STAFF_LEADER_PAGE_SIZE_M);
                        return (
                            <>
                                {pageItemsS.map((item: any, index: number) => {
                                    const rank = safePageS * STAFF_LEADER_PAGE_SIZE_M + index;
                                    return (
                                        <div key={`${item.name}-${rank}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: index < pageItemsS.length - 1 ? `1px solid ${C.divider}` : 'none' }}>
                                            <div style={{ width: 18, height: 18, borderRadius: 5, background: rank === 0 ? C.accentSoft : C.hover, color: rank === 0 ? C.accent : C.t3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>{rank + 1}</div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                                                {(item.role || item.hoursLabel) && <div style={{ fontSize: 9, color: C.t2, fontWeight: 600 }}>{item.role}{item.role && item.hoursLabel ? ' · ' : ''}{item.hoursLabel}</div>}
                                            </div>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>{fmtCurrency(item.sales)}</div>
                                        </div>
                                    );
                                })}
                                {totalPagesS > 1 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.divider}` }}>
                                        <div style={{ fontSize: 10, color: C.t3 }}>Showing {safePageS * STAFF_LEADER_PAGE_SIZE_M + 1}–{Math.min((safePageS + 1) * STAFF_LEADER_PAGE_SIZE_M, staffLeaderboardM.length)} of {staffLeaderboardM.length}</div>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            <button onClick={() => setStaffLeaderPageM((p) => Math.max(0, p - 1))} disabled={safePageS === 0} style={{ padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.cardBorder}`, background: safePageS === 0 ? C.hover : C.card, color: safePageS === 0 ? C.t3 : C.t1, fontSize: 10, fontWeight: 600, cursor: safePageS === 0 ? 'default' : 'pointer' }}>← Prev</button>
                                            <span style={{ fontSize: 10, color: C.t2, padding: '3px 4px' }}>{safePageS + 1} / {totalPagesS}</span>
                                            <button onClick={() => setStaffLeaderPageM((p) => Math.min(totalPagesS - 1, p + 1))} disabled={safePageS >= totalPagesS - 1} style={{ padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.cardBorder}`, background: safePageS >= totalPagesS - 1 ? C.hover : C.card, color: safePageS >= totalPagesS - 1 ? C.t3 : C.t1, fontSize: 10, fontWeight: 600, cursor: safePageS >= totalPagesS - 1 ? 'default' : 'pointer' }}>Next →</button>
                                        </div>
                                    </div>
                                )}
                            </>
                        );
                    })() : <SectionEmpty kpiName="staff leaderboard" minHeight={120} />}
                </div>
            )}
            {/* Sales by Tender Type — mobile (ClickHouse Redux) */}
            {(() => {
                const sortedPaymentsM = [...livePaymentsM].sort((a, b) => b.tenderAmount - a.tenderAmount);
                const sharesM = distribute100At2dp(sortedPaymentsM.map((item) => item.tenderAmount));
                return (
                    <div className="snapshot-mobile-top-items-card" style={{ background: C.card, border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow, marginTop: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 2 }}>Sales by Tender Type</div>
                                <div style={{ fontSize: 9, color: C.t3, marginBottom: 10 }}>Payment method breakdown</div>
                            </div>
                            {sortedPaymentsM.length > 0 && (
                                <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                    variant="mobile"
                                    title="Sales by Tender Type"
                                    filename="snapshot-sales-by-tender-type"
                                    buildExport={async (format, opts) => {
                                        let payments = sortedPaymentsM;
                                        if (opts?.locationId) {
                                            const res: any = await getLivePaymentsApi({ locationId: opts.locationId, ...chDatesView, ...orderSourceParam, token });
                                            const list: any[] = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
                                            payments = [...list].sort((a: any, b: any) => b.tenderAmount - a.tenderAmount);
                                        }
                                        const paymentShares = distribute100At2dp(payments.map((item: any) => item.tenderAmount));
                                        return {
                                            headers: ['Type', 'Transactions', 'Amount', 'Share'],
                                            rows: payments.map((item: LivePaymentsItem, i: number) => [item.tenderType.toUpperCase(), item.transactionCount, formatMoneyForExport(item.tenderAmount), `${paymentShares[i].toFixed(2)}%`]),
                                        };
                                    }}
                                />
                            )}
                        </div>
                        {livePaymentsLoadingM ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {[1, 2, 3].map((i) => <Bone key={i} w={null} h={12} style={{ flex: 1 }} />)}
                            </div>
                        ) : livePaymentsFailureM ? (
                            <SectionError kpiName="sales by tender type" onRetry={() => dispatch(fetchLivePaymentsRequest({ locationId, ...chDatesView, ...orderSourceParam }))} minHeight={140} />
                        ) : sortedPaymentsM.length === 0 ? (
                            <SectionEmpty kpiName="sales by tender type" minHeight={140} />
                        ) : (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                                    {['TYPE', 'TXN', 'AMOUNT', 'SHARE'].map((h, ci) => (
                                        <span key={h} style={{ ...(ci === 0 ? { flex: 1 } : { width: [36, 72, 52][ci - 1], flexShrink: 0 }), fontSize: 8, fontWeight: 600, color: C.t3, textAlign: ci === 0 ? 'left' : 'right', letterSpacing: 0.4 }}>{h}</span>
                                    ))}
                                </div>
                                {sortedPaymentsM.map((item: LivePaymentsItem, i: number) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '7px 0', borderTop: `1px solid ${C.divider}` }}>
                                        <span style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 600, color: C.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 6 }}>{item.tenderType.toUpperCase()}</span>
                                        <span style={{ width: 36, flexShrink: 0, fontSize: 11, color: C.t2, textAlign: 'right' }}>{item.transactionCount}</span>
                                        <span style={{ width: 72, flexShrink: 0, fontSize: 11, fontWeight: 600, color: C.accent, textAlign: 'right' }}>{fmtCurrencyMobileFull(item.tenderAmount)}</span>
                                        <span style={{ width: 52, flexShrink: 0, fontSize: 11, color: C.t2, textAlign: 'right' }}>{sharesM[i].toFixed(2)}%</span>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                );
            })()}
            </div>
            <BottomNav />
        </Phone>
    );
};

export default function SnapshotScreen() {
    const isDesktop = useIsDesktop();
    return isDesktop ? <DesktopLayout><DesktopSnapshotScreen /></DesktopLayout> : <MobileSnapshotScreen />;
}


